import { useState, useEffect, useCallback, useRef } from "react";
import {
  LayoutDashboard, Receipt, FilePlus2, Zap, Package, Users, Wallet,
  BarChart3, FileBadge, Settings as SettingsIcon, Shield, LogOut,
  AlertCircle, Loader2, Menu, RefreshCw, TrendingUp,
  CircleCheck, Euro, ChevronLeft, ChevronRight,
  User as UserIcon, ArrowUpRight,
  Search, X, Download, Filter as FilterIcon, ChevronsUpDown,
  ChevronUp, ChevronDown, Building2, Calendar,
  ExternalLink, Clock,
  Plus, Edit3, Trash2, Power, RotateCcw,
  FileText, Send, ArrowRight,
  CreditCard, Banknote,
  Image as ImageIcon, Mail, Upload,
} from "lucide-react";

// ════════════════════════════════════════════════════════════════════════════
// API BASE (mirrors POS resolver — file:// for Electron, localhost dev, else prod)
// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
// API endpoint resolution by hostname:
//   - localhost          → local XAMPP/MAMP dev
//   - *.efis.cloud       → TEST environment (efis.cloud points at test DB/server)
//   - everything else    → PROD (efis.pro)
//
// Routing by hostname rather than a build-time env var means the same compiled
// JS works on both domains. Deploy the same build/ folder to b2b.efis.pro and
// b2b.efis.cloud — each calls its own backend automatically.
// ════════════════════════════════════════════════════════════════════════════
const API_BASE = (() => {
  const h = window.location.hostname, p = window.location.port, pr = window.location.protocol;
  if (pr === "file:") return "https://efis.pro/api";
  if (h === "localhost" && p) return "http://localhost/fiscalization/api";
  if (h === "localhost" && !p) return "http://localhost/fiscalization/api";
  // TEST environment — matches efis.cloud and any subdomain (b2b.efis.cloud, etc)
  if (h === "efis.cloud" || h.endsWith(".efis.cloud")) return "https://efis.cloud/api";
  return "https://efis.pro/api";
})();

// ════════════════════════════════════════════════════════════════════════════
// LocalStorage keys (mirrors POS naming: pos_token, self_token)
// ════════════════════════════════════════════════════════════════════════════
const TOKEN_KEY = "b2b_token";
const USER_KEY  = "b2b_user";

// Map BuyerIDType → user-facing label. Mirrors B2bInvoicePDF::buyerIdLabel().
// "TIN" or any unknown value falls back to "PIB" so legacy invoices that
// never had buyer_id_type set show the familiar label.
function buyerIdLabel(idType) {
  switch (String(idType || "").toUpperCase()) {
    case "ID":   return "JMBG";
    case "PASS": return "Pasoš";
    case "VAT":  return "VAT ID";
    case "TAX":  return "Tax ID";
    case "TIN":
    default:     return "PIB";
  }
}

// ════════════════════════════════════════════════════════════════════════════
// API helper — Bearer auth, 401 auto-logout, 8s timeout
// ════════════════════════════════════════════════════════════════════════════
async function api(path, opts = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  const headers = { "Content-Type": "application/json", ...(opts.headers || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  try {
    const fetchPromise = fetch(`${API_BASE}${path}`, { ...opts, headers });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error("Timeout")), 8000)
    );
    const r = await Promise.race([fetchPromise, timeoutPromise]);

    if (r.status === 401) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      window.location.reload();
      return { success: false };
    }
    return await r.json();
  } catch (e) {
    return { success: false, message: "Network error: " + e.message };
  }
}

// ════════════════════════════════════════════════════════════════════════════
// Design tokens (same palette + fonts as POS)
// ════════════════════════════════════════════════════════════════════════════
const S = {
  font: "'DM Sans', -apple-system, sans-serif",
  mono: "'JetBrains Mono', monospace",
  bg: "#f6f6f7",
  card: "#ffffff",
  dark: "#1a1a2e",
  accent: "#4f46e5",
  accentSoft: "#eef2ff",
  green: "#059669",
  red: "#dc2626",
  orange: "#ea580c",
  amber: "#d97706",
  blue: "#0284c7",
  border: "#e5e7eb",
  borderSoft: "#f1f1f4",
  muted: "#6b7280",
  mutedLight: "#9ca3af",
  input: {
    width: "100%", padding: "12px 14px", border: "1px solid #e5e7eb",
    borderRadius: 8, fontSize: 15, outline: "none", background: "#f9fafb",
    fontFamily: "'DM Sans', sans-serif", boxSizing: "border-box",
    transition: "border-color 0.15s, background 0.15s",
  },
  btn: (bg, color = "white") => ({
    padding: "12px 20px", background: bg, color, border: "none",
    borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer",
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    gap: 6, fontFamily: "'DM Sans', sans-serif",
    transition: "filter 0.15s, transform 0.05s",
  }),
  btnSm: (bg, color = "white") => ({
    padding: "9px 16px", background: bg, color, border: "none",
    borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 5,
    fontFamily: "'DM Sans', sans-serif",
  }),
};

// ════════════════════════════════════════════════════════════════════════════
// Formatters
// ════════════════════════════════════════════════════════════════════════════
function fmtMoney(n, currency = "EUR") {
  const v = parseFloat(n || 0);
  return v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " " + (currency === "EUR" ? "€" : currency);
}
function fmtNum(n) {
  return parseFloat(n || 0).toLocaleString("de-DE");
}

// Defensive HTML entity decoder.
// Why this exists: the legacy backend `sanitize()` method HTML-encodes user input
// before storage (htmlspecialchars). So old DB rows have `S&amp;A` instead of `S&A`.
// React renders text as-is and does NOT auto-decode entities, so without this
// helper, users see literal `&amp;` `&quot;` `&lt;` strings.
//
// We use the browser's own HTML parser (a one-line textarea trick) — handles every
// named entity correctly (&aacute;, &Scaron;, etc.) without bundling a library.
// New data saved via the patched backend stores raw UTF-8; decode is a no-op on it.
const _decodeEl = typeof document !== "undefined" ? document.createElement("textarea") : null;
function decodeHtml(s) {
  if (s == null) return "";
  const str = String(s);
  if (str.indexOf("&") === -1) return str; // fast path — no entities
  if (!_decodeEl) return str;
  _decodeEl.innerHTML = str;
  return _decodeEl.value;
}
// Alias for use throughout templates
const dh = decodeHtml;
function fmtDate(s) {
  if (!s) return "";
  const d = new Date(s.includes("T") ? s : s.replace(" ", "T"));
  if (isNaN(d.getTime())) return s;
  const day = String(d.getDate()).padStart(2, "0");
  const mon = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}.${mon}.${d.getFullYear()}.`;
}
function fmtDateTime(s) {
  if (!s) return "";
  const d = new Date(s.includes("T") ? s : s.replace(" ", "T"));
  if (isNaN(d.getTime())) return s;
  const day = String(d.getDate()).padStart(2, "0");
  const mon = String(d.getMonth() + 1).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${day}.${mon}.${d.getFullYear()} ${hh}:${mm}`;
}

// ════════════════════════════════════════════════════════════════════════════
// Responsive hook — detect mobile (<768px)
// ════════════════════════════════════════════════════════════════════════════
function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const onResize = () => setMobile(window.innerWidth < 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return mobile;
}

// ════════════════════════════════════════════════════════════════════════════
// LOGIN SCREEN
// ════════════════════════════════════════════════════════════════════════════
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e?.preventDefault();
    if (loading) return;
    setLoading(true);
    setError("");
    const data = await api("/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password, app: "webapp" }),
    });
    setLoading(false);
    if (data.success) {
      onLogin(data.data.token, data.data.operator);
    } else {
      setError(data.message || "Prijava neuspješna");
    }
  };

  return (
    <div style={{
      minHeight: "100vh", background: S.bg, display: "flex",
      alignItems: "center", justifyContent: "center", padding: 16,
      fontFamily: S.font,
    }}>
      <div style={{
        width: "100%", maxWidth: 420, background: S.card, borderRadius: 16,
        boxShadow: "0 4px 24px rgba(0,0,0,0.06)", padding: "40px 32px",
        border: `1px solid ${S.border}`,
      }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{
            display: "inline-flex", width: 56, height: 56, borderRadius: 14,
            background: S.accentSoft, alignItems: "center", justifyContent: "center",
            marginBottom: 14,
          }}>
            <FileBadge size={28} color={S.accent} />
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: S.dark, margin: 0 }}>
            EFIS.PRO <span style={{ color: S.accent, fontWeight: 600 }}>B2B</span>
          </h1>
          <p style={{ color: S.muted, fontSize: 14, marginTop: 6, marginBottom: 0 }}>
            Bezgotovinski računi
          </p>
        </div>

        <form onSubmit={submit} autoComplete="on">
          <label style={labelStyle}>Email</label>
          <input
            type="email" value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="operator@firma.me"
            autoComplete="email"
            style={{ ...S.input, marginBottom: 14 }}
          />
          <label style={labelStyle}>Lozinka</label>
          <input
            type="password" value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="••••••••"
            autoComplete="current-password"
            onKeyDown={(e) => e.key === "Enter" && submit(e)}
            style={{ ...S.input, marginBottom: 20 }}
          />

          {error && (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8,
              padding: "10px 14px", marginBottom: 14, display: "flex",
              alignItems: "center", gap: 8,
            }}>
              <AlertCircle size={15} color={S.red} />
              <span style={{ color: S.red, fontSize: 14 }}>{error}</span>
            </div>
          )}

          <button
            type="submit" disabled={loading}
            style={{
              ...S.btn(S.dark), width: "100%", padding: "14px",
              fontSize: 16, opacity: loading ? 0.7 : 1, borderRadius: 10,
            }}
          >
            {loading ? <><Loader2 size={16} className="spin" /> Prijava...</> : "Prijavi se"}
          </button>
        </form>

        <div style={{
          marginTop: 24, paddingTop: 20, borderTop: `1px solid ${S.borderSoft}`,
          textAlign: "center", fontSize: 12, color: S.mutedLight,
        }}>
          EFIS.PRO &copy; 2026 &middot; BISHA D.O.O.
        </div>
      </div>
    </div>
  );
}

const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600,
  color: S.muted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1,
};

// ════════════════════════════════════════════════════════════════════════════
// NAVIGATION CONFIG
// ════════════════════════════════════════════════════════════════════════════
const NAV_ITEMS = [
  { id: "dashboard",      label: "Kontrolna tabla", icon: LayoutDashboard, ready: true  },
  { id: "new-invoice",    label: "Novi račun",      icon: FilePlus2,       ready: true  },
  { id: "invoices",       label: "Računi",          icon: Receipt,         ready: true  },
  { id: "proformas",      label: "Predračuni",      icon: FileText,        ready: true  },
  { id: "brzi-virman",    label: "Brzi virman",     icon: Zap,             ready: true  },
  { id: "products",       label: "Proizvodi",       icon: Package,         ready: true  },
  { id: "partners",       label: "Partneri",        icon: Users,           ready: true  },
  { id: "payments",       label: "Uplate",          icon: CreditCard,      ready: true  },
  { id: "expenses",       label: "Troškovi",        icon: Wallet,          ready: true  },
  { id: "reports",        label: "Izvještaji",      icon: BarChart3,       ready: true  },
  { id: "pdf-config",     label: "PDF šabloni",     icon: FileBadge,       ready: true  },
  { id: "settings",       label: "Podešavanja",     icon: SettingsIcon,    ready: true  },
];

const MOBILE_NAV = [
  { id: "dashboard",   label: "Tabla",    icon: LayoutDashboard, ready: true  },
  { id: "new-invoice", label: "Novi",     icon: FilePlus2,       ready: true  },
  { id: "invoices",    label: "Računi",   icon: Receipt,         ready: true  },
  { id: "partners",    label: "Partneri", icon: Users,           ready: true  },
  { id: "menu",        label: "Više",     icon: Menu,            ready: true  },
];

// ════════════════════════════════════════════════════════════════════════════
// DESKTOP SIDEBAR
// ════════════════════════════════════════════════════════════════════════════
function Sidebar({ view, setView, me, onLogout, collapsed, setCollapsed }) {
  const isSuperadmin = !!me?.permissions?.is_superadmin;
  const width = collapsed ? 76 : 248;

  return (
    <aside style={{
      width, flexShrink: 0, background: S.card,
      borderRight: `1px solid ${S.border}`, height: "100vh",
      position: "sticky", top: 0, display: "flex", flexDirection: "column",
      transition: "width 0.2s",
    }} className="no-select">
      {/* Brand */}
      <div style={{
        padding: "20px 18px", borderBottom: `1px solid ${S.borderSoft}`,
        display: "flex", alignItems: "center", gap: 10,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, background: S.accentSoft,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <FileBadge size={20} color={S.accent} />
        </div>
        {!collapsed && (
          <div style={{ overflow: "hidden" }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: S.dark, lineHeight: 1.2 }}>
              EFIS.PRO
            </div>
            <div style={{ fontSize: 11, color: S.muted, fontWeight: 500 }}>B2B</div>
          </div>
        )}
      </div>

      {/* Nav items */}
      <nav style={{ flex: 1, overflowY: "auto", padding: "12px 10px" }}>
        {NAV_ITEMS.map((item) => (
          <NavBtn
            key={item.id}
            item={item}
            active={view === item.id}
            onClick={() => item.ready && setView(item.id)}
            collapsed={collapsed}
          />
        ))}
        {isSuperadmin && (
          <>
            <div style={{
              height: 1, background: S.borderSoft, margin: "12px 8px",
            }} />
            <NavBtn
              item={{ id: "superadmin", label: "Superadmin", icon: Shield, ready: true }}
              active={view === "superadmin"}
              onClick={() => setView("superadmin")}
              collapsed={collapsed}
              accent
            />
          </>
        )}
      </nav>

      {/* User card */}
      <div style={{ padding: 10, borderTop: `1px solid ${S.borderSoft}` }}>
        {!collapsed ? (
          <div style={{
            display: "flex", alignItems: "center", gap: 10,
            padding: "10px 8px", borderRadius: 8,
          }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, background: S.accentSoft,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
            }}>
              <UserIcon size={17} color={S.accent} />
            </div>
            <div style={{ overflow: "hidden", flex: 1 }}>
              <div style={{
                fontSize: 13, fontWeight: 600, color: S.dark,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {me?.operator?.name || "—"}
              </div>
              <div style={{
                fontSize: 11, color: S.muted,
                whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
              }}>
                {me?.company?.name || "—"}
              </div>
            </div>
            <button onClick={onLogout} title="Odjavi se" style={{
              background: "none", border: "none", cursor: "pointer", padding: 6,
              color: S.muted, borderRadius: 6,
            }}>
              <LogOut size={16} />
            </button>
          </div>
        ) : (
          <button onClick={onLogout} title="Odjavi se" style={{
            width: "100%", padding: 10, background: "none", border: "none",
            cursor: "pointer", color: S.muted, display: "flex", justifyContent: "center",
          }}>
            <LogOut size={18} />
          </button>
        )}

        <button
          onClick={() => setCollapsed(!collapsed)}
          style={{
            width: "100%", padding: 6, background: "none", border: "none",
            cursor: "pointer", color: S.mutedLight, display: "flex",
            justifyContent: "center", marginTop: 4, borderRadius: 6,
          }}
          title={collapsed ? "Proširi" : "Suzi"}
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </div>
    </aside>
  );
}

function NavBtn({ item, active, onClick, collapsed, accent }) {
  const Icon = item.icon;
  const enabled = item.ready;
  const bg     = active ? (accent ? "#fef3c7" : S.accentSoft) : "transparent";
  const color  = active ? (accent ? S.amber : S.accent) : enabled ? S.dark : S.mutedLight;
  return (
    <button
      onClick={enabled ? onClick : undefined}
      disabled={!enabled}
      title={collapsed ? item.label : undefined}
      style={{
        width: "100%", display: "flex", alignItems: "center",
        gap: 12, padding: collapsed ? "11px 0" : "11px 12px",
        justifyContent: collapsed ? "center" : "flex-start",
        background: bg, color, border: "none", borderRadius: 8,
        fontSize: 14, fontWeight: active ? 600 : 500,
        cursor: enabled ? "pointer" : "not-allowed",
        textAlign: "left", marginBottom: 2, fontFamily: S.font,
        opacity: enabled ? 1 : 0.6,
      }}
    >
      <Icon size={18} />
      {!collapsed && (
        <>
          <span style={{ flex: 1 }}>{item.label}</span>
          {!enabled && (
            <span style={{
              fontSize: 10, padding: "2px 6px", background: S.borderSoft,
              color: S.mutedLight, borderRadius: 4, fontWeight: 600,
            }}>uskoro</span>
          )}
        </>
      )}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// MOBILE BOTTOM-TAB NAV
// ════════════════════════════════════════════════════════════════════════════
function MobileNav({ view, setView, openMoreMenu }) {
  return (
    <div style={{
      position: "fixed", left: 0, right: 0, bottom: 0,
      background: S.card, borderTop: `1px solid ${S.border}`,
      display: "flex", padding: "6px 0 calc(6px + env(safe-area-inset-bottom))",
      zIndex: 50, fontFamily: S.font,
    }}>
      {MOBILE_NAV.map((item) => {
        const Icon = item.icon;
        const active = view === item.id;
        const onTap = () => {
          if (item.id === "menu") return openMoreMenu();
          if (item.ready) setView(item.id);
        };
        return (
          <button
            key={item.id}
            onClick={onTap}
            disabled={!item.ready && item.id !== "menu"}
            style={{
              flex: 1, background: "none", border: "none",
              display: "flex", flexDirection: "column", alignItems: "center",
              gap: 3, padding: "4px 0", cursor: "pointer",
              color: active ? S.accent : S.muted,
              opacity: !item.ready && item.id !== "menu" ? 0.45 : 1,
            }}
          >
            <Icon size={20} />
            <span style={{ fontSize: 10, fontWeight: active ? 700 : 500 }}>
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MobileMoreMenu({ open, onClose, setView, me, onLogout }) {
  const isSuperadmin = !!me?.permissions?.is_superadmin;
  if (!open) return null;
  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 100,
      display: "flex", alignItems: "flex-end",
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="slide-up"
        style={{
          width: "100%", background: S.card, borderTopLeftRadius: 16,
          borderTopRightRadius: 16, maxHeight: "85vh", overflowY: "auto",
          padding: "8px 12px 28px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", padding: "8px 0" }}>
          <div style={{ width: 40, height: 4, background: S.border, borderRadius: 2 }} />
        </div>
        <div style={{ padding: "8px 6px 16px" }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: S.dark }}>
            {me?.operator?.name || "—"}
          </div>
          <div style={{ fontSize: 12, color: S.muted }}>
            {me?.company?.name || "—"} {me?.operator?.role && ` · ${me.operator.role}`}
          </div>
        </div>
        <div style={{ height: 1, background: S.borderSoft, marginBottom: 8 }} />

        {NAV_ITEMS.filter(n => !MOBILE_NAV.find(m => m.id === n.id)).map((item) => (
          <button
            key={item.id}
            onClick={() => { if (item.ready) { setView(item.id); onClose(); } }}
            disabled={!item.ready}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 14,
              padding: "14px 8px", background: "none", border: "none",
              borderBottom: `1px solid ${S.borderSoft}`, fontSize: 15,
              color: item.ready ? S.dark : S.mutedLight, cursor: "pointer",
              textAlign: "left", fontFamily: S.font,
            }}
          >
            <item.icon size={20} color={item.ready ? S.accent : S.mutedLight} />
            <span style={{ flex: 1 }}>{item.label}</span>
            {!item.ready && (
              <span style={{
                fontSize: 10, padding: "2px 6px", background: S.borderSoft,
                color: S.mutedLight, borderRadius: 4, fontWeight: 600,
              }}>uskoro</span>
            )}
          </button>
        ))}

        {isSuperadmin && (
          <button
            onClick={() => { setView("superadmin"); onClose(); }}
            style={{
              width: "100%", display: "flex", alignItems: "center", gap: 14,
              padding: "14px 8px", background: "none", border: "none",
              borderBottom: `1px solid ${S.borderSoft}`, fontSize: 15,
              color: S.dark, cursor: "pointer", textAlign: "left",
              fontFamily: S.font,
            }}
          >
            <Shield size={20} color={S.amber} />
            <span style={{ flex: 1 }}>Superadmin</span>
          </button>
        )}

        <button
          onClick={onLogout}
          style={{
            width: "100%", display: "flex", alignItems: "center", gap: 14,
            padding: "14px 8px", background: "none", border: "none",
            fontSize: 15, color: S.red, cursor: "pointer", textAlign: "left",
            marginTop: 8, fontFamily: S.font,
          }}
        >
          <LogOut size={20} />
          <span>Odjavi se</span>
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// TOP HEADER (mobile + desktop)
// ════════════════════════════════════════════════════════════════════════════
function TopHeader({ title, subtitle, right, isMobile }) {
  return (
    <div style={{
      background: S.card, borderBottom: `1px solid ${S.border}`,
      padding: isMobile ? "14px 16px" : "16px 28px",
      display: "flex", alignItems: "center", gap: 12,
      position: "sticky", top: 0, zIndex: 20,
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h1 style={{
          fontSize: isMobile ? 18 : 22, fontWeight: 700, color: S.dark,
          margin: 0, lineHeight: 1.2,
        }}>{title}</h1>
        {subtitle && (
          <div style={{ fontSize: 13, color: S.muted, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      {right}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// DASHBOARD SCREEN
// ════════════════════════════════════════════════════════════════════════════
const PERIOD_OPTIONS = [
  { id: "today", label: "Danas" },
  { id: "week",  label: "7 dana" },
  { id: "month", label: "Mjesec" },
  { id: "year",  label: "Godina" },
];

function Dashboard({ isMobile }) {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const r = await api(`/b2b/dashboard?period=${period}`);
    setLoading(false);
    if (r.success) setData(r.data);
    else setError(r.message || "Greška pri učitavanju");
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const kpis = data?.kpis || {};
  const vat      = Array.isArray(data?.vat_breakdown)   ? data.vat_breakdown   : [];
  const timeline = Array.isArray(data?.timeline)        ? data.timeline        : [];
  const top      = Array.isArray(data?.top_partners)    ? data.top_partners    : [];
  const recent   = Array.isArray(data?.recent_invoices) ? data.recent_invoices : [];

  return (
    <>
      <TopHeader
        title="Kontrolna tabla"
        subtitle={
          data?.date_from && data?.date_to
            ? `${fmtDate(data.date_from)} — ${fmtDate(data.date_to)}`
            : "Pregled bezgotovinskog prometa"
        }
        isMobile={isMobile}
        right={
          <button
            onClick={load}
            disabled={loading}
            title="Osvježi"
            style={{
              ...S.btnSm("white", S.muted),
              border: `1px solid ${S.border}`,
              padding: "8px 10px",
            }}
          >
            <RefreshCw size={14} className={loading ? "spin" : ""} />
            {!isMobile && (loading ? "Učitavam" : "Osvježi")}
          </button>
        }
      />

      <div style={{ padding: isMobile ? 16 : 28, maxWidth: 1400 }}>
        {/* Period selector */}
        <div style={{
          display: "flex", gap: 8, marginBottom: 20, overflowX: "auto",
          paddingBottom: 4,
        }} className="hide-scrollbar">
          {PERIOD_OPTIONS.map((p) => (
            <PeriodPill
              key={p.id}
              active={period === p.id}
              onClick={() => setPeriod(p.id)}
              label={p.label}
            />
          ))}
        </div>

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca",
            borderRadius: 10, padding: "14px 18px", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* KPI Grid */}
        <div style={{
          display: "grid",
          gridTemplateColumns: isMobile
            ? "repeat(2, 1fr)"
            : "repeat(auto-fit, minmax(240px, 1fr))",
          gap: 14, marginBottom: 22,
        }}>
          <KpiCard
            icon={Receipt} color={S.accent}
            label="Broj računa" value={fmtNum(kpis.count_total)}
            loading={loading}
          />
          <KpiCard
            icon={Euro} color={S.green}
            label="Ukupno" value={fmtMoney(kpis.amount_total)}
            loading={loading}
          />
          <KpiCard
            icon={CircleCheck} color={S.blue}
            label="Naplaćeno" value={fmtMoney(kpis.amount_paid)}
            loading={loading}
          />
          <KpiCard
            icon={TrendingUp} color={S.orange}
            label="Potraživanja" value={fmtMoney(kpis.amount_outstanding)}
            loading={loading}
          />
        </div>

        {/* Status badges row */}
        <div style={{
          display: "grid", gap: 10, marginBottom: 22,
          gridTemplateColumns: isMobile
            ? "repeat(3, 1fr)"
            : "repeat(auto-fit, minmax(150px, 1fr))",
        }}>
          <BadgeStat color={S.green} label="Fiskalizovano" value={kpis.count_fiscalized} loading={loading} />
          <BadgeStat color={S.amber} label="Nacrti"       value={kpis.count_draft}      loading={loading} />
          <BadgeStat color={S.red}   label="Neuspjeli"    value={kpis.count_failed}     loading={loading} />
          <BadgeStat color={S.muted} label="Stornirani"   value={kpis.count_storno}     loading={loading} />
          <BadgeStat color={S.blue}  label="Korektivni"   value={kpis.count_corrective} loading={loading} />
          <BadgeStat color={S.accent} label="Avansni"     value={kpis.count_advance}    loading={loading} />
        </div>

        {/* 2-column: VAT breakdown + Timeline */}
        <div style={{
          display: "grid", gap: 14,
          gridTemplateColumns: isMobile ? "1fr" : "minmax(280px, 1fr) 2fr",
          marginBottom: 22,
        }}>
          <Card title="PDV razrada">
            {loading && !vat.length ? <Skeleton lines={3} /> : vat.length === 0 ? (
              <Empty text="Nema podataka za period" />
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
                <thead>
                  <tr>
                    <th style={th}>Stopa</th>
                    <th style={{ ...th, textAlign: "right" }}>Osnovica</th>
                    <th style={{ ...th, textAlign: "right" }}>PDV</th>
                  </tr>
                </thead>
                <tbody>
                  {vat.map((v, i) => (
                    <tr key={i} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                      <td style={td}>
                        <span style={{
                          background: S.accentSoft, color: S.accent,
                          padding: "3px 8px", borderRadius: 4, fontSize: 12,
                          fontWeight: 600,
                        }}>{parseFloat(v.vat_rate || 0)}%</span>
                      </td>
                      <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>
                        {fmtMoney(v.subtotal || v.net || v.base || 0)}
                      </td>
                      <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>
                        {fmtMoney(v.vat_amount || 0)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card title="Dnevni promet" subtitle={`${timeline.length} dana sa prometom`}>
            {loading && !timeline.length ? <Skeleton lines={4} /> : timeline.length === 0 ? (
              <Empty text="Nema prometa za period" />
            ) : (
              <Timeline data={timeline} />
            )}
          </Card>
        </div>

        {/* Top partners */}
        <Card title="Najveći partneri" style={{ marginBottom: 22 }}>
          {loading && !top.length ? <Skeleton lines={3} /> : top.length === 0 ? (
            <Empty text="Nema fiskalizovanih računa za period" />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 480 }}>
                <thead>
                  <tr>
                    <th style={th}>Partner</th>
                    <th style={{ ...th, textAlign: "right" }}>PIB</th>
                    <th style={{ ...th, textAlign: "right" }}>Br. računa</th>
                    <th style={{ ...th, textAlign: "right" }}>Iznos</th>
                  </tr>
                </thead>
                <tbody>
                  {top.map((p) => (
                    <tr key={p.id} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                      <td style={{ ...td, fontWeight: 600 }}>{dh(p.name)}</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: S.mono, color: S.muted }}>
                        {p.tax_id || "—"}
                      </td>
                      <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>
                        {fmtNum(p.invoice_count)}
                      </td>
                      <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>
                        {fmtMoney(p.total_amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Recent invoices */}
        <Card title="Poslednji računi" subtitle="Posljednjih 10 bezgotovinskih">
          {loading && !recent.length ? <Skeleton lines={5} /> : recent.length === 0 ? (
            <Empty text="Još nema izdatih računa" />
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 600 }}>
                <thead>
                  <tr>
                    <th style={th}>Broj</th>
                    <th style={th}>Datum</th>
                    <th style={th}>Kupac</th>
                    <th style={{ ...th, textAlign: "right" }}>Iznos</th>
                    <th style={{ ...th, textAlign: "right" }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.id} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                      <td style={{ ...td, fontFamily: S.mono, fontWeight: 600 }}>
                        {r.display_no || "—"}
                        {r.is_storno      ? <Tag color={S.muted} text="STORNO" /> : null}
                        {r.is_corrective  ? <Tag color={S.blue}  text="KOR" /> : null}
                        {r.is_advance     ? <Tag color={S.accent} text="AVANS" /> : null}
                      </td>
                      <td style={{ ...td, color: S.muted }}>{fmtDateTime(r.invoice_date)}</td>
                      <td style={td}>{dh(r.buyer_name) || <span style={{ color: S.mutedLight }}>—</span>}</td>
                      <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>
                        {fmtMoney(r.total_amount)}
                      </td>
                      <td style={{ ...td, textAlign: "right" }}>
                        <StatusBadge status={r.status} paid={r.is_paid} isStorno={r.is_storno} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {isMobile && <div style={{ height: 80 }} />}
      </div>
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Dashboard subcomponents
// ────────────────────────────────────────────────────────────────────────────
function PeriodPill({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 18px", border: `1px solid ${active ? S.accent : S.border}`,
      background: active ? S.accent : "white", color: active ? "white" : S.muted,
      borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer",
      whiteSpace: "nowrap", fontFamily: S.font,
    }}>
      {label}
    </button>
  );
}

function KpiCard({ icon: Icon, label, value, color, loading }) {
  return (
    <div style={{
      background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
      padding: 18,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
        <div style={{
          width: 38, height: 38, borderRadius: 10,
          background: `${color}18`, display: "flex",
          alignItems: "center", justifyContent: "center",
        }}>
          <Icon size={18} color={color} />
        </div>
        <span style={{ fontSize: 13, color: S.muted, fontWeight: 500 }}>{label}</span>
      </div>
      <div style={{
        fontSize: 22, fontWeight: 700, color: S.dark,
        fontFamily: S.mono, minHeight: 28,
      }}>
        {loading ? <span className="pulse" style={{ display: "inline-block", width: 100, height: 22, background: S.borderSoft, borderRadius: 4 }} /> : value}
      </div>
    </div>
  );
}

function BadgeStat({ color, label, value, loading, numericOverride }) {
  return (
    <div style={{
      background: S.card, border: `1px solid ${S.border}`,
      borderRadius: 10, padding: "12px 14px",
      display: "flex", alignItems: "center", gap: 10,
    }}>
      <div style={{
        width: 8, height: 8, borderRadius: 4, background: color, flexShrink: 0,
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: S.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.3 }}>
          {label}
        </div>
        <div style={{ fontSize: 17, fontWeight: 700, color: S.dark, fontFamily: S.mono }}>
          {loading ? "—" : (numericOverride != null ? numericOverride : fmtNum(value || 0))}
        </div>
      </div>
    </div>
  );
}

function Card({ title, subtitle, children, style }) {
  return (
    <div style={{
      background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
      overflow: "hidden", ...style,
    }}>
      <div style={{
        padding: "16px 20px", borderBottom: `1px solid ${S.borderSoft}`,
      }}>
        <div style={{ fontSize: 15, fontWeight: 700, color: S.dark }}>{title}</div>
        {subtitle && (
          <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>{subtitle}</div>
        )}
      </div>
      <div style={{ padding: "12px 20px 16px" }}>{children}</div>
    </div>
  );
}

function Timeline({ data }) {
  const max = Math.max(...data.map((d) => d.amount), 1);
  // Selected bar index (sticky on click/tap). null = none.
  const [selected, setSelected] = useState(null);
  // Hovered bar index (transient). On touch devices this is generally null.
  const [hovered, setHovered] = useState(null);

  const activeIdx = hovered ?? selected; // hover wins; falls back to selected
  const activeBar = activeIdx !== null ? data[activeIdx] : null;

  // Clicking outside any bar clears the pinned selection.
  const handleContainerClick = (e) => {
    if (e.target.dataset?.bar === undefined) setSelected(null);
  };

  return (
    <div onClick={handleContainerClick}>
      {/* Tooltip / details strip — always reserves space so layout doesn't jump */}
      <div style={{
        minHeight: 50, padding: "8px 12px", marginBottom: 8,
        background: activeBar ? S.accentSoft : "transparent",
        border: `1px solid ${activeBar ? S.accent + "55" : "transparent"}`,
        borderRadius: 8, fontSize: 13,
        display: "flex", alignItems: "center", gap: 12,
        transition: "all 0.15s",
      }}>
        {activeBar ? (
          <>
            <Calendar size={14} color={S.accent} />
            <span style={{ color: S.dark, fontWeight: 600, fontFamily: S.mono }}>
              {fmtDate(activeBar.day)}
            </span>
            <span style={{ color: S.muted }}>·</span>
            <span style={{ color: S.dark, fontWeight: 700, fontFamily: S.mono }}>
              {fmtMoney(activeBar.amount)}
            </span>
            <span style={{ color: S.muted, fontSize: 12 }}>
              ({fmtNum(activeBar.count)} {activeBar.count === 1 ? "račun" : "računa"})
            </span>
          </>
        ) : (
          <span style={{ color: S.mutedLight, fontSize: 12, fontStyle: "italic" }}>
            Kliknite ili dodirnite stupac za detalje
          </span>
        )}
      </div>

      <div style={{
        display: "flex", alignItems: "flex-end", gap: 4,
        height: 140, marginTop: 6,
      }}>
        {data.map((d, i) => {
          const h = Math.max(2, (d.amount / max) * 130);
          const isActive = activeIdx === i;
          return (
            <div
              key={i}
              data-bar="1"
              onClick={(e) => {
                e.stopPropagation();
                setSelected(selected === i ? null : i);
              }}
              onMouseEnter={() => setHovered(i)}
              onMouseLeave={() => setHovered(null)}
              title={`${fmtDate(d.day)}: ${fmtMoney(d.amount)} (${d.count} računa)`}
              style={{
                flex: 1, minWidth: 6, height: h,
                background: isActive
                  ? `linear-gradient(180deg, ${S.dark} 0%, ${S.accent} 100%)`
                  : `linear-gradient(180deg, ${S.accent} 0%, ${S.accent}99 100%)`,
                borderRadius: "3px 3px 0 0",
                cursor: "pointer",
                transition: "background 0.15s, filter 0.15s",
                filter: isActive ? "brightness(1.1)" : "none",
              }}
            />
          );
        })}
      </div>
      <div style={{
        display: "flex", justifyContent: "space-between", marginTop: 8,
        fontSize: 11, color: S.mutedLight, fontFamily: S.mono,
      }}>
        <span>{data.length > 0 ? fmtDate(data[0].day) : ""}</span>
        <span>{data.length > 0 ? fmtDate(data[data.length - 1].day) : ""}</span>
      </div>
    </div>
  );
}

function Tag({ color, text }) {
  return (
    <span style={{
      display: "inline-block", marginLeft: 6, padding: "1px 6px",
      background: `${color}18`, color, borderRadius: 4, fontSize: 10,
      fontWeight: 700, fontFamily: S.font, verticalAlign: "middle",
    }}>{text}</span>
  );
}

function StatusBadge({ status, paid, isStorno }) {
  let bg, color, text;
  // Storno + Plaćeno is a contradiction: a stornoed invoice has been cancelled,
  // so even if paid_amount is technically equal to total, we don't show "Plaćeno"
  // on it. The STORNO tag elsewhere conveys the actual state.
  if (status === "fiscalized") {
    if (paid && !isStorno) {
      bg = "#dcfce7"; color = S.green; text = "Plaćeno";
    } else {
      bg = S.accentSoft; color = S.accent; text = "Fiskalizovano";
    }
  } else if (status === "draft") {
    bg = "#fef3c7"; color = S.amber; text = "Nacrt";
  } else if (status === "failed") {
    bg = "#fee2e2"; color = S.red; text = "Neuspjelo";
  } else {
    bg = S.borderSoft; color = S.muted; text = status || "—";
  }
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", background: bg, color,
      borderRadius: 12, fontSize: 11, fontWeight: 600, fontFamily: S.font,
    }}>
      {text}
    </span>
  );
}

function Empty({ text }) {
  return (
    <div style={{
      padding: "24px 12px", textAlign: "center", color: S.mutedLight,
      fontSize: 13,
    }}>
      {text}
    </div>
  );
}

function Skeleton({ lines = 3 }) {
  return (
    <div style={{ padding: "8px 0" }}>
      {Array.from({ length: lines }).map((_, i) => (
        <div
          key={i}
          className="pulse"
          style={{
            height: 14, background: S.borderSoft, borderRadius: 4,
            marginBottom: 10, width: i === lines - 1 ? "60%" : "100%",
          }}
        />
      ))}
    </div>
  );
}

const th = {
  padding: "10px 12px", textAlign: "left", fontWeight: 600,
  color: S.muted, fontSize: 12, textTransform: "uppercase",
  letterSpacing: 0.3, borderBottom: `1px solid ${S.border}`,
};
const td = {
  padding: "12px", color: S.dark, fontSize: 14,
};

// ════════════════════════════════════════════════════════════════════════════
// PDF DOWNLOAD HELPER (uses existing /api/invoices/pdf/:id endpoint)
// ════════════════════════════════════════════════════════════════════════════
// ════════════════════════════════════════════════════════════════════════════
// FILE DOWNLOAD HELPERS
// Browsers can't send Bearer tokens via <a href> so we fetch + Blob + click.
// ════════════════════════════════════════════════════════════════════════════
async function downloadExport(path, filename) {
  const token = localStorage.getItem(TOKEN_KEY);
  try {
    const r = await fetch(`${API_BASE}${path}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) {
      const text = await r.text().catch(() => "");
      // Try to parse as JSON error
      let msg = `HTTP ${r.status}`;
      try {
        const json = JSON.parse(text);
        if (json.message) msg = json.message;
      } catch {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return { success: true };
  } catch (e) {
    return { success: false, message: e.message || "Greška pri preuzimanju" };
  }
}

async function downloadInvoicePdf(invoiceId, displayNo) {
  const token = localStorage.getItem(TOKEN_KEY);
  try {
    const r = await fetch(`${API_BASE}/b2b/invoice-pdf/${invoiceId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) {
      const text = await r.text().catch(() => "");
      throw new Error(text || `HTTP ${r.status}`);
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `racun-${(displayNo || invoiceId).toString().replace(/[^\w-]+/g, "_")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return { success: true };
  } catch (e) {
    return { success: false, message: e.message || "Greška pri preuzimanju" };
  }
}

// Mirror of downloadInvoicePdf for proformas — hits /b2b/proforma-pdf/:id which
// returns the same multi-template PDF but with title "PREDRAČUN" and the
// "interni dokument" notice (no Poreska info).
async function downloadProformaPdf(proformaId, proformaNumber) {
  const token = localStorage.getItem(TOKEN_KEY);
  try {
    const r = await fetch(`${API_BASE}/b2b/proforma-pdf/${proformaId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) {
      const text = await r.text().catch(() => "");
      let msg = `HTTP ${r.status}`;
      try {
        const json = JSON.parse(text);
        if (json.message) msg = json.message;
      } catch {
        if (text) msg = text;
      }
      throw new Error(msg);
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `predracun-${(proformaNumber || proformaId).toString().replace(/[^\w-]+/g, "_")}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
    return { success: true };
  } catch (e) {
    return { success: false, message: e.message || "Greška pri preuzimanju" };
  }
}

// Debounce helper for search input
function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

// ════════════════════════════════════════════════════════════════════════════
// INVOICES SCREEN
// ════════════════════════════════════════════════════════════════════════════
const TYPE_CHIPS = [
  { id: "all",        label: "Svi" },
  { id: "standard",   label: "Standardni" },
  { id: "storno",     label: "Storno" },
  { id: "corrective", label: "Korektivni" },
  { id: "advance",    label: "Avansni" },
];
// Source filter — separate from type chips so user can combine
// (e.g., "Stolovi" + "Storno" to see only stornoed table orders).
// Default is "b2b" which EXCLUDES POS table orders from the list,
// because table orders are operationally POS data leaking through
// the non-cash filter and shouldn't pollute the B2B Računi view.
const SOURCE_CHIPS = [
  { id: "b2b",    label: "B2B" },
  { id: "tables", label: "Stolovi" },
  { id: "all",    label: "Sve" },
];
const STATUS_OPTIONS = [
  { id: "all",        label: "Svi statusi" },
  { id: "fiscalized", label: "Fiskalizovani" },
  { id: "draft",      label: "Nacrti" },
  { id: "failed",     label: "Neuspjeli" },
];
const DATE_RANGES = [
  { id: "all",   label: "Sve" },
  { id: "today", label: "Danas" },
  { id: "week",  label: "7 dana" },
  { id: "month", label: "Mjesec" },
  { id: "year",  label: "Godina" },
];

function resolvePresetRange(preset) {
  const today = new Date();
  const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (preset === "today") return { from: fmt(today), to: fmt(today) };
  if (preset === "week")  { const d = new Date(today); d.setDate(d.getDate() - 6); return { from: fmt(d), to: fmt(today) }; }
  if (preset === "month") { const d = new Date(today.getFullYear(), today.getMonth(), 1); return { from: fmt(d), to: fmt(today) }; }
  if (preset === "year")  { const d = new Date(today.getFullYear(), 0, 1); return { from: fmt(d), to: fmt(today) }; }
  return { from: null, to: null };
}

function InvoicesScreen({ isMobile, canWrite, onEditDraft }) {
  // Filters
  const [type, setType]         = useState("all");
  const [status, setStatus]     = useState("all");
  // Source filter — defaults to "b2b" (excludes POS table orders).
  // The user can switch to "Stolovi" to see only POS table-session invoices,
  // or "Sve" to see everything together. Backend enforces the filter via
  // is_table_order column.
  const [source, setSource]     = useState("b2b");
  const [datePreset, setPreset] = useState("month");
  const [partnerId, setPartnerId] = useState(0);
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);

  // Sort + paging
  const [sort, setSort]           = useState("date");
  const [direction, setDirection] = useState("desc");
  const [page, setPage]           = useState(1);
  const perPage = isMobile ? 25 : 50;

  // Data
  const [data, setData]       = useState({ items: [], total: 0, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  // Selected invoice (detail panel)
  const [selectedId, setSelectedId] = useState(null);

  // Filter UI for mobile (collapsible)
  const [filterOpen, setFilterOpen] = useState(false);

  // Partners — for picker
  const [partners, setPartners] = useState([]);

  useEffect(() => {
    let active = true;
    (async () => {
      const r = await api("/partners");
      if (!active) return;
      if (r.success && Array.isArray(r.data)) setPartners(r.data);
      else if (r.success && Array.isArray(r.data?.partners)) setPartners(r.data.partners);
    })();
    return () => { active = false; };
  }, []);

  // Reset page when filters change
  useEffect(() => { setPage(1); }, [type, status, source, datePreset, partnerId, search, sort, direction]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const { from, to } = resolvePresetRange(datePreset);
    const qs = new URLSearchParams();
    if (type !== "all")       qs.set("type", type);
    if (status !== "all")     qs.set("status", status);
    // source is always sent (backend default is also "b2b" but explicit is clearer)
    qs.set("source", source);
    if (partnerId > 0)        qs.set("partner_id", String(partnerId));
    if (search)               qs.set("search", search);
    if (from)                 qs.set("date_from", from);
    if (to)                   qs.set("date_to", to);
    qs.set("sort", sort);
    qs.set("direction", direction);
    qs.set("page", String(page));
    qs.set("per_page", String(perPage));
    const r = await api(`/b2b/invoices?${qs.toString()}`);
    setLoading(false);
    if (r.success) {
      setData(r.data);
    } else {
      setError(r.message || "Greška pri učitavanju");
    }
  }, [type, status, source, datePreset, partnerId, search, sort, direction, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const items = Array.isArray(data.items) ? data.items : [];
  const total = data.total || 0;
  const totalPages = data.total_pages || 1;

  const setSortColumn = (col) => {
    if (sort === col) {
      setDirection(direction === "asc" ? "desc" : "asc");
    } else {
      setSort(col);
      setDirection("desc");
    }
  };

  const filterPanel = (
    <FilterPanel
      type={type} setType={setType}
      status={status} setStatus={setStatus}
      source={source} setSource={setSource}
      // Only show the source filter chips if this company has at least one
      // table-order invoice. Backend sets has_table_orders=true once any
      // POS table order has ever been fiscalized for this company. Clients
      // that never use the Stolovi feature of POS (or don't use POS at all)
      // see no source chips — keeps the UI clean.
      hasTableOrders={!!data?.has_table_orders}
      datePreset={datePreset} setPreset={setPreset}
      partnerId={partnerId} setPartnerId={setPartnerId}
      partners={partners}
      searchInput={searchInput} setSearchInput={setSearchInput}
      isMobile={isMobile}
    />
  );

  return (
    <>
      <TopHeader
        title="Računi"
        subtitle={loading ? "Učitavam…" : `${fmtNum(total)} ${total === 1 ? "račun" : "računa"}`}
        isMobile={isMobile}
        right={
          isMobile ? (
            <button
              onClick={() => setFilterOpen(true)}
              style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "8px 12px" }}
            >
              <FilterIcon size={14} /> Filteri
            </button>
          ) : (
            <button
              onClick={load}
              disabled={loading}
              title="Osvježi"
              style={{ ...S.btnSm("white", S.muted), border: `1px solid ${S.border}`, padding: "8px 10px" }}
            >
              <RefreshCw size={14} className={loading ? "spin" : ""} />
              {loading ? "Učitavam" : "Osvježi"}
            </button>
          )
        }
      />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1600 }}>
        {/* Desktop: filter panel always visible */}
        {!isMobile && (
          <div style={{ marginBottom: 16 }}>{filterPanel}</div>
        )}

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca",
            borderRadius: 10, padding: "14px 18px", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Table / Cards */}
        <div style={{
          background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
          overflow: "hidden",
        }}>
          {loading && items.length === 0 ? (
            <div style={{ padding: 24 }}>
              <Skeleton lines={6} />
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: S.mutedLight }}>
              <Receipt size={36} color={S.mutedLight} />
              <div style={{ marginTop: 12, fontSize: 14 }}>Nema računa za izabrane filtere</div>
            </div>
          ) : isMobile ? (
            <InvoiceCardList items={items} onSelect={setSelectedId} />
          ) : (
            <InvoiceTable
              items={items}
              sort={sort} direction={direction}
              onSort={setSortColumn}
              onSelect={setSelectedId}
            />
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            onChange={setPage}
            isMobile={isMobile}
          />
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {/* Mobile filter sheet */}
      {isMobile && filterOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 110,
            display: "flex", alignItems: "flex-end",
          }}
          onClick={() => setFilterOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="slide-up"
            style={{
              width: "100%", background: S.card, borderTopLeftRadius: 16,
              borderTopRightRadius: 16, maxHeight: "85vh", overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px" }}>
              <div style={{ width: 40, height: 4, background: S.border, borderRadius: 2 }} />
            </div>
            <div style={{
              display: "flex", justifyContent: "space-between", alignItems: "center",
              padding: "8px 16px 12px",
            }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: S.dark }}>Filteri</div>
              <button
                onClick={() => setFilterOpen(false)}
                style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}
              >
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: "0 16px 24px" }}>
              {filterPanel}
              <button
                onClick={() => setFilterOpen(false)}
                style={{ ...S.btn(S.dark), width: "100%", marginTop: 18 }}
              >
                Primijeni
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Detail panel (drawer on desktop, full sheet on mobile) */}
      {selectedId !== null && (
        <InvoiceDetailPanel
          id={selectedId}
          onClose={() => setSelectedId(null)}
          isMobile={isMobile}
          canWrite={canWrite}
          onEditDraft={onEditDraft}
          onMutated={load}
        />
      )}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Filter panel — used inline on desktop, in bottom sheet on mobile
// ────────────────────────────────────────────────────────────────────────────
function FilterPanel({
  type, setType,
  status, setStatus,
  source, setSource,
  hasTableOrders,
  datePreset, setPreset,
  partnerId, setPartnerId, partners,
  searchInput, setSearchInput,
  isMobile,
}) {
  return (
    <div style={{
      background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
      padding: isMobile ? 12 : 14,
    }}>
      {/* Search */}
      <div style={{
        position: "relative", marginBottom: 12,
      }}>
        <Search size={16} color={S.mutedLight} style={{
          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
        }} />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Pretraži broj računa, kupca, IIC…"
          style={{ ...S.input, paddingLeft: 36 }}
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput("")}
            style={{
              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted,
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {/* Source chips — only rendered if this company has at least one POS
          table order in their history. Companies that don't use POS (or don't
          use the Stolovi feature) never see these chips — keeps the UI clean
          and avoids exposing a meaningless toggle. Backend sets has_table_orders
          via a single EXISTS query in the /b2b/invoices response.

          Default source filter is "b2b" even when chips are hidden, so the
          behavior is: B2B-only invoices shown, table orders excluded — exactly
          what every other invoice screen in the app should show. */}
      {hasTableOrders && (
        <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          {SOURCE_CHIPS.map((s) => (
            <PeriodPill
              key={s.id}
              active={source === s.id}
              onClick={() => setSource(s.id)}
              label={s.label}
            />
          ))}
        </div>
      )}

      {/* Type chips */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {TYPE_CHIPS.map((t) => (
          <PeriodPill
            key={t.id}
            active={type === t.id}
            onClick={() => setType(t.id)}
            label={t.label}
          />
        ))}
      </div>

      {/* Bottom row: status, date, partner */}
      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
      }}>
        <Select value={status} onChange={setStatus} options={STATUS_OPTIONS} icon={CircleCheck} />
        <Select value={datePreset} onChange={setPreset} options={DATE_RANGES} icon={Calendar} />
        <Select
          value={String(partnerId)}
          onChange={(v) => setPartnerId(parseInt(v, 10) || 0)}
          options={[
            { id: "0", label: "Svi partneri" },
            ...partners.map((p) => ({ id: String(p.id), label: dh(p.name) })),
          ]}
          icon={Building2}
        />
      </div>
    </div>
  );
}

function Select({ value, onChange, options, icon: Icon }) {
  return (
    <div style={{ position: "relative" }}>
      {Icon && (
        <Icon size={14} color={S.mutedLight} style={{
          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
          pointerEvents: "none",
        }} />
      )}
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...S.input,
          paddingLeft: Icon ? 34 : 14,
          paddingRight: 32,
          appearance: "none",
          cursor: "pointer",
        }}
      >
        {options.map((o) => (
          <option key={o.id} value={o.id}>{o.label}</option>
        ))}
      </select>
      <ChevronsUpDown size={14} color={S.mutedLight} style={{
        position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
        pointerEvents: "none",
      }} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Desktop table
// ────────────────────────────────────────────────────────────────────────────
function InvoiceTable({ items, sort, direction, onSort, onSelect }) {
  const Sortable = ({ col, label, align }) => (
    <th
      onClick={() => onSort(col)}
      style={{
        ...th, textAlign: align || "left", cursor: "pointer", userSelect: "none",
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        {sort === col ? (
          direction === "asc" ? <ChevronUp size={12} /> : <ChevronDown size={12} />
        ) : (
          <ChevronsUpDown size={11} color={S.mutedLight} />
        )}
      </span>
    </th>
  );

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 900 }}>
        <thead style={{ background: "#fafafb" }}>
          <tr>
            <Sortable col="number" label="Br. računa" />
            <Sortable col="date" label="Datum" />
            <th style={th}>Kupac</th>
            <th style={th}>Tip</th>
            <Sortable col="amount" label="Iznos" align="right" />
            <th style={{ ...th, textAlign: "right" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr
              key={r.id}
              onClick={() => onSelect(r.id)}
              style={{
                borderBottom: `1px solid ${S.borderSoft}`,
                cursor: "pointer",
                transition: "background 0.1s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#fafafb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <td style={{ ...td, fontFamily: S.mono, fontWeight: 600 }}>
                {r.display_no || "—"}
              </td>
              <td style={{ ...td, color: S.muted, whiteSpace: "nowrap" }}>
                {fmtDateTime(r.invoice_date)}
              </td>
              <td style={td}>
                {dh(r.buyer_name) || <span style={{ color: S.mutedLight }}>—</span>}
                {r.buyer_pib && (
                  <div style={{ fontSize: 11, color: S.mutedLight, fontFamily: S.mono }}>
                    {buyerIdLabel(r.buyer_id_type)}: {r.buyer_pib}
                  </div>
                )}
              </td>
              <td style={td}>
                <TypeBadge r={r} />
              </td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600, whiteSpace: "nowrap" }}>
                {fmtMoney(r.total_amount, r.currency)}
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                <StatusBadge status={r.status} paid={r.is_paid} isStorno={r.is_storno} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile card list
// ────────────────────────────────────────────────────────────────────────────
function InvoiceCardList({ items, onSelect }) {
  return (
    <div>
      {items.map((r, i) => (
        <div
          key={r.id}
          onClick={() => onSelect(r.id)}
          style={{
            padding: "14px 16px",
            borderBottom: i < items.length - 1 ? `1px solid ${S.borderSoft}` : "none",
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ fontFamily: S.mono, fontWeight: 700, fontSize: 15, color: S.dark }}>
                  {r.display_no || "—"}
                </span>
                <TypeBadge r={r} />
              </div>
              <div style={{ fontSize: 14, color: S.dark, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {dh(r.buyer_name) || "—"}
              </div>
              <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                {fmtDateTime(r.invoice_date)}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontFamily: S.mono, fontWeight: 700, color: S.dark }}>
                {fmtMoney(r.total_amount, r.currency)}
              </div>
              <div style={{ marginTop: 4 }}>
                <StatusBadge status={r.status} paid={r.is_paid} isStorno={r.is_storno} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Type badge (storno/korektivni/avansni/stornirani-original/table-order)
// ────────────────────────────────────────────────────────────────────────────
function TypeBadge({ r }) {
  if (r.is_storno)             return <Tag color={S.muted}  text="STORNO" />;
  // Original invoice that has been stornoed: shows STORNIRAN (past participle)
  // to distinguish from STORNO (the storno record itself). Helps users see at
  // a glance which invoices in the list are no longer valid.
  if (r.stornoed_by_invoice_id) return <Tag color="#9ca3af" text="STORNIRAN" />;
  if (r.is_corrective)         return <Tag color={S.blue}   text="KOR" />;
  if (r.is_advance)            return <Tag color={S.accent} text="AVANS" />;
  // POS table order — visible only when user opens the "Stolovi" source filter
  // (otherwise these rows are excluded server-side). Includes the table name
  // if available so the user can tell at a glance which table session it was.
  if (r.is_table_order) {
    const label = r.table_name ? `STO · ${r.table_name}` : "STO";
    return <Tag color="#f59e0b" text={label} />;
  }
  return <span style={{ fontSize: 12, color: S.mutedLight }}>—</span>;
}

// ────────────────────────────────────────────────────────────────────────────
// Pagination
// ────────────────────────────────────────────────────────────────────────────
function Pagination({ page, totalPages, onChange, isMobile }) {
  const go = (p) => onChange(Math.max(1, Math.min(totalPages, p)));
  return (
    <div style={{
      display: "flex", justifyContent: "center", alignItems: "center",
      gap: 6, marginTop: 18,
    }}>
      <button
        onClick={() => go(page - 1)} disabled={page <= 1}
        style={pageBtn(false, page <= 1)}
      >
        <ChevronLeft size={16} /> {!isMobile && "Prethodna"}
      </button>
      <div style={{
        padding: "8px 14px", fontSize: 13, color: S.muted,
        fontFamily: S.mono,
      }}>
        {page} / {totalPages}
      </div>
      <button
        onClick={() => go(page + 1)} disabled={page >= totalPages}
        style={pageBtn(false, page >= totalPages)}
      >
        {!isMobile && "Sljedeća"} <ChevronRight size={16} />
      </button>
    </div>
  );
}
const pageBtn = (active, disabled) => ({
  display: "inline-flex", alignItems: "center", gap: 4,
  padding: "8px 12px", borderRadius: 8, fontSize: 13, fontWeight: 600,
  background: active ? S.accent : "white",
  color: active ? "white" : (disabled ? S.mutedLight : S.dark),
  border: `1px solid ${active ? S.accent : S.border}`,
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: S.font,
  opacity: disabled ? 0.5 : 1,
});

// ════════════════════════════════════════════════════════════════════════════
// INVOICE DETAIL PANEL — drawer (desktop) / full sheet (mobile)
// ════════════════════════════════════════════════════════════════════════════
function InvoiceDetailPanel({ id, onClose, isMobile, canWrite, embedded = false, onEditDraft, onMutated }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [toast, setToast] = useState(null);
  const [showMarkPaid, setShowMarkPaid] = useState(false);

  // Close on ESC key
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const r = await api(`/b2b/invoice/${id}`);
    setLoading(false);
    if (r.success) setData(r.data?.invoice);
    else setError(r.message || "Greška");
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const inv = data;

  const onDownload = async () => {
    if (!inv) return;
    setDownloading(true);
    const r = await downloadInvoicePdf(inv.id, inv.display_no);
    setDownloading(false);
    setToast({
      message: r.success ? "PDF preuzet" : (r.message || "Greška pri preuzimanju"),
      type: r.success ? "success" : "error",
    });
    setTimeout(() => setToast(null), 3000);
  };

  // Quick action handlers (storno, fiscalize, delete, send mail)
  const [confirming, setConfirming] = useState(null); // "storno" | "fiscalize" | "delete" | "sendmail"
  const [emailTo, setEmailTo] = useState("");
  const [actionBusy, setActionBusy] = useState(false);

  // Storno reason picked by user in the confirmation modal (STORNO_OP | STORNO_BUYER | STORNO_RETURN).
  // Defaults to STORNO_OP which matches what POS uses when the operator triggers storno from the receipt.
  const [stornoReason, setStornoReason] = useState("STORNO_OP");

  const doStorno = async () => {
    if (!inv) return;
    setActionBusy(true);
    // Per POS conventions, storno endpoint requires:
    //   - storno_reason: STORNO_OP | STORNO_BUYER | STORNO_RETURN
    //   - fiscalize_now: true (otherwise storno is created as draft and never fiscalized)
    const r = await api(`/invoices/storno/${inv.id}`, {
      method: "POST",
      body: JSON.stringify({
        storno_reason: stornoReason || "STORNO_OP",
        fiscalize_now: true,
      }),
    });
    setActionBusy(false);
    setConfirming(null);
    if (r.success) {
      setToast({ type: "success", message: "Storno račun kreiran i fiskalizovan" });
      load();
    } else {
      setToast({ type: "error", message: r.message || "Greška" });
    }
    setTimeout(() => setToast(null), 3500);
  };

  const doFiscalize = async () => {
    if (!inv) return;
    setActionBusy(true);
    const r = await api(`/invoices/fiscalize/${inv.id}`, { method: "POST", body: "{}" });
    setActionBusy(false);
    setConfirming(null);
    if (r.success) {
      setToast({ type: "success", message: "Račun fiskalizovan" });
      load();
      if (onMutated) onMutated();
    } else {
      setToast({ type: "error", message: r.message || "Fiskalizacija neuspješna" });
    }
    setTimeout(() => setToast(null), 4000);
  };

  // Eligibility for edit/delete: only PLAIN non-fiscalized invoices.
  // Storno/corrective/advance records keep cross-row pointers to other
  // invoices — deleting or editing them would corrupt those links, so both
  // actions are restricted to ordinary drafts/failed invoices. The backend
  // (/b2b/invoice-delete) enforces the same guards server-side; this check
  // just hides the buttons where the action would be rejected anyway.
  const isEditableDraft = !!inv
    && (inv.status === "draft" || inv.status === "failed")
    && !inv.is_storno && !inv.is_corrective && !inv.is_advance;

  const doDelete = async () => {
    if (!inv) return;
    setActionBusy(true);
    const r = await api(`/b2b/invoice-delete/${inv.id}`, { method: "POST", body: "{}" });
    setActionBusy(false);
    setConfirming(null);
    if (r.success) {
      // Refresh the parent list, then close — the invoice no longer exists
      // so keeping the panel open would just show a stale object.
      if (onMutated) onMutated();
      onClose();
    } else {
      setToast({ type: "error", message: r.message || "Brisanje neuspješno" });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const doSendMail = async () => {
    if (!inv) return;
    if (!emailTo || !/^\S+@\S+\.\S+$/.test(emailTo)) {
      setToast({ type: "error", message: "Unesite ispravan email" });
      setTimeout(() => setToast(null), 3000);
      return;
    }
    setActionBusy(true);
    // B2B-specific endpoint — attaches a PDF generated by B2bInvoicePDF using
    // the company's currently-selected template (set in PDF Šabloni section).
    // Body key matches POS convention: { email }.
    const r = await api(`/b2b/invoice-email/${inv.id}`, {
      method: "POST",
      body: JSON.stringify({ email: emailTo }),
    });
    setActionBusy(false);
    setConfirming(null);
    setEmailTo("");
    setToast({
      type: r.success ? "success" : "error",
      message: r.success ? "Email poslat" : (r.message || "Greška pri slanju"),
    });
    setTimeout(() => setToast(null), 3500);
  };

  // `embedded`=true is the post-create flow where the panel renders inline
  // inside the page (not as a slide-over modal). No fixed positioning, no
  // overlay, no shadow — just an inline card so the user sees the invoice
  // details right where they expect them.
  const panelStyle = embedded
    ? {
        position: "relative", background: S.bg,
        display: "flex", flexDirection: "column",
        borderRadius: 12, overflow: "hidden",
        border: `1px solid ${S.border}`,
      }
    : isMobile
    ? {
        position: "fixed", inset: 0, background: S.bg, zIndex: 120,
        display: "flex", flexDirection: "column",
        animation: "slideUp 0.22s ease-out",
      }
    : {
        position: "fixed", top: 0, right: 0, bottom: 0, width: 720, maxWidth: "85vw",
        background: S.bg, zIndex: 120, boxShadow: "-12px 0 40px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
      };

  return (
    <>
      {/* Overlay only for non-embedded desktop variant */}
      {!isMobile && !embedded && (
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 115,
          }}
        />
      )}

      <div style={panelStyle}>
        {/* Header */}
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card,
          borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          {/* Close X — only when used as a slide-over panel.
              In embedded mode (post-create flow) the action buttons below
              the panel handle navigation, so X would be confusing. */}
          {!embedded && (
            <button
              onClick={onClose}
              style={{
                background: "none", border: "none", cursor: "pointer",
                padding: 6, color: S.muted, borderRadius: 6,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              <X size={20} />
            </button>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
              {loading ? "Učitavam…" : inv ? `Račun ${inv.display_no}` : "Račun"}
            </div>
            {inv && (
              <div style={{ fontSize: 12, color: S.muted }}>
                {fmtDateTime(inv.invoice_date)}
              </div>
            )}
          </div>
          {inv && (
            <button
              onClick={onDownload}
              disabled={downloading}
              title={
                inv.status === "fiscalized"
                  ? "Preuzmi PDF fiskalizovanog računa"
                  : inv.status === "draft"
                    ? "Preuzmi PDF (interni dokument — račun nije fiskalizovan)"
                    : "Preuzmi PDF"
              }
              style={{
                ...S.btnSm(S.dark, "white"),
                padding: "9px 14px",
                opacity: downloading ? 0.6 : 1,
              }}
            >
              {downloading ? <Loader2 size={14} className="spin" /> : <Download size={14} />}
              {!isMobile && (downloading ? "Preuzimam…" : (inv.status === "draft" ? "PDF (draft)" : "PDF"))}
            </button>
          )}
        </div>

        {/* Scrollable body */}
        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 14 : 24 }}>
          {loading ? (
            <Skeleton lines={10} />
          ) : error ? (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 8, color: S.red,
            }}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : inv ? (
            <InvoiceDetailBody inv={inv} isMobile={isMobile} />
          ) : null}
        </div>

        {/* Action bar — context-aware actions based on invoice status */}
        {!loading && inv && canWrite && (
          <div style={{
            padding: isMobile ? "12px 14px calc(12px + env(safe-area-inset-bottom))" : "14px 24px",
            background: S.card, borderTop: `1px solid ${S.border}`,
            display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap",
          }}>
            {/* Fiscalize (drafts only) */}
            {inv.status === "draft" && (
              <button
                onClick={() => setConfirming("fiscalize")}
                style={{ ...S.btnSm(S.accent), padding: "10px 14px", flex: isMobile ? "1 1 100%" : "1", justifyContent: "center" }}
              >
                <Send size={14} /> Fiskalizuj
              </button>
            )}

            {/* Edit + Delete — plain drafts / failed invoices only (never
                fiscalized, no storno/corrective/advance cross-links).
                Uredi loads this draft into Novi račun prefilled; after the
                new invoice fiscalizes, the old draft is deleted automatically.
                Obriši removes the draft immediately (with confirmation). */}
            {isEditableDraft && onEditDraft && (
              <button
                onClick={() => { onEditDraft(inv); onClose(); }}
                style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 14px", justifyContent: "center" }}
              >
                <Edit3 size={14} /> Uredi
              </button>
            )}
            {isEditableDraft && (
              <button
                onClick={() => setConfirming("delete")}
                style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "10px 14px", justifyContent: "center" }}
              >
                <Trash2 size={14} /> Obriši
              </button>
            )}

            {/* Mark paid (unpaid fiscalized non-cash, with partner, not stornoed) */}
            {inv.status === "fiscalized" && !inv.is_storno && !inv.related?.stornoed_by
             && !inv.is_paid && inv.partner_id
             && (inv.invoice_type === "non_cash" || inv.type_of_inv === "NONCASH") && (
              <button
                onClick={() => setShowMarkPaid(true)}
                style={{ ...S.btnSm(S.green), padding: "10px 14px", flex: 1, justifyContent: "center" }}
              >
                <CircleCheck size={14} /> Označi plaćenim
              </button>
            )}

            {/* Storno button — hide if:
                  - invoice is itself a storno (is_storno=1)
                  - OR invoice has already been stornoed (related.stornoed_by set)
                The original invoice keeps is_storno=0 but gets stornoed_by_invoice_id
                set to the storno record's id (returned by backend as related.stornoed_by).
                The backend's canBeStornoed() also rejects re-storno attempts, but it's
                cleaner UX to hide the button than show it and toast an error. */}
            {inv.status === "fiscalized" && !inv.is_storno && !inv.related?.stornoed_by && (
              <button
                onClick={() => setConfirming("storno")}
                style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "10px 14px", justifyContent: "center" }}
              >
                <X size={14} /> Storno
              </button>
            )}

            {/* Send mail (fiscalized only) */}
            {inv.status === "fiscalized" && (
              <button
                onClick={() => { setEmailTo(inv.buyer_email || ""); setConfirming("sendmail"); }}
                style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 14px", justifyContent: "center" }}
              >
                <Mail size={14} /> Pošalji mail
              </button>
            )}
          </div>
        )}

        {/* Confirm modal for storno / fiscalize / send mail */}
        {confirming === "storno" && (
          <div
            onClick={() => !actionBusy && setConfirming(null)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
              display: "flex", alignItems: "center", justifyContent: "center",
              padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: S.card, borderRadius: 14,
                width: "100%", maxWidth: 480, padding: 20,
              }}
            >
              <div style={{
                display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10, background: "#fef2f2",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <RotateCcw size={20} color={S.red} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, color: S.dark }}>
                    Stornirati račun {inv?.display_no}?
                  </div>
                  <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                    Storno kreira korektivni račun koji poništava ovaj račun
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 14 }}>
                <div style={{
                  fontSize: 12, fontWeight: 600, color: S.muted,
                  textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 8,
                }}>
                  Razlog storniranja
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {[
                    { v: "STORNO_OP",     label: "Greška operatera",       hint: "Pogrešno izdat račun, sistemska greška" },
                    { v: "STORNO_BUYER",  label: "Kupac otkazao",         hint: "Kupac je odustao od kupovine/usluge" },
                    { v: "STORNO_RETURN", label: "Povrat robe",           hint: "Kupac vraća robu/uslugu" },
                  ].map((opt) => (
                    <label
                      key={opt.v}
                      style={{
                        display: "flex", alignItems: "flex-start", gap: 10,
                        padding: "10px 12px", cursor: "pointer",
                        border: `1px solid ${stornoReason === opt.v ? S.red : S.border}`,
                        background: stornoReason === opt.v ? "#fef2f2" : "white",
                        borderRadius: 8,
                      }}
                    >
                      <input
                        type="radio"
                        checked={stornoReason === opt.v}
                        onChange={() => setStornoReason(opt.v)}
                        style={{ marginTop: 2, accentColor: S.red, cursor: "pointer" }}
                      />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: S.dark }}>{opt.label}</div>
                        <div style={{ fontSize: 11, color: S.muted, marginTop: 2 }}>{opt.hint}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              <div style={{
                marginTop: 16, display: "flex", gap: 8, justifyContent: "flex-end",
              }}>
                <button
                  onClick={() => setConfirming(null)}
                  disabled={actionBusy}
                  style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 18px" }}
                >Otkaži</button>
                <button
                  onClick={doStorno}
                  disabled={actionBusy}
                  style={{ ...S.btn(S.red), padding: "10px 18px" }}
                >
                  {actionBusy ? <Loader2 size={14} className="spin" /> : <RotateCcw size={14} />}
                  {actionBusy ? " Storniram…" : " Storniraj i fiskalizuj"}
                </button>
              </div>
            </div>
          </div>
        )}
        {confirming === "fiscalize" && (
          <ConfirmModal
            title={`Fiskalizovati račun ${inv?.display_no}?`}
            body="Račun će biti poslat Poreskoj upravi i dobiti fiskalni broj. Nakon fiskalizacije nije moguće menjati stavke."
            confirmLabel="Fiskalizuj"
            onConfirm={doFiscalize}
            onCancel={() => setConfirming(null)}
            busy={actionBusy}
          />
        )}
        {confirming === "delete" && (
          <ConfirmModal
            title={`Obrisati nacrt ${inv?.display_no}?`}
            body="Nacrt i njegove stavke će biti trajno obrisani. Ova akcija se ne može poništiti. Fiskalizovani računi se ne mogu brisati."
            confirmLabel="Obriši"
            onConfirm={doDelete}
            onCancel={() => setConfirming(null)}
            busy={actionBusy}
          />
        )}
        {confirming === "sendmail" && (
          <div
            onClick={() => !actionBusy && setConfirming(null)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 220,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
            }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{
              background: S.card, borderRadius: 14, padding: 24, maxWidth: 480, width: "100%",
            }}>
              <h3 style={{ margin: 0, marginBottom: 6, color: S.dark }}>Pošalji račun mailom</h3>
              <p style={{ fontSize: 13, color: S.muted, marginTop: 0, marginBottom: 14 }}>
                PDF računa će biti poslat kao prilog. SMTP postavke treba da budu konfigurisane u Podešavanjima.
              </p>
              <Field label="Primaoc" required>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  placeholder="kupac@example.com"
                  autoFocus
                  style={S.input}
                />
              </Field>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
                <button onClick={() => setConfirming(null)} disabled={actionBusy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>Otkaži</button>
                <button onClick={doSendMail} disabled={actionBusy} style={{ ...S.btn(S.dark), padding: "10px 20px" }}>
                  {actionBusy ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Pošalji
                </button>
              </div>
            </div>
          </div>
        )}

        {showMarkPaid && inv && (
          <QuickMarkPaidModal
            invoice={inv}
            onClose={() => setShowMarkPaid(false)}
            onSaved={() => {
              setShowMarkPaid(false);
              load();
              setToast({ type: "success", message: "Račun označen plaćenim" });
              setTimeout(() => setToast(null), 3000);
            }}
            isMobile={isMobile}
          />
        )}

        {toast && (
          <div style={{
            position: "fixed", bottom: isMobile ? 16 : 20,
            left: "50%", transform: "translateX(-50%)",
            background: toast.type === "success" ? S.green : S.red,
            color: "white", padding: "10px 18px", borderRadius: 10,
            fontSize: 14, fontWeight: 500, zIndex: 200,
            boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
          }}>
            {toast.message}
          </div>
        )}
      </div>
    </>
  );
}

function InvoiceDetailBody({ inv, isMobile }) {
  const buyerLines = [];
  if (inv.partner_name || inv.buyer_name) {
    buyerLines.push(dh(inv.partner_name || inv.buyer_name));
  }
  const pib = inv.partner_tax_id || inv.buyer_pib || inv.buyer_tax_id;
  if (pib) buyerLines.push(`${buyerIdLabel(inv.buyer_id_type)}: ${pib}`);
  const addr = [dh(inv.partner_address || inv.buyer_address || ""), dh(inv.partner_city || inv.buyer_city || "")]
    .filter(Boolean).join(", ");
  if (addr) buyerLines.push(addr);
  // Show country whenever it's set and isn't MNE (which is the default
  // for legacy invoices). Resolve to readable name via COUNTRY_CODES when
  // possible: e.g. "Njemačka (DEU)" instead of bare "DEU".
  if (inv.buyer_country && inv.buyer_country !== "MNE") {
    const cc = String(inv.buyer_country).toUpperCase();
    const found = COUNTRY_CODES.find((c) => c.code === cc);
    buyerLines.push(`Država: ${found ? `${found.name} (${cc})` : cc}`);
  }
  if (inv.partner_email) buyerLines.push(dh(inv.partner_email));
  if (inv.partner_phone) buyerLines.push(dh(inv.partner_phone));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Status banner */}
      <StatusBanner inv={inv} />

      {/* Buyer */}
      <Card title="Kupac">
        {buyerLines.length > 0 ? (
          <div style={{ fontSize: 14, color: S.dark, lineHeight: 1.7 }}>
            {buyerLines.map((line, i) => (
              <div key={i} style={i === 0 ? { fontWeight: 600 } : { color: S.muted }}>
                {line}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ color: S.mutedLight, fontSize: 14 }}>—</div>
        )}
      </Card>

      {/* Items */}
      <Card title="Stavke" subtitle={`${inv.items?.length || 0} stavki`}>
        {!inv.items || inv.items.length === 0 ? (
          <Empty text="Nema stavki" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 480 }}>
              <thead>
                <tr>
                  <th style={th}>Naziv</th>
                  <th style={{ ...th, textAlign: "right" }}>Kol.</th>
                  <th style={{ ...th, textAlign: "right" }}>Cijena</th>
                  <th style={{ ...th, textAlign: "right" }}>PDV</th>
                  <th style={{ ...th, textAlign: "right" }}>Ukupno</th>
                </tr>
              </thead>
              <tbody>
                {inv.items.map((it, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={td}>{dh(it.name)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>
                      {it.quantity} {it.unit}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>
                      {fmtMoney(it.unit_price)}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, color: S.muted }}>
                      {it.vat_rate}%
                    </td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>
                      {fmtMoney(it.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Totals */}
      <Card title="Iznosi">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Row label="Osnovica" value={fmtMoney(inv.subtotal, inv.currency)} />
          {inv.discount_amount > 0 && (
            <Row label="Popust" value={`− ${fmtMoney(inv.discount_amount, inv.currency)}`} muted />
          )}
          <Row label="PDV" value={fmtMoney(inv.vat_amount, inv.currency)} />
          <div style={{ height: 1, background: S.border, margin: "4px 0" }} />
          <Row label="Ukupno" value={fmtMoney(inv.total_amount, inv.currency)} bold large />
          {inv.payment_due && (
            <Row label="Rok plaćanja" value={fmtDate(inv.payment_due)} muted />
          )}
        </div>
      </Card>

      {/* Fiscal data (Poreska) */}
      <Card title="Fiskalni podaci">
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
          <KV label="Broj računa (puni format)" value={inv.invoice_number} mono />
          <KV label="Status" value={statusLabel(inv.status)} />
          <KV label="Datum fiskalizacije" value={inv.fiscalized_at ? fmtDateTime(inv.fiscalized_at) : "—"} />
          <KV label="TCR kod" value={inv.tcr_code} mono />
          <KV label="Poslovna jedinica" value={inv.business_unit_code} mono />
          <KV label="Operater" value={`${inv.operator_name || ""} (${inv.operator_code || "—"})`} />
          <KV label="Lokacija" value={inv.location_name} />
          <KV label="IIC" value={inv.iic} mono small />
          <KV label="JIKR (FIC)" value={inv.jikr_code || inv.fic} mono small />
          {inv.verification_url && (
            <div style={{ gridColumn: isMobile ? "auto" : "1 / -1" }}>
              <div style={{ fontSize: 11, color: S.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>
                URL za provjeru
              </div>
              <a
                href={inv.verification_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: 12, color: S.accent, fontFamily: S.mono,
                  textDecoration: "none", wordBreak: "break-all",
                  display: "inline-flex", alignItems: "center", gap: 4,
                }}
              >
                {inv.verification_url} <ExternalLink size={11} />
              </a>
            </div>
          )}
        </div>

        {/* QR code for verification — rendered when fiscalized */}
        {inv.verification_url && (
          <div style={{
            marginTop: 16, paddingTop: 16, borderTop: `1px solid ${S.borderSoft}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            flexDirection: "column", gap: 8,
          }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&margin=4&data=${encodeURIComponent(inv.verification_url)}`}
              alt="QR kod za provjeru"
              width={180}
              height={180}
              style={{ borderRadius: 8, background: "white", padding: 6, border: `1px solid ${S.border}` }}
            />
            <div style={{ fontSize: 11, color: S.muted, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 600 }}>
              Skeniraj za provjeru kod Poreske
            </div>
          </div>
        )}
      </Card>

      {/* Error message if any */}
      {inv.error_message && (
        <Card title="Greška pri fiskalizaciji">
          <div style={{
            fontSize: 12, fontFamily: S.mono, color: S.red,
            background: "#fef2f2", padding: 12, borderRadius: 8,
            wordBreak: "break-word", whiteSpace: "pre-wrap",
            maxHeight: 200, overflowY: "auto",
          }}>
            {inv.error_message}
          </div>
        </Card>
      )}

      {/* Related invoices */}
      {inv.related && Object.keys(inv.related).length > 0 && (
        <Card title="Povezani računi">
          <div style={{ fontSize: 13, color: S.muted, lineHeight: 1.8 }}>
            {inv.related.storno_of       && <div>Storno računa #{inv.related.storno_of}</div>}
            {inv.related.stornoed_by     && <div>Storniran računom #{inv.related.stornoed_by}</div>}
            {inv.related.corrective_of   && <div>Korektivni za #{inv.related.corrective_of}</div>}
            {inv.related.corrected_by    && <div>Korigovan računom #{inv.related.corrected_by}</div>}
            {inv.related.advance_invoice && <div>Avans #{inv.related.advance_invoice}</div>}
            {inv.related.finalized_by    && <div>Finaliziran računom #{inv.related.finalized_by}</div>}
            {inv.storno_reason && <div>Razlog storna: <strong>{inv.storno_reason}</strong></div>}
          </div>
        </Card>
      )}

      {/* Podaci ugovora (contract_info) — optional contract/annex reference.
          Mirrors the PDF ("Račun po: …" above the items table). */}
      {inv.contract_info && String(inv.contract_info).trim() !== "" && (
        <Card title="Podaci ugovora">
          <div style={{ fontSize: 13, color: S.dark, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
            {dh(inv.contract_info)}
          </div>
        </Card>
      )}

      {/* Napomena (notes) — entered on Novi račun and saved to invoices.notes.
          Renders only if the field has content. Pre-wrap preserves newlines. */}
      {inv.notes && String(inv.notes).trim() !== "" && (
        <Card title="Napomena">
          <div style={{ fontSize: 13, color: S.dark, whiteSpace: "pre-wrap", lineHeight: 1.6 }}>
            {dh(inv.notes)}
          </div>
        </Card>
      )}

      <div style={{ height: 16 }} />
    </div>
  );
}

function StatusBanner({ inv }) {
  let bg = S.accentSoft, color = S.accent, icon = CircleCheck, msg = "Fiskalizovan";
  if (inv.status === "draft")  { bg = "#fef3c7"; color = S.amber; icon = Clock;        msg = "Nacrt — nije fiskalizovan"; }
  if (inv.status === "failed") { bg = "#fee2e2"; color = S.red;   icon = AlertCircle;  msg = "Fiskalizacija neuspješna"; }
  if (inv.is_storno)           { bg = "#f3f4f6"; color = S.muted; icon = X;            msg = "Stornirani račun"; }
  if (inv.status === "fiscalized" && inv.is_paid) {
    bg = "#dcfce7"; color = S.green; icon = CircleCheck; msg = "Fiskalizovan i plaćen";
  }
  const Icon = icon;
  return (
    <div style={{
      background: bg, color, padding: "12px 16px", borderRadius: 10,
      display: "flex", alignItems: "center", gap: 10,
      fontSize: 14, fontWeight: 600,
    }}>
      <Icon size={18} /> {msg}
    </div>
  );
}

function statusLabel(s) {
  if (s === "fiscalized") return "Fiskalizovan";
  if (s === "draft")      return "Nacrt";
  if (s === "failed")     return "Neuspjela fiskalizacija";
  if (s === "cancelled")  return "Otkazan";
  return s || "—";
}

function Row({ label, value, muted, bold, large }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
      <span style={{ color: muted ? S.muted : S.dark, fontSize: large ? 15 : 14 }}>{label}</span>
      <span style={{
        fontFamily: S.mono, fontWeight: bold ? 700 : 500,
        fontSize: large ? 18 : 14,
        color: muted ? S.muted : S.dark,
      }}>{value}</span>
    </div>
  );
}

function KV({ label, value, mono, small }) {
  return (
    <div>
      <div style={{
        fontSize: 11, color: S.muted, fontWeight: 600,
        textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 3,
      }}>{label}</div>
      <div style={{
        fontSize: small ? 11 : 13,
        fontFamily: mono ? S.mono : S.font,
        color: S.dark,
        wordBreak: mono ? "break-all" : "normal",
      }}>
        {value || <span style={{ color: S.mutedLight }}>—</span>}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// SHELL — wires sidebar + content together for authenticated users
// ════════════════════════════════════════════════════════════════════════════
function B2BApp({ user, onLogout }) {
  const isMobile = useIsMobile();
  const [view, setView] = useState("dashboard");
  const [collapsed, setCollapsed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  // Deep-link helper: from Partner detail → NewInvoice with that partner preselected
  const [prefilledPartnerId, setPrefilledPartnerId] = useState(null);
  const goToNewInvoiceWithPartner = (partnerId) => {
    setPrefilledPartnerId(partnerId);
    setView("new-invoice");
  };
  // Clear the prefill when leaving new-invoice
  useEffect(() => { if (view !== "new-invoice") setPrefilledPartnerId(null); }, [view]);

  // Draft editing: from a draft's detail panel → NewInvoice prefilled with the
  // draft's data. Same navigation pattern as prefilledPartnerId. Holds the
  // full invoice-detail object; NewInvoiceScreen maps it into its form state
  // and, after the corrected invoice fiscalizes successfully, deletes the old
  // draft via /b2b/invoice-delete.
  const [draftEdit, setDraftEdit] = useState(null);
  const goToEditDraft = (inv) => {
    setDraftEdit(inv);
    setView("new-invoice");
  };
  useEffect(() => { if (view !== "new-invoice") setDraftEdit(null); }, [view]);

  // Fetch /me to get permissions + B2B settings
  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(true);
  const [meError, setMeError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setMeLoading(true);
      const r = await api("/b2b/me");
      if (!active) return;
      setMeLoading(false);
      if (r.success) setMe(r.data);
      else setMeError(r.message || "Greška pri provjeri pristupa B2B-u");
    })();
    return () => { active = false; };
  }, []);

  // /b2b/me failures (e.g. company doesn't have B2B → 403) are terminal here
  if (meError) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", padding: 24, background: S.bg,
      }}>
        <div style={{
          background: S.card, borderRadius: 14, padding: 28, maxWidth: 460,
          width: "100%", border: `1px solid ${S.border}`, textAlign: "center",
        }}>
          <div style={{
            display: "inline-flex", width: 56, height: 56, borderRadius: 14,
            background: "#fef2f2", alignItems: "center", justifyContent: "center",
            marginBottom: 14,
          }}>
            <AlertCircle size={28} color={S.red} />
          </div>
          <h2 style={{ margin: "0 0 8px", fontSize: 18, color: S.dark }}>
            Pristup odbijen
          </h2>
          <p style={{ color: S.muted, fontSize: 14, marginBottom: 20 }}>
            {meError}
          </p>
          <button onClick={onLogout} style={{ ...S.btn(S.dark), width: "100%" }}>
            <LogOut size={16} /> Odjavi se
          </button>
        </div>
      </div>
    );
  }

  if (meLoading) {
    return (
      <div style={{
        minHeight: "100vh", display: "flex", alignItems: "center",
        justifyContent: "center", background: S.bg,
      }}>
        <Loader2 size={32} color={S.accent} className="spin" />
      </div>
    );
  }

  const Content = () => {
    if (view === "dashboard")   return <Dashboard isMobile={isMobile} />;
    if (view === "invoices")    return <InvoicesScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} onEditDraft={goToEditDraft} />;
    if (view === "new-invoice") return <NewInvoiceScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} goToInvoices={() => setView("invoices")} company={me?.company} prefilledPartnerId={prefilledPartnerId} draftEdit={draftEdit} />;
    if (view === "partners")    return <PartnersScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} onCreateInvoice={goToNewInvoiceWithPartner} />;
    if (view === "products")    return <ProductsScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} company={me?.company} />;
    if (view === "proformas")   return <ProformasScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} company={me?.company} goToInvoices={() => setView("invoices")} />;
    if (view === "brzi-virman") return <BrziVirmanScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} company={me?.company} goToInvoices={() => setView("invoices")} />;
    if (view === "payments")    return <PaymentsScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} />;
    if (view === "expenses")    return <ExpensesScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} />;
    if (view === "reports")     return <ReportsScreen isMobile={isMobile} />;
    if (view === "pdf-config")  return <PdfTemplateScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} />;
    if (view === "settings")    return <SettingsScreen isMobile={isMobile} canWrite={!!me?.permissions?.can_write} />;
    if (view === "superadmin")  return <SuperadminScreen isMobile={isMobile} isSuperadmin={!!me?.permissions?.is_superadmin} />;
    return <ComingSoon view={view} isMobile={isMobile} />;
  };

  if (isMobile) {
    return (
      <div style={{
        minHeight: "100vh", background: S.bg, paddingBottom: 76,
        fontFamily: S.font,
        // NOTE: do NOT set overflowX:hidden on this root. Doing so kills
        // horizontal scroll INSIDE descendants like the buyer tabs strip
        // (Registrovani partner / Pravno lice / Strana firma / Fizičko lice)
        // which need to be horizontally swipeable on narrow phones.
        // Horizontal page-overflow is prevented per-component instead
        // (Card has overflow:hidden, the tab strip has its own overflowX:auto).
      }}>
        <Content />
        <MobileNav
          view={view}
          setView={setView}
          openMoreMenu={() => setMoreOpen(true)}
        />
        <MobileMoreMenu
          open={moreOpen}
          onClose={() => setMoreOpen(false)}
          setView={setView}
          me={me}
          onLogout={onLogout}
        />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: "100vh", background: S.bg, display: "flex",
      fontFamily: S.font,
    }}>
      <Sidebar
        view={view} setView={setView}
        me={me} onLogout={onLogout}
        collapsed={collapsed} setCollapsed={setCollapsed}
      />
      <main style={{ flex: 1, minWidth: 0 }}>
        <Content />
      </main>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// NEW INVOICE SCREEN
// ════════════════════════════════════════════════════════════════════════════
const VAT_RATES = [0, 7, 15, 21];

const PAYMENT_DUE_PRESETS = [
  { id: "po_prijemu",    label: "Po prijemu" },
  { id: "8_dana",        label: "8 dana" },
  { id: "15_dana",       label: "15 dana" },
  { id: "30_dana",       label: "30 dana" },
  { id: "end_of_month",  label: "Do kraja mjeseca" },
  { id: "custom",        label: "Tačan datum…" },
];

// Parse "12,50" or "12.50" → 12.5
function parseNum(s) {
  if (s === null || s === undefined || s === "") return 0;
  const n = parseFloat(String(s).replace(",", "."));
  return Number.isFinite(n) ? n : 0;
}

// Initial empty item shape
const emptyItem = (defaultVat = 21) => ({
  uid:            Math.random().toString(36).slice(2),
  product_id:     null,
  name:           "",
  quantity:       1,
  unit:           "kom",
  unit_price:     0,
  vat_rate:       defaultVat,
  discount_type:  "none",
  discount_value: 0,
});

function NewInvoiceScreen({ isMobile, canWrite, goToInvoices, company, prefilledPartnerId, draftEdit }) {
  const isPausalac = !!company?.is_pausalac;

  // When editing an existing draft (arrived via "Uredi" on a draft's detail
  // panel), this holds the old draft's id. The form is prefilled from the
  // draft; on successful fiscalization of the NEW invoice, the old draft is
  // deleted via /b2b/invoice-delete so it doesn't linger as a duplicate.
  const [editingDraftId, setEditingDraftId] = useState(null);
  const [editingDraftNo, setEditingDraftNo] = useState("");

  // Config
  const [config, setConfig]         = useState(null);
  const [configLoading, setCfgLoad] = useState(true);
  const [configError, setCfgErr]    = useState("");

  // Form state
  const [buyerTab, setBuyerTab] = useState("partner"); // "partner" | "newCompany"
  const [partnerId, setPartnerId] = useState(0);
  const [partners, setPartners] = useState([]);

  // New-company fields
  const [newCompany, setNewCompany] = useState({
    name: "", tax_id: "", address: "", city: "", country: "Montenegro",
    email: "", phone: "", bank_account: "",
  });
  const [saveAsPartner, setSaveAsPartner] = useState(false);

  // Individual (Fizičko lice) fields — per Poreska tech docs
  const [individual, setIndividual] = useState({
    subtype: "",     // "cg" | "foreign" | ""
    name: "",
    personal_id: "",
    id_type: "ID",   // "ID" | "PASSPORT"
    country: "",     // ISO 3166-1 alpha-3 code, or "OTHER" if manual
    country_manual: "",  // used when country === "OTHER"
    address: "", city: "", email: "", phone: "",
  });

  // Foreign company (Strana firma) fields — May 2026 non-TIN feature
  //   subtype "vat" → BuyerIDType="VAT" (EU/EEA, ID like IT12345678901)
  //   subtype "tax" → BuyerIDType="TAX" (non-EU, generic foreign tax ID)
  const [foreignCompany, setForeignCompany] = useState({
    subtype: "",     // "vat" | "tax" | ""
    id_type: "VAT",  // "VAT" or "TAX" — kept in sync with subtype
    name: "",
    tax_id: "",
    country_code: "",    // ISO alpha-3, or "OTHER" for manual
    country_manual: "",  // when country_code === "OTHER"
    address: "", city: "", email: "",
  });

  // Items
  const defaultVat = isPausalac ? 0 : 21;
  const [items, setItems] = useState([emptyItem(defaultVat)]);

  // Header / config
  const today = new Date().toISOString().slice(0, 10);
  const [tcrCode, setTcrCode]     = useState("");
  const [invoiceDate, setInvDate] = useState(today);
  const [paymentDue, setPayDue]   = useState("15_dana");
  const [paymentDueDate, setPayDueDate] = useState("");
  const [currency, setCurrency]   = useState("EUR");
  const [note, setNote]           = useState("");
  // Podaci ugovora — optional contract/annex reference (e.g. "Anex ugovora
  // broj: 06-2026/2"). Rendered on the PDF above the items table as
  // "Račun po: …" and shown on the detail panel. Sent as contract_info.
  const [contractInfo, setContractInfo] = useState("");

  // Invoice-level discount
  const [invDiscType, setInvDiscType] = useState("none");
  const [invDiscValue, setInvDiscValue] = useState(0);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [successInvoiceId, setSuccessInvoiceId] = useState(null);
  const [toast, setToast] = useState(null);
  // Confirmation modal — shown before fiscalization so the operator gets a
  // last-chance review with summary (buyer, totals, item count) before sending
  // to Poreska. Stores the action to run if confirmed: "fiscalize" | null.
  const [confirmAction, setConfirmAction] = useState(null);

  // Invoice mode — "standard" (current flow) | "corrective" (korektivni) | "advance" (avansni)
  const [mode, setMode] = useState("standard");

  // ── Draft edit prefill ────────────────────────────────────────────────
  // Maps the invoice-detail object of a draft into the form state. Runs once
  // per draftEdit change. Only PLAIN drafts reach here (the detail panel
  // hides "Uredi" for storno/corrective/advance), so mode stays "standard".
  // The invoice-level discount is NOT prefilled: at store time it was folded
  // into the items (distributeInvoiceDiscount), so the loaded items already
  // carry it — prefilling it again would double-apply the discount.
  useEffect(() => {
    if (!draftEdit) return;
    const d = draftEdit;
    setEditingDraftId(d.id);
    setEditingDraftNo(d.display_no || d.invoice_number || "");

    // Buyer → correct tab. buyer_pib holds the identifier for ALL id types.
    const idType = (d.buyer_id_type || "").toUpperCase();
    if (d.partner_id) {
      setBuyerTab("partner");
      setPartnerId(d.partner_id);
    } else if (idType === "VAT" || idType === "TAX") {
      setBuyerTab("foreignCompany");
      setForeignCompany((prev) => ({
        ...prev,
        subtype: idType === "VAT" ? "vat" : "tax",
        id_type: idType,
        name: dh(d.buyer_name) || "",
        tax_id: dh(d.buyer_pib) || "",
        country_code: d.buyer_country || "",
        address: dh(d.buyer_address) || "",
        city: dh(d.buyer_city) || "",
      }));
    } else if (idType === "ID" || idType === "PASS") {
      setBuyerTab("individual");
      setIndividual((prev) => ({
        ...prev,
        subtype: idType === "ID" ? "cg" : "foreign",
        id_type: idType === "ID" ? "ID" : "PASSPORT",
        name: dh(d.buyer_name) || "",
        personal_id: dh(d.buyer_pib) || "",
        country: idType === "PASS" ? (d.buyer_country || "") : "",
        address: dh(d.buyer_address) || "",
        city: dh(d.buyer_city) || "",
      }));
    } else if (d.buyer_name || d.buyer_pib) {
      // TIN (or legacy rows without buyer_id_type) → Pravno lice
      setBuyerTab("newCompany");
      setNewCompany((prev) => ({
        ...prev,
        name: dh(d.buyer_name) || "",
        tax_id: dh(d.buyer_pib) || "",
        address: dh(d.buyer_address) || "",
        city: dh(d.buyer_city) || "",
      }));
    }

    // Items — map detail items into the editor's row shape. unit_price in
    // the detail response is the NET price as originally entered.
    if (Array.isArray(d.items) && d.items.length > 0) {
      setItems(d.items.map((it) => ({
        uid:            Math.random().toString(36).slice(2),
        product_id:     it.product_id ?? null,
        name:           dh(it.name) || "",
        quantity:       Number(it.quantity) || 1,
        unit:           it.unit || "kom",
        unit_price:     Number(it.unit_price) || 0,
        vat_rate:       Number(it.vat_rate) ?? (isPausalac ? 0 : 21),
        discount_type:  it.discount_type || "none",
        discount_value: Number(it.discount_value) || 0,
      })));
    }

    // Header fields
    if (d.tcr_code) setTcrCode(d.tcr_code);
    if (d.invoice_date) setInvDate(String(d.invoice_date).slice(0, 10));
    if (d.payment_due) {
      const known = PAYMENT_DUE_PRESETS.some((p) => p.id === d.payment_due);
      if (known) {
        setPayDue(d.payment_due);
      } else if (/^\d{4}-\d{2}-\d{2}/.test(d.payment_due)) {
        setPayDue("custom");
        setPayDueDate(String(d.payment_due).slice(0, 10));
      }
    }
    if (d.notes) setNote(dh(d.notes));
    if (d.contract_info) setContractInfo(dh(d.contract_info));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftEdit]);

  // Load config + partners on mount
  useEffect(() => {
    if (!canWrite) return;
    let active = true;
    (async () => {
      setCfgLoad(true);
      const [cfg, parts] = await Promise.all([
        api("/b2b/config"),
        api("/partners"),
      ]);
      if (!active) return;
      setCfgLoad(false);
      if (cfg.success) {
        setConfig(cfg.data);
        if (cfg.data.default_tcr_code) setTcrCode(cfg.data.default_tcr_code);
        if (cfg.data.b2b_settings?.default_currency) setCurrency(cfg.data.b2b_settings.default_currency);
      } else {
        setCfgErr(cfg.message || "Greška pri učitavanju konfiguracije");
      }
      if (parts.success) {
        const list = Array.isArray(parts.data) ? parts.data
                   : (Array.isArray(parts.data?.partners) ? parts.data.partners : []);
        setPartners(list);
        // Auto-apply prefilled partner from deep-link
        if (prefilledPartnerId) {
          const exists = list.find((p) => p.id === prefilledPartnerId);
          if (exists) {
            setBuyerTab("partner");
            setPartnerId(prefilledPartnerId);
          }
        }
      }
    })();
    return () => { active = false; };
  }, [canWrite, prefilledPartnerId]);

  // Block viewers entirely — AFTER all hooks
  if (!canWrite) {
    return (
      <>
        <TopHeader title="Novi račun" isMobile={isMobile} />
        <div style={{ padding: isMobile ? 16 : 28 }}>
          <div style={{
            background: S.card, borderRadius: 14, padding: 32, textAlign: "center",
            border: `1px solid ${S.border}`,
          }}>
            <div style={{
              display: "inline-flex", width: 56, height: 56, borderRadius: 14,
              background: "#fef2f2", alignItems: "center", justifyContent: "center",
              marginBottom: 12,
            }}>
              <AlertCircle size={26} color={S.red} />
            </div>
            <h2 style={{ margin: 0, fontSize: 18, color: S.dark }}>Pristup je samo za čitanje</h2>
            <p style={{ color: S.muted, fontSize: 14, marginTop: 8 }}>
              Vaša uloga (viewer) nema dozvolu za kreiranje računa.
            </p>
          </div>
        </div>
      </>
    );
  }

  const tcrs = Array.isArray(config?.all_tcrs) ? config.all_tcrs : [];

  // ── Live totals ─────────────────────────────────────────────────────
  const totals = (() => {
    let itemNet = 0;
    const byRate = {};
    for (const it of items) {
      const qty = parseNum(it.quantity);
      const up  = parseNum(it.unit_price);
      const rate = parseNum(it.vat_rate);
      if (qty <= 0 || up < 0) continue;

      const gross = qty * up;
      let disc = 0;
      const dv = parseNum(it.discount_value);
      if (it.discount_type === "percentage") disc = gross * (dv / 100);
      else if (it.discount_type === "fixed") disc = dv;
      const net = Math.max(0, gross - disc);
      itemNet += net;

      const key = String(rate);
      if (!byRate[key]) byRate[key] = { rate, net: 0, vat: 0 };
      byRate[key].net += net;
    }

    // Apply invoice-level discount proportionally
    let invDiscFixed = 0;
    if (invDiscType === "percentage") invDiscFixed = itemNet * (parseNum(invDiscValue) / 100);
    else if (invDiscType === "fixed") invDiscFixed = parseNum(invDiscValue);
    invDiscFixed = Math.min(itemNet, Math.max(0, invDiscFixed));

    if (itemNet > 0 && invDiscFixed > 0) {
      const factor = (itemNet - invDiscFixed) / itemNet;
      for (const k of Object.keys(byRate)) {
        byRate[k].net = byRate[k].net * factor;
      }
    }

    let netTotal = 0, vatTotal = 0;
    for (const k of Object.keys(byRate)) {
      byRate[k].vat = byRate[k].net * (byRate[k].rate / 100);
      netTotal += byRate[k].net;
      vatTotal += byRate[k].vat;
    }

    const breakdown = Object.values(byRate)
      .sort((a, b) => b.rate - a.rate)
      .map((r) => ({
        rate: r.rate,
        net:  Math.round(r.net * 100) / 100,
        vat:  Math.round(r.vat * 100) / 100,
      }));

    return {
      itemNet: Math.round(itemNet * 100) / 100,
      invDiscount: Math.round(invDiscFixed * 100) / 100,
      net: Math.round(netTotal * 100) / 100,
      vat: Math.round(vatTotal * 100) / 100,
      total: Math.round((netTotal + vatTotal) * 100) / 100,
      breakdown,
    };
  })();

  // ── Validation ──────────────────────────────────────────────────────
  const validate = () => {
    const e = {};
    if (!tcrCode) e.tcrCode = "Izaberite TCR";

    if (buyerTab === "partner") {
      if (!partnerId) e.partnerId = "Izaberite partnera";
    } else if (buyerTab === "newCompany") {
      if (!newCompany.name.trim()) e.newCompanyName = "Naziv je obavezan";
      if (!newCompany.tax_id.trim()) e.newCompanyTaxId = "PIB je obavezan";
      else if (!/^\d{8}$/.test(newCompany.tax_id.trim())) {
        e.newCompanyTaxId = "PIB mora imati 8 cifara";
      }
    } else if (buyerTab === "foreignCompany") {
      if (!foreignCompany.subtype) e.foreignSubtype = "Izaberite tip strane firme";
      if (!foreignCompany.name.trim()) e.foreignName = "Naziv firme je obavezan";
      const fId = (foreignCompany.tax_id || "").toUpperCase().trim();
      if (!fId) {
        e.foreignId = "Identifikacioni broj je obavezan";
      } else if (foreignCompany.subtype === "vat" && !/^[A-Z]{2}[A-Z0-9]{8,12}$/.test(fId)) {
        e.foreignId = "VAT broj: 2 slova države + 8–12 alfanumeričkih (npr. IT12345678901)";
      } else if (foreignCompany.subtype === "tax" && !/^[A-Z0-9-]{4,20}$/.test(fId)) {
        e.foreignId = "Tax broj: 4–20 alfanumeričkih karaktera";
      }
      // Country: dropdown must be set, and if "OTHER" then manual must be valid alpha-3
      if (!foreignCompany.country_code) {
        e.foreignCountry = "Izaberite državu";
      } else if (foreignCompany.country_code === "OTHER") {
        const m = (foreignCompany.country_manual || "").toUpperCase().replace(/[^A-Z]/g, "");
        if (m.length !== 3) e.foreignCountry = "Šifra države mora imati 3 slova (alpha-3)";
      }
    } else if (buyerTab === "individual") {
      if (!individual.subtype) e.individualSubtype = "Izaberite tip fizičkog lica";
      if (!individual.name.trim()) e.individualName = "Ime i prezime su obavezni";
      if (!individual.personal_id.trim()) e.individualId = "Identifikacioni broj je obavezan";
      if (individual.subtype === "cg" && individual.personal_id.length !== 13) {
        e.individualId = "JMBG mora imati 13 cifara";
      }
      if (individual.subtype === "foreign") {
        const pId = (individual.personal_id || "").toUpperCase();
        if (pId && !/^[A-Z0-9]{5,15}$/.test(pId)) {
          e.individualId = "Broj pasoša: 5–15 alfanumeričkih karaktera";
        }
        if (!individual.country) {
          e.individualCountry = "Izaberite državu";
        } else if (individual.country === "OTHER") {
          const m = (individual.country_manual || "").toUpperCase().replace(/[^A-Z]/g, "");
          if (m.length !== 3) e.individualCountry = "Šifra države mora imati 3 slova (alpha-3)";
        }
      }
    }

    const validItems = items.filter(
      (it) => it.name.trim() && parseNum(it.quantity) > 0 && parseNum(it.unit_price) >= 0
    );
    if (validItems.length === 0) e.items = "Dodajte barem jednu stavku";

    if (isPausalac) {
      const hasNonZeroVat = validItems.some((it) => parseNum(it.vat_rate) > 0);
      if (hasNonZeroVat) e.pausalacVat = "Pausalna firma — sve stavke moraju biti na 0% PDV";
    }

    if (paymentDue === "custom" && !paymentDueDate) e.paymentDueDate = "Izaberite datum";

    setValidationErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── Build POST body for /api/invoices/store ─────────────────────────
  const buildInvoiceBody = (resolvedPartnerId, locationId) => {
    const validItems = items
      .filter((it) => it.name.trim() && parseNum(it.quantity) > 0 && parseNum(it.unit_price) >= 0)
      .map((it) => ({
        name:           it.name.trim(),
        product_id:     it.product_id || null,
        quantity:       parseNum(it.quantity),
        unit_price:     parseNum(it.unit_price),
        vat_rate:       parseNum(it.vat_rate),
        discount_type:  it.discount_type,
        discount_value: parseNum(it.discount_value),
        unit:           it.unit || "kom",
      }));

    // Resolve the country alpha-3 code from "OTHER" → manual entry, else the
    // dropdown value, else null.
    const resolveCountryCode = (selectValue, manualValue) => {
      if (selectValue === "OTHER") {
        const m = (manualValue || "").toUpperCase().replace(/[^A-Z]/g, "");
        return m.length === 3 ? m : null;
      }
      return selectValue || null;
    };

    const body = {
      location_id:    locationId,
      tcr_code:       tcrCode,
      // type_of_inv / invoice_type / payment_method are forced by /b2b/invoice-store
      // (always NONCASH bank_transfer) — sending them anyway for clarity and
      // backward compat if we ever wire this body to the POS endpoint as fallback.
      invoice_type:   "non_cash",
      type_of_inv:    "NONCASH",
      payment_method: "bank_transfer",
      currency,
      invoice_date:   invoiceDate,
      items:          validItems,
      discount_type:  invDiscType,
      discount_value: parseNum(invDiscValue),
      // Server reads body['notes'] — keep the legacy alias too in case any
      // other code path still expects 'note'.
      notes:          note || null,
      contract_info:  contractInfo || null,
      note:           note || null,
    };

    // ── Resolve buyer based on which tab is active ────────────────────────
    // The new B2B endpoint reads: buyer_name, buyer_pib, buyer_id_type,
    // buyer_country (ISO alpha-3), buyer_address, buyer_city, partner_id.
    // For registered partners, the backend pulls id_type/country_code FROM
    // the partner row, so we only need to send partner_id. Frontend overrides
    // still take precedence at the controller level if present.

    if (buyerTab === "partner" && resolvedPartnerId) {
      body.partner_id = resolvedPartnerId;
      // For partners, buyer fields come from the partner row at the backend.
      // We don't override buyer_id_type / buyer_country — the backend reads
      // them from partners.id_type / partners.country_code.
    } else if (buyerTab === "newCompany") {
      body.buyer_name    = newCompany.name || "";
      body.buyer_pib     = newCompany.tax_id || "";
      body.buyer_address = newCompany.address || "";
      body.buyer_city    = newCompany.city || "";
      body.buyer_id_type = "TIN";           // MNE company = always TIN
      body.buyer_country = "MNE";
    } else if (buyerTab === "foreignCompany" && foreignCompany.subtype) {
      body.buyer_name    = foreignCompany.name || "";
      body.buyer_pib     = foreignCompany.tax_id || "";
      body.buyer_address = foreignCompany.address || "";
      body.buyer_city    = foreignCompany.city || "";
      body.buyer_id_type = foreignCompany.subtype === "vat" ? "VAT" : "TAX";
      body.buyer_country = resolveCountryCode(foreignCompany.country_code, foreignCompany.country_manual) || "MNE";
    } else if (buyerTab === "individual" && individual.subtype) {
      body.buyer_name    = individual.name;
      body.buyer_pib     = individual.personal_id;
      body.buyer_address = individual.address;
      body.buyer_city    = individual.city;
      if (individual.subtype === "cg") {
        body.buyer_id_type = "ID";          // MNE JMBG
        body.buyer_country = "MNE";
      } else {
        // foreign — passport
        body.buyer_id_type = "PASS";
        body.buyer_country = resolveCountryCode(individual.country, individual.country_manual) || "MNE";
      }
    }

    // Payment due
    if (paymentDue === "custom" && paymentDueDate) body.payment_due = paymentDueDate;
    else body.payment_due = paymentDue;

    return body;
  };

  // ── Submit (create + optionally fiscalize) ──────────────────────────
  const submit = async ({ fiscalize }) => {
    if (!validate()) {
      setToast({ type: "error", message: "Provjerite obavezna polja" });
      setTimeout(() => setToast(null), 3500);
      return;
    }
    if (!config?.location?.id) {
      setToast({ type: "error", message: "Lokacija nije pronađena" });
      setTimeout(() => setToast(null), 3500);
      return;
    }

    setSubmitting(true);

    // 1) Resolve/create partner if needed.
    //
    // Save-as-partner now supports 3 tab variants. In each case we pass the
    // correct partner_kind + id_type + country_code so the partner row
    // accurately reflects what kind of buyer this is — which matters because
    // future invoices to this partner will pull these fields from the partner
    // record (B2bApiController::invoiceStore reads partner.id_type/country_code).
    let resolvedPartnerId = partnerId || null;
    const resolveCountryAlpha3 = (selectValue, manualValue) => {
      if (selectValue === "OTHER") {
        const m = (manualValue || "").toUpperCase().replace(/[^A-Z]/g, "");
        return m.length === 3 ? m : null;
      }
      return selectValue || null;
    };

    // Determine which tab triggered save-as-partner and build the payload
    let partnerPayload = null;
    if (buyerTab === "newCompany" && saveAsPartner) {
      partnerPayload = {
        partner_type: "customer",
        partner_kind: "company",
        name:         newCompany.name,
        tax_id:       newCompany.tax_id,
        id_type:      "TIN",
        address:      newCompany.address,
        city:         newCompany.city,
        country:      newCompany.country || "Montenegro",
        country_code: "MNE",
        email:        newCompany.email,
        phone:        newCompany.phone,
        bank_account: newCompany.bank_account,
      };
    } else if (buyerTab === "foreignCompany" && saveAsPartner && foreignCompany.subtype) {
      const cc = resolveCountryAlpha3(foreignCompany.country_code, foreignCompany.country_manual);
      partnerPayload = {
        partner_type: "customer",
        partner_kind: "company",
        name:         foreignCompany.name,
        tax_id:       foreignCompany.tax_id,
        id_type:      foreignCompany.subtype === "vat" ? "VAT" : "TAX",
        address:      foreignCompany.address,
        city:         foreignCompany.city,
        country:      cc ? (COUNTRY_CODES.find((c) => c.code === cc)?.name || cc) : "",
        country_code: cc,
        email:        foreignCompany.email,
      };
    } else if (buyerTab === "individual" && saveAsPartner && individual.subtype) {
      const cc = individual.subtype === "cg"
        ? "MNE"
        : resolveCountryAlpha3(individual.country, individual.country_manual);
      partnerPayload = {
        partner_type: "customer",
        partner_kind: "individual",
        name:         individual.name,
        tax_id:       individual.personal_id,
        id_type:      individual.subtype === "cg" ? "ID" : "PASS",
        address:      individual.address,
        city:         individual.city,
        country:      cc ? (COUNTRY_CODES.find((c) => c.code === cc)?.name || cc) : "Montenegro",
        country_code: cc,
        email:        individual.email,
      };
    }

    if (partnerPayload) {
      const pr = await api("/partners", {
        method: "POST",
        body:   JSON.stringify(partnerPayload),
      });
      if (pr.success && pr.data?.partner?.id) {
        resolvedPartnerId = pr.data.partner.id;
        setPartners((prev) => [...prev, pr.data.partner]);
      } else {
        setSubmitting(false);
        setToast({ type: "error", message: pr.message || "Greška pri kreiranju partnera" });
        setTimeout(() => setToast(null), 4000);
        return;
      }
    }

    // 2) Create the invoice
    // Using the NEW B2B-only endpoint that supports full BuyerIDType set
    // (TIN/ID/PASS/VAT/TAX) and ISO 3166-1 alpha-3 country codes. The POS
    // endpoint /api/invoices/store is left untouched and continues to
    // serve POS clients with the legacy TIN-only flow.
    const body = buildInvoiceBody(resolvedPartnerId, config.location.id);
    const r = await api("/b2b/invoice-store", { method: "POST", body: JSON.stringify(body) });
    if (!r.success) {
      setSubmitting(false);
      setToast({ type: "error", message: r.message || "Greška pri kreiranju računa" });
      setTimeout(() => setToast(null), 5000);
      return;
    }
    const invoiceId = r.data?.invoice?.id || r.data?.id || r.data?.invoice_id;
    if (!invoiceId) {
      setSubmitting(false);
      setToast({ type: "error", message: "Račun kreiran ali ID nije vraćen" });
      setTimeout(() => setToast(null), 4000);
      return;
    }

    // 3) Fiscalize (if requested)
    if (fiscalize) {
      const f = await api(`/invoices/fiscalize/${invoiceId}`, { method: "POST" });
      setSubmitting(false);
      if (f.success) {
        setSuccessInvoiceId(invoiceId);
        // Draft-edit flow: the NEW invoice replaced the old draft — remove
        // the old draft so it doesn't linger as a duplicate. Order matters:
        // delete only AFTER successful fiscalization, so a failed attempt
        // never loses the user's draft. If the cleanup itself fails, the new
        // invoice is still valid — tell the user to remove the draft by hand.
        if (editingDraftId) {
          const del = await api(`/b2b/invoice-delete/${editingDraftId}`, { method: "POST", body: "{}" });
          setEditingDraftId(null);
          setEditingDraftNo("");
          if (del.success) {
            setToast({ type: "success", message: "Račun fiskalizovan — stari nacrt obrisan" });
          } else {
            setToast({ type: "error", message: "Račun fiskalizovan, ali stari nacrt nije obrisan — obrišite ga ručno u listi računa" });
          }
        } else {
          setToast({ type: "success", message: "Račun fiskalizovan" });
        }
      } else {
        // Created but failed to fiscalize — surface the actual error
        setSuccessInvoiceId(invoiceId);
        setToast({
          type: "error",
          message: "Račun kreiran ali fiskalizacija nije uspjela: " + (f.message || "Nepoznata greška"),
        });
      }
    } else {
      setSubmitting(false);
      setSuccessInvoiceId(invoiceId);
      setToast({ type: "success", message: "Račun sačuvan kao nacrt" });
    }
    setTimeout(() => setToast(null), 5000);
  };

  const resetForm = () => {
    setItems([emptyItem(defaultVat)]);
    setPartnerId(0);
    setNewCompany({ name: "", tax_id: "", address: "", city: "", country: "Montenegro", email: "", phone: "", bank_account: "" });
    setSaveAsPartner(false);
    setNote("");
    setContractInfo("");
    setInvDiscType("none");
    setInvDiscValue(0);
    setValidationErrors({});
    setSuccessInvoiceId(null);
  };

  if (configLoading) {
    return (
      <>
        <TopHeader title="Novi račun" isMobile={isMobile} />
        <div style={{ padding: isMobile ? 16 : 28 }}>
          <Skeleton lines={8} />
        </div>
      </>
    );
  }
  if (configError) {
    return (
      <>
        <TopHeader title="Novi račun" isMobile={isMobile} />
        <div style={{ padding: isMobile ? 16 : 28 }}>
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
            padding: "14px 18px", display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <AlertCircle size={16} /> {configError}
          </div>
        </div>
      </>
    );
  }

  const showCertWarning = config && !config.has_certificate;

  // After success — show success card with link to detail
  if (successInvoiceId) {
    // Post-create view: show the full just-created invoice details (so the
    // operator can verify everything is correct) PLUS action buttons at the
    // bottom for next steps. Reuses InvoiceDetailPanel embedded inline.
    return (
      <>
        <TopHeader title="Račun kreiran" isMobile={isMobile} />
        <div style={{ padding: isMobile ? 12 : 24, maxWidth: 900, margin: "0 auto" }}>
          {/* Success banner — slim, doesn't dominate the screen */}
          <div style={{
            display: "flex", alignItems: "center", gap: 12,
            background: "#dcfce7", border: `1px solid ${S.green}30`,
            padding: "12px 16px", borderRadius: 10, marginBottom: 16,
          }}>
            <CircleCheck size={20} color={S.green} style={{ flexShrink: 0 }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, color: S.dark, fontSize: 14 }}>Račun je uspješno kreiran</div>
              <div style={{ fontSize: 12, color: S.muted }}>ID: #{successInvoiceId}</div>
            </div>
          </div>

          {/* Embedded invoice detail — loads via InvoiceDetailPanel which
              calls /b2b/invoice/:id and renders buyer, items, totals, status,
              napomena, etc. The onClose prop is wired to resetForm so the
              user gets a quick way to start a new invoice. */}
          <InvoiceDetailPanel
            id={successInvoiceId}
            onClose={resetForm}
            isMobile={isMobile}
            canWrite={canWrite}
            embedded={true}
          />

          {/* Action buttons row — sticky on desktop at top, just inline on mobile */}
          <div style={{
            display: "flex", flexDirection: isMobile ? "column" : "row",
            gap: 10, marginTop: 16, justifyContent: "center",
          }}>
            <button
              onClick={resetForm}
              style={{ ...S.btn(S.accent), justifyContent: "center", padding: "12px 20px" }}
            >
              <FilePlus2 size={16} /> Novi račun
            </button>
            <button
              onClick={goToInvoices}
              style={{ ...S.btn("white", S.dark), border: `1px solid ${S.border}`, justifyContent: "center", padding: "12px 20px" }}
            >
              <Receipt size={16} /> Lista svih računa
            </button>
          </div>

          {isMobile && <div style={{ height: 80 }} />}
        </div>
        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </>
    );
  }

  return (
    <>
      <TopHeader
        title={editingDraftId ? "Uredi nacrt" : "Novi račun"}
        subtitle={
          editingDraftId ? `Uređivanje nacrta ${editingDraftNo || `#${editingDraftId}`} — nakon fiskalizacije stari nacrt se briše` :
          mode === "corrective" ? "Korektivni račun (ispravka)" :
          mode === "advance" ? "Avansni račun (predujam)" :
          "Bezgotovinski račun (virman)"
        }
        isMobile={isMobile}
      />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1400 }}>
        {/* Mode tabs */}
        <div style={{
          background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
          padding: 4, marginBottom: 16, display: "flex", gap: 4,
          overflowX: "auto",
        }} className="hide-scrollbar">
          <ViewModeBtn active={mode === "standard"}   onClick={() => setMode("standard")}   icon={Receipt}    label="Standardni" />
          <ViewModeBtn active={mode === "corrective"} onClick={() => setMode("corrective")} icon={Edit3}      label="Korektivni" />
          <ViewModeBtn active={mode === "advance"}    onClick={() => setMode("advance")}    icon={CreditCard} label="Avansni" />
        </div>

        {mode === "corrective" ? (
          <CorrectiveInvoicePanel
            isMobile={isMobile}
            config={config}
            onSuccess={(id) => setSuccessInvoiceId(id)}
            isPausalac={isPausalac}
          />
        ) : mode === "advance" ? (
          <AdvanceInvoicePanel
            isMobile={isMobile}
            config={config}
            onSuccess={(id) => setSuccessInvoiceId(id)}
            isPausalac={isPausalac}
          />
        ) : (
          <>
        {showCertWarning && (
          <div style={{
            background: "#fef3c7", border: "1px solid #fde68a", color: S.amber,
            borderRadius: 10, padding: "12px 16px", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8, fontSize: 14,
          }}>
            <AlertCircle size={16} /> Nemate aktivan certifikat. Fiskalizacija neće raditi.
          </div>
        )}

        <div style={{
          display: "grid", gap: 16,
          gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1.5fr) minmax(320px, 1fr)",
        }}>
          {/* LEFT — Buyer + Items */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <BuyerSection
              tab={buyerTab}
              setTab={setBuyerTab}
              partners={partners}
              partnerId={partnerId}
              setPartnerId={setPartnerId}
              newCompany={newCompany}
              setNewCompany={setNewCompany}
              saveAsPartner={saveAsPartner}
              setSaveAsPartner={setSaveAsPartner}
              individual={individual}
              setIndividual={setIndividual}
              foreignCompany={foreignCompany}
              setForeignCompany={setForeignCompany}
              errors={validationErrors}
              isMobile={isMobile}
            />

            <ItemsEditor
              items={items}
              setItems={setItems}
              defaultVat={defaultVat}
              isPausalac={isPausalac}
              errors={validationErrors}
              isMobile={isMobile}
            />
          </div>

          {/* RIGHT — Config + Totals */}
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <InvoiceConfig
              tcrCode={tcrCode} setTcrCode={setTcrCode}
              tcrs={tcrs}
              invoiceDate={invoiceDate} setInvDate={setInvDate}
              paymentDue={paymentDue} setPayDue={setPayDue}
              paymentDueDate={paymentDueDate} setPayDueDate={setPayDueDate}
              currency={currency} setCurrency={setCurrency}
              note={note} setNote={setNote}
              contractInfo={contractInfo} setContractInfo={setContractInfo}
              invDiscType={invDiscType} setInvDiscType={setInvDiscType}
              invDiscValue={invDiscValue} setInvDiscValue={setInvDiscValue}
              errors={validationErrors}
            />

            <TotalsBox totals={totals} currency={currency} />

            <ActionButtons
              submitting={submitting}
              canFiscalize={!!config?.has_certificate}
              onDraft={() => submit({ fiscalize: false })}
              onFiscalize={() => {
                // Validate first — no point showing confirm modal if the form is broken
                if (!validate()) return;
                setConfirmAction("fiscalize");
              }}
            />
          </div>
        </div>
          </>
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {/* Confirmation modal — last-chance review before fiscalization */}
      {confirmAction === "fiscalize" && (() => {
        const buyerSummary =
          buyerTab === "partner"
            ? (partners.find((p) => p.id === partnerId)?.name || "—")
            : buyerTab === "newCompany"
              ? (newCompany.name || "—")
              : buyerTab === "foreignCompany"
                ? (foreignCompany.name || "—")
                : buyerTab === "individual"
                  ? (individual.name || "—")
                  : "—";
        const itemCount = items.filter((it) => it.name.trim() && parseNum(it.quantity) > 0).length;
        return (
          <div
            onClick={() => !submitting && setConfirmAction(null)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 300,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
            }}
          >
            <div onClick={(e) => e.stopPropagation()} style={{
              background: S.card, borderRadius: 14, padding: 22, maxWidth: 460, width: "100%",
            }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12, marginBottom: 14 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: 12, background: "#dcfce7",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}>
                  <CircleCheck size={22} color={S.green} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 17, color: S.dark }}>Fiskalizovati račun?</h3>
                  <p style={{ fontSize: 12, color: S.muted, marginTop: 4, marginBottom: 0, lineHeight: 1.5 }}>
                    Račun će biti poslat Poreskoj upravi i postaje važeći fiskalni dokument.
                    Nakon fiskalizacije može se samo stornirati ili korigovati.
                  </p>
                </div>
              </div>
              <div style={{
                background: S.bg, borderRadius: 10, padding: "12px 14px",
                marginBottom: 16, display: "flex", flexDirection: "column", gap: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: S.muted }}>Kupac:</span>
                  <strong style={{ color: S.dark, textAlign: "right", marginLeft: 12, wordBreak: "break-word" }}>{buyerSummary}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13 }}>
                  <span style={{ color: S.muted }}>Stavki:</span>
                  <strong style={{ color: S.dark }}>{itemCount}</strong>
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 15, paddingTop: 6, borderTop: `1px solid ${S.borderSoft}` }}>
                  <span style={{ color: S.muted, fontWeight: 600 }}>Ukupno za plaćanje:</span>
                  <strong style={{ color: S.green, fontFamily: S.mono }}>{fmtMoney(totals.total)} {currency}</strong>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setConfirmAction(null)} disabled={submitting} style={{
                  flex: 1, ...S.btnSm("white", S.dark),
                  border: `1px solid ${S.border}`, padding: "12px", justifyContent: "center",
                }}>Otkaži</button>
                <button onClick={() => { setConfirmAction(null); submit({ fiscalize: true }); }} disabled={submitting} style={{
                  flex: 1, ...S.btn(S.green), padding: "12px", justifyContent: "center", fontWeight: 600,
                }}>
                  <CircleCheck size={15} /> Potvrdi i fiskalizuj
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// CORRECTIVE INVOICE PANEL (Korektivni račun)
//
// Workflow:
//   1. User searches for an existing fiscalized invoice by number or ID
//   2. Items are loaded into editable rows; user adjusts (price, qty)
//   3. POST /invoices/correct/:id — backend creates correction invoice
//
// Backend reference: InvoicesApiController::correct + POS app implementation
// ════════════════════════════════════════════════════════════════════════════
function CorrectiveInvoicePanel({ isMobile, config, onSuccess, isPausalac }) {
  const [search, setSearch] = useState("");
  const [origInvoice, setOrigInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [buyerName, setBuyerName] = useState("");
  const [buyerTin, setBuyerTin] = useState("");
  const [reason, setReason] = useState("");
  // Payment method always Virman for B2B (see Avans for full rationale)
  const paymentMethod = "bank_transfer";
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message, ms = 3500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  const loadInvoice = async () => {
    if (!search.trim()) return;
    setLoading(true);
    setOrigInvoice(null);

    // Try search by display_no/invoice_number
    let r = await api(`/invoices?search=${encodeURIComponent(search.trim())}&per_page=5`);
    let found = null;
    if (r.success && Array.isArray(r.data?.invoices) && r.data.invoices.length > 0) {
      found = r.data.invoices.find(
        (inv) => inv.status === "fiscalized" && !inv.stornoed_by_invoice_id
                 && !inv.corrected_by_invoice_id && inv.is_storno !== 1
      ) || r.data.invoices[0];
    }

    // Fallback to numeric ID
    if (!found) {
      const idNum = parseInt(search.trim(), 10);
      if (idNum > 0) {
        const r2 = await api(`/invoices/show/${idNum}`);
        if (r2.success && r2.data?.invoice) found = r2.data.invoice;
      }
    }

    if (!found) {
      setLoading(false);
      showToast("error", "Račun nije pronađen ili ne može biti korigovan", 4500);
      return;
    }

    // Load full detail
    const det = await api(`/invoices/show/${found.id}`);
    setLoading(false);
    if (!det.success || !det.data) {
      showToast("error", det.message || "Greška pri učitavanju računa", 4000);
      return;
    }

    const full = det.data;
    setOrigInvoice(full);
    // payment_method is now hardcoded "bank_transfer" — no setter to call
    setBuyerName(dh(full.invoice?.buyer_name) || "");
    setBuyerTin(full.invoice?.buyer_pib || "");
    const itemList = (full.items || []).map((it) => ({
      uid: Math.random().toString(36).slice(2, 9),
      product_id: it.product_id || null,
      name:           dh(it.item_name || it.name) || "Stavka",
      quantity:       Math.abs(parseFloat(it.quantity || 1)),
      unit_price:     parseFloat(it.unit_price || 0),
      vat_rate:       Number(it.vat_rate != null ? it.vat_rate : (isPausalac ? 0 : 21)),
      unit:           it.unit || "kom",
      discount_type:  it.discount_type || "none",
      discount_value: parseFloat(it.discount_value || 0),
    }));
    setItems(itemList);
  };

  const updateItem = (uid, patch) => {
    setItems((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));
  };
  const removeItem = (uid) => {
    setItems((prev) => prev.filter((it) => it.uid !== uid));
  };

  const submit = async () => {
    if (!origInvoice) return;
    if (items.length === 0) {
      showToast("error", "Nema stavki za korekciju");
      return;
    }
    if (!reason.trim()) {
      showToast("error", "Razlog korekcije je obavezan");
      return;
    }

    setSubmitting(true);
    const body = {
      items: items.map((it) => ({
        product_id:     it.product_id,
        name:           it.name,
        quantity:       parseNum(it.quantity),
        unit_price:     parseNum(it.unit_price),
        vat_rate:       parseNum(it.vat_rate),
        unit:           it.unit || "kom",
        discount_type:  it.discount_type || "none",
        discount_value: parseNum(it.discount_value),
      })),
      payment_method: paymentMethod,
      buyer_name: buyerName,
      buyer_tin: buyerTin,
      correction_reason: reason,
    };

    const r = await api(`/invoices/correct/${origInvoice.invoice.id}`, {
      method: "POST",
      body: JSON.stringify(body),
    });
    setSubmitting(false);

    if (r.success) {
      const newId = r.data?.corrected_invoice?.id || r.data?.invoice?.id;
      showToast("success", `Korekcija uspješna! ${r.data?.corrected_invoice?.invoice_number || ""}`);
      setSearch(""); setOrigInvoice(null); setItems([]); setReason("");
      if (newId) onSuccess(newId);
    } else {
      showToast("error", r.message || "Greška pri korekciji", 5000);
    }
  };

  const totals = useInvoiceTotals(items);

  return (
    <>
      <div style={{
        background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10,
        padding: "12px 16px", marginBottom: 16, color: "#92400e",
        display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13,
      }}>
        <Edit3 size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          Korektivni račun ispravlja prethodno fiskalizovan račun. Originalni račun
          ostaje validan, a novi korektivni se referencira na njega. Možete izmijeniti
          stavke (cijene, količine, PDV) i razlog korekcije je obavezan.
        </div>
      </div>

      {/* Search for original */}
      <Card title="Pronađi originalni račun" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadInvoice()}
            placeholder="Broj računa (npr. 5/2026/lc460yo052/ge719sy492) ili ID"
            style={{ ...S.input, flex: 1, minWidth: 240, fontFamily: S.mono }}
          />
          <button
            onClick={loadInvoice}
            disabled={loading || !search.trim()}
            style={{ ...S.btn(S.accent), padding: "10px 18px" }}
          >
            {loading ? <Loader2 size={14} className="spin" /> : <Search size={14} />} Traži
          </button>
        </div>
      </Card>

      {origInvoice && (
        <div style={{
          display: "grid", gap: 16,
          gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1.5fr) minmax(320px, 1fr)",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card title={`Originalni račun: ${origInvoice.invoice?.display_no || origInvoice.invoice?.invoice_number}`}>
              <div style={{ display: "grid", gap: 6, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", fontSize: 13 }}>
                <KV label="Datum" value={fmtDateTime(origInvoice.invoice?.invoice_date)} />
                <KV label="Status" value={statusLabel(origInvoice.invoice?.status)} />
                <KV label="Iznos" value={fmtMoney(origInvoice.invoice?.total_amount)} />
                <KV label="TCR" value={origInvoice.invoice?.tcr_code} mono />
              </div>
            </Card>

            <Card title="Stavke za korekciju" subtitle={`${items.length} ${items.length === 1 ? "stavka" : "stavki"}`}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {items.map((it, idx) => (
                  <ItemRow
                    key={it.uid}
                    item={it}
                    index={idx}
                    onUpdate={(patch) => updateItem(it.uid, patch)}
                    onRemove={() => removeItem(it.uid)}
                    onOpenPicker={() => {}} // not used in corrective
                    isPausalac={isPausalac}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            </Card>

            <Card title="Razlog korekcije">
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="npr. Pogrešno upisana cijena, ispravljena količina…"
                style={{ ...S.input, resize: "vertical", minHeight: 70, fontFamily: S.font }}
              />
            </Card>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card title="Kupac">
              <Field label="Naziv">
                <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} style={S.input} />
              </Field>
              <div style={{ marginTop: 10 }}>
                <Field label="PIB / Lični broj">
                  <input value={buyerTin} onChange={(e) => setBuyerTin(e.target.value)} style={{ ...S.input, fontFamily: S.mono }} />
                </Field>
              </div>
              <div style={{ marginTop: 10 }}>
                {/* Payment method dropdown removed — B2B corrective is always
                    Virman (bezgotovinski). The paymentMethod state remains
                    "bank_transfer" so the backend correctly sets
                    type_of_inv=NONCASH for Poreska XML. */}
                <div style={{
                  padding: "8px 12px", background: S.bg,
                  border: `1px solid ${S.border}`, borderRadius: 8,
                  fontSize: 12, color: S.muted, display: "flex", alignItems: "center", gap: 6,
                }}>
                  <CreditCard size={13} />
                  <span>Način plaćanja: <strong style={{ color: S.dark }}>Virman (bezgotovinski)</strong></span>
                </div>
              </div>
            </Card>

            <TotalsBox totals={totals} currency={origInvoice.invoice?.currency || "EUR"} />

            <div style={{
              background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: 12,
              display: "flex", flexDirection: "column", gap: 8,
            }}>
              <button
                onClick={submit}
                disabled={submitting || !reason.trim()}
                style={{ ...S.btn(S.accent), padding: "12px 18px", justifyContent: "center" }}
              >
                {submitting ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
                {submitting ? " Obrađujem…" : " Kreiraj korektivni i fiskalizuj"}
              </button>
              <div style={{ fontSize: 11, color: S.muted, textAlign: "center" }}>
                Korekcija se fiskalizuje odmah po slanju
              </div>
            </div>
          </div>
        </div>
      )}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ADVANCE INVOICE PANEL (Avansni račun)
//
// Two flows:
//   A) Create new advance invoice (buyer prepays before delivery)
//   B) Finalize existing advance (deliver goods, create final invoice that
//      references the advance via /invoices/finalizeAdvance/:advance_id)
//
// Backend reference: InvoicesApiController::advance + ::finalizeAdvance
// ════════════════════════════════════════════════════════════════════════════
function AdvanceInvoicePanel({ isMobile, config, onSuccess, isPausalac }) {
  const [subTab, setSubTab] = useState("create"); // "create" | "finalize"
  return (
    <>
      <div style={{
        background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 10,
        padding: "12px 16px", marginBottom: 16, color: "#1e40af",
        display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13,
      }}>
        <CreditCard size={16} style={{ flexShrink: 0, marginTop: 1 }} />
        <div>
          <strong>Avansni račun</strong> — kreira se kada kupac plati unaprijed.
          Kada se isporuči roba/usluga, kreira se finalni račun koji referencira ovaj avans
          (Poreska ovo zahtijeva za pravilno PDV evidenciju).
        </div>
      </div>

      <div style={{
        display: "flex", gap: 6, marginBottom: 16,
        background: S.card, border: `1px solid ${S.border}`, borderRadius: 10, padding: 4,
      }}>
        <ViewModeBtn active={subTab === "create"}   onClick={() => setSubTab("create")}   icon={FilePlus2} label="Kreiraj novi avansni" />
        <ViewModeBtn active={subTab === "finalize"} onClick={() => setSubTab("finalize")} icon={CircleCheck} label="Finalizuj postojeći" />
      </div>

      {subTab === "create" ? (
        <AdvanceCreatePanel isMobile={isMobile} config={config} onSuccess={onSuccess} isPausalac={isPausalac} />
      ) : (
        <AdvanceFinalizePanel isMobile={isMobile} config={config} onSuccess={onSuccess} isPausalac={isPausalac} />
      )}
    </>
  );
}

function AdvanceCreatePanel({ isMobile, config, onSuccess, isPausalac }) {
  const defaultVat = isPausalac ? 0 : 21;
  const [items, setItems] = useState([emptyItem(defaultVat)]);

  // ── Buyer state — 4 tabs same as NewInvoiceScreen (Pravno lice / Strana firma /
  //    Fizičko lice / Registrovani partner). B2B avans must support every buyer
  //    type because B2B is by definition foreign-friendly. The previous version
  //    only had a name + PIB field which was a regression vs the main flow.
  const [buyerTab, setBuyerTab] = useState("partner");
  const [partnerId, setPartnerId] = useState(0);
  const [partners, setPartners] = useState([]);
  const [newCompany, setNewCompany] = useState({
    name: "", tax_id: "", address: "", city: "", email: "", phone: "",
  });
  const [individual, setIndividual] = useState({
    subtype: "", name: "", personal_id: "", id_type: "ID",
    country: "", country_manual: "", address: "", city: "", email: "", phone: "",
  });
  const [foreignCompany, setForeignCompany] = useState({
    subtype: "", id_type: "VAT", name: "", tax_id: "",
    country_code: "", country_manual: "",
    address: "", city: "", email: "",
  });
  const [validationErrors, setValidationErrors] = useState({});

  // Payment method removed from UI — B2B avans is ALWAYS bank_transfer (Virman).
  // POS handles cash/card avans separately (different controller flow).
  // Hardcoded so the backend gets the right type_of_inv=NONCASH.
  const paymentMethod = "bank_transfer";

  const [tcrCode, setTcrCode] = useState(config?.default_tcr_code || "");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [prodLoaded, setProdLoaded] = useState(false);

  const tcrs = Array.isArray(config?.all_tcrs) ? config.all_tcrs : [];

  const showToast = (type, message, ms = 3500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  useEffect(() => {
    if (config?.default_tcr_code && !tcrCode) setTcrCode(config.default_tcr_code);
  }, [config, tcrCode]);

  // Load partners list for the "Registrovani partner" tab
  useEffect(() => {
    (async () => {
      const r = await api("/partners");
      if (r.success) {
        const list = Array.isArray(r.data) ? r.data
                   : (Array.isArray(r.data?.partners) ? r.data.partners : []);
        setPartners(list);
      }
    })();
  }, []);

  const loadProducts = async () => {
    if (prodLoaded) return;
    const r = await api("/products");
    if (r.success) {
      const list = Array.isArray(r.data) ? r.data
                 : (Array.isArray(r.data?.products) ? r.data.products : []);
      setProducts(list);
    }
    setProdLoaded(true);
  };

  const openPicker = () => { loadProducts(); setPickerOpen(true); };
  const addProductFromPicker = (p) => {
    setItems((prev) => [...prev, {
      ...emptyItem(defaultVat),
      product_id: p.id,
      name: p.name,
      unit_price: parseFloat(p.price ?? 0),
      vat_rate: isPausalac ? 0 : parseFloat(p.vat_rate ?? defaultVat),
      unit: p.unit || "kom",
    }]);
    setPickerOpen(false);
  };
  const addBlank = () => {
    setItems((prev) => [...prev, emptyItem(defaultVat)]);
    setPickerOpen(false);
  };
  const updateItem = (uid, patch) => {
    setItems((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));
  };
  const removeItem = (uid) => {
    setItems((prev) => prev.filter((it) => it.uid !== uid));
  };

  const totals = useInvoiceTotals(items);

  const validate = () => {
    const e = {};
    const validItems = items.filter((it) => it.name && parseNum(it.quantity) > 0 && parseNum(it.unit_price) >= 0);
    if (validItems.length === 0) e.items = "Dodajte barem jednu stavku";
    if (!tcrCode) e.tcr = "Izaberite TCR";

    if (buyerTab === "partner") {
      if (!partnerId) e.partnerId = "Izaberite partnera";
    } else if (buyerTab === "newCompany") {
      if (!newCompany.name.trim()) e.newCompanyName = "Naziv je obavezan";
      if (!newCompany.tax_id.trim()) e.newCompanyTaxId = "PIB je obavezan";
      else if (!/^\d{8}$/.test(newCompany.tax_id.trim())) e.newCompanyTaxId = "PIB mora imati 8 cifara";
    } else if (buyerTab === "foreignCompany") {
      if (!foreignCompany.subtype) e.foreignSubtype = "Izaberite tip strane firme";
      if (!foreignCompany.name.trim()) e.foreignName = "Naziv firme je obavezan";
      const fId = (foreignCompany.tax_id || "").toUpperCase().trim();
      if (!fId) e.foreignId = "Identifikacioni broj je obavezan";
      else if (foreignCompany.subtype === "vat" && !/^[A-Z]{2}[A-Z0-9]{8,12}$/.test(fId)) {
        e.foreignId = "VAT: 2 slova države + 8-12 alfanumeričkih";
      } else if (foreignCompany.subtype === "tax" && !/^[A-Z0-9-]{4,20}$/.test(fId)) {
        e.foreignId = "Tax: 4-20 alfanumeričkih";
      }
      if (!foreignCompany.country_code) e.foreignCountry = "Izaberite državu";
    } else if (buyerTab === "individual") {
      if (!individual.subtype) e.individualSubtype = "Izaberite tip fizičkog lica";
      if (!individual.name.trim()) e.individualName = "Ime i prezime obavezni";
      if (!individual.personal_id.trim()) e.individualId = "Identifikacioni broj obavezan";
      if (individual.subtype === "cg" && individual.personal_id.length !== 13) {
        e.individualId = "JMBG mora imati 13 cifara";
      }
      if (individual.subtype === "foreign" && !individual.country) {
        e.individualCountry = "Izaberite državu";
      }
    }

    setValidationErrors(e);
    return Object.keys(e).length === 0;
  };

  const resolveCountryCode = (selectValue, manualValue) => {
    if (selectValue === "OTHER") {
      const m = (manualValue || "").toUpperCase().replace(/[^A-Z]/g, "");
      return m.length === 3 ? m : null;
    }
    return selectValue || null;
  };

  const submit = async () => {
    if (!validate()) return;
    setSubmitting(true);

    const validItems = items.filter((it) => it.name && parseNum(it.quantity) > 0 && parseNum(it.unit_price) >= 0);

    // Resolve buyer fields based on active tab — same logic as NewInvoiceScreen's
    // buildInvoiceBody. The /invoices/advance endpoint now accepts buyer_id_type
    // and buyer_country (added in this fix batch) so we can send the right values.
    let buyer_name = "", buyer_tin = "", buyer_address = "", buyer_city = "";
    let buyer_id_type = "TIN", buyer_country = "MNE";

    if (buyerTab === "partner") {
      const p = partners.find((x) => x.id === partnerId);
      if (p) {
        buyer_name    = p.name || "";
        buyer_tin     = p.tax_id || "";
        buyer_address = p.address || "";
        buyer_city    = p.city || "";
        buyer_id_type = (p.id_type || "TIN").toUpperCase();
        if (p.country_code) buyer_country = String(p.country_code).toUpperCase();
      }
    } else if (buyerTab === "newCompany") {
      buyer_name    = newCompany.name;
      buyer_tin     = newCompany.tax_id;
      buyer_address = newCompany.address;
      buyer_city    = newCompany.city;
      buyer_id_type = "TIN";
      buyer_country = "MNE";
    } else if (buyerTab === "foreignCompany") {
      buyer_name    = foreignCompany.name;
      buyer_tin     = foreignCompany.tax_id;
      buyer_address = foreignCompany.address;
      buyer_city    = foreignCompany.city;
      buyer_id_type = foreignCompany.subtype === "vat" ? "VAT" : "TAX";
      buyer_country = resolveCountryCode(foreignCompany.country_code, foreignCompany.country_manual) || "MNE";
    } else if (buyerTab === "individual") {
      buyer_name    = individual.name;
      buyer_tin     = individual.personal_id;
      buyer_address = individual.address;
      buyer_city    = individual.city;
      if (individual.subtype === "cg") {
        buyer_id_type = "ID";
        buyer_country = "MNE";
      } else {
        buyer_id_type = "PASS";
        buyer_country = resolveCountryCode(individual.country, individual.country_manual) || "MNE";
      }
    }

    const body = {
      items: validItems.map((it) => ({
        product_id: it.product_id,
        name:       it.name,
        quantity:   parseNum(it.quantity),
        unit_price: parseNum(it.unit_price),
        vat_rate:   parseNum(it.vat_rate),
        unit:       it.unit || "kom",
      })),
      payment_method: paymentMethod, // always bank_transfer for B2B avans
      tcr_code: tcrCode,
      buyer_name,
      buyer_tin,
      buyer_address,
      buyer_city,
      buyer_id_type,
      buyer_country,
    };

    const r = await api("/invoices/advance", { method: "POST", body: JSON.stringify(body) });
    setSubmitting(false);

    if (r.success) {
      const newId = r.data?.invoice?.id;
      showToast("success", `Avansni račun kreiran! ${r.data?.invoice?.invoice_number || ""}`);
      // Reset form
      setItems([emptyItem(defaultVat)]);
      setNewCompany({ name: "", tax_id: "", address: "", city: "", email: "", phone: "" });
      setIndividual({ subtype: "", name: "", personal_id: "", id_type: "ID", country: "", country_manual: "", address: "", city: "", email: "", phone: "" });
      setForeignCompany({ subtype: "", id_type: "VAT", name: "", tax_id: "", country_code: "", country_manual: "", address: "", city: "", email: "" });
      setPartnerId(0);
      if (newId) onSuccess(newId);
    } else {
      showToast("error", r.message || "Greška", 5000);
    }
  };

  return (
    <>
      <div style={{
        display: "grid", gap: 16,
        gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1.5fr) minmax(320px, 1fr)",
      }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Full BuyerSection — same 4 tabs as NewInvoiceScreen.
              Allows avans for: registered partner / MNE company / foreign
              company (VAT or TAX) / individual (CG JMBG or foreign passport).
              Backend /invoices/advance now propagates buyer_id_type +
              buyer_country into the avans invoice's XML correctly. */}
          <BuyerSection
            tab={buyerTab}
            setTab={setBuyerTab}
            partners={partners}
            partnerId={partnerId}
            setPartnerId={setPartnerId}
            newCompany={newCompany}
            setNewCompany={setNewCompany}
            saveAsPartner={false}
            setSaveAsPartner={() => {}}
            individual={individual}
            setIndividual={setIndividual}
            foreignCompany={foreignCompany}
            setForeignCompany={setForeignCompany}
            errors={validationErrors}
            isMobile={isMobile}
          />

          <Card
            title="Stavke (proizvodi/usluge na koje se odnosi avans)"
            subtitle={`${items.length} ${items.length === 1 ? "stavka" : "stavki"}`}
            style={{ position: "relative" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {items.map((it, idx) => (
                <ItemRow
                  key={it.uid}
                  item={it}
                  index={idx}
                  onUpdate={(patch) => updateItem(it.uid, patch)}
                  onRemove={() => removeItem(it.uid)}
                  onOpenPicker={openPicker}
                  isPausalac={isPausalac}
                  isMobile={isMobile}
                />
              ))}
            </div>
            <button
              onClick={openPicker}
              style={{ ...S.btn(S.accent), width: "100%", marginTop: 14, justifyContent: "center" }}
            >
              <FilePlus2 size={16} /> Dodaj stavku
            </button>
            {pickerOpen && (
              <ProductPicker
                products={products}
                loaded={prodLoaded}
                onPick={addProductFromPicker}
                onBlank={addBlank}
                onClose={() => setPickerOpen(false)}
              />
            )}
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <Card title="Konfiguracija">
            <Field label="TCR" required>
              <Select
                value={tcrCode}
                onChange={setTcrCode}
                options={tcrs.filter((t) => t.status === "active").map((t) => ({
                  value: t.tcr_code, label: `${t.tcr_code} · ${t.tcr_name || "—"}`,
                }))}
                icon={LayoutDashboard}
              />
            </Field>
            {/* Payment method dropdown REMOVED — B2B avans is always Virman
                (bank_transfer). Showing cash/card options here was confusing
                because B2B clients never receive cash via the platform.
                The hardcoded paymentMethod="bank_transfer" goes to backend
                which converts to type_of_inv=NONCASH for Poreska XML. */}
            <div style={{
              marginTop: 10, padding: "8px 12px", background: S.bg,
              border: `1px solid ${S.border}`, borderRadius: 8,
              fontSize: 12, color: S.muted, display: "flex", alignItems: "center", gap: 6,
            }}>
              <CreditCard size={13} />
              <span>Način plaćanja: <strong style={{ color: S.dark }}>Virman (bezgotovinski)</strong></span>
            </div>
          </Card>

          <TotalsBox totals={totals} currency="EUR" />

          <div style={{
            background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: 12,
          }}>
            <button
              onClick={submit}
              disabled={submitting}
              style={{ ...S.btn(S.accent), width: "100%", padding: "12px 18px", justifyContent: "center" }}
            >
              {submitting ? <Loader2 size={14} className="spin" /> : <Send size={14} />}
              {submitting ? " Obrađujem…" : " Kreiraj avansni račun"}
            </button>
          </div>
        </div>
      </div>

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

function AdvanceFinalizePanel({ isMobile, config, onSuccess, isPausalac }) {
  const [search, setSearch] = useState("");
  const [advInvoice, setAdvInvoice] = useState(null);
  const [items, setItems] = useState([]);
  const [buyerName, setBuyerName] = useState("");
  const [buyerTin, setBuyerTin] = useState("");
  // Payment method is always Virman for B2B — hardcoded, not a state.
  // (Previously a useState with setter, but the setter is unused since
  // we removed the payment method dropdown. Const keeps it referenced
  // by submit() without the unused-var warning.)
  const paymentMethod = "bank_transfer";
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message, ms = 3500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  const loadAdvance = async () => {
    if (!search.trim()) return;
    setLoading(true);
    setAdvInvoice(null);

    const r = await api(`/invoices?search=${encodeURIComponent(search.trim())}&per_page=10`);
    setLoading(false);
    let found = null;
    if (r.success && Array.isArray(r.data?.invoices)) {
      found = r.data.invoices.find(
        (inv) => inv.is_advance === 1 && !inv.finalized_by_invoice_id
      );
    }
    if (!found) {
      showToast("error", "Avansni račun nije pronađen ili je već finalizovan", 4500);
      return;
    }
    const det = await api(`/invoices/show/${found.id}`);
    if (!det.success) {
      showToast("error", det.message || "Greška");
      return;
    }
    const full = det.data;
    setAdvInvoice(full);
    setBuyerName(dh(full.invoice?.buyer_name) || "");
    setBuyerTin(full.invoice?.buyer_pib || "");
    const itemList = (full.items || []).map((it) => ({
      uid: Math.random().toString(36).slice(2, 9),
      product_id: it.product_id || null,
      name:       dh(it.item_name || it.name) || "Stavka",
      quantity:   Math.abs(parseFloat(it.quantity || 1)),
      unit_price: parseFloat(it.unit_price || 0),
      vat_rate:   Number(it.vat_rate != null ? it.vat_rate : (isPausalac ? 0 : 21)),
      unit:       it.unit || "kom",
      discount_type: "none",
      discount_value: 0,
    }));
    setItems(itemList);
  };

  const updateItem = (uid, patch) => {
    setItems((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));
  };
  const removeItem = (uid) => {
    setItems((prev) => prev.filter((it) => it.uid !== uid));
  };

  const totals = useInvoiceTotals(items);

  const submit = async () => {
    if (!advInvoice) return;
    if (items.length === 0) {
      showToast("error", "Nema stavki za finalizaciju");
      return;
    }
    setSubmitting(true);
    const body = {
      items: items.map((it) => ({
        product_id: it.product_id,
        name:       it.name,
        quantity:   parseNum(it.quantity),
        unit_price: parseNum(it.unit_price),
        vat_rate:   parseNum(it.vat_rate),
        unit:       it.unit || "kom",
      })),
      payment_method: paymentMethod,
      buyer_name: buyerName,
      buyer_tin: buyerTin,
    };
    const r = await api(`/invoices/finalizeAdvance/${advInvoice.invoice.id}`, {
      method: "POST", body: JSON.stringify(body),
    });
    setSubmitting(false);

    if (r.success) {
      const newId = r.data?.invoice?.id || r.data?.final_invoice?.id;
      const advUsed = parseFloat(r.data?.advance_used || 0);
      const remaining = parseFloat(r.data?.remaining_paid || 0);
      showToast("success", `Finalni račun kreiran! Avans: ${fmtMoney(advUsed)}, Ostatak: ${fmtMoney(remaining)}`, 5000);
      setAdvInvoice(null); setSearch("");
      setItems([]); setBuyerName(""); setBuyerTin("");
      if (newId) onSuccess(newId);
    } else {
      showToast("error", r.message || "Greška", 5000);
    }
  };

  return (
    <>
      <Card title="Pronađi avansni račun" style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && loadAdvance()}
            placeholder="Broj avansnog računa ili ID"
            style={{ ...S.input, flex: 1, minWidth: 240, fontFamily: S.mono }}
          />
          <button
            onClick={loadAdvance}
            disabled={loading || !search.trim()}
            style={{ ...S.btn(S.accent), padding: "10px 18px" }}
          >
            {loading ? <Loader2 size={14} className="spin" /> : <Search size={14} />} Traži
          </button>
        </div>
      </Card>

      {advInvoice && (
        <div style={{
          display: "grid", gap: 16,
          gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1.5fr) minmax(320px, 1fr)",
        }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card title={`Avansni: ${advInvoice.invoice?.display_no || advInvoice.invoice?.invoice_number}`}>
              <div style={{ display: "grid", gap: 6, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", fontSize: 13 }}>
                <KV label="Plaćeni iznos avansa" value={fmtMoney(advInvoice.invoice?.total_amount)} />
                <KV label="Datum avansa" value={fmtDateTime(advInvoice.invoice?.invoice_date)} />
              </div>
            </Card>

            <Card title="Stavke finalnog računa" subtitle={`${items.length} ${items.length === 1 ? "stavka" : "stavki"}`}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {items.map((it, idx) => (
                  <ItemRow
                    key={it.uid}
                    item={it}
                    index={idx}
                    onUpdate={(patch) => updateItem(it.uid, patch)}
                    onRemove={() => removeItem(it.uid)}
                    onOpenPicker={() => {}}
                    isPausalac={isPausalac}
                    isMobile={isMobile}
                  />
                ))}
              </div>
            </Card>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card title="Kupac">
              <Field label="Naziv">
                <input value={buyerName} onChange={(e) => setBuyerName(e.target.value)} style={S.input} />
              </Field>
              <div style={{ marginTop: 10 }}>
                <Field label="PIB">
                  <input value={buyerTin} onChange={(e) => setBuyerTin(e.target.value)} style={{ ...S.input, fontFamily: S.mono }} />
                </Field>
              </div>
              <div style={{ marginTop: 10 }}>
                {/* Payment method dropdown removed — B2B finalize is always
                    Virman (bezgotovinski). Same reasoning as Avans/Corrective. */}
                <div style={{
                  padding: "8px 12px", background: S.bg,
                  border: `1px solid ${S.border}`, borderRadius: 8,
                  fontSize: 12, color: S.muted, display: "flex", alignItems: "center", gap: 6,
                }}>
                  <CreditCard size={13} />
                  <span>Način plaćanja: <strong style={{ color: S.dark }}>Virman (bezgotovinski)</strong></span>
                </div>
              </div>
            </Card>

            <TotalsBox totals={totals} currency="EUR" />

            <div style={{
              background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: 12,
            }}>
              <button
                onClick={submit}
                disabled={submitting}
                style={{ ...S.btn(S.accent), width: "100%", padding: "12px 18px", justifyContent: "center" }}
              >
                {submitting ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />}
                {submitting ? " Obrađujem…" : " Finalizuj i fiskalizuj"}
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// Helper: live totals for items array (used by Corrective/Advance panels)
function useInvoiceTotals(items) {
  return (() => {
    let itemNet = 0;
    const byRate = {};
    for (const it of items) {
      const qty = parseNum(it.quantity);
      const up  = parseNum(it.unit_price);
      const rate = parseNum(it.vat_rate);
      if (qty <= 0 || up < 0) continue;

      const gross = qty * up;
      let disc = 0;
      const dv = parseNum(it.discount_value);
      if (it.discount_type === "percentage") disc = gross * (dv / 100);
      else if (it.discount_type === "fixed") disc = dv;
      const net = Math.max(0, gross - disc);
      itemNet += net;

      const key = String(rate);
      if (!byRate[key]) byRate[key] = { rate, net: 0, vat: 0 };
      byRate[key].net += net;
    }

    let netTotal = 0, vatTotal = 0;
    for (const k of Object.keys(byRate)) {
      byRate[k].vat = byRate[k].net * (byRate[k].rate / 100);
      netTotal += byRate[k].net;
      vatTotal += byRate[k].vat;
    }

    const breakdown = Object.values(byRate).sort((a, b) => b.rate - a.rate).map((r) => ({
      rate: r.rate,
      net:  Math.round(r.net * 100) / 100,
      vat:  Math.round(r.vat * 100) / 100,
    }));

    return {
      itemNet: Math.round(itemNet * 100) / 100,
      invDiscount: 0,
      net: Math.round(netTotal * 100) / 100,
      vat: Math.round(vatTotal * 100) / 100,
      total: Math.round((netTotal + vatTotal) * 100) / 100,
      breakdown,
    };
  })();
}

// ────────────────────────────────────────────────────────────────────────────
// BUYER SECTION — 4 tabs: Registrovani | Pravno lice | Strana firma | Fizičko lice
// ────────────────────────────────────────────────────────────────────────────
function BuyerSection({
  tab, setTab, partners, partnerId, setPartnerId,
  newCompany, setNewCompany, saveAsPartner, setSaveAsPartner,
  individual, setIndividual,
  foreignCompany, setForeignCompany,
  errors, isMobile,
}) {
  return (
    <Card title="Kupac">
      {/* Tab row — uses a horizontally-scrolling flex on mobile so 4 tabs
          with localized labels (some of which are 2 words) don't break out
          of the card. On desktop it just wraps naturally. */}
      <div style={{
        display: "flex", gap: 6, marginBottom: 14,
        overflowX: "auto", overflowY: "hidden",
        paddingBottom: 4,
        // WebKit smooth scroll on iOS
        WebkitOverflowScrolling: "touch",
        // Stop the tab row from being pushed by the card's negative margin
        marginLeft: 0, marginRight: 0,
        // Avoid scrolling the whole page when the user pans the tab strip
        touchAction: "pan-x",
      }} className="hide-scrollbar">
        <TabBtn active={tab === "partner"} onClick={() => setTab("partner")} label="Registrovani partner" />
        <TabBtn active={tab === "newCompany"} onClick={() => setTab("newCompany")} label="Pravno lice" />
        <TabBtn active={tab === "foreignCompany"} onClick={() => setTab("foreignCompany")} label="Strana firma" />
        <TabBtn active={tab === "individual"} onClick={() => setTab("individual")} label="Fizičko lice" />
      </div>

      {tab === "partner" && (
        <PartnerPicker
          partners={partners}
          partnerId={partnerId}
          setPartnerId={setPartnerId}
          error={errors.partnerId}
          isMobile={isMobile}
        />
      )}
      {tab === "newCompany" && (
        <NewCompanyForm
          data={newCompany}
          setData={setNewCompany}
          saveAsPartner={saveAsPartner}
          setSaveAsPartner={setSaveAsPartner}
          errors={errors}
          isMobile={isMobile}
        />
      )}
      {tab === "foreignCompany" && (
        <ForeignCompanyForm
          data={foreignCompany}
          setData={setForeignCompany}
          saveAsPartner={saveAsPartner}
          setSaveAsPartner={setSaveAsPartner}
          errors={errors}
          isMobile={isMobile}
        />
      )}
      {tab === "individual" && (
        <IndividualBuyerForm
          data={individual}
          setData={setIndividual}
          errors={errors}
          isMobile={isMobile}
        />
      )}
    </Card>
  );
}

function TabBtn({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: "8px 14px", border: `1px solid ${active ? S.accent : S.border}`,
      background: active ? S.accent : "white", color: active ? "white" : S.muted,
      borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
      fontFamily: S.font, whiteSpace: "nowrap", flexShrink: 0,
    }}>{label}</button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// PARTNER PICKER — large clickable trigger + full modal picker
// ────────────────────────────────────────────────────────────────────────────
function PartnerPicker({ partners, partnerId, setPartnerId, error, isMobile }) {
  const [open, setOpen] = useState(false);
  const selected = partners.find((p) => p.id === partnerId);

  return (
    <>
      <div
        onClick={() => setOpen(true)}
        style={{
          padding: "12px 14px",
          border: `1px solid ${error ? S.red : S.border}`,
          borderRadius: 10,
          background: selected ? S.accentSoft : "white",
          cursor: "pointer",
          display: "flex", alignItems: "center", gap: 12,
          minHeight: 56,
          transition: "all 0.12s",
        }}
        onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "#fafafb"; }}
        onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "white"; }}
      >
        <div style={{
          width: 36, height: 36, borderRadius: 8,
          background: selected ? S.accent : S.borderSoft,
          color: selected ? "white" : S.muted,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}>
          <Users size={18} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          {selected ? (
            <>
              <div style={{ fontWeight: 700, color: S.dark, fontSize: 15, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {dh(selected.name)}
              </div>
              <div style={{ fontSize: 12, color: S.muted, fontFamily: S.mono, marginTop: 2 }}>
                {selected.tax_id && `${buyerIdLabel(selected.id_type)}: ${selected.tax_id}`}
                {selected.city && ` · ${selected.city}`}
              </div>
            </>
          ) : (
            <>
              <div style={{ fontWeight: 600, color: S.dark, fontSize: 14 }}>
                Izaberite partnera
              </div>
              <div style={{ fontSize: 12, color: S.mutedLight, marginTop: 2 }}>
                Kliknite da otvorite listu
              </div>
            </>
          )}
        </div>
        {selected && (
          <button
            onClick={(e) => { e.stopPropagation(); setPartnerId(0); }}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: S.muted, padding: 6, borderRadius: 6, flexShrink: 0,
            }}
            title="Ukloni"
          >
            <X size={16} />
          </button>
        )}
        <ChevronRight size={16} color={S.mutedLight} style={{ flexShrink: 0 }} />
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {open && (
        <PartnerPickerModal
          partners={partners}
          selectedId={partnerId}
          onPick={(id) => { setPartnerId(id); setOpen(false); }}
          onClose={() => setOpen(false)}
          isMobile={isMobile}
        />
      )}
    </>
  );
}

function PartnerPickerModal({ partners, selectedId, onPick, onClose, isMobile }) {
  const [search, setSearch] = useState("");
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const filtered = !search ? partners
    : partners.filter((p) =>
        (p.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (p.tax_id || "").includes(search) ||
        (p.city || "").toLowerCase().includes(search.toLowerCase())
      );

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
        display: "flex",
        // On mobile: full-screen modal (top-aligned, fills viewport). Bottom
        // sheet was causing problems on iOS Safari because when the search
        // input is tapped, the virtual keyboard reduces the visual viewport
        // but the modal stayed anchored to the bottom of the LAYOUT viewport,
        // ending up hidden behind the keyboard. Full-screen modals don't
        // suffer this — content scrolls naturally inside the modal as the
        // keyboard opens.
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: "center",
        padding: isMobile ? 0 : 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: S.card,
          // No rounded corners on mobile (full-screen), rounded on desktop (centered card)
          borderRadius: isMobile ? 0 : 14,
          width: "100%", maxWidth: isMobile ? "100%" : 600,
          // Mobile: fill viewport height. Desktop: 80vh centered card.
          // Using 100dvh (dynamic viewport height) on mobile so the modal
          // shrinks when iOS keyboard opens, instead of extending under it.
          height: isMobile ? "100dvh" : "auto",
          maxHeight: isMobile ? "100dvh" : "80vh",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <div style={{
          padding: "14px 16px", borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <Users size={18} color={S.accent} />
          <div style={{ flex: 1, fontSize: 16, fontWeight: 700, color: S.dark }}>
            Izaberite partnera
          </div>
          <button
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}
          >
            <X size={20} />
          </button>
        </div>

        <div style={{ padding: 12, borderBottom: `1px solid ${S.borderSoft}` }}>
          <div style={{ position: "relative" }}>
            <Search size={14} color={S.mutedLight} style={{
              position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            }} />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži po nazivu, PIB-u, gradu…"
              style={{ ...S.input, paddingLeft: 36, fontSize: 15 }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: S.mutedLight }}>
              {partners.length === 0 ? "Nema dodatih partnera" : "Nema rezultata"}
            </div>
          ) : (
            filtered.map((p) => (
              <div
                key={p.id}
                onClick={() => onPick(p.id)}
                style={{
                  padding: "12px 16px", cursor: "pointer",
                  borderBottom: `1px solid ${S.borderSoft}`,
                  background: p.id === selectedId ? S.accentSoft : "white",
                  display: "flex", alignItems: "center", gap: 10,
                }}
                onMouseEnter={(e) => p.id !== selectedId && (e.currentTarget.style.background = "#fafafb")}
                onMouseLeave={(e) => p.id !== selectedId && (e.currentTarget.style.background = "white")}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: S.dark, fontSize: 14 }}>{dh(p.name)}</div>
                  <div style={{ fontSize: 12, color: S.muted, fontFamily: S.mono, marginTop: 2 }}>
                    {p.tax_id && `${buyerIdLabel(p.id_type)}: ${p.tax_id}`}
                    {p.city && ` · ${p.city}`}
                  </div>
                </div>
                {p.id === selectedId && <CircleCheck size={16} color={S.accent} />}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// COUNTRY CODES — ISO 3166-1 alpha-3, curated for MNE-based B2B businesses.
// These are what Poreska's fiscalization XML expects in <Buyer Country="..."/>.
//
// Ordered: regional (Balkans) first, then alphabetical by Montenegrin name for
// EU/Europe, then non-European. This list covers ~95% of realistic foreign
// invoices an MNE business will issue — hotels billing tourists, exporters
// shipping abroad, consultants invoicing EU clients. "Ostalo (drugo)" is the
// fallback for the long tail; users select it and type the alpha-3 manually.
//
// Not included on purpose: Kosovo (no official ISO alpha-3 — falls under
// "Ostalo" if needed and user can enter XKX or RKS depending on what Poreska
// accepts at the moment of invoicing).
// ────────────────────────────────────────────────────────────────────────────
const COUNTRY_CODES = [
  // Regional (Balkans)
  { code: "ALB", name: "Albanija" },
  { code: "BIH", name: "Bosna i Hercegovina" },
  { code: "HRV", name: "Hrvatska" },
  { code: "MKD", name: "Sjeverna Makedonija" },
  { code: "SRB", name: "Srbija" },
  { code: "SVN", name: "Slovenija" },
  // EU + broader Europe (alphabetical by Montenegrin name)
  { code: "AUT", name: "Austrija" },
  { code: "BEL", name: "Belgija" },
  { code: "BGR", name: "Bugarska" },
  { code: "CZE", name: "Češka" },
  { code: "DNK", name: "Danska" },
  { code: "EST", name: "Estonija" },
  { code: "FIN", name: "Finska" },
  { code: "FRA", name: "Francuska" },
  { code: "GRC", name: "Grčka" },
  { code: "NLD", name: "Holandija" },
  { code: "IRL", name: "Irska" },
  { code: "ITA", name: "Italija" },
  { code: "LVA", name: "Letonija" },
  { code: "LTU", name: "Litvanija" },
  { code: "LUX", name: "Luksemburg" },
  { code: "HUN", name: "Mađarska" },
  { code: "MLT", name: "Malta" },
  { code: "DEU", name: "Njemačka" },
  { code: "NOR", name: "Norveška" },
  { code: "POL", name: "Poljska" },
  { code: "PRT", name: "Portugal" },
  { code: "ROU", name: "Rumunija" },
  { code: "SVK", name: "Slovačka" },
  { code: "ESP", name: "Španija" },
  { code: "SWE", name: "Švedska" },
  { code: "CHE", name: "Švicarska" },
  { code: "TUR", name: "Turska" },
  { code: "UKR", name: "Ukrajina" },
  { code: "GBR", name: "Velika Britanija" },
  // Outside Europe (common tourists / trading partners)
  { code: "USA", name: "SAD" },
  { code: "CAN", name: "Kanada" },
  { code: "RUS", name: "Rusija" },
  { code: "CHN", name: "Kina" },
  { code: "JPN", name: "Japan" },
  { code: "ARE", name: "UAE" },
  { code: "ISR", name: "Izrael" },
];

// ────────────────────────────────────────────────────────────────────────────
// INDIVIDUAL BUYER (Fizičko lice) — per Poreska tech docs
//   - CG citizen      → JMBG (ID, 13 digits)
//   - Foreign citizen → passport (any non-MNE country including Albania)
//
// Albanian citizens are handled as foreign passport holders — Poreska doesn't
// have a special "AL JMBG" category, and any non-MNE national ID maps to the
// PASS BuyerIDType in the XML. Keeping a separate Albanian option created
// confusion and inconsistent ID format expectations.
//
// Backend BuyerType expansion is deferred to the non-TIN feature batch.
// ────────────────────────────────────────────────────────────────────────────
function IndividualBuyerForm({ data, setData, errors, isMobile }) {
  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
        <RadioOption
          label="Državljanin Crne Gore"
          hint="JMBG (13 cifara)"
          selected={data.subtype === "cg"}
          onClick={() => setData((d) => ({ ...d, subtype: "cg", id_type: "ID" }))}
        />
        <RadioOption
          label="Strani državljanin"
          hint="Pasoš (bilo koja država)"
          selected={data.subtype === "foreign"}
          onClick={() => setData((d) => ({ ...d, subtype: "foreign", id_type: "PASSPORT" }))}
        />
      </div>

      {data.subtype && (
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
          <Field label="Ime i prezime" required error={errors.individualName}>
            <input
              value={data.name}
              onChange={(e) => update("name", e.target.value)}
              style={{ ...S.input, borderColor: errors.individualName ? S.red : S.border }}
            />
          </Field>

          {data.subtype === "cg" && (
            <Field label="JMBG" required hint="13 cifara" error={errors.individualId}>
              <input
                value={data.personal_id}
                onChange={(e) => update("personal_id", e.target.value.replace(/\D/g, "").slice(0, 13))}
                placeholder="0101990123456"
                maxLength={13}
                style={{ ...S.input, fontFamily: S.mono, borderColor: errors.individualId ? S.red : S.border }}
              />
            </Field>
          )}

          {data.subtype === "foreign" && (
            <>
              <Field label="Država" required hint="ISO kod države (alpha-3)">
                <Select
                  value={data.country || ""}
                  onChange={(v) => update("country", v)}
                  options={[
                    { id: "",     label: "— izaberite državu —" },
                    ...COUNTRY_CODES.map((c) => ({
                      id: c.code,
                      // Show name first for scannability, code after for confirmation.
                      label: `${c.name} (${c.code})`,
                    })),
                    { id: "OTHER", label: "Ostalo (drugo) — unesite ručno" },
                  ]}
                  icon={Building2}
                />
              </Field>

              {/* Manual entry fallback when "Ostalo" is picked. Stored as
                  data.country_manual; the alpha-3 code that goes to the
                  XML is whichever is non-empty: data.country (if set to a
                  known code) or data.country_manual (uppercased, 2-4 chars). */}
              {data.country === "OTHER" && (
                <Field
                  label="Šifra države"
                  required
                  hint="ISO 3166-1 alpha-3 (npr. AUS za Australiju, ZAF za J. Afriku)"
                >
                  <input
                    value={data.country_manual || ""}
                    onChange={(e) => update("country_manual", e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4))}
                    placeholder="ABC"
                    maxLength={4}
                    style={{ ...S.input, fontFamily: S.mono, letterSpacing: 2 }}
                  />
                </Field>
              )}

              <Field label="Broj pasoša" required error={errors.individualId}>
                <input
                  value={data.personal_id}
                  onChange={(e) => update("personal_id", e.target.value.toUpperCase())}
                  placeholder="P12345678"
                  style={{ ...S.input, fontFamily: S.mono, borderColor: errors.individualId ? S.red : S.border }}
                />
              </Field>
            </>
          )}

          <Field label="Adresa">
            <input
              value={data.address}
              onChange={(e) => update("address", e.target.value)}
              style={S.input}
            />
          </Field>
          <Field label="Grad">
            <input
              value={data.city}
              onChange={(e) => update("city", e.target.value)}
              style={S.input}
            />
          </Field>
          <Field label="Email (opciono)">
            <input
              type="email"
              value={data.email}
              onChange={(e) => update("email", e.target.value)}
              style={S.input}
            />
          </Field>
          <Field label="Telefon (opciono)">
            <input
              value={data.phone}
              onChange={(e) => update("phone", e.target.value)}
              style={S.input}
            />
          </Field>
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// FOREIGN COMPANY (Strana firma) — for buyers outside Montenegro:
//   - EU/EEA company with VAT registration → BuyerIDType="VAT", IDNum like
//     "IT12345678901" (2-letter country prefix + 8-12 alphanumeric)
//   - Non-EU foreign company → BuyerIDType="TAX", IDNum is the foreign
//     tax authority number (Serbian PIB, Turkish vergi, US EIN, etc.)
//
// Both subtypes need a country code (ISO 3166-1 alpha-3) which is stored on
// the invoice as buyer_country and emitted in the XML <Buyer Country="..."/>
// ────────────────────────────────────────────────────────────────────────────
function ForeignCompanyForm({ data, setData, saveAsPartner, setSaveAsPartner, errors, isMobile }) {
  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  return (
    <div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
        <RadioOption
          label="EU/EEA firma sa VAT registracijom"
          hint="VAT broj sa prefiksom države (npr. IT12345678901, HR12345678901)"
          selected={data.subtype === "vat"}
          onClick={() => setData((d) => ({ ...d, subtype: "vat", id_type: "VAT" }))}
        />
        <RadioOption
          label="Strana firma (van EU)"
          hint="Strani poreski broj (Srbija, Turska, Velika Britanija, SAD…)"
          selected={data.subtype === "tax"}
          onClick={() => setData((d) => ({ ...d, subtype: "tax", id_type: "TAX" }))}
        />
      </div>

      {data.subtype && (
        <div style={{ display: "grid", gap: 12, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
          <Field label="Naziv firme" required error={errors.foreignName}>
            <input
              value={data.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder={data.subtype === "vat" ? "ITALIA TRADING S.R.L." : "EXPORT COMPANY LTD"}
              style={{ ...S.input, borderColor: errors.foreignName ? S.red : S.border }}
            />
          </Field>

          <Field
            label={data.subtype === "vat" ? "VAT broj (sa prefiksom države)" : "Poreski broj"}
            required
            hint={data.subtype === "vat"
              ? "2 slova države + 8–12 cifara/slova (npr. IT12345678901)"
              : "Strani poreski identifikacioni broj"}
            error={errors.foreignId}
          >
            <input
              value={data.tax_id}
              onChange={(e) => update("tax_id", e.target.value.toUpperCase().replace(/\s/g, ""))}
              placeholder={data.subtype === "vat" ? "IT12345678901" : "100123456"}
              style={{ ...S.input, fontFamily: S.mono, borderColor: errors.foreignId ? S.red : S.border }}
            />
          </Field>

          <Field label="Država" required hint="ISO kod države (alpha-3)" error={errors.foreignCountry}>
            <Select
              value={data.country_code || ""}
              onChange={(v) => update("country_code", v)}
              options={[
                { id: "",     label: "— izaberite državu —" },
                ...COUNTRY_CODES.map((c) => ({
                  id: c.code,
                  label: `${c.name} (${c.code})`,
                })),
                { id: "OTHER", label: "Ostalo (drugo) — unesite ručno" },
              ]}
              icon={Building2}
            />
          </Field>

          {data.country_code === "OTHER" && (
            <Field
              label="Šifra države"
              required
              hint="ISO 3166-1 alpha-3 kod (npr. AUS, ZAF, NZL)"
            >
              <input
                value={data.country_manual || ""}
                onChange={(e) => update("country_manual", e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 4))}
                placeholder="ABC"
                maxLength={4}
                style={{ ...S.input, fontFamily: S.mono, letterSpacing: 2 }}
              />
            </Field>
          )}

          <Field label="Adresa">
            <input
              value={data.address}
              onChange={(e) => update("address", e.target.value)}
              style={S.input}
            />
          </Field>

          <Field label="Grad">
            <input
              value={data.city}
              onChange={(e) => update("city", e.target.value)}
              style={S.input}
            />
          </Field>

          <Field label="Email (opciono)">
            <input
              type="email"
              value={data.email || ""}
              onChange={(e) => update("email", e.target.value)}
              style={S.input}
            />
          </Field>
        </div>
      )}

      {data.subtype && (
        <label style={{
          display: "flex", alignItems: "center", gap: 8, marginTop: 14,
          padding: "10px 12px", border: `1px solid ${S.border}`, borderRadius: 8,
          background: saveAsPartner ? S.accentSoft : "white", cursor: "pointer",
        }}>
          <input
            type="checkbox"
            checked={saveAsPartner}
            onChange={(e) => setSaveAsPartner(e.target.checked)}
            style={{ accentColor: S.accent, cursor: "pointer" }}
          />
          <span style={{ fontSize: 13, color: S.dark }}>
            Sačuvaj kao partnera (za buduće račune)
          </span>
        </label>
      )}
    </div>
  );
}

function RadioOption({ label, hint, selected, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        padding: "12px 14px",
        border: `1px solid ${selected ? S.accent : S.border}`,
        background: selected ? S.accentSoft : "white",
        borderRadius: 10, cursor: "pointer",
        display: "flex", alignItems: "center", gap: 12,
        transition: "all 0.12s",
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: "50%",
        border: `2px solid ${selected ? S.accent : S.border}`,
        display: "flex", alignItems: "center", justifyContent: "center",
        flexShrink: 0,
      }}>
        {selected && (
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: S.accent }} />
        )}
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 600, color: S.dark, fontSize: 14 }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>{hint}</div>}
      </div>
    </div>
  );
}

function NewCompanyForm({ data, setData, saveAsPartner, setSaveAsPartner, errors, isMobile }) {
  const update = (key, value) => setData((prev) => ({ ...prev, [key]: value }));
  return (
    <div>
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr" }}>
        <Field label="Naziv firme" required error={errors.newCompanyName}>
          <input
            value={data.name}
            onChange={(e) => update("name", e.target.value)}
            placeholder="Npr. Bisha DOO"
            style={{ ...S.input, borderColor: errors.newCompanyName ? S.red : S.border }}
          />
        </Field>
        <Field label="PIB" required error={errors.newCompanyTaxId}>
          <input
            value={data.tax_id}
            onChange={(e) => update("tax_id", e.target.value.replace(/\D/g, "").slice(0, 12))}
            placeholder="12345678"
            inputMode="numeric"
            style={{ ...S.input, fontFamily: S.mono, borderColor: errors.newCompanyTaxId ? S.red : S.border }}
          />
        </Field>
      </div>

      <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", marginTop: 10 }}>
        <Field label="Adresa">
          <input value={data.address} onChange={(e) => update("address", e.target.value)} style={S.input} />
        </Field>
        <Field label="Grad">
          <input value={data.city} onChange={(e) => update("city", e.target.value)} style={S.input} />
        </Field>
      </div>

      <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", marginTop: 10 }}>
        <Field label="Email">
          <input type="email" value={data.email} onChange={(e) => update("email", e.target.value)} style={S.input} />
        </Field>
        <Field label="Telefon">
          <input value={data.phone} onChange={(e) => update("phone", e.target.value)} style={S.input} />
        </Field>
      </div>

      <div style={{ marginTop: 10 }}>
        <Field label="Žiro račun">
          <input
            value={data.bank_account}
            onChange={(e) => update("bank_account", e.target.value)}
            placeholder="510-XXXXXXXX-XX"
            style={{ ...S.input, fontFamily: S.mono }}
          />
        </Field>
      </div>

      <label style={{
        display: "flex", alignItems: "center", gap: 8, marginTop: 14,
        cursor: "pointer", fontSize: 13, color: S.dark,
      }}>
        <input
          type="checkbox"
          checked={saveAsPartner}
          onChange={(e) => setSaveAsPartner(e.target.checked)}
          style={{ width: 16, height: 16, accentColor: S.accent, cursor: "pointer" }}
        />
        Sačuvaj kao stalnog partnera (za buduće račune)
      </label>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ITEMS EDITOR
// ────────────────────────────────────────────────────────────────────────────
function ItemsEditor({ items, setItems, defaultVat, isPausalac, errors, isMobile }) {
  // pickerForUid:
  //   null     → picker closed
  //   "new"    → picker was opened by the "Dodaj stavku" button (creates new row)
  //   <uid>    → picker was opened by clicking on that specific empty row
  //              (slobodan unos → mark row as manual; pick product → fill row)
  const [pickerForUid, setPickerForUid] = useState(null);
  const [products, setProducts]     = useState([]);
  const [prodLoaded, setProdLoaded] = useState(false);

  const loadProducts = async () => {
    if (prodLoaded) return;
    const r = await api("/products");
    if (r.success) {
      const list = Array.isArray(r.data) ? r.data
                 : (Array.isArray(r.data?.products) ? r.data.products : []);
      setProducts(list);
    }
    setProdLoaded(true);
  };

  const openPicker = (uid = "new") => { loadProducts(); setPickerForUid(uid); };
  const closePicker = () => setPickerForUid(null);

  const addProduct = (p) => {
    const patch = {
      product_id: p.id,
      name:       p.name,
      unit_price: parseFloat(p.price ?? p.unit_price ?? 0),
      vat_rate:   isPausalac ? 0 : parseFloat(p.vat_rate ?? defaultVat),
      unit:       p.unit || "kom",
      quantity:   1,
      _manual:    true,
    };
    if (pickerForUid === "new" || !pickerForUid) {
      setItems((prev) => [...prev, { ...emptyItem(defaultVat), ...patch }]);
    } else {
      const uid = pickerForUid;
      setItems((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));
    }
    closePicker();
  };

  const addBlank = () => {
    if (pickerForUid === "new" || !pickerForUid) {
      // From "Dodaj stavku" button → new empty row in manual mode
      setItems((prev) => [...prev, { ...emptyItem(defaultVat), _manual: true }]);
    } else {
      // From a row's onClick → mark THAT row as manual so it shows the input
      const uid = pickerForUid;
      setItems((prev) => prev.map((it) => (it.uid === uid ? { ...it, _manual: true } : it)));
    }
    closePicker();
  };

  const updateItem = (uid, patch) => {
    setItems((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));
  };
  const removeItem = (uid) => {
    setItems((prev) => prev.filter((it) => it.uid !== uid));
  };

  return (
    <Card
      title="Stavke"
      subtitle={`${items.length} ${items.length === 1 ? "stavka" : "stavki"}`}
      style={{ position: "relative" }}
    >
      {errors.items && (
        <div style={{
          marginBottom: 10, padding: "8px 12px", background: "#fef2f2",
          border: "1px solid #fecaca", color: S.red, borderRadius: 6, fontSize: 13,
        }}>{errors.items}</div>
      )}
      {errors.pausalacVat && (
        <div style={{
          marginBottom: 10, padding: "8px 12px", background: "#fef3c7",
          border: "1px solid #fde68a", color: S.amber, borderRadius: 6, fontSize: 13,
        }}>{errors.pausalacVat}</div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {items.map((it, idx) => (
          <ItemRow
            key={it.uid}
            item={it}
            index={idx}
            onUpdate={(patch) => updateItem(it.uid, patch)}
            onRemove={() => removeItem(it.uid)}
            onOpenPicker={() => openPicker(it.uid)}
            isPausalac={isPausalac}
            isMobile={isMobile}
          />
        ))}
      </div>

      <button
        onClick={() => openPicker("new")}
        style={{
          ...S.btn(S.accent),
          width: "100%", marginTop: 14, justifyContent: "center",
        }}
      >
        <FilePlus2 size={16} /> Dodaj stavku
      </button>

      {pickerForUid !== null && (
        <ProductPicker
          products={products}
          loaded={prodLoaded}
          onPick={addProduct}
          onBlank={addBlank}
          onClose={closePicker}
        />
      )}
    </Card>
  );
}

function ItemRow({ item, index, onUpdate, onRemove, onOpenPicker, isPausalac, isMobile }) {
  const qty   = parseNum(item.quantity);
  const up    = parseNum(item.unit_price);
  const rate  = parseNum(item.vat_rate);
  const dv    = parseNum(item.discount_value);
  const gross = qty * up;
  const disc  = item.discount_type === "percentage" ? gross * (dv / 100)
              : item.discount_type === "fixed" ? dv : 0;
  const net   = Math.max(0, gross - disc);
  const total = net * (1 + rate / 100);

  // An item is "empty" if no name AND no price set — show picker trigger.
  // Show the picker trigger only when this row is "fresh" — i.e., the user
  // hasn't typed anything AND hasn't explicitly chosen the manual-edit path
  // (via "Slobodan unos" or picking a product).
  const isEmpty = !item._manual && !item.name && !parseNum(item.unit_price);

  return (
    <div style={{
      border: `1px solid ${S.border}`, borderRadius: 10, padding: 12,
      background: "#fafafb",
    }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10 }}>
        <span style={{
          width: 22, height: 22, borderRadius: 6, background: S.accent, color: "white",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 11, fontWeight: 700, fontFamily: S.mono, flexShrink: 0,
        }}>{index + 1}</span>

        {isEmpty ? (
          <button
            type="button"
            onClick={onOpenPicker}
            style={{
              flex: 1, minWidth: 0, padding: "10px 12px",
              border: `1px dashed ${S.accent}`,
              background: "white", color: S.accent,
              borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: "pointer", fontFamily: S.font,
              display: "flex", alignItems: "center", gap: 8,
              justifyContent: "flex-start",
              textAlign: "left",
            }}
          >
            <Search size={14} style={{ flexShrink: 0 }} />
            <span style={{
              flex: 1, minWidth: 0,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>Kliknite da izaberete proizvod ili slobodan unos</span>
            <ChevronRight size={14} style={{ flexShrink: 0 }} />
          </button>
        ) : (
          <>
            <input
              value={item.name}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="Naziv stavke"
              style={{ ...S.input, flex: 1, padding: "9px 12px" }}
            />
            <button
              type="button"
              onClick={onOpenPicker}
              title="Promijeni proizvod"
              style={{
                background: "white", border: `1px solid ${S.border}`,
                cursor: "pointer", padding: "8px 10px", borderRadius: 8,
                color: S.muted, flexShrink: 0,
                display: "flex", alignItems: "center",
              }}
            >
              <Search size={14} />
            </button>
          </>
        )}

        <button
          onClick={onRemove}
          title="Ukloni"
          style={{
            background: "none", border: "none", cursor: "pointer", padding: 6,
            color: S.red, borderRadius: 6, flexShrink: 0,
          }}
        >
          <X size={16} />
        </button>
      </div>

      <div style={{
        display: "grid", gap: 8,
        gridTemplateColumns: isMobile
          ? "1fr 1fr"
          : "repeat(auto-fit, minmax(110px, 1fr))",
      }}>
        <SmallField label="Količina">
          <input
            type="text" inputMode="decimal"
            value={item.quantity}
            onChange={(e) => onUpdate({ quantity: e.target.value })}
            style={{ ...S.input, padding: "8px 10px", fontFamily: S.mono, fontSize: 13 }}
          />
        </SmallField>
        <SmallField label="Jed.">
          <input
            value={item.unit}
            onChange={(e) => onUpdate({ unit: e.target.value })}
            placeholder="kom"
            style={{ ...S.input, padding: "8px 10px", fontSize: 13 }}
          />
        </SmallField>
        <SmallField label="Cijena (neto)">
          <input
            type="text" inputMode="decimal"
            value={item.unit_price}
            onChange={(e) => onUpdate({ unit_price: e.target.value })}
            style={{ ...S.input, padding: "8px 10px", fontFamily: S.mono, fontSize: 13 }}
          />
        </SmallField>
        <SmallField label="PDV %">
          <select
            value={item.vat_rate}
            onChange={(e) => onUpdate({ vat_rate: parseFloat(e.target.value) })}
            disabled={isPausalac}
            style={{
              ...S.input, padding: "8px 10px", fontFamily: S.mono, fontSize: 13,
              cursor: "pointer", width: "100%", boxSizing: "border-box",
              appearance: "none", WebkitAppearance: "none", MozAppearance: "none",
              backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23737373' stroke-width='2'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
              backgroundRepeat: "no-repeat", backgroundPosition: "right 8px center",
              paddingRight: 26,
            }}
          >
            {(isPausalac ? [0] : VAT_RATES).map((r) => (
              <option key={r} value={r}>{r}%</option>
            ))}
          </select>
        </SmallField>
      </div>

      <div style={{
        display: "grid", gap: 8, marginTop: 8,
        gridTemplateColumns: isMobile ? "1fr 1fr" : "minmax(140px, 1.5fr) minmax(110px, 1fr) minmax(120px, 1.5fr)",
      }}>
        <SmallField label="Popust tip">
          <select
            value={item.discount_type}
            onChange={(e) => onUpdate({ discount_type: e.target.value })}
            style={{ ...S.input, padding: "8px 10px", fontSize: 13, cursor: "pointer" }}
          >
            <option value="none">Bez popusta</option>
            <option value="percentage">Procenat (%)</option>
            <option value="fixed">Iznos (€)</option>
          </select>
        </SmallField>
        <SmallField label="Vrijednost">
          <input
            type="text" inputMode="decimal"
            value={item.discount_value}
            onChange={(e) => onUpdate({ discount_value: e.target.value })}
            disabled={item.discount_type === "none"}
            style={{
              ...S.input, padding: "8px 10px", fontFamily: S.mono, fontSize: 13,
              opacity: item.discount_type === "none" ? 0.5 : 1,
            }}
          />
        </SmallField>
        <div style={{
          display: "flex", flexDirection: "column", justifyContent: "flex-end",
          padding: "8px 12px", background: "white",
          border: `1px solid ${S.border}`, borderRadius: 8,
        }}>
          <span style={{ fontSize: 10, color: S.muted, fontWeight: 600, textTransform: "uppercase" }}>Ukupno (sa PDV)</span>
          <span style={{ fontSize: 15, fontWeight: 700, color: S.dark, fontFamily: S.mono }}>
            {fmtMoney(total)}
          </span>
        </div>
      </div>
    </div>
  );
}

function ProductPicker({ products, loaded, onPick, onBlank, onClose }) {
  const [search, setSearch] = useState("");
  const filtered = !search ? products.slice(0, 100)
    : products.filter((p) => (p.name || "").toLowerCase().includes(search.toLowerCase())).slice(0, 100);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: S.card, borderRadius: 14, width: "100%", maxWidth: 560,
          maxHeight: "85vh", overflow: "hidden", display: "flex", flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
        }}
      >
        <div style={{
          padding: "14px 18px", borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: S.dark }}>Dodaj stavku</div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: 14, borderBottom: `1px solid ${S.borderSoft}` }}>
          <div style={{ position: "relative", marginBottom: 8 }}>
            <Search size={14} color={S.mutedLight} style={{
              position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
            }} />
            <input
              autoFocus
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Pretraži proizvod…"
              style={{ ...S.input, paddingLeft: 36 }}
            />
          </div>
          <button
            onClick={onBlank}
            style={{
              ...S.btnSm("white", S.accent),
              border: `1px dashed ${S.accent}`, width: "100%",
              justifyContent: "center", padding: "10px",
            }}
          >
            <FilePlus2 size={14} /> Slobodan unos (bez proizvoda)
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto" }}>
          {!loaded ? (
            <div style={{ padding: 24 }}><Skeleton lines={5} /></div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: S.mutedLight, fontSize: 13 }}>
              {products.length === 0 ? "Nema proizvoda u katalogu" : "Nema rezultata"}
            </div>
          ) : (
            filtered.map((p) => (
              <div
                key={p.id}
                onClick={() => onPick(p)}
                style={{
                  padding: "10px 16px", cursor: "pointer",
                  borderBottom: `1px solid ${S.borderSoft}`,
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12,
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#fafafb"}
                onMouseLeave={(e) => e.currentTarget.style.background = "white"}
              >
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ fontWeight: 600, color: S.dark, fontSize: 14 }}>{dh(p.name)}</div>
                  {p.unit && <div style={{ fontSize: 11, color: S.muted }}>{p.unit}</div>}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ fontSize: 14, fontFamily: S.mono, fontWeight: 600, color: S.dark }}>
                    {fmtMoney(p.price ?? p.unit_price ?? 0)}
                  </div>
                  <div style={{ fontSize: 11, color: S.muted, fontFamily: S.mono }}>
                    {parseFloat(p.vat_rate ?? 21)}% PDV
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// INVOICE CONFIG (right sidebar)
// ────────────────────────────────────────────────────────────────────────────
function InvoiceConfig({
  tcrCode, setTcrCode, tcrs,
  invoiceDate, setInvDate,
  paymentDue, setPayDue,
  paymentDueDate, setPayDueDate,
  currency, setCurrency,
  note, setNote,
  contractInfo, setContractInfo,
  invDiscType, setInvDiscType,
  invDiscValue, setInvDiscValue,
  errors,
}) {
  return (
    <Card title="Detalji računa">
      <Field label="TCR" required error={errors.tcrCode}>
        <select
          value={tcrCode}
          onChange={(e) => setTcrCode(e.target.value)}
          style={{ ...S.input, cursor: "pointer", borderColor: errors.tcrCode ? S.red : S.border }}
        >
          <option value="">— Izaberite TCR —</option>
          {tcrs.map((t) => (
            <option key={t.id} value={t.tcr_code}>
              {t.tcr_code} {t.tcr_name ? `· ${t.tcr_name}` : ""} ({t.location_name})
            </option>
          ))}
        </select>
      </Field>

      <div style={{ marginTop: 12 }}>
        <Field label="Datum računa">
          <input
            type="date"
            value={invoiceDate}
            onChange={(e) => setInvDate(e.target.value)}
            max={new Date().toISOString().slice(0, 10)}
            style={S.input}
          />
        </Field>
      </div>

      <div style={{ marginTop: 12 }}>
        <Field label="Rok plaćanja">
          <select
            value={paymentDue}
            onChange={(e) => setPayDue(e.target.value)}
            style={{ ...S.input, cursor: "pointer" }}
          >
            {PAYMENT_DUE_PRESETS.map((o) => (
              <option key={o.id} value={o.id}>{o.label}</option>
            ))}
          </select>
        </Field>
        {paymentDue === "custom" && (
          <div style={{ marginTop: 8 }}>
            <input
              type="date"
              value={paymentDueDate}
              onChange={(e) => setPayDueDate(e.target.value)}
              style={{ ...S.input, borderColor: errors.paymentDueDate ? S.red : S.border }}
            />
            {errors.paymentDueDate && <div style={errorStyle}>{errors.paymentDueDate}</div>}
          </div>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <Field label="Popust na cijeli račun">
          <div style={{ display: "flex", gap: 8 }}>
            <select
              value={invDiscType}
              onChange={(e) => setInvDiscType(e.target.value)}
              style={{ ...S.input, cursor: "pointer", flex: "1 1 50%" }}
            >
              <option value="none">Bez popusta</option>
              <option value="percentage">Procenat (%)</option>
              <option value="fixed">Iznos (€)</option>
            </select>
            <input
              type="text" inputMode="decimal"
              value={invDiscValue}
              onChange={(e) => setInvDiscValue(e.target.value)}
              disabled={invDiscType === "none"}
              style={{ ...S.input, fontFamily: S.mono, flex: "1 1 50%", opacity: invDiscType === "none" ? 0.5 : 1 }}
            />
          </div>
        </Field>
      </div>

      <div style={{ marginTop: 12 }}>
        <Field label="Podaci ugovora">
          <input
            value={contractInfo}
            onChange={(e) => setContractInfo(e.target.value)}
            maxLength={500}
            placeholder="npr. Anex ugovora broj: 06-2026/2 (opciono)"
            style={{ ...S.input, fontFamily: S.font }}
          />
        </Field>
        <div style={{ fontSize: 11, color: S.mutedLight, marginTop: 4, lineHeight: 1.4 }}>
          Prikazuje se na PDF-u iznad tabele stavki kao „Račun po: …"
        </div>
      </div>

      <div style={{ marginTop: 12 }}>
        <Field label="Napomena">
          <textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder="Opciono…"
            style={{ ...S.input, resize: "vertical", minHeight: 60, fontFamily: S.font }}
          />
        </Field>
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// TOTALS BOX
// ────────────────────────────────────────────────────────────────────────────
function TotalsBox({ totals, currency }) {
  return (
    <Card title="Iznosi" style={{ position: "sticky", top: 96 }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <Row label="Stavke (brutto)" value={fmtMoney(totals.itemNet, currency)} />
        {totals.invDiscount > 0 && (
          <Row label="Popust na račun" value={`− ${fmtMoney(totals.invDiscount, currency)}`} muted />
        )}
        <Row label="Osnovica" value={fmtMoney(totals.net, currency)} />

        {totals.breakdown.length > 0 && (
          <div style={{ background: "#fafafb", borderRadius: 8, padding: "8px 10px", marginTop: 2 }}>
            {totals.breakdown.map((r) => (
              <div key={r.rate} style={{
                display: "flex", justifyContent: "space-between",
                fontSize: 12, color: S.muted, padding: "2px 0",
              }}>
                <span>PDV {r.rate}%</span>
                <span style={{ fontFamily: S.mono }}>{fmtMoney(r.vat, currency)}</span>
              </div>
            ))}
          </div>
        )}

        <Row label="PDV ukupno" value={fmtMoney(totals.vat, currency)} />
        <div style={{ height: 1, background: S.border, margin: "4px 0" }} />
        <Row label="Ukupno za uplatu" value={fmtMoney(totals.total, currency)} bold large />
      </div>
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// ACTION BUTTONS
// ────────────────────────────────────────────────────────────────────────────
function ActionButtons({ submitting, canFiscalize, onDraft, onFiscalize }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <button
        onClick={onFiscalize}
        disabled={submitting || !canFiscalize}
        style={{
          ...S.btn(S.green), justifyContent: "center", padding: "14px",
          fontSize: 16, opacity: (submitting || !canFiscalize) ? 0.6 : 1,
        }}
      >
        {submitting ? <Loader2 size={16} className="spin" /> : <CircleCheck size={18} />}
        {submitting ? "Fiskalizujem…" : "Fiskalizuj račun"}
      </button>
      <button
        onClick={onDraft}
        disabled={submitting}
        style={{
          ...S.btn("white", S.dark), justifyContent: "center", padding: "12px",
          border: `1px solid ${S.border}`, opacity: submitting ? 0.6 : 1,
        }}
      >
        <Clock size={15} /> Sačuvaj kao nacrt
      </button>
      {!canFiscalize && (
        <div style={{
          fontSize: 11, color: S.amber, textAlign: "center", marginTop: 2,
        }}>
          Fiskalizacija nedostupna — nedostaje certifikat
        </div>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// FloatingToast (used in NewInvoiceScreen)
// ────────────────────────────────────────────────────────────────────────────
function FloatingToast({ toast, isMobile }) {
  return (
    <div style={{
      position: "fixed", bottom: isMobile ? 90 : 24,
      left: "50%", transform: "translateX(-50%)",
      background: toast.type === "success" ? S.green : S.red,
      color: "white", padding: "12px 20px", borderRadius: 12,
      fontSize: 14, fontWeight: 500, zIndex: 200,
      boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
      maxWidth: "calc(100vw - 32px)",
      animation: "slideUp 0.25s ease-out",
    }}>
      {toast.message}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Form helpers
// ────────────────────────────────────────────────────────────────────────────
function Field({ label, required, error, children }) {
  return (
    <div>
      <label style={{
        display: "block", fontSize: 11, fontWeight: 600,
        color: S.muted, marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.3,
      }}>
        {label} {required && <span style={{ color: S.red }}>*</span>}
      </label>
      {children}
      {error && <div style={errorStyle}>{error}</div>}
    </div>
  );
}

function SmallField({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block", fontSize: 10, fontWeight: 600,
        color: S.muted, marginBottom: 3, textTransform: "uppercase", letterSpacing: 0.3,
      }}>{label}</label>
      {children}
    </div>
  );
}

const errorStyle = {
  marginTop: 4, fontSize: 12, color: S.red, fontWeight: 500,
};

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// PARTNERS SCREEN
// ════════════════════════════════════════════════════════════════════════════
const PARTNER_SORT_OPTIONS = [
  { id: "name",         label: "Naziv (A-Z)",        sort: "name",         direction: "asc"  },
  { id: "name_desc",    label: "Naziv (Z-A)",        sort: "name",         direction: "desc" },
  { id: "total_desc",   label: "Najveći promet",     sort: "total",        direction: "desc" },
  { id: "outstanding_desc", label: "Najveći dug",    sort: "outstanding",  direction: "desc" },
  { id: "last_invoice_desc", label: "Najnoviji račun", sort: "last_invoice", direction: "desc" },
];
const PARTNER_STATUS_OPTIONS = [
  { id: "active",   label: "Aktivni" },
  { id: "inactive", label: "Neaktivni" },
  { id: "all",      label: "Svi" },
];

function PartnersScreen({ isMobile, canWrite, onCreateInvoice }) {
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);

  const [status, setStatus] = useState("active");
  const [hasBalance, setHasBalance] = useState(false);
  const [sortKey, setSortKey] = useState("name");
  const [page, setPage] = useState(1);
  const perPage = isMobile ? 25 : 50;

  const [data, setData]     = useState({ items: [], total: 0, total_pages: 1, summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [editingPartner, setEditingPartner] = useState(null); // null | "new" | partner obj
  const [filterOpen, setFilterOpen] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => { setPage(1); }, [search, status, hasBalance, sortKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const opt = PARTNER_SORT_OPTIONS.find((o) => o.id === sortKey) || PARTNER_SORT_OPTIONS[0];
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    qs.set("active", status);
    if (hasBalance) qs.set("has_balance", "1");
    qs.set("sort", opt.sort);
    qs.set("direction", opt.direction);
    qs.set("page", String(page));
    qs.set("per_page", String(perPage));
    const r = await api(`/b2b/partners?${qs.toString()}`);
    setLoading(false);
    if (r.success) setData(r.data);
    else setError(r.message || "Greška pri učitavanju");
  }, [search, status, hasBalance, sortKey, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const items = Array.isArray(data.items) ? data.items : [];
  const summary = data.summary || {};

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleAfterSave = () => {
    setEditingPartner(null);
    load();
    showToast("success", "Partner sačuvan");
    // If detail is open for this partner, refresh it via the panel's own reload
  };

  const filterPanel = (
    <PartnersFilterPanel
      searchInput={searchInput} setSearchInput={setSearchInput}
      status={status} setStatus={setStatus}
      hasBalance={hasBalance} setHasBalance={setHasBalance}
      sortKey={sortKey} setSortKey={setSortKey}
      isMobile={isMobile}
    />
  );

  return (
    <>
      <TopHeader
        title="Partneri"
        subtitle={loading ? "Učitavam…" : `${fmtNum(summary.total_partners || 0)} partnera · Potraživanja: ${fmtMoney(summary.total_outstanding || 0)}`}
        isMobile={isMobile}
        right={
          isMobile ? (
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => setFilterOpen(true)}
                style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "8px 10px" }}
              >
                <FilterIcon size={14} />
              </button>
              {canWrite && (
                <button
                  onClick={() => setEditingPartner("new")}
                  style={{ ...S.btnSm(S.dark, "white"), padding: "8px 12px" }}
                >
                  <Plus size={14} />
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={load}
                disabled={loading}
                style={{ ...S.btnSm("white", S.muted), border: `1px solid ${S.border}`, padding: "8px 10px" }}
              >
                <RefreshCw size={14} className={loading ? "spin" : ""} />
                {loading ? "Učitavam" : "Osvježi"}
              </button>
              {canWrite && (
                <button
                  onClick={() => setEditingPartner("new")}
                  style={{ ...S.btnSm(S.dark, "white"), padding: "8px 14px" }}
                >
                  <Plus size={14} /> Novi partner
                </button>
              )}
            </div>
          )
        }
      />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1600 }}>
        {!isMobile && (
          <div style={{ marginBottom: 16 }}>{filterPanel}</div>
        )}

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
            padding: "14px 18px", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Summary cards */}
        {!loading && items.length > 0 && (
          <div style={{
            display: "grid", gap: 12, marginBottom: 16,
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(220px, 1fr))",
          }}>
            <SummaryCard icon={Users} color={S.accent} label="Partnera" value={fmtNum(summary.total_partners || 0)} />
            <SummaryCard icon={Euro}  color={S.green}  label="Ukupno fakturisano" value={fmtMoney(summary.total_invoiced || 0)} />
            <SummaryCard icon={TrendingUp} color={S.orange} label="Potraživanja" value={fmtMoney(summary.total_outstanding || 0)} />
          </div>
        )}

        <div style={{
          background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
          overflow: "hidden",
        }}>
          {loading && items.length === 0 ? (
            <div style={{ padding: 24 }}>
              <Skeleton lines={6} />
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: S.mutedLight }}>
              <Users size={36} color={S.mutedLight} />
              <div style={{ marginTop: 12, fontSize: 14 }}>
                {search ? "Nema rezultata za pretragu" : "Još uvijek nemate partnera"}
              </div>
              {canWrite && !search && (
                <button
                  onClick={() => setEditingPartner("new")}
                  style={{ ...S.btn(S.accent), marginTop: 14 }}
                >
                  <Plus size={16} /> Dodaj prvog partnera
                </button>
              )}
            </div>
          ) : isMobile ? (
            <PartnerCardList items={items} onSelect={setSelectedId} />
          ) : (
            <PartnerTable items={items} onSelect={setSelectedId} />
          )}
        </div>

        {data.total_pages > 1 && (
          <Pagination
            page={page}
            totalPages={data.total_pages}
            onChange={setPage}
            isMobile={isMobile}
          />
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {/* Mobile filter sheet */}
      {isMobile && filterOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 110,
            display: "flex", alignItems: "flex-end",
          }}
          onClick={() => setFilterOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="slide-up"
            style={{
              width: "100%", background: S.card, borderTopLeftRadius: 16,
              borderTopRightRadius: 16, maxHeight: "85vh", overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px" }}>
              <div style={{ width: 40, height: 4, background: S.border, borderRadius: 2 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px 12px" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: S.dark }}>Filteri</div>
              <button onClick={() => setFilterOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: "0 16px 24px" }}>
              {filterPanel}
              <button onClick={() => setFilterOpen(false)} style={{ ...S.btn(S.dark), width: "100%", marginTop: 18 }}>Primijeni</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selectedId !== null && (
        <PartnerDetailPanel
          id={selectedId}
          onClose={() => setSelectedId(null)}
          isMobile={isMobile}
          canWrite={canWrite}
          onEdit={(partner) => setEditingPartner(partner)}
          onCreateInvoice={(pid) => { setSelectedId(null); onCreateInvoice(pid); }}
          onAfterDelete={() => { setSelectedId(null); load(); showToast("success", "Partner obrisan"); }}
          onAfterToggle={() => { load(); }}
        />
      )}

      {/* Create/Edit modal */}
      {editingPartner !== null && (
        <PartnerFormModal
          partner={editingPartner === "new" ? null : editingPartner}
          onClose={() => setEditingPartner(null)}
          onSaved={handleAfterSave}
          isMobile={isMobile}
        />
      )}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Partners filter panel
// ────────────────────────────────────────────────────────────────────────────
function PartnersFilterPanel({
  searchInput, setSearchInput,
  status, setStatus,
  hasBalance, setHasBalance,
  sortKey, setSortKey,
  isMobile,
}) {
  return (
    <div style={{
      background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
      padding: isMobile ? 12 : 14,
    }}>
      <div style={{ position: "relative", marginBottom: 12 }}>
        <Search size={16} color={S.mutedLight} style={{
          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
        }} />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Pretraži po nazivu, PIB-u, gradu…"
          style={{ ...S.input, paddingLeft: 36 }}
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput("")}
            style={{
              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted,
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
      }}>
        <Select value={status} onChange={setStatus} options={PARTNER_STATUS_OPTIONS} icon={Power} />
        <Select value={sortKey} onChange={setSortKey} options={PARTNER_SORT_OPTIONS} icon={ChevronsUpDown} />
        <label style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 12px", border: `1px solid ${hasBalance ? S.accent : S.border}`,
          background: hasBalance ? S.accentSoft : "#f9fafb",
          borderRadius: 8, cursor: "pointer", fontSize: 13,
          color: hasBalance ? S.accent : S.dark, fontWeight: hasBalance ? 600 : 500,
        }}>
          <input
            type="checkbox" checked={hasBalance}
            onChange={(e) => setHasBalance(e.target.checked)}
            style={{ width: 14, height: 14, accentColor: S.accent }}
          />
          Samo sa potraživanjima
        </label>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, color, label, value }) {
  return (
    <div style={{
      background: S.card, borderRadius: 10, border: `1px solid ${S.border}`,
      padding: 14, display: "flex", alignItems: "center", gap: 12,
    }}>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: `${color}18`, display: "flex",
        alignItems: "center", justifyContent: "center",
      }}>
        <Icon size={18} color={color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: S.muted, fontWeight: 500, textTransform: "uppercase", letterSpacing: 0.3 }}>
          {label}
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: S.dark, fontFamily: S.mono }}>
          {value}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Desktop table
// ────────────────────────────────────────────────────────────────────────────
function PartnerTable({ items, onSelect }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 900 }}>
        <thead style={{ background: "#fafafb" }}>
          <tr>
            <th style={th}>Naziv</th>
            <th style={th}>PIB / ID</th>
            <th style={th}>Grad</th>
            <th style={{ ...th, textAlign: "right" }}>Računa</th>
            <th style={{ ...th, textAlign: "right" }}>Fakturisano</th>
            <th style={{ ...th, textAlign: "right" }}>Duguje</th>
            <th style={{ ...th, textAlign: "right" }}>Poslednji</th>
          </tr>
        </thead>
        <tbody>
          {items.map((p) => (
            <tr
              key={p.id}
              onClick={() => onSelect(p.id)}
              style={{
                borderBottom: `1px solid ${S.borderSoft}`,
                cursor: "pointer", transition: "background 0.1s",
                opacity: p.is_active ? 1 : 0.55,
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#fafafb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <td style={{ ...td, fontWeight: 600 }}>
                {dh(p.name)}
                {!p.is_active && <Tag color={S.muted} text="NEAKTIVAN" />}
              </td>
              <td style={{ ...td, fontFamily: S.mono, color: S.muted }}>{p.tax_id || "—"}</td>
              <td style={td}>{p.city || <span style={{ color: S.mutedLight }}>—</span>}</td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtNum(p.invoice_count)}</td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtMoney(p.total_invoiced)}</td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600, color: p.outstanding > 0 ? S.orange : S.mutedLight }}>
                {p.outstanding > 0 ? fmtMoney(p.outstanding) : "—"}
              </td>
              <td style={{ ...td, textAlign: "right", color: S.muted, whiteSpace: "nowrap" }}>
                {p.last_invoice_date ? fmtDate(p.last_invoice_date) : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile card list
// ────────────────────────────────────────────────────────────────────────────
function PartnerCardList({ items, onSelect }) {
  return (
    <div>
      {items.map((p, i) => (
        <div
          key={p.id}
          onClick={() => onSelect(p.id)}
          style={{
            padding: "14px 16px",
            borderBottom: i < items.length - 1 ? `1px solid ${S.borderSoft}` : "none",
            cursor: "pointer",
            opacity: p.is_active ? 1 : 0.6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {dh(p.name)}
                </span>
                {!p.is_active && <Tag color={S.muted} text="NEAKT" />}
              </div>
              <div style={{ fontSize: 12, color: S.muted, fontFamily: S.mono }}>
                {p.tax_id && `${buyerIdLabel(p.id_type)}: ${p.tax_id}`}
                {p.city && ` · ${p.city}`}
              </div>
              <div style={{ fontSize: 11, color: S.mutedLight, marginTop: 2 }}>
                {p.invoice_count} računa · poslednji {p.last_invoice_date ? fmtDate(p.last_invoice_date) : "—"}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 14, fontFamily: S.mono, fontWeight: 700, color: S.dark }}>
                {fmtMoney(p.total_invoiced)}
              </div>
              {p.outstanding > 0 && (
                <div style={{ fontSize: 11, fontFamily: S.mono, color: S.orange, fontWeight: 600, marginTop: 2 }}>
                  Duguje: {fmtMoney(p.outstanding)}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PARTNER DETAIL PANEL — drawer (desktop) / full sheet (mobile)
// ════════════════════════════════════════════════════════════════════════════
function PartnerDetailPanel({
  id, onClose, isMobile, canWrite,
  onEdit, onCreateInvoice, onAfterDelete, onAfterToggle,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const r = await api(`/b2b/partner/${id}`);
    setLoading(false);
    if (r.success) setData(r.data);
    else setError(r.message || "Greška");
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const partner = data?.partner;
  const stats   = data?.stats || {};
  const history = data?.history || [];

  const handleDelete = async () => {
    setBusy(true);
    const r = await api(`/partners/delete/${id}`, { method: "POST", body: JSON.stringify({}) });
    setBusy(false);
    setConfirmDelete(false);
    if (r.success) {
      onAfterDelete();
    } else {
      setToast({ type: "error", message: r.message || "Greška pri brisanju" });
      setTimeout(() => setToast(null), 3500);
    }
  };

  const handleToggleActive = async () => {
    if (!partner) return;
    setBusy(true);
    const r = await api(`/partners/update/${id}`, {
      method: "POST",
      body: JSON.stringify({ is_active: partner.is_active ? 0 : 1 }),
    });
    setBusy(false);
    if (r.success) {
      load();
      onAfterToggle();
      setToast({ type: "success", message: partner.is_active ? "Partner deaktiviran" : "Partner aktiviran" });
    } else {
      setToast({ type: "error", message: r.message || "Greška" });
    }
    setTimeout(() => setToast(null), 3500);
  };

  const panelStyle = isMobile
    ? {
        position: "fixed", inset: 0, background: S.bg, zIndex: 120,
        display: "flex", flexDirection: "column",
        animation: "slideUp 0.22s ease-out",
      }
    : {
        position: "fixed", top: 0, right: 0, bottom: 0, width: 720, maxWidth: "85vw",
        background: S.bg, zIndex: 120, boxShadow: "-12px 0 40px rgba(0,0,0,0.18)",
        display: "flex", flexDirection: "column",
      };

  return (
    <>
      {!isMobile && (
        <div onClick={onClose} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 115,
        }} />
      )}

      <div style={panelStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card, borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6,
            color: S.muted, borderRadius: 6,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
              {loading ? "Učitavam…" : dh(partner?.name) || "Partner"}
            </div>
            {partner && (
              <div style={{ fontSize: 12, color: S.muted, fontFamily: S.mono }}>
                {partner.tax_id && `${buyerIdLabel(partner.id_type)}: ${partner.tax_id}`}
                {!partner.is_active && (
                  <span style={{ marginLeft: 6, padding: "1px 6px", background: S.borderSoft, color: S.muted, borderRadius: 4, fontWeight: 600, fontSize: 10 }}>NEAKTIVAN</span>
                )}
              </div>
            )}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 14 : 24 }}>
          {loading ? (
            <Skeleton lines={10} />
          ) : error ? (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 8, color: S.red,
            }}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : partner ? (
            <PartnerDetailBody partner={partner} stats={stats} history={history} isMobile={isMobile} />
          ) : null}
        </div>

        {/* Action bar */}
        {!loading && partner && (
          <div style={{
            padding: isMobile ? "12px 14px calc(12px + env(safe-area-inset-bottom))" : "14px 24px",
            background: S.card, borderTop: `1px solid ${S.border}`,
            display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap",
          }}>
            {canWrite && (
              <button
                onClick={() => onCreateInvoice(partner.id)}
                style={{ ...S.btnSm(S.accent), padding: "10px 14px", flex: 1, minWidth: 160, justifyContent: "center" }}
              >
                <FilePlus2 size={14} /> Novi račun
              </button>
            )}
            {canWrite && (
              <>
                <button
                  onClick={() => onEdit(partner)}
                  style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 14px" }}
                >
                  <Edit3 size={14} /> Uredi
                </button>
                <button
                  onClick={handleToggleActive}
                  disabled={busy}
                  style={{ ...S.btnSm("white", partner.is_active ? S.orange : S.green), border: `1px solid ${partner.is_active ? S.orange : S.green}`, padding: "10px 14px" }}
                >
                  <Power size={14} /> {partner.is_active ? "Deaktiviraj" : "Aktiviraj"}
                </button>
                <button
                  onClick={() => setConfirmDelete(true)}
                  disabled={busy}
                  style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "10px 14px" }}
                >
                  <Trash2 size={14} /> Obriši
                </button>
              </>
            )}
          </div>
        )}

        {/* Delete confirmation */}
        {confirmDelete && (
          <div
            onClick={() => !busy && setConfirmDelete(false)}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: S.card, borderRadius: 14, padding: 24, maxWidth: 420, width: "100%",
                boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
              }}
            >
              <h3 style={{ margin: 0, fontSize: 17, color: S.dark }}>Obrisati partnera?</h3>
              <p style={{ fontSize: 14, color: S.muted, marginTop: 8, marginBottom: 20 }}>
                Ako postoje računi izdati ovom partneru, biće samo deaktiviran umjesto obrisan.
              </p>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button
                  onClick={() => setConfirmDelete(false)}
                  disabled={busy}
                  style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}
                >
                  Otkaži
                </button>
                <button
                  onClick={handleDelete}
                  disabled={busy}
                  style={{ ...S.btnSm(S.red, "white"), padding: "10px 16px" }}
                >
                  {busy ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Obriši
                </button>
              </div>
            </div>
          </div>
        )}

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </>
  );
}

function PartnerDetailBody({ partner, stats, history, isMobile }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Stats */}
      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(4, 1fr)",
      }}>
        <BadgeStat color={S.accent} label="Računa" value={stats.invoice_count} />
        <BadgeStat color={S.green}  label="Fakturisano" value={null}
                   numericOverride={fmtMoney(stats.total_invoiced || 0)} />
        <BadgeStat color={S.orange} label="Potraživanja" value={null}
                   numericOverride={fmtMoney(stats.outstanding || 0)} />
        <BadgeStat color={S.blue}   label="Plaćeno" value={null}
                   numericOverride={fmtMoney(stats.paid_total || 0)} />
      </div>

      {/* Period totals */}
      <Card title="Period">
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
          <KV label="Ovaj mjesec" value={`${fmtNum(stats.month_count || 0)} računa · ${fmtMoney(stats.month_total || 0)}`} />
          <KV label="Ova godina"  value={`${fmtNum(stats.year_count || 0)} računa · ${fmtMoney(stats.year_total || 0)}`} />
          <KV label="Prvi račun"  value={stats.first_invoice_date ? fmtDate(stats.first_invoice_date) : "—"} />
          <KV label="Poslednji"   value={stats.last_invoice_date ? fmtDate(stats.last_invoice_date) : "—"} />
        </div>
      </Card>

      {/* Basic info */}
      <Card title="Osnovni podaci">
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
          <KV label="Naziv" value={dh(partner.name)} />
          <KV label="Tip" value={partner.partner_type === "supplier" ? "Dobavljač" : partner.partner_type === "both" ? "Kupac i dobavljač" : "Kupac"} />
          <KV label={buyerIdLabel(partner.id_type)} value={partner.tax_id} mono />
          <KV label="Reg. broj" value={partner.registration_number} mono />
          <KV label="Aktivan" value={partner.is_active ? "Da" : "Ne"} />
        </div>
      </Card>

      {/* Contact */}
      {(partner.email || partner.phone || partner.mobile || partner.website || partner.contact_person) && (
        <Card title="Kontakt">
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
            {partner.email && <KV label="Email" value={dh(partner.email)} />}
            {partner.phone && <KV label="Telefon" value={partner.phone} />}
            {partner.mobile && <KV label="Mobilni" value={partner.mobile} />}
            {partner.website && <KV label="Website" value={partner.website} />}
            {partner.contact_person && <KV label="Kontakt osoba" value={dh(partner.contact_person)} />}
          </div>
        </Card>
      )}

      {/* Address */}
      {(partner.address || partner.city || partner.country) && (
        <Card title="Adresa">
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
            <KV label="Adresa" value={dh(partner.address)} />
            <KV label="Grad" value={dh(partner.city)} />
            <KV label="Poštanski broj" value={partner.postal_code} mono />
            <KV label="Država" value={partner.country} />
          </div>
        </Card>
      )}

      {/* Banking */}
      {(partner.bank_name || partner.bank_account) && (
        <Card title="Bankarski podaci">
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
            <KV label="Banka" value={dh(partner.bank_name)} />
            <KV label="Žiro račun" value={partner.bank_account} mono />
          </div>
        </Card>
      )}

      {partner.notes && (
        <Card title="Napomene">
          <div style={{ fontSize: 13, color: S.dark, whiteSpace: "pre-wrap" }}>{dh(partner.notes)}</div>
        </Card>
      )}

      {/* History */}
      <Card title="Istorija računa" subtitle={`Posljednjih ${history.length}`}>
        {history.length === 0 ? (
          <Empty text="Još nema izdatih računa" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 420 }}>
              <thead>
                <tr>
                  <th style={th}>Broj</th>
                  <th style={th}>Datum</th>
                  <th style={{ ...th, textAlign: "right" }}>Iznos</th>
                  <th style={{ ...th, textAlign: "right" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.id} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={{ ...td, fontFamily: S.mono, fontWeight: 600 }}>
                      {h.display_no || "—"}
                      {h.is_storno     && <Tag color={S.muted}  text="STORNO" />}
                      {h.is_corrective && <Tag color={S.blue}   text="KOR" />}
                      {h.is_advance    && <Tag color={S.accent} text="AVANS" />}
                    </td>
                    <td style={{ ...td, color: S.muted, whiteSpace: "nowrap" }}>{fmtDateTime(h.invoice_date)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>
                      {fmtMoney(h.total_amount, h.currency)}
                    </td>
                    <td style={{ ...td, textAlign: "right" }}>
                      <StatusBadge status={h.status} paid={h.is_paid} isStorno={h.is_storno} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div style={{ height: 16 }} />
    </div>
  );
}

// Adjusted BadgeStat to accept override for non-numeric content
// (Original BadgeStat at top of file only handles fmtNum)
// We use a parallel implementation here that accepts pre-formatted strings.
// But existing BadgeStat already works fine if `value` is null and we pass a numericOverride.
// Patch: BadgeStat already uses fmtNum(value || 0). Let me just inline a different component:
// Actually we'll just use the existing BadgeStat with numbers OR build one inline.
// To avoid refactoring, use the existing BadgeStat above where helpful.

// ════════════════════════════════════════════════════════════════════════════
// PARTNER FORM MODAL — create + edit
// ════════════════════════════════════════════════════════════════════════════
function PartnerFormModal({ partner, onClose, onSaved, isMobile }) {
  const isEdit = !!partner;
  const [data, setData] = useState({
    partner_type:        partner?.partner_type        || "customer",
    // Buyer identification type — same set as the invoice buyer tabs.
    // TIN (MNE company) | VAT (EU company) | TAX (non-EU company) |
    // ID (MNE citizen, JMBG) | PASS (foreign citizen, passport).
    // partner_kind is DERIVED from this at submit (ID/PASS → individual).
    id_type:             (partner?.id_type || "TIN").toUpperCase(),
    country_code:        partner?.country_code        || "",
    country_manual:      "",
    name:                dh(partner?.name)            || "",
    tax_id:              dh(partner?.tax_id)          || "",
    registration_number: dh(partner?.registration_number) || "",
    email:               dh(partner?.email)           || "",
    phone:               dh(partner?.phone)           || "",
    mobile:              dh(partner?.mobile)          || "",
    address:             dh(partner?.address)         || "",
    city:                dh(partner?.city)            || "",
    postal_code:         dh(partner?.postal_code)     || "",
    country:             dh(partner?.country)         || "Montenegro",
    bank_name:           dh(partner?.bank_name)       || "",
    bank_account:        dh(partner?.bank_account)    || "",
    contact_person:      dh(partner?.contact_person)  || "",
    website:             dh(partner?.website)         || "",
    notes:               dh(partner?.notes)           || "",
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  const update = (key, value) => setData((prev) => ({ ...prev, [key]: value }));

  // ESC closes
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  const submit = async () => {
    const e = {};
    if (!data.name.trim()) e.name = "Naziv je obavezan";
    // Foreign types + passport need a country; "OTHER" needs a manual alpha-3
    const needsCountry = ["VAT", "TAX", "PASS"].includes(data.id_type);
    if (needsCountry) {
      if (!data.country_code) e.country_code = "Izaberite državu";
      else if (data.country_code === "OTHER") {
        const m = data.country_manual.trim().toUpperCase();
        if (m.length !== 3) e.country_code = "Šifra države mora imati 3 slova (alpha-3)";
      }
    }
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setBusy(true);
    // Resolve the final alpha-3 country code. TIN/ID are domestic — send ""
    // (backend stores NULL = treated as MNE at invoice time, same convention
    // as auto-created partners from the invoice screen).
    const resolvedCountry = !needsCountry ? "" :
      (data.country_code === "OTHER" ? data.country_manual.trim().toUpperCase() : data.country_code);

    const payload = {
      ...data,
      id_type:      data.id_type,
      country_code: resolvedCountry,
      // Derived, matching the invoice screen's convention: individuals are
      // JMBG/passport buyers; every company variant (TIN/VAT/TAX) is 'company'.
      partner_kind: (data.id_type === "ID" || data.id_type === "PASS") ? "individual" : "company",
    };
    delete payload.country_manual; // UI-only helper field

    const url = isEdit ? `/partners/update/${partner.id}` : `/partners`;
    const r = await api(url, { method: "POST", body: JSON.stringify(payload) });
    setBusy(false);

    if (r.success) {
      onSaved(r.data?.partner);
    } else {
      setToast({ type: "error", message: r.message || "Greška pri čuvanju" });
      setTimeout(() => setToast(null), 3500);
    }
  };

  const modalStyle = isMobile
    ? { position: "fixed", inset: 0, background: S.bg, zIndex: 140, display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease-out" }
    : {
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 140,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      };

  const contentStyle = isMobile
    ? { background: S.bg, flex: 1, display: "flex", flexDirection: "column" }
    : {
        background: S.card, borderRadius: 14, width: "100%", maxWidth: 680, maxHeight: "90vh",
        overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      };

  return (
    <div onClick={!isMobile && !busy ? onClose : undefined} style={modalStyle}>
      <div onClick={(e) => e.stopPropagation()} style={contentStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card,
          borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} disabled={busy} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted, borderRadius: 6,
          }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
            {isEdit ? "Uredi partnera" : "Novi partner"}
          </div>
          <button
            onClick={submit}
            disabled={busy}
            style={{ ...S.btnSm(S.dark, "white"), padding: "10px 18px" }}
          >
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />}
            {busy ? "Čuvam…" : "Sačuvaj"}
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 24 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card title="Osnovni podaci">
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr" }}>
                <Field label="Naziv firme / ime" required error={errors.name}>
                  <input
                    value={data.name}
                    onChange={(e) => update("name", e.target.value)}
                    style={{ ...S.input, borderColor: errors.name ? S.red : S.border }}
                  />
                </Field>
                <Field label="Tip partnera">
                  <select
                    value={data.partner_type}
                    onChange={(e) => update("partner_type", e.target.value)}
                    style={{ ...S.input, cursor: "pointer" }}
                  >
                    <option value="customer">Kupac</option>
                    <option value="supplier">Dobavljač</option>
                    <option value="both">Kupac i dobavljač</option>
                  </select>
                </Field>
              </div>

              {/* Vrsta kupca — mirrors the invoice buyer tabs. Determines the
                  ID field's label/validation and whether a country is needed.
                  Existing partners default to TIN (legacy behavior). */}
              <div style={{ marginTop: 10 }}>
                <Field label="Vrsta kupca">
                  <select
                    value={data.id_type}
                    onChange={(e) => {
                      const t = e.target.value;
                      setData((prev) => ({
                        ...prev, id_type: t,
                        // Reset country when switching to a domestic type so a
                        // stale foreign code can't linger invisibly.
                        country_code: ["VAT", "TAX", "PASS"].includes(t) ? prev.country_code : "",
                        country_manual: ["VAT", "TAX", "PASS"].includes(t) ? prev.country_manual : "",
                      }));
                    }}
                    style={{ ...S.input, cursor: "pointer" }}
                  >
                    <option value="TIN">Pravno lice (Crna Gora) — PIB</option>
                    <option value="VAT">Strana firma (EU) — VAT ID</option>
                    <option value="TAX">Strana firma (van EU) — Tax ID</option>
                    <option value="ID">Fizičko lice (Crna Gora) — JMBG</option>
                    <option value="PASS">Strani državljanin — Pasoš</option>
                  </select>
                </Field>
              </div>

              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", marginTop: 10 }}>
                <Field label={buyerIdLabel(data.id_type)}>
                  <input
                    value={data.tax_id}
                    onChange={(e) => update("tax_id", e.target.value)}
                    placeholder={
                      data.id_type === "TIN" ? "8 cifara" :
                      data.id_type === "ID" ? "13 cifara" :
                      data.id_type === "PASS" ? "broj pasoša" :
                      data.id_type === "VAT" ? "npr. DE123456789" :
                      "poreski broj"
                    }
                    inputMode={data.id_type === "TIN" || data.id_type === "ID" ? "numeric" : "text"}
                    style={{ ...S.input, fontFamily: S.mono }}
                  />
                </Field>
                <Field label="Registracioni broj">
                  <input
                    value={data.registration_number}
                    onChange={(e) => update("registration_number", e.target.value)}
                    style={{ ...S.input, fontFamily: S.mono }}
                  />
                </Field>
              </div>

              {/* Country — only for foreign buyer types (VAT/TAX/PASS). Same
                  ISO alpha-3 list as the invoice screen, with a manual escape
                  hatch for countries not on the list. */}
              {["VAT", "TAX", "PASS"].includes(data.id_type) && (
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : (data.country_code === "OTHER" ? "1fr 1fr" : "1fr"), marginTop: 10 }}>
                  <Field label="Država" required error={errors.country_code}>
                    <select
                      value={data.country_code}
                      onChange={(e) => update("country_code", e.target.value)}
                      style={{ ...S.input, cursor: "pointer", borderColor: errors.country_code ? S.red : S.border }}
                    >
                      <option value="">— izaberite —</option>
                      {COUNTRY_CODES.map((c) => (
                        <option key={c.code} value={c.code}>{c.name} ({c.code})</option>
                      ))}
                      <option value="OTHER">Ostalo (unesi ručno)</option>
                    </select>
                  </Field>
                  {data.country_code === "OTHER" && (
                    <Field label="Alpha-3 šifra">
                      <input
                        value={data.country_manual}
                        onChange={(e) => update("country_manual", e.target.value.toUpperCase())}
                        placeholder="npr. ARE"
                        maxLength={3}
                        style={{ ...S.input, fontFamily: S.mono, textTransform: "uppercase" }}
                      />
                    </Field>
                  )}
                </div>
              )}
            </Card>

            <Card title="Kontakt">
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
                <Field label="Email">
                  <input type="email" value={data.email} onChange={(e) => update("email", e.target.value)} style={S.input} />
                </Field>
                <Field label="Kontakt osoba">
                  <input value={data.contact_person} onChange={(e) => update("contact_person", e.target.value)} style={S.input} />
                </Field>
                <Field label="Telefon">
                  <input value={data.phone} onChange={(e) => update("phone", e.target.value)} style={S.input} />
                </Field>
                <Field label="Mobilni">
                  <input value={data.mobile} onChange={(e) => update("mobile", e.target.value)} style={S.input} />
                </Field>
                <Field label="Website">
                  <input value={data.website} onChange={(e) => update("website", e.target.value)} placeholder="https://" style={S.input} />
                </Field>
              </div>
            </Card>

            <Card title="Adresa">
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr" }}>
                <Field label="Adresa">
                  <input value={data.address} onChange={(e) => update("address", e.target.value)} style={S.input} />
                </Field>
                <Field label="Grad">
                  <input value={data.city} onChange={(e) => update("city", e.target.value)} style={S.input} />
                </Field>
              </div>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", marginTop: 10 }}>
                <Field label="Poštanski broj">
                  <input value={data.postal_code} onChange={(e) => update("postal_code", e.target.value)} style={{ ...S.input, fontFamily: S.mono }} />
                </Field>
                <Field label="Država">
                  <input value={data.country} onChange={(e) => update("country", e.target.value)} style={S.input} />
                </Field>
              </div>
            </Card>

            <Card title="Bankarski podaci">
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 2fr" }}>
                <Field label="Naziv banke">
                  <input value={data.bank_name} onChange={(e) => update("bank_name", e.target.value)} style={S.input} />
                </Field>
                <Field label="Žiro račun">
                  <input
                    value={data.bank_account}
                    onChange={(e) => update("bank_account", e.target.value)}
                    placeholder="510-XXXXXXXX-XX"
                    style={{ ...S.input, fontFamily: S.mono }}
                  />
                </Field>
              </div>
            </Card>

            <Card title="Napomene">
              <textarea
                value={data.notes}
                onChange={(e) => update("notes", e.target.value)}
                rows={3}
                placeholder="Opciono…"
                style={{ ...S.input, resize: "vertical", minHeight: 60, fontFamily: S.font }}
              />
            </Card>
          </div>
        </div>

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// PRODUCTS SCREEN
// ════════════════════════════════════════════════════════════════════════════
const PRODUCT_SORT_OPTIONS = [
  { id: "name",      label: "Naziv (A-Z)",  sort: "name",  direction: "asc"  },
  { id: "name_desc", label: "Naziv (Z-A)",  sort: "name",  direction: "desc" },
  { id: "price",     label: "Cijena ↑",     sort: "price", direction: "asc"  },
  { id: "price_desc", label: "Cijena ↓",    sort: "price", direction: "desc" },
];
const PRODUCT_STATUS_OPTIONS = [
  { id: "active",   label: "Aktivni" },
  { id: "inactive", label: "Neaktivni" },
  { id: "all",      label: "Svi" },
];

function ProductsScreen({ isMobile, canWrite, company }) {
  const isPausalac = !!company?.is_pausalac;

  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);

  const [status, setStatus]       = useState("active");
  const [categoryId, setCategoryId] = useState(0);
  const [sortKey, setSortKey]     = useState("name");
  const [page, setPage]           = useState(1);
  const perPage = isMobile ? 25 : 50;

  const [data, setData]       = useState({ items: [], total: 0, total_pages: 1, categories: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");

  const [editingProduct, setEditingProduct] = useState(null); // null | "new" | product obj
  const [confirmToggle, setConfirmToggle]   = useState(null); // product object
  const [filterOpen, setFilterOpen]         = useState(false);
  const [toast, setToast]                   = useState(null);

  useEffect(() => { setPage(1); }, [search, status, categoryId, sortKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const opt = PRODUCT_SORT_OPTIONS.find((o) => o.id === sortKey) || PRODUCT_SORT_OPTIONS[0];
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    qs.set("active", status);
    if (categoryId > 0) qs.set("category_id", String(categoryId));
    qs.set("sort", opt.sort);
    qs.set("direction", opt.direction);
    qs.set("page", String(page));
    qs.set("per_page", String(perPage));
    const r = await api(`/b2b/products?${qs.toString()}`);
    setLoading(false);
    if (r.success) setData(r.data);
    else setError(r.message || "Greška pri učitavanju");
  }, [search, status, categoryId, sortKey, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const items = Array.isArray(data.items) ? data.items : [];
  const categories = Array.isArray(data.categories) ? data.categories : [];

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleToggle = async (product) => {
    const r = await api(`/products/update/${product.id}`, {
      method: "POST",
      body: JSON.stringify({ is_active: product.is_active ? 0 : 1 }),
    });
    setConfirmToggle(null);
    if (r.success) {
      load();
      showToast("success", product.is_active ? "Proizvod deaktiviran" : "Proizvod aktiviran");
    } else {
      showToast("error", r.message || "Greška");
    }
  };

  const handleAfterSave = () => {
    setEditingProduct(null);
    load();
    showToast("success", "Proizvod sačuvan");
  };

  const filterPanel = (
    <ProductsFilterPanel
      searchInput={searchInput} setSearchInput={setSearchInput}
      status={status} setStatus={setStatus}
      categoryId={categoryId} setCategoryId={setCategoryId}
      categories={categories}
      sortKey={sortKey} setSortKey={setSortKey}
      isMobile={isMobile}
    />
  );

  return (
    <>
      <TopHeader
        title="Proizvodi"
        subtitle={loading ? "Učitavam…" : `${fmtNum(data.total || 0)} proizvoda`}
        isMobile={isMobile}
        right={
          isMobile ? (
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => setFilterOpen(true)}
                style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "8px 10px" }}
              >
                <FilterIcon size={14} />
              </button>
              {canWrite && (
                <button
                  onClick={() => setEditingProduct("new")}
                  style={{ ...S.btnSm(S.dark, "white"), padding: "8px 12px" }}
                >
                  <Plus size={14} />
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={load}
                disabled={loading}
                style={{ ...S.btnSm("white", S.muted), border: `1px solid ${S.border}`, padding: "8px 10px" }}
              >
                <RefreshCw size={14} className={loading ? "spin" : ""} />
                {loading ? "Učitavam" : "Osvježi"}
              </button>
              {canWrite && (
                <button
                  onClick={() => setEditingProduct("new")}
                  style={{ ...S.btnSm(S.dark, "white"), padding: "8px 14px" }}
                >
                  <Plus size={14} /> Novi proizvod
                </button>
              )}
            </div>
          )
        }
      />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1600 }}>
        {!isMobile && (
          <div style={{ marginBottom: 16 }}>{filterPanel}</div>
        )}

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
            padding: "14px 18px", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        <div style={{
          background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
          overflow: "hidden",
        }}>
          {loading && items.length === 0 ? (
            <div style={{ padding: 24 }}><Skeleton lines={6} /></div>
          ) : items.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: S.mutedLight }}>
              <Package size={36} color={S.mutedLight} />
              <div style={{ marginTop: 12, fontSize: 14 }}>
                {search ? "Nema rezultata za pretragu" : "Još uvijek nemate proizvoda"}
              </div>
              {canWrite && !search && (
                <button
                  onClick={() => setEditingProduct("new")}
                  style={{ ...S.btn(S.accent), marginTop: 14 }}
                >
                  <Plus size={16} /> Dodaj prvi proizvod
                </button>
              )}
            </div>
          ) : isMobile ? (
            <ProductCardList
              items={items}
              canWrite={canWrite}
              onEdit={(p) => setEditingProduct(p)}
              onToggle={(p) => setConfirmToggle(p)}
            />
          ) : (
            <ProductTable
              items={items}
              canWrite={canWrite}
              onEdit={(p) => setEditingProduct(p)}
              onToggle={(p) => setConfirmToggle(p)}
            />
          )}
        </div>

        {data.total_pages > 1 && (
          <Pagination
            page={page}
            totalPages={data.total_pages}
            onChange={setPage}
            isMobile={isMobile}
          />
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {/* Mobile filter sheet */}
      {isMobile && filterOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 110,
            display: "flex", alignItems: "flex-end",
          }}
          onClick={() => setFilterOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="slide-up"
            style={{
              width: "100%", background: S.card, borderTopLeftRadius: 16,
              borderTopRightRadius: 16, maxHeight: "85vh", overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px" }}>
              <div style={{ width: 40, height: 4, background: S.border, borderRadius: 2 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px 12px" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: S.dark }}>Filteri</div>
              <button onClick={() => setFilterOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: "0 16px 24px" }}>
              {filterPanel}
              <button onClick={() => setFilterOpen(false)} style={{ ...S.btn(S.dark), width: "100%", marginTop: 18 }}>Primijeni</button>
            </div>
          </div>
        </div>
      )}

      {/* Toggle confirmation */}
      {confirmToggle && (
        <div
          onClick={() => setConfirmToggle(null)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 420, width: "100%" }}
          >
            <h3 style={{ margin: 0, fontSize: 17, color: S.dark }}>
              {confirmToggle.is_active ? "Deaktivirati proizvod?" : "Aktivirati proizvod?"}
            </h3>
            <p style={{ fontSize: 14, color: S.muted, marginTop: 8, marginBottom: 20 }}>
              {confirmToggle.is_active
                ? `"${confirmToggle.name}" neće biti vidljiv pri kreiranju novih računa.`
                : `"${confirmToggle.name}" će opet biti dostupan u listi.`}
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button
                onClick={() => setConfirmToggle(null)}
                style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}
              >
                Otkaži
              </button>
              <button
                onClick={() => handleToggle(confirmToggle)}
                style={{ ...S.btnSm(confirmToggle.is_active ? S.orange : S.green, "white"), padding: "10px 16px" }}
              >
                <Power size={14} /> Potvrdi
              </button>
            </div>
          </div>
        </div>
      )}

      {editingProduct !== null && (
        <ProductFormModal
          product={editingProduct === "new" ? null : editingProduct}
          categories={categories}
          isPausalac={isPausalac}
          onClose={() => setEditingProduct(null)}
          onSaved={handleAfterSave}
          isMobile={isMobile}
        />
      )}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Filter panel for products
// ────────────────────────────────────────────────────────────────────────────
function ProductsFilterPanel({
  searchInput, setSearchInput,
  status, setStatus,
  categoryId, setCategoryId, categories,
  sortKey, setSortKey,
  isMobile,
}) {
  return (
    <div style={{
      background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
      padding: isMobile ? 12 : 14,
    }}>
      <div style={{ position: "relative", marginBottom: 12 }}>
        <Search size={16} color={S.mutedLight} style={{
          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
        }} />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Pretraži po nazivu, SKU, barkodu…"
          style={{ ...S.input, paddingLeft: 36 }}
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput("")}
            style={{
              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted,
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
      }}>
        <Select value={status} onChange={setStatus} options={PRODUCT_STATUS_OPTIONS} icon={Power} />
        <Select
          value={String(categoryId)}
          onChange={(v) => setCategoryId(parseInt(v, 10) || 0)}
          options={[
            { id: "0", label: "Sve kategorije" },
            ...categories.map((c) => ({ id: String(c.id), label: dh(c.name) })),
          ]}
          icon={Package}
        />
        <Select value={sortKey} onChange={setSortKey} options={PRODUCT_SORT_OPTIONS} icon={ChevronsUpDown} />
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Desktop products table
// ────────────────────────────────────────────────────────────────────────────
function ProductTable({ items, canWrite, onEdit, onToggle }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 880 }}>
        <thead style={{ background: "#fafafb" }}>
          <tr>
            <th style={th}>Naziv</th>
            <th style={th}>Kategorija</th>
            <th style={{ ...th, textAlign: "right" }}>Cijena (neto)</th>
            <th style={{ ...th, textAlign: "right" }}>Cijena (bruto)</th>
            <th style={{ ...th, textAlign: "right" }}>PDV</th>
            <th style={th}>Jed.</th>
            <th style={{ ...th, textAlign: "right" }}>Stanje</th>
            {canWrite && <th style={{ ...th, textAlign: "right" }}>Akcije</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((p) => {
            const neto = parseFloat(p.price) || 0;
            const vat  = parseFloat(p.vat_rate) || 0;
            const bruto = neto * (1 + vat / 100);
            return (
            <tr
              key={p.id}
              style={{
                borderBottom: `1px solid ${S.borderSoft}`,
                opacity: p.is_active ? 1 : 0.55,
              }}
            >
              <td style={{ ...td, fontWeight: 600 }}>
                {dh(p.name)}
                {!p.is_active && <Tag color={S.muted} text="NEAKTIVAN" />}
                {p.description && (
                  <div style={{ fontSize: 11, color: S.mutedLight, marginTop: 2, fontWeight: 400 }}>
                    {dh(p.description.length > 60 ? p.description.slice(0, 60) + "…" : p.description)}
                  </div>
                )}
              </td>
              <td style={{ ...td, color: S.muted }}>{p.category_name || <span style={{ color: S.mutedLight }}>—</span>}</td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>
                {fmtMoney(neto)}
              </td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600, color: S.accent }}>
                {fmtMoney(bruto)}
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                <span style={{
                  background: S.accentSoft, color: S.accent,
                  padding: "2px 8px", borderRadius: 4, fontSize: 12,
                  fontWeight: 600, fontFamily: S.mono,
                }}>{p.vat_rate}%</span>
              </td>
              <td style={{ ...td, color: S.muted }}>{p.unit}</td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, color: S.muted }}>
                {p.track_stock
                  ? <span style={{ color: p.stock_quantity <= (p.low_stock_alert || 0) ? S.red : S.dark }}>
                      {fmtNum(p.stock_quantity || 0)}
                    </span>
                  : <span style={{ color: S.mutedLight }}>—</span>}
              </td>
              {canWrite && (
                <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                  <button
                    onClick={() => onEdit(p)}
                    title="Uredi"
                    style={{
                      background: "none", border: `1px solid ${S.border}`, borderRadius: 6,
                      padding: "5px 9px", cursor: "pointer", color: S.dark, marginRight: 4,
                    }}
                  >
                    <Edit3 size={13} />
                  </button>
                  <button
                    onClick={() => onToggle(p)}
                    title={p.is_active ? "Deaktiviraj" : "Aktiviraj"}
                    style={{
                      background: "none",
                      border: `1px solid ${p.is_active ? S.orange : S.green}`,
                      borderRadius: 6, padding: "5px 9px", cursor: "pointer",
                      color: p.is_active ? S.orange : S.green,
                    }}
                  >
                    <Power size={13} />
                  </button>
                </td>
              )}
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Mobile products card list
// ────────────────────────────────────────────────────────────────────────────
function ProductCardList({ items, canWrite, onEdit, onToggle }) {
  return (
    <div>
      {items.map((p, i) => (
        <div
          key={p.id}
          style={{
            padding: "14px 16px",
            borderBottom: i < items.length - 1 ? `1px solid ${S.borderSoft}` : "none",
            opacity: p.is_active ? 1 : 0.6,
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <span style={{ fontSize: 15, fontWeight: 700, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {dh(p.name)}
                </span>
                {!p.is_active && <Tag color={S.muted} text="NEAKT" />}
              </div>
              <div style={{ fontSize: 12, color: S.muted }}>
                {dh(p.category_name) || "—"}
              </div>
              <div style={{ fontSize: 11, color: S.mutedLight, marginTop: 2 }}>
                Jed: {p.unit}
                {p.track_stock && ` · Stanje: ${fmtNum(p.stock_quantity || 0)}`}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontFamily: S.mono, fontWeight: 700, color: S.dark }}>
                {fmtMoney(p.price)}
              </div>
              <div style={{ fontSize: 11, fontFamily: S.mono, color: S.accent, fontWeight: 600 }}>
                Bruto: {fmtMoney((parseFloat(p.price) || 0) * (1 + (parseFloat(p.vat_rate) || 0) / 100))}
              </div>
              <div style={{
                marginTop: 2, fontSize: 11, fontFamily: S.mono, color: S.accent,
              }}>{p.vat_rate}% PDV</div>
            </div>
          </div>

          {canWrite && (
            <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
              <button
                onClick={() => onEdit(p)}
                style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, flex: 1, justifyContent: "center", padding: "8px" }}
              >
                <Edit3 size={13} /> Uredi
              </button>
              <button
                onClick={() => onToggle(p)}
                style={{
                  ...S.btnSm("white", p.is_active ? S.orange : S.green),
                  border: `1px solid ${p.is_active ? S.orange : S.green}`,
                  flex: 1, justifyContent: "center", padding: "8px",
                }}
              >
                <Power size={13} /> {p.is_active ? "Deaktiv." : "Aktivi."}
              </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PRODUCT FORM MODAL — create + edit
// ════════════════════════════════════════════════════════════════════════════
function ProductFormModal({ product, categories, isPausalac, onClose, onSaved, isMobile }) {
  const isEdit = !!product;
  const [data, setData] = useState({
    name:            dh(product?.name)        || "",
    description:     dh(product?.description) || "",
    price:           product?.price ?? "",
    vat_rate:        product?.vat_rate ?? (isPausalac ? 0 : 21),
    unit:            dh(product?.unit)        || "kom",
    is_decimal_unit: product?.is_decimal_unit ? 1 : 0,
    category_id:     product?.category_id     || "",
    sku:             dh(product?.sku)         || "",
    barcode:         dh(product?.barcode)     || "",
    track_stock:     product?.track_stock ? 1 : 0,
    stock_quantity:  product?.stock_quantity ?? 0,
    low_stock_alert: product?.low_stock_alert ?? 10,
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy]     = useState(false);
  const [toast, setToast]   = useState(null);

  const update = (key, value) => setData((prev) => ({ ...prev, [key]: value }));

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  const submit = async () => {
    const e = {};
    if (!data.name.trim()) e.name = "Naziv je obavezan";
    if (data.price === "" || isNaN(parseNum(data.price))) e.price = "Cijena je obavezna";
    if (isPausalac && parseNum(data.vat_rate) > 0) e.vat_rate = "Pausalna firma — PDV mora biti 0%";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setBusy(true);
    const body = {
      name:             data.name.trim(),
      description:      data.description,
      price:            parseNum(data.price),
      vat_rate:         parseNum(data.vat_rate),
      unit:             data.unit || "kom",
      is_decimal_unit:  data.is_decimal_unit ? 1 : 0,
      category_id:      data.category_id ? parseInt(data.category_id, 10) : null,
      sku:              data.sku,
      barcode:          data.barcode,
      track_stock:      data.track_stock ? 1 : 0,
      stock_quantity:   parseNum(data.stock_quantity),
      low_stock_alert:  parseNum(data.low_stock_alert),
      // Note: _priceMode and _brutoTyped are local-only and NOT sent to backend
    };
    const url = isEdit ? `/products/update/${product.id}` : `/products`;
    const r = await api(url, { method: "POST", body: JSON.stringify(body) });
    setBusy(false);

    if (r.success) {
      onSaved(r.data?.product);
    } else {
      setToast({ type: "error", message: r.message || "Greška pri čuvanju" });
      setTimeout(() => setToast(null), 3500);
    }
  };

  const modalStyle = isMobile
    ? { position: "fixed", inset: 0, background: S.bg, zIndex: 140, display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease-out" }
    : {
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 140,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      };

  const contentStyle = isMobile
    ? { background: S.bg, flex: 1, display: "flex", flexDirection: "column" }
    : {
        background: S.card, borderRadius: 14, width: "100%", maxWidth: 680, maxHeight: "90vh",
        overflow: "hidden", display: "flex", flexDirection: "column",
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
      };

  return (
    <div onClick={!isMobile && !busy ? onClose : undefined} style={modalStyle}>
      <div onClick={(e) => e.stopPropagation()} style={contentStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card,
          borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} disabled={busy} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted, borderRadius: 6,
          }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
            {isEdit ? "Uredi proizvod" : "Novi proizvod"}
          </div>
          <button
            onClick={submit}
            disabled={busy}
            style={{ ...S.btnSm(S.dark, "white"), padding: "10px 18px" }}
          >
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />}
            {busy ? "Čuvam…" : "Sačuvaj"}
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 16 : 24 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Card title="Osnovni podaci">
              <Field label="Naziv" required error={errors.name}>
                <input
                  value={data.name}
                  onChange={(e) => update("name", e.target.value)}
                  style={{ ...S.input, borderColor: errors.name ? S.red : S.border }}
                />
              </Field>
              <div style={{ marginTop: 10 }}>
                <Field label="Opis (opciono)">
                  <textarea
                    value={data.description}
                    onChange={(e) => update("description", e.target.value)}
                    rows={2}
                    style={{ ...S.input, resize: "vertical", minHeight: 50, fontFamily: S.font }}
                  />
                </Field>
              </div>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", marginTop: 10 }}>
                <Field label="Kategorija">
                  <select
                    value={String(data.category_id || "")}
                    onChange={(e) => update("category_id", e.target.value)}
                    style={{ ...S.input, cursor: "pointer" }}
                  >
                    <option value="">— Bez kategorije —</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </Field>
                <Field label="SKU">
                  <input
                    value={data.sku}
                    onChange={(e) => update("sku", e.target.value)}
                    style={{ ...S.input, fontFamily: S.mono }}
                  />
                </Field>
              </div>
              <div style={{ marginTop: 10 }}>
                <Field label="Barkod">
                  <input
                    value={data.barcode}
                    onChange={(e) => update("barcode", e.target.value)}
                    style={{ ...S.input, fontFamily: S.mono }}
                  />
                </Field>
              </div>
            </Card>

            <Card title="Cijena i PDV">
              <div style={{
                display: "inline-flex", border: `1px solid ${S.border}`, borderRadius: 8,
                overflow: "hidden", marginBottom: 12,
              }}>
                <button
                  type="button"
                  onClick={() => update("_priceMode", "neto")}
                  style={{
                    padding: "7px 16px", fontSize: 12, fontWeight: 600,
                    background: (data._priceMode || "neto") === "neto" ? S.dark : "white",
                    color: (data._priceMode || "neto") === "neto" ? "white" : S.muted,
                    border: "none", cursor: "pointer", fontFamily: S.font,
                  }}
                >Unos kao neto</button>
                <button
                  type="button"
                  onClick={() => update("_priceMode", "bruto")}
                  style={{
                    padding: "7px 16px", fontSize: 12, fontWeight: 600,
                    background: data._priceMode === "bruto" ? S.dark : "white",
                    color: data._priceMode === "bruto" ? "white" : S.muted,
                    border: "none", borderLeft: `1px solid ${S.border}`,
                    cursor: "pointer", fontFamily: S.font,
                  }}
                >Unos kao bruto</button>
              </div>

              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr" }}>
                {(data._priceMode || "neto") === "neto" ? (
                  <Field label="Cijena (neto)" required error={errors.price}>
                    <input
                      type="text" inputMode="decimal"
                      value={data.price}
                      onChange={(e) => update("price", e.target.value)}
                      placeholder="0,00"
                      style={{ ...S.input, fontFamily: S.mono, borderColor: errors.price ? S.red : S.border }}
                    />
                  </Field>
                ) : (
                  <Field label="Cijena (bruto)" required error={errors.price}>
                    <input
                      type="text" inputMode="decimal"
                      value={data._brutoTyped ?? (
                        data.price !== "" && !isNaN(parseNum(data.price))
                          ? (parseNum(data.price) * (1 + parseNum(data.vat_rate) / 100)).toFixed(2).replace(".", ",")
                          : ""
                      )}
                      onChange={(e) => {
                        const v = e.target.value;
                        const bruto = parseNum(v);
                        const vat = parseNum(data.vat_rate);
                        const neto = isNaN(bruto) ? "" : (bruto / (1 + vat / 100));
                        setData((d) => ({ ...d, _brutoTyped: v, price: isNaN(neto) ? "" : neto.toFixed(4) }));
                      }}
                      placeholder="0,00"
                      style={{ ...S.input, fontFamily: S.mono, borderColor: errors.price ? S.red : S.border }}
                    />
                  </Field>
                )}
                <Field label="PDV %" error={errors.vat_rate}>
                  <select
                    value={data.vat_rate}
                    onChange={(e) => {
                      const newVat = parseFloat(e.target.value);
                      setData((d) => {
                        // If bruto mode and bruto typed exists, recalc neto with new VAT
                        if (d._priceMode === "bruto" && d._brutoTyped !== undefined) {
                          const bruto = parseNum(d._brutoTyped);
                          if (!isNaN(bruto)) {
                            return { ...d, vat_rate: newVat, price: (bruto / (1 + newVat / 100)).toFixed(4) };
                          }
                        }
                        return { ...d, vat_rate: newVat };
                      });
                    }}
                    disabled={isPausalac}
                    style={{ ...S.input, fontFamily: S.mono, cursor: "pointer", borderColor: errors.vat_rate ? S.red : S.border }}
                  >
                    {(isPausalac ? [0] : VAT_RATES).map((r) => (
                      <option key={r} value={r}>{r}%</option>
                    ))}
                  </select>
                </Field>
                <Field label="Jedinica">
                  <input
                    value={data.unit}
                    onChange={(e) => update("unit", e.target.value)}
                    placeholder="kom"
                    style={S.input}
                  />
                </Field>
              </div>

              {/* Live preview: show both neto and bruto */}
              {data.price !== "" && !isNaN(parseNum(data.price)) && (
                <div style={{
                  marginTop: 10, padding: "8px 12px",
                  background: S.accentSoft, borderRadius: 8, fontSize: 12,
                  display: "flex", gap: 16, color: S.dark, fontFamily: S.mono,
                }}>
                  <span>Neto: <strong>{fmtMoney(parseNum(data.price))}</strong></span>
                  <span>·</span>
                  <span>Bruto: <strong>{fmtMoney(parseNum(data.price) * (1 + parseNum(data.vat_rate) / 100))}</strong></span>
                </div>
              )}

              <label style={{
                display: "flex", alignItems: "center", gap: 8, marginTop: 12,
                cursor: "pointer", fontSize: 13, color: S.dark,
              }}>
                <input
                  type="checkbox"
                  checked={!!data.is_decimal_unit}
                  onChange={(e) => update("is_decimal_unit", e.target.checked ? 1 : 0)}
                  style={{ width: 16, height: 16, accentColor: S.accent }}
                />
                Decimalna količina (npr. kg, l, m²)
              </label>
            </Card>

            <Card title="Zalihe">
              <label style={{
                display: "flex", alignItems: "center", gap: 8,
                cursor: "pointer", fontSize: 13, color: S.dark, marginBottom: 12,
              }}>
                <input
                  type="checkbox"
                  checked={!!data.track_stock}
                  onChange={(e) => update("track_stock", e.target.checked ? 1 : 0)}
                  style={{ width: 16, height: 16, accentColor: S.accent }}
                />
                Prati stanje zaliha
              </label>
              {data.track_stock ? (
                <div style={{ display: "grid", gap: 10, gridTemplateColumns: "1fr 1fr" }}>
                  <Field label="Trenutno stanje">
                    <input
                      type="text" inputMode="decimal"
                      value={data.stock_quantity}
                      onChange={(e) => update("stock_quantity", e.target.value)}
                      style={{ ...S.input, fontFamily: S.mono }}
                    />
                  </Field>
                  <Field label="Alarm na (min stanje)">
                    <input
                      type="text" inputMode="decimal"
                      value={data.low_stock_alert}
                      onChange={(e) => update("low_stock_alert", e.target.value)}
                      style={{ ...S.input, fontFamily: S.mono }}
                    />
                  </Field>
                </div>
              ) : null}
            </Card>
          </div>
        </div>

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// PROFORMAS SCREEN (Predračuni)
// ════════════════════════════════════════════════════════════════════════════
const PROFORMA_STATUS_OPTIONS = [
  { id: "all",       label: "Svi statusi" },
  { id: "draft",     label: "Nacrt" },
  { id: "sent",      label: "Poslat" },
  { id: "accepted",  label: "Prihvaćen" },
  { id: "rejected",  label: "Odbijen" },
  { id: "expired",   label: "Istekao" },
  { id: "converted", label: "Konvertovan" },
];
const PROFORMA_STATUS_META = {
  draft:     { label: "Nacrt",       color: "#d97706", bg: "#fef3c7" },
  sent:      { label: "Poslat",      color: "#0284c7", bg: "#dbeafe" },
  accepted:  { label: "Prihvaćen",   color: "#059669", bg: "#dcfce7" },
  rejected:  { label: "Odbijen",     color: "#dc2626", bg: "#fee2e2" },
  expired:   { label: "Istekao",     color: "#6b7280", bg: "#f3f4f6" },
  converted: { label: "Konvertovan", color: "#4f46e5", bg: "#eef2ff" },
};

function ProformasScreen({ isMobile, canWrite, company, goToInvoices }) {
  // Filters
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);
  const [status, setStatus] = useState("all");
  const [datePreset, setPreset] = useState("month");
  const [partnerId, setPartnerId] = useState(0);
  const [partners, setPartners] = useState([]);

  const [sort] = useState("date");
  const [direction] = useState("desc");
  const [page, setPage] = useState(1);
  const perPage = isMobile ? 25 : 50;

  const [data, setData] = useState({ items: [], total: 0, total_pages: 1, summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const r = await api("/partners");
      if (!active) return;
      if (r.success) {
        const list = Array.isArray(r.data) ? r.data
                   : (Array.isArray(r.data?.partners) ? r.data.partners : []);
        setPartners(list);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => { setPage(1); }, [search, status, datePreset, partnerId, sort, direction]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const { from, to } = resolvePresetRange(datePreset);
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (status !== "all") qs.set("status", status);
    if (partnerId > 0) qs.set("partner_id", String(partnerId));
    if (from) qs.set("date_from", from);
    if (to) qs.set("date_to", to);
    qs.set("sort", sort);
    qs.set("direction", direction);
    qs.set("page", String(page));
    qs.set("per_page", String(perPage));
    const r = await api(`/b2b/proformas?${qs.toString()}`);
    setLoading(false);
    if (r.success) setData(r.data);
    else setError(r.message || "Greška pri učitavanju");
  }, [search, status, datePreset, partnerId, sort, direction, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const items = Array.isArray(data.items) ? data.items : [];
  const summary = data.summary || {};

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const filterPanel = (
    <ProformasFilterPanel
      searchInput={searchInput} setSearchInput={setSearchInput}
      status={status} setStatus={setStatus}
      datePreset={datePreset} setPreset={setPreset}
      partnerId={partnerId} setPartnerId={setPartnerId}
      partners={partners}
      isMobile={isMobile}
    />
  );

  return (
    <>
      <TopHeader
        title="Predračuni"
        subtitle={loading ? "Učitavam…" : `${fmtNum(summary.total_count || 0)} predračuna · ${fmtMoney(summary.total_amount || 0)}`}
        isMobile={isMobile}
        right={
          isMobile ? (
            <div style={{ display: "flex", gap: 6 }}>
              <button
                onClick={() => setFilterOpen(true)}
                style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "8px 10px" }}
              >
                <FilterIcon size={14} />
              </button>
              {canWrite && (
                <button onClick={() => setShowForm(true)} style={{ ...S.btnSm(S.dark, "white"), padding: "8px 12px" }}>
                  <Plus size={14} />
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={load} disabled={loading}
                style={{ ...S.btnSm("white", S.muted), border: `1px solid ${S.border}`, padding: "8px 10px" }}
              >
                <RefreshCw size={14} className={loading ? "spin" : ""} />
                {loading ? "Učitavam" : "Osvježi"}
              </button>
              {canWrite && (
                <button onClick={() => setShowForm(true)} style={{ ...S.btnSm(S.dark, "white"), padding: "8px 14px" }}>
                  <Plus size={14} /> Novi predračun
                </button>
              )}
            </div>
          )
        }
      />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1600 }}>
        {!isMobile && <div style={{ marginBottom: 16 }}>{filterPanel}</div>}

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
            padding: "14px 18px", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Status badges row */}
        {!loading && items.length > 0 && (
          <div style={{
            display: "grid", gap: 10, marginBottom: 14,
            gridTemplateColumns: isMobile ? "repeat(3, 1fr)" : "repeat(auto-fit, minmax(150px, 1fr))",
          }}>
            <BadgeStat color={S.amber}  label="Nacrti"      value={summary.count_draft} />
            <BadgeStat color={S.blue}   label="Poslati"     value={summary.count_sent} />
            <BadgeStat color={S.green}  label="Prihvaćeni"  value={summary.count_accepted} />
            <BadgeStat color={S.accent} label="Konvertovani" value={summary.count_converted} />
            <BadgeStat color={S.muted}  label="Istekli"     value={summary.count_expired} />
          </div>
        )}

        <div style={{
          background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
          overflow: "hidden",
        }}>
          {loading && items.length === 0 ? (
            <div style={{ padding: 24 }}><Skeleton lines={6} /></div>
          ) : items.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: S.mutedLight }}>
              <FileText size={36} color={S.mutedLight} />
              <div style={{ marginTop: 12, fontSize: 14 }}>
                {search ? "Nema rezultata" : "Još nemate predračuna"}
              </div>
              {canWrite && !search && (
                <button onClick={() => setShowForm(true)} style={{ ...S.btn(S.accent), marginTop: 14 }}>
                  <Plus size={16} /> Kreiraj prvi predračun
                </button>
              )}
            </div>
          ) : isMobile ? (
            <ProformaCardList items={items} onSelect={setSelectedId} />
          ) : (
            <ProformaTable items={items} onSelect={setSelectedId} />
          )}
        </div>

        {data.total_pages > 1 && (
          <Pagination page={page} totalPages={data.total_pages} onChange={setPage} isMobile={isMobile} />
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {/* Mobile filter sheet */}
      {isMobile && filterOpen && (
        <div
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 110,
            display: "flex", alignItems: "flex-end",
          }}
          onClick={() => setFilterOpen(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()} className="slide-up"
            style={{ width: "100%", background: S.card, borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "85vh", overflowY: "auto" }}
          >
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px" }}>
              <div style={{ width: 40, height: 4, background: S.border, borderRadius: 2 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px 12px" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: S.dark }}>Filteri</div>
              <button onClick={() => setFilterOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: "0 16px 24px" }}>
              {filterPanel}
              <button onClick={() => setFilterOpen(false)} style={{ ...S.btn(S.dark), width: "100%", marginTop: 18 }}>Primijeni</button>
            </div>
          </div>
        </div>
      )}

      {/* Detail panel */}
      {selectedId !== null && (
        <ProformaDetailPanel
          id={selectedId}
          onClose={() => setSelectedId(null)}
          isMobile={isMobile}
          canWrite={canWrite}
          onAfterChange={() => { load(); }}
          onAfterDelete={() => { setSelectedId(null); load(); showToast("success", "Predračun obrisan"); }}
          onAfterConvert={(newInvoiceId, invoiceNumber) => {
            // Proforma → invoice conversion succeeded. Close the panel, reload
            // the proforma list (status changed to 'converted'), then jump
            // to the Računi sector so the user can see the new draft.
            setSelectedId(null);
            load();
            showToast("success", `Predračun konvertovan u račun ${invoiceNumber}. Otvaranje sektora Računi…`);
            // Small delay so the toast is visible before navigation
            setTimeout(() => { if (typeof goToInvoices === "function") goToInvoices(); }, 900);
          }}
        />
      )}

      {/* Create form modal */}
      {showForm && (
        <ProformaFormModal
          onClose={() => setShowForm(false)}
          onSaved={(id) => {
            setShowForm(false);
            load();
            showToast("success", "Predračun kreiran");
            setSelectedId(id);
          }}
          isMobile={isMobile}
          company={company}
          partners={partners}
        />
      )}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

function ProformasFilterPanel({
  searchInput, setSearchInput,
  status, setStatus,
  datePreset, setPreset,
  partnerId, setPartnerId, partners,
  isMobile,
}) {
  return (
    <div style={{
      background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
      padding: isMobile ? 12 : 14,
    }}>
      <div style={{ position: "relative", marginBottom: 12 }}>
        <Search size={16} color={S.mutedLight} style={{
          position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
        }} />
        <input
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Pretraži broj, kupca, partnera…"
          style={{ ...S.input, paddingLeft: 36 }}
        />
        {searchInput && (
          <button
            onClick={() => setSearchInput("")}
            style={{
              position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
              background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted,
            }}
          >
            <X size={14} />
          </button>
        )}
      </div>

      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
      }}>
        <Select value={status} onChange={setStatus} options={PROFORMA_STATUS_OPTIONS} icon={CircleCheck} />
        <Select value={datePreset} onChange={setPreset} options={DATE_RANGES} icon={Calendar} />
        <Select
          value={String(partnerId)}
          onChange={(v) => setPartnerId(parseInt(v, 10) || 0)}
          options={[
            { id: "0", label: "Svi partneri" },
            ...partners.map((p) => ({ id: String(p.id), label: dh(p.name) })),
          ]}
          icon={Building2}
        />
      </div>
    </div>
  );
}

function ProformaStatusBadge({ status }) {
  const meta = PROFORMA_STATUS_META[status] || { label: status, color: S.muted, bg: S.borderSoft };
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px",
      background: meta.bg, color: meta.color,
      borderRadius: 12, fontSize: 11, fontWeight: 600, fontFamily: S.font,
    }}>
      {meta.label}
    </span>
  );
}

function ProformaTable({ items, onSelect }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 900 }}>
        <thead style={{ background: "#fafafb" }}>
          <tr>
            <th style={th}>Broj</th>
            <th style={th}>Datum</th>
            <th style={th}>Kupac</th>
            <th style={th}>Važi do</th>
            <th style={{ ...th, textAlign: "right" }}>Iznos</th>
            <th style={{ ...th, textAlign: "right" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr
              key={r.id}
              onClick={() => onSelect(r.id)}
              style={{
                borderBottom: `1px solid ${S.borderSoft}`,
                cursor: "pointer", transition: "background 0.1s",
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#fafafb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <td style={{ ...td, fontFamily: S.mono, fontWeight: 600 }}>{r.proforma_number}</td>
              <td style={{ ...td, color: S.muted, whiteSpace: "nowrap" }}>{fmtDate(r.invoice_date)}</td>
              <td style={td}>
                {dh(r.partner_name || r.buyer_name) || <span style={{ color: S.mutedLight }}>—</span>}
                {(r.partner_id ? null : r.buyer_pib) && (
                  <div style={{ fontSize: 11, color: S.mutedLight, fontFamily: S.mono }}>{buyerIdLabel(r.buyer_id_type)}: {r.buyer_pib}</div>
                )}
              </td>
              <td style={{ ...td, color: S.muted, whiteSpace: "nowrap" }}>{r.valid_until ? fmtDate(r.valid_until) : "—"}</td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600, whiteSpace: "nowrap" }}>
                {fmtMoney(r.total_amount, r.currency)}
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                <ProformaStatusBadge status={r.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ProformaCardList({ items, onSelect }) {
  return (
    <div>
      {items.map((r, i) => (
        <div
          key={r.id}
          onClick={() => onSelect(r.id)}
          style={{
            padding: "14px 16px",
            borderBottom: i < items.length - 1 ? `1px solid ${S.borderSoft}` : "none",
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontFamily: S.mono, fontWeight: 700, fontSize: 15, color: S.dark, marginBottom: 3 }}>
                {r.proforma_number}
              </div>
              <div style={{ fontSize: 14, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {dh(r.partner_name || r.buyer_name) || "—"}
              </div>
              <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                {fmtDate(r.invoice_date)}
                {r.valid_until && ` · do ${fmtDate(r.valid_until)}`}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontFamily: S.mono, fontWeight: 700, color: S.dark }}>
                {fmtMoney(r.total_amount, r.currency)}
              </div>
              <div style={{ marginTop: 4 }}>
                <ProformaStatusBadge status={r.status} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PROFORMA DETAIL PANEL
// ════════════════════════════════════════════════════════════════════════════
function ProformaDetailPanel({ id, onClose, isMobile, canWrite, onAfterChange, onAfterDelete, onAfterConvert }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [confirmConvert, setConfirmConvert] = useState(false);
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const r = await api(`/b2b/proforma/${id}`);
    setLoading(false);
    if (r.success) setData(r.data?.proforma);
    else setError(r.message || "Greška");
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const inv = data;

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleDelete = async () => {
    setBusy(true);
    const r = await api(`/b2b/proforma-delete/${id}`, { method: "POST", body: JSON.stringify({}) });
    setBusy(false);
    setConfirmDelete(false);
    if (r.success) onAfterDelete();
    else showToast("error", r.message || "Greška pri brisanju");
  };

  // Converts the proforma into a non-cash invoice (draft). The new invoice
  // shows up in Računi sector ready to be fiscalized — we don't auto-fiscalize
  // here because (a) it mirrors the PHP webapp behavior and (b) the user
  // typically wants to review the draft once more before sending to Poreska.
  const handleConvert = async () => {
    setBusy(true);
    const r = await api(`/b2b/proforma-convert/${id}`, { method: "POST", body: JSON.stringify({}) });
    setBusy(false);
    setConfirmConvert(false);
    if (r.success) {
      const newInvoiceId = r.data?.invoice_id;
      const invoiceNumber = r.data?.invoice_number;
      if (typeof onAfterConvert === "function" && newInvoiceId) {
        onAfterConvert(newInvoiceId, invoiceNumber || "");
      } else {
        // Fallback if parent didn't wire the callback
        onAfterChange?.();
        showToast("success", `Predračun konvertovan u račun ${invoiceNumber || ""}`);
      }
    } else {
      showToast("error", r.message || "Greška pri konverziji");
    }
  };

  // Pre-fill the email input when opening the email modal — use the proforma's
  // buyer_email if available so the user doesn't have to retype it.
  const openEmailModal = () => {
    setEmailTo(inv?.buyer_email || "");
    setConfirmEmail(true);
  };

  const handleSendEmail = async () => {
    if (!emailTo || !/^\S+@\S+\.\S+$/.test(emailTo)) {
      showToast("error", "Unesite ispravnu email adresu");
      return;
    }
    setBusy(true);
    const r = await api(`/b2b/proforma-email/${id}`, {
      method: "POST",
      body: JSON.stringify({ email: emailTo }),
    });
    setBusy(false);
    setConfirmEmail(false);
    if (r.success) {
      showToast("success", `Predračun poslat na ${emailTo}`);
    } else {
      showToast("error", r.message || "Greška pri slanju emaila");
    }
  };

  const panelStyle = isMobile
    ? { position: "fixed", inset: 0, background: S.bg, zIndex: 120, display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease-out" }
    : { position: "fixed", top: 0, right: 0, bottom: 0, width: 720, maxWidth: "85vw", background: S.bg, zIndex: 120, boxShadow: "-12px 0 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" };

  return (
    <>
      {!isMobile && (
        <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 115 }} />
      )}
      <div style={panelStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card, borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted, borderRadius: 6 }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
              {loading ? "Učitavam…" : inv ? `Predračun ${inv.proforma_number}` : "Predračun"}
            </div>
            {inv && <div style={{ fontSize: 12, color: S.muted }}>{fmtDate(inv.invoice_date)}</div>}
          </div>
          {inv && <ProformaStatusBadge status={inv.status} />}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 14 : 24 }}>
          {loading ? (
            <Skeleton lines={10} />
          ) : error ? (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca",
              borderRadius: 10, padding: "14px 18px",
              display: "flex", alignItems: "center", gap: 8, color: S.red,
            }}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : inv ? (
            <ProformaDetailBody inv={inv} isMobile={isMobile} />
          ) : null}
        </div>

        {/* Action bar.

            Removed the "Označi poslat / Prihvaćen / Odbijen" status buttons —
            they were lifecycle-tracking only and most B2B users either convert
            to invoice or delete. The status badge in the body still shows
            current state. The 4 actions that matter:
              - Konvertuj u račun (primary — turn proforma into a draft invoice)
              - PDF (download)
              - Pošalji mail
              - Obriši
         */}
        {!loading && inv && canWrite && inv.status !== "converted" && (
          <div style={{
            padding: isMobile ? "12px 14px calc(12px + env(safe-area-inset-bottom))" : "14px 24px",
            background: S.card, borderTop: `1px solid ${S.border}`,
            display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap",
          }}>
            <button
              onClick={() => setConfirmConvert(true)}
              disabled={busy}
              style={{
                ...S.btnSm(S.green), padding: "10px 14px",
                flex: 1, minWidth: 170, justifyContent: "center",
                fontWeight: 600,
              }}
              title="Konvertuje predračun u račun (status: draft, spreman za fiskalizaciju)"
            >
              <ArrowRight size={14} /> Konvertuj u račun
            </button>

            <button
              onClick={async () => {
                // Uses /b2b/proforma-pdf/:id — streams a PDF with the user's
                // selected template, title "PREDRAČUN" and the proper
                // interni-dokument notice. Auth via the helper (which reads
                // TOKEN_KEY, not the raw "token" string).
                const r = await downloadProformaPdf(id, inv?.proforma_number);
                if (!r.success) showToast("error", r.message || "Greška pri preuzimanju PDF-a");
              }}
              disabled={busy}
              style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 14px" }}
              title="Preuzmi PDF predračuna"
            >
              <FileText size={14} /> PDF
            </button>

            <button
              onClick={openEmailModal}
              disabled={busy}
              style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 14px" }}
              title="Pošalji predračun emailom"
            >
              <Mail size={14} /> Pošalji mail
            </button>

            <button
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
              style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "10px 14px" }}
              title="Obriši predračun"
            >
              <Trash2 size={14} />
            </button>
          </div>
        )}

        {confirmConvert && (
          <div onClick={() => !busy && setConfirmConvert(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              background: S.card, borderRadius: 14, padding: 24, maxWidth: 480, width: "100%",
            }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 8 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: "#dcfce7", display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <ArrowRight size={20} color={S.green} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 17, color: S.dark }}>
                    Konvertovati u račun?
                  </h3>
                  <p style={{ fontSize: 13, color: S.muted, marginTop: 6, marginBottom: 0, lineHeight: 1.5 }}>
                    Predračun <strong>{inv?.proforma_number}</strong> će biti konvertovan u nefiskalizovani račun (draft),
                    sa istim stavkama, kupcem i iznosom. Predračun ostaje u sistemu sa statusom <strong>konvertovan</strong>.
                  </p>
                  <p style={{ fontSize: 12, color: S.mutedLight, marginTop: 8, marginBottom: 0, lineHeight: 1.5 }}>
                    Račun se KREIRA kao draft — fiskalizacija se radi ručno iz sektora Računi.
                  </p>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
                <button onClick={() => setConfirmConvert(false)} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>
                  Otkaži
                </button>
                <button onClick={handleConvert} disabled={busy} style={{ ...S.btnSm(S.green), padding: "10px 18px", fontWeight: 600 }}>
                  {busy ? <Loader2 size={14} className="spin" /> : <ArrowRight size={14} />} Konvertuj
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmEmail && (
          <div onClick={() => !busy && setConfirmEmail(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              background: S.card, borderRadius: 14, padding: 24, maxWidth: 480, width: "100%",
            }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", marginBottom: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: "#dbeafe", display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  <Mail size={20} color={S.blue} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 17, color: S.dark }}>
                    Pošalji predračun emailom
                  </h3>
                  <p style={{ fontSize: 12, color: S.muted, marginTop: 4, marginBottom: 0 }}>
                    Predračun <strong>{inv?.proforma_number}</strong> će biti poslat kao PDF u prilogu.
                  </p>
                </div>
              </div>
              <input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="primalac@example.com"
                autoFocus
                style={{ ...S.input, width: "100%", marginTop: 6 }}
                onKeyDown={(e) => { if (e.key === "Enter" && !busy) handleSendEmail(); }}
              />
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 16 }}>
                <button onClick={() => setConfirmEmail(false)} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>
                  Otkaži
                </button>
                <button onClick={handleSendEmail} disabled={busy} style={{ ...S.btnSm(S.blue), padding: "10px 18px" }}>
                  {busy ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Pošalji
                </button>
              </div>
            </div>
          </div>
        )}

        {confirmDelete && (
          <div onClick={() => !busy && setConfirmDelete(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{
              background: S.card, borderRadius: 14, padding: 24, maxWidth: 420, width: "100%",
            }}>
              <h3 style={{ margin: 0, fontSize: 17, color: S.dark }}>Obrisati predračun?</h3>
              <p style={{ fontSize: 14, color: S.muted, marginTop: 8, marginBottom: 20 }}>
                Ova akcija se ne može poništiti.
              </p>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setConfirmDelete(false)} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>
                  Otkaži
                </button>
                <button onClick={handleDelete} disabled={busy} style={{ ...S.btnSm(S.red), padding: "10px 16px" }}>
                  {busy ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Obriši
                </button>
              </div>
            </div>
          </div>
        )}

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </>
  );
}

function ProformaDetailBody({ inv, isMobile }) {
  const buyerLines = [];
  if (inv.partner_name || inv.buyer_name) {
    buyerLines.push(dh(inv.partner_name || inv.buyer_name));
  }
  const pib = inv.partner_tax_id || inv.buyer_pib;
  if (pib) buyerLines.push(`PIB: ${pib}`);
  const addr = [dh(inv.partner_address || inv.buyer_address || ""), dh(inv.partner_city || inv.buyer_city || "")]
    .filter(Boolean).join(", ");
  if (addr) buyerLines.push(addr);
  if (inv.partner_email || inv.buyer_email) buyerLines.push(dh(inv.partner_email || inv.buyer_email));
  if (inv.partner_phone || inv.buyer_phone) buyerLines.push(dh(inv.partner_phone || inv.buyer_phone));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {inv.status === "converted" && inv.converted_to_invoice_id && (
        <div style={{
          background: S.accentSoft, color: S.accent, padding: "12px 16px",
          borderRadius: 10, fontSize: 14, fontWeight: 500,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <ArrowRight size={16} /> Konvertovan u račun #{inv.converted_to_invoice_id}
        </div>
      )}

      <Card title="Kupac">
        {buyerLines.length > 0 ? (
          <div style={{ fontSize: 14, color: S.dark, lineHeight: 1.7 }}>
            {buyerLines.map((line, i) => (
              <div key={i} style={i === 0 ? { fontWeight: 600 } : { color: S.muted }}>{line}</div>
            ))}
          </div>
        ) : (
          <div style={{ color: S.mutedLight, fontSize: 14 }}>—</div>
        )}
      </Card>

      <Card title="Stavke" subtitle={`${inv.items?.length || 0} stavki`}>
        {!inv.items || inv.items.length === 0 ? (
          <Empty text="Nema stavki" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 480 }}>
              <thead>
                <tr>
                  <th style={th}>Naziv</th>
                  <th style={{ ...th, textAlign: "right" }}>Kol.</th>
                  <th style={{ ...th, textAlign: "right" }}>Cijena</th>
                  <th style={{ ...th, textAlign: "right" }}>PDV</th>
                  <th style={{ ...th, textAlign: "right" }}>Ukupno</th>
                </tr>
              </thead>
              <tbody>
                {inv.items.map((it, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={td}>{dh(it.name)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{it.quantity} {it.unit}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtMoney(it.unit_price)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, color: S.muted }}>{it.vat_rate}%</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>{fmtMoney(it.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card title="Iznosi">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Row label="Osnovica" value={fmtMoney(inv.subtotal, inv.currency)} />
          {inv.discount_amount > 0 && (
            <Row label="Popust" value={`− ${fmtMoney(inv.discount_amount, inv.currency)}`} muted />
          )}
          <Row label="PDV" value={fmtMoney(inv.vat_amount, inv.currency)} />
          <div style={{ height: 1, background: S.border, margin: "4px 0" }} />
          <Row label="Ukupno" value={fmtMoney(inv.total_amount, inv.currency)} bold large />
          {inv.valid_until && <Row label="Važi do" value={fmtDate(inv.valid_until)} muted />}
        </div>
      </Card>

      {(inv.contract_info || inv.notes || inv.terms) && (
        <Card title="Napomene i uslovi">
          {inv.contract_info && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: S.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>Podaci ugovora</div>
              <div style={{ fontSize: 13, color: S.dark, whiteSpace: "pre-wrap" }}>{dh(inv.contract_info)}</div>
            </div>
          )}
          {inv.notes && (
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: S.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>Napomena</div>
              <div style={{ fontSize: 13, color: S.dark, whiteSpace: "pre-wrap" }}>{dh(inv.notes)}</div>
            </div>
          )}
          {inv.terms && (
            <div>
              <div style={{ fontSize: 11, color: S.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>Uslovi plaćanja</div>
              <div style={{ fontSize: 13, color: S.dark, whiteSpace: "pre-wrap" }}>{dh(inv.terms)}</div>
            </div>
          )}
        </Card>
      )}

      <div style={{ height: 16 }} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PROFORMA FORM MODAL (create new)
// ════════════════════════════════════════════════════════════════════════════
function ProformaFormModal({ onClose, onSaved, isMobile, company, partners }) {
  const isPausalac = !!company?.is_pausalac;
  const today = new Date().toISOString().slice(0, 10);
  const validUntilDefault = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 15);
    return d.toISOString().slice(0, 10);
  })();

  const [buyerTab, setBuyerTab] = useState("partner");
  const [partnerId, setPartnerId] = useState(0);
  const [newCompany, setNewCompany] = useState({
    name: "", tax_id: "", address: "", city: "", email: "", phone: "",
  });
  const [individual, setIndividual] = useState({
    subtype: "", name: "", personal_id: "", id_type: "ID",
    country: "", country_manual: "", address: "", city: "", email: "", phone: "",
  });
  const [foreignCompany, setForeignCompany] = useState({
    subtype: "", id_type: "VAT", name: "", tax_id: "",
    country_code: "", country_manual: "",
    address: "", city: "", email: "",
  });

  const defaultVat = isPausalac ? 0 : 21;
  const [items, setItems] = useState([emptyItem(defaultVat)]);

  const [invoiceDate, setInvoiceDate] = useState(today);
  const [validUntil, setValidUntil] = useState(validUntilDefault);
  const [currency, setCurrency] = useState("EUR");
  const [notes, setNotes] = useState("");
  // Podaci ugovora — carried to the invoice on proforma→invoice conversion.
  const [contractInfo, setContractInfo] = useState("");
  const [terms, setTerms] = useState("Plaćanje u roku od 15 dana od datuma izdavanja.");

  const [invDiscType, setInvDiscType] = useState("none");
  const [invDiscValue, setInvDiscValue] = useState(0);

  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  // Compute live totals
  const totals = (() => {
    let itemNet = 0, itemVat = 0;
    for (const it of items) {
      const qty = parseNum(it.quantity);
      const up  = parseNum(it.unit_price);
      const rate = parseNum(it.vat_rate);
      if (qty <= 0) continue;
      const gross = qty * up;
      let disc = 0;
      const dv = parseNum(it.discount_value);
      if (it.discount_type === "percentage") disc = gross * (dv / 100);
      else if (it.discount_type === "fixed") disc = dv;
      const net = Math.max(0, gross - disc);
      itemNet += net;
      itemVat += net * (rate / 100);
    }
    let invDisc = 0;
    if (invDiscType === "percentage") invDisc = itemNet * (parseNum(invDiscValue) / 100);
    else if (invDiscType === "fixed") invDisc = parseNum(invDiscValue);
    invDisc = Math.min(itemNet, Math.max(0, invDisc));
    let net = itemNet - invDisc;
    let vat = itemVat;
    if (itemNet > 0 && invDisc > 0) vat = itemVat * (net / itemNet);
    return {
      itemNet: Math.round(itemNet * 100) / 100,
      invDiscount: Math.round(invDisc * 100) / 100,
      net: Math.round(net * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      total: Math.round((net + vat) * 100) / 100,
    };
  })();

  const validate = () => {
    const e = {};
    if (buyerTab === "partner" && !partnerId) e.partnerId = "Izaberite partnera";
    if (buyerTab === "newCompany" && !newCompany.name.trim()) e.buyerName = "Naziv je obavezan";
    const valid = items.filter((it) => it.name.trim() && parseNum(it.quantity) > 0);
    if (valid.length === 0) e.items = "Dodajte barem jednu stavku";
    if (isPausalac) {
      if (valid.some((it) => parseNum(it.vat_rate) > 0)) e.pausalac = "Pausalna firma — sve stavke moraju biti na 0% PDV";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (status) => {
    if (!validate()) return;
    setBusy(true);

    const validItems = items
      .filter((it) => it.name.trim() && parseNum(it.quantity) > 0)
      .map((it) => ({
        product_id: it.product_id || null,
        name: it.name.trim(),
        quantity: parseNum(it.quantity),
        unit: it.unit || "kom",
        unit_price: parseNum(it.unit_price),
        vat_rate: parseNum(it.vat_rate),
        discount_type: it.discount_type,
        discount_value: parseNum(it.discount_value),
      }));

    const body = {
      partner_id: buyerTab === "partner" ? partnerId : null,
      buyer_name: buyerTab === "newCompany" ? newCompany.name
                : buyerTab === "individual" ? individual.name : "",
      buyer_pib:  buyerTab === "newCompany" ? newCompany.tax_id
                : buyerTab === "individual" ? individual.personal_id : "",
      buyer_address: buyerTab === "newCompany" ? newCompany.address
                   : buyerTab === "individual" ? individual.address : "",
      buyer_city: buyerTab === "newCompany" ? newCompany.city
                : buyerTab === "individual" ? individual.city : "",
      buyer_email: buyerTab === "newCompany" ? newCompany.email
                 : buyerTab === "individual" ? individual.email : "",
      buyer_phone: buyerTab === "newCompany" ? newCompany.phone
                 : buyerTab === "individual" ? individual.phone : "",
      invoice_date: invoiceDate,
      valid_until: validUntil,
      currency,
      items: validItems,
      discount_type: invDiscType,
      discount_value: parseNum(invDiscValue),
      notes,
      contract_info: contractInfo || null,
      terms,
      status,
    };

    const r = await api("/b2b/proforma-store", { method: "POST", body: JSON.stringify(body) });
    setBusy(false);

    if (r.success) {
      onSaved(r.data?.id);
    } else {
      setToast({ type: "error", message: r.message || "Greška pri kreiranju" });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const modalStyle = isMobile
    ? {
        // Mobile: full-screen modal. Using 100dvh (dynamic viewport height)
        // so the modal shrinks when iOS Safari keyboard opens, instead of
        // extending behind it. Also explicit width: 100vw + overflow: hidden
        // to keep it pinned to viewport — without this, any descendant that
        // overflows horizontally would let the user scroll the whole modal
        // sideways (same bug we fixed on NewInvoiceScreen).
        position: "fixed", inset: 0, background: S.bg, zIndex: 140,
        display: "flex", flexDirection: "column",
        animation: "slideUp 0.22s ease-out",
        height: "100dvh", maxHeight: "100dvh",
        width: "100vw", maxWidth: "100vw",
        overflow: "hidden",
      }
    : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 140, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  const contentStyle = isMobile
    ? {
        // Fill mobile modal area. flex:1 lets the inner scroll div fill
        // remaining space below the sticky header. minHeight:0 is critical
        // — without it, flex children with overflow:auto don't actually
        // scroll, they push parent wider/taller. Standard flex+overflow trick.
        background: S.bg, flex: 1, minHeight: 0, minWidth: 0,
        display: "flex", flexDirection: "column",
      }
    : { background: S.card, borderRadius: 14, width: "100%", maxWidth: 880, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" };

  return (
    <div onClick={!isMobile && !busy ? onClose : undefined} style={modalStyle}>
      <div onClick={(e) => e.stopPropagation()} style={contentStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card,
          borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} disabled={busy} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted, borderRadius: 6,
          }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
            Novi predračun
          </div>
        </div>

        <div style={{
          flex: 1, minHeight: 0,
          // overflowY:auto for vertical scroll. overflowX:hidden so any
          // descendant that's still wider than the modal (e.g. a long text
          // line that bypasses our minWidth:0 fixes) gets clipped here
          // instead of pushing the modal sideways. Belt + suspenders with
          // the outer modal's overflow:hidden.
          overflowY: "auto", overflowX: "hidden",
          padding: isMobile ? 14 : 24,
          // WebkitOverflowScrolling for momentum scroll on iOS
          WebkitOverflowScrolling: "touch",
        }}>
          <div style={{
            display: "grid", gap: 16,
            // minmax(0, …) on mobile column so descendants with long content
            // (like the picker button label) don't force the grid wider than
            // the modal — same fix as NewInvoiceScreen. Without this, any
            // unbreakable text inside expands the column → modal width breaks.
            gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "minmax(0, 1.5fr) minmax(300px, 1fr)",
            // Belt + suspenders: ensure grid itself can shrink
            minWidth: 0, width: "100%",
          }}>
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <BuyerSection
                tab={buyerTab} setTab={setBuyerTab}
                partners={partners}
                partnerId={partnerId} setPartnerId={setPartnerId}
                newCompany={newCompany} setNewCompany={setNewCompany}
                saveAsPartner={false} setSaveAsPartner={() => {}}
                individual={individual} setIndividual={setIndividual}
                foreignCompany={foreignCompany} setForeignCompany={setForeignCompany}
                errors={{ partnerId: errors.partnerId, newCompanyName: errors.buyerName }}
                isMobile={isMobile}
              />
              <ItemsEditor
                items={items} setItems={setItems}
                defaultVat={defaultVat} isPausalac={isPausalac}
                errors={{ items: errors.items, pausalacVat: errors.pausalac }}
                isMobile={isMobile}
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <Card title="Detalji">
                <Field label="Datum predračuna">
                  <input
                    type="date" value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                    style={S.input}
                  />
                </Field>
                <div style={{ marginTop: 10 }}>
                  <Field label="Važi do">
                    <input
                      type="date" value={validUntil}
                      onChange={(e) => setValidUntil(e.target.value)}
                      style={S.input}
                    />
                  </Field>
                </div>
                <div style={{ marginTop: 10 }}>
                  <Field label="Valuta">
                    <input value={currency} onChange={(e) => setCurrency(e.target.value)} style={{ ...S.input, fontFamily: S.mono }} />
                  </Field>
                </div>
                <div style={{ marginTop: 10 }}>
                  <Field label="Popust na cijeli predračun">
                    <div style={{ display: "flex", gap: 8 }}>
                      <select
                        value={invDiscType}
                        onChange={(e) => setInvDiscType(e.target.value)}
                        style={{ ...S.input, cursor: "pointer", flex: "1 1 50%" }}
                      >
                        <option value="none">Bez popusta</option>
                        <option value="percentage">Procenat (%)</option>
                        <option value="fixed">Iznos (€)</option>
                      </select>
                      <input
                        type="text" inputMode="decimal"
                        value={invDiscValue}
                        onChange={(e) => setInvDiscValue(e.target.value)}
                        disabled={invDiscType === "none"}
                        style={{ ...S.input, fontFamily: S.mono, flex: "1 1 50%", opacity: invDiscType === "none" ? 0.5 : 1 }}
                      />
                    </div>
                  </Field>
                </div>
                <div style={{ marginTop: 10 }}>
                  <Field label="Podaci ugovora">
                    <input
                      value={contractInfo}
                      onChange={(e) => setContractInfo(e.target.value)}
                      maxLength={500}
                      placeholder="npr. Anex ugovora broj: 06-2026/2 (opciono)"
                      style={{ ...S.input, fontFamily: S.font }}
                    />
                  </Field>
                </div>
                <div style={{ marginTop: 10 }}>
                  <Field label="Napomena">
                    <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} style={{ ...S.input, minHeight: 50, fontFamily: S.font, resize: "vertical" }} />
                  </Field>
                </div>
                <div style={{ marginTop: 10 }}>
                  <Field label="Uslovi plaćanja">
                    <textarea value={terms} onChange={(e) => setTerms(e.target.value)} rows={2} style={{ ...S.input, minHeight: 50, fontFamily: S.font, resize: "vertical" }} />
                  </Field>
                </div>
              </Card>

              <Card title="Iznosi">
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <Row label="Stavke (bruto)" value={fmtMoney(totals.itemNet, currency)} />
                  {totals.invDiscount > 0 && (
                    <Row label="Popust" value={`− ${fmtMoney(totals.invDiscount, currency)}`} muted />
                  )}
                  <Row label="Osnovica" value={fmtMoney(totals.net, currency)} />
                  <Row label="PDV ukupno" value={fmtMoney(totals.vat, currency)} />
                  <div style={{ height: 1, background: S.border, margin: "4px 0" }} />
                  <Row label="Ukupno" value={fmtMoney(totals.total, currency)} bold large />
                </div>
              </Card>

              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <button onClick={() => submit("sent")} disabled={busy} style={{ ...S.btn(S.blue), justifyContent: "center", padding: "14px", fontSize: 16, opacity: busy ? 0.6 : 1 }}>
                  {busy ? <Loader2 size={16} className="spin" /> : <Send size={18} />}
                  {busy ? "Čuvam…" : "Sačuvaj i pošalji"}
                </button>
                <button onClick={() => submit("draft")} disabled={busy} style={{ ...S.btn("white", S.dark), justifyContent: "center", padding: "12px", border: `1px solid ${S.border}`, opacity: busy ? 0.6 : 1 }}>
                  <Clock size={15} /> Sačuvaj kao nacrt
                </button>
              </div>
            </div>
          </div>
        </div>

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// BRZI VIRMAN — fast non-cash invoice creation, POS-style product tiles
// ════════════════════════════════════════════════════════════════════════════
function BrziVirmanScreen({ isMobile, canWrite, company, goToInvoices }) {
  const isPausalac = !!company?.is_pausalac;

  // Config + data
  const [config, setConfig] = useState(null);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  // Filters
  const [productSearch, setProductSearch] = useState("");
  const productSearchDebounced = useDebounced(productSearch, 200);
  const [activeCategory, setActiveCategory] = useState(0); // 0 = all

  // Cart — items[uid] = { product_id, name, unit, vat_rate, unit_price, quantity }
  const [cart, setCart] = useState([]);

  // Buyer
  const [buyerTab, setBuyerTab] = useState("partner");
  const [partnerId, setPartnerId] = useState(0);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [newBuyer, setNewBuyer] = useState({ name: "", tax_id: "", address: "", city: "" });

  // Other
  const [paymentDue, setPaymentDue] = useState("15_dana");
  const [paymentDueDate, setPaymentDueDate] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [successInvoiceId, setSuccessInvoiceId] = useState(null);

  // Load everything in parallel
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const [cfg, prodsR, partsR] = await Promise.all([
        api("/b2b/config"),
        api("/b2b/products?active=active&per_page=200"),
        api("/partners"),
      ]);
      if (!active) return;
      setLoading(false);
      if (cfg.success) setConfig(cfg.data);
      else { setLoadError(cfg.message || "Greška pri učitavanju konfiguracije"); return; }
      if (prodsR.success) {
        setProducts(Array.isArray(prodsR.data?.items) ? prodsR.data.items : []);
        setCategories(Array.isArray(prodsR.data?.categories) ? prodsR.data.categories : []);
      }
      if (partsR.success) {
        const list = Array.isArray(partsR.data) ? partsR.data
                   : (Array.isArray(partsR.data?.partners) ? partsR.data.partners : []);
        setPartners(list);
      }
    })();
    return () => { active = false; };
  }, []);

  const showToast = (type, message, ms = 3500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  // ── Filter visible products ───────────────────────────────────────────
  const visibleProducts = (() => {
    const q = productSearchDebounced.trim().toLowerCase();
    return products.filter((p) => {
      if (!p.is_active) return false;
      if (activeCategory > 0 && p.category_id !== activeCategory) return false;
      if (q) {
        const name = (p.name || "").toLowerCase();
        const sku  = (p.sku || "").toLowerCase();
        const bar  = (p.barcode || "").toLowerCase();
        if (!name.includes(q) && !sku.includes(q) && !bar.includes(q)) return false;
      }
      return true;
    }).slice(0, 200);
  })();

  // ── Cart helpers ──────────────────────────────────────────────────────
  const addProduct = (p) => {
    setCart((prev) => {
      // If same product already in cart, increment qty
      const existing = prev.find((it) => it.product_id === p.id);
      if (existing) {
        return prev.map((it) =>
          it.product_id === p.id ? { ...it, quantity: parseNum(it.quantity) + 1 } : it
        );
      }
      return [
        ...prev,
        {
          uid: Math.random().toString(36).slice(2),
          product_id: p.id,
          name: dh(p.name),
          unit: p.unit || "kom",
          vat_rate: isPausalac ? 0 : parseFloat(p.vat_rate ?? 21),
          unit_price: parseFloat(p.price ?? 0),
          quantity: 1,
        },
      ];
    });
  };

  const updateCartItem = (uid, patch) => {
    setCart((prev) => prev.map((it) => (it.uid === uid ? { ...it, ...patch } : it)));
  };
  const removeCartItem = (uid) => setCart((prev) => prev.filter((it) => it.uid !== uid));
  const incCart = (uid, by = 1) => updateCartItem(uid, {
    quantity: Math.max(0, parseNum(cart.find((it) => it.uid === uid)?.quantity || 0) + by),
  });

  const cartCount = cart.reduce((sum, it) => sum + parseNum(it.quantity), 0);

  // ── Live totals ──────────────────────────────────────────────────────
  const totals = (() => {
    let net = 0, vat = 0;
    for (const it of cart) {
      const q = parseNum(it.quantity);
      const p = parseNum(it.unit_price);
      const r = parseNum(it.vat_rate);
      if (q <= 0) continue;
      const lineNet = q * p;
      net += lineNet;
      vat += lineNet * (r / 100);
    }
    return {
      net: Math.round(net * 100) / 100,
      vat: Math.round(vat * 100) / 100,
      total: Math.round((net + vat) * 100) / 100,
    };
  })();

  // ── Selected partner (resolved object) ────────────────────────────────
  const selectedPartner = partners.find((p) => p.id === partnerId);

  // ── Partner search filtering ──────────────────────────────────────────
  const filteredPartners = (() => {
    if (!partnerSearch) return partners.slice(0, 100);
    const q = partnerSearch.toLowerCase();
    return partners.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.tax_id || "").includes(partnerSearch)
    ).slice(0, 100);
  })();

  // ── Fiskalizuj ────────────────────────────────────────────────────────
  const fiscalize = async () => {
    if (submitting) return;

    // Validation
    if (cart.length === 0) { showToast("error", "Dodajte barem jedan proizvod"); return; }
    if (totals.total <= 0) { showToast("error", "Iznos mora biti veći od nule"); return; }
    if (!config?.default_tcr_code) { showToast("error", "TCR nije konfigurisan"); return; }
    if (!config?.location?.id) { showToast("error", "Lokacija nije pronađena"); return; }
    if (!config?.has_certificate) { showToast("error", "Nedostaje certifikat — fiskalizacija nije moguća"); return; }

    let resolvedPartnerId = null;
    let buyer_name = "", buyer_pib = "", buyer_address = "", buyer_city = "";

    if (buyerTab === "partner") {
      if (!partnerId) { showToast("error", "Izaberite partnera"); return; }
      resolvedPartnerId = partnerId;
    } else {
      if (!newBuyer.name.trim()) { showToast("error", "Naziv kupca je obavezan"); return; }
      buyer_name = newBuyer.name;
      buyer_pib = newBuyer.tax_id;
      buyer_address = newBuyer.address;
      buyer_city = newBuyer.city;
    }

    if (isPausalac && cart.some((it) => parseNum(it.vat_rate) > 0)) {
      showToast("error", "Pausalna firma — sve stavke moraju biti na 0% PDV");
      return;
    }

    setSubmitting(true);

    const validItems = cart
      .filter((it) => it.name.trim() && parseNum(it.quantity) > 0 && parseNum(it.unit_price) >= 0)
      .map((it) => ({
        name: it.name.trim(),
        product_id: it.product_id || null,
        quantity: parseNum(it.quantity),
        unit_price: parseNum(it.unit_price),
        vat_rate: parseNum(it.vat_rate),
        discount_type: "none",
        discount_value: 0,
        unit: it.unit || "kom",
      }));

    const body = {
      location_id: config.location.id,
      tcr_code: config.default_tcr_code,
      invoice_type: "non_cash",
      type_of_inv: "NONCASH",
      payment_method: "bank_transfer",
      currency: "EUR",
      invoice_date: new Date().toISOString().slice(0, 10),
      items: validItems,
      discount_type: "none",
      discount_value: 0,
      payment_due: paymentDue === "custom" && paymentDueDate ? paymentDueDate : paymentDue,
    };
    if (resolvedPartnerId) body.partner_id = resolvedPartnerId;
    else {
      body.buyer_name = buyer_name;
      body.buyer_pib = buyer_pib;
      body.buyer_address = buyer_address;
      body.buyer_city = buyer_city;
    }

    // 1) Create
    // Brzi Virman uses registered partners only (all MNE TIN companies),
    // but we still route through /b2b/invoice-store for Option A consistency:
    // one B2B code path for ALL invoice creation. The backend reads
    // partner.id_type (defaults TIN for legacy partners) and partner.country_code
    // (NULL → falls back to MNE), so existing partners work unchanged.
    const r = await api("/b2b/invoice-store", { method: "POST", body: JSON.stringify(body) });
    if (!r.success) {
      setSubmitting(false);
      showToast("error", r.message || "Greška pri kreiranju računa", 5000);
      return;
    }
    const invId = r.data?.invoice?.id || r.data?.id || r.data?.invoice_id;
    if (!invId) {
      setSubmitting(false);
      showToast("error", "Račun kreiran ali ID nije vraćen", 4000);
      return;
    }

    // 2) Fiscalize
    const f = await api(`/invoices/fiscalize/${invId}`, { method: "POST" });
    setSubmitting(false);

    if (f.success) {
      setSuccessInvoiceId(invId);
      showToast("success", "Račun fiskalizovan", 4500);
    } else {
      setSuccessInvoiceId(invId);
      showToast("error", "Kreiran, ali fiskalizacija neuspjela: " + (f.message || "?"), 6000);
    }
  };

  const reset = () => {
    setCart([]);
    setBuyerTab("partner");
    setPartnerId(0);
    setNewBuyer({ name: "", tax_id: "", address: "", city: "" });
    setSuccessInvoiceId(null);
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (!canWrite) {
    return (
      <>
        <TopHeader title="Brzi virman" isMobile={isMobile} />
        <div style={{ padding: isMobile ? 16 : 28 }}>
          <div style={{
            background: S.card, borderRadius: 14, padding: 32, textAlign: "center",
            border: `1px solid ${S.border}`,
          }}>
            <div style={{
              display: "inline-flex", width: 56, height: 56, borderRadius: 14,
              background: "#fef2f2", alignItems: "center", justifyContent: "center",
              marginBottom: 12,
            }}>
              <AlertCircle size={26} color={S.red} />
            </div>
            <h2 style={{ margin: 0, fontSize: 18, color: S.dark }}>Pristup je samo za čitanje</h2>
            <p style={{ color: S.muted, fontSize: 14, marginTop: 8 }}>
              Vaša uloga (viewer) nema dozvolu za kreiranje računa.
            </p>
          </div>
        </div>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <TopHeader title="Brzi virman" isMobile={isMobile} />
        <div style={{ padding: isMobile ? 16 : 28 }}>
          <Skeleton lines={10} />
        </div>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <TopHeader title="Brzi virman" isMobile={isMobile} />
        <div style={{ padding: isMobile ? 16 : 28 }}>
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
            padding: "14px 18px", display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <AlertCircle size={16} /> {loadError}
          </div>
        </div>
      </>
    );
  }

  if (successInvoiceId) {
    return (
      <>
        <TopHeader title="Brzi virman" isMobile={isMobile} />
        <div style={{ padding: isMobile ? 16 : 28, maxWidth: 520, margin: "0 auto" }}>
          <div style={{
            background: S.card, borderRadius: 14, padding: 32, textAlign: "center",
            border: `1px solid ${S.border}`,
          }}>
            <div style={{
              display: "inline-flex", width: 64, height: 64, borderRadius: 16,
              background: "#dcfce7", alignItems: "center", justifyContent: "center",
              marginBottom: 14,
            }}>
              <CircleCheck size={32} color={S.green} />
            </div>
            <h2 style={{ margin: 0, fontSize: 20, color: S.dark }}>Račun je fiskalizovan</h2>
            <p style={{ color: S.muted, fontSize: 14, marginTop: 8, marginBottom: 24 }}>
              ID: #{successInvoiceId}
            </p>
            <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 10, justifyContent: "center" }}>
              <button
                onClick={async () => { await downloadInvoicePdf(successInvoiceId); }}
                style={{ ...S.btn(S.dark), justifyContent: "center" }}
              >
                <Download size={16} /> Preuzmi PDF
              </button>
              <button onClick={goToInvoices} style={{ ...S.btn("white", S.dark), border: `1px solid ${S.border}`, justifyContent: "center" }}>
                <Receipt size={16} /> Računi
              </button>
              <button onClick={reset} style={{ ...S.btn(S.accent), justifyContent: "center" }}>
                <Zap size={16} /> Novi virman
              </button>
            </div>
          </div>
          {isMobile && <div style={{ height: 80 }} />}
        </div>
      </>
    );
  }

  const noCertWarning = !config?.has_certificate;

  return (
    <>
      <TopHeader
        title="Brzi virman"
        subtitle={`${fmtNum(cartCount)} u korpi · ${fmtMoney(totals.total)}`}
        isMobile={isMobile}
        right={
          !isMobile && (
            <button
              onClick={fiscalize}
              disabled={submitting || cart.length === 0 || noCertWarning}
              style={{
                ...S.btnSm(S.green, "white"),
                padding: "10px 18px", fontSize: 14,
                opacity: (submitting || cart.length === 0 || noCertWarning) ? 0.5 : 1,
              }}
            >
              {submitting ? <Loader2 size={14} className="spin" /> : <Zap size={14} />}
              {submitting ? "Fiskalizujem…" : "Fiskalizuj"}
            </button>
          )
        }
      />

      {noCertWarning && (
        <div style={{
          margin: isMobile ? "12px 12px 0" : "16px 28px 0",
          background: "#fef3c7", border: "1px solid #fde68a", color: S.amber,
          borderRadius: 10, padding: "12px 16px",
          display: "flex", alignItems: "center", gap: 8, fontSize: 14,
        }}>
          <AlertCircle size={16} /> Nemate aktivan certifikat — fiskalizacija neće raditi
        </div>
      )}

      <div style={{
        padding: isMobile ? 12 : 28,
        display: "grid", gap: 14,
        gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 2fr) minmax(320px, 1fr)",
        maxWidth: 1600,
      }}>
        {/* LEFT — Product browser */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, minWidth: 0 }}>
          {/* Search + category chips */}
          <div style={{
            background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
            padding: 12,
          }}>
            <div style={{ position: "relative", marginBottom: 10 }}>
              <Search size={16} color={S.mutedLight} style={{
                position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
              }} />
              <input
                value={productSearch}
                onChange={(e) => setProductSearch(e.target.value)}
                placeholder="Pretraži proizvode (naziv, SKU, barkod)…"
                style={{ ...S.input, paddingLeft: 36 }}
              />
              {productSearch && (
                <button onClick={() => setProductSearch("")} style={{
                  position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
                  background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted,
                }}>
                  <X size={14} />
                </button>
              )}
            </div>
            {categories.length > 0 && (
              <div style={{ display: "flex", gap: 6, overflowX: "auto", paddingBottom: 4 }} className="hide-scrollbar">
                <CategoryChip
                  active={activeCategory === 0}
                  onClick={() => setActiveCategory(0)}
                  label="Sve"
                />
                {categories.map((c) => (
                  <CategoryChip
                    key={c.id}
                    active={activeCategory === c.id}
                    onClick={() => setActiveCategory(c.id)}
                    label={dh(c.name)}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Product grid */}
          <div style={{
            background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
            padding: 12, minHeight: 200,
          }}>
            {visibleProducts.length === 0 ? (
              <div style={{ padding: 40, textAlign: "center", color: S.mutedLight }}>
                <Package size={32} color={S.mutedLight} />
                <div style={{ marginTop: 10, fontSize: 14 }}>
                  {products.length === 0 ? "Nema proizvoda — dodajte ih u Proizvodi sekciji" : "Nema rezultata"}
                </div>
              </div>
            ) : (
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile
                  ? "repeat(auto-fill, minmax(140px, 1fr))"
                  : "repeat(auto-fill, minmax(160px, 1fr))",
                gap: 8,
              }}>
                {visibleProducts.map((p) => {
                  const inCart = cart.find((it) => it.product_id === p.id);
                  const cartQty = inCart ? parseNum(inCart.quantity) : 0;
                  return (
                    <ProductTile
                      key={p.id}
                      product={p}
                      cartQty={cartQty}
                      onClick={() => addProduct(p)}
                    />
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT — Cart + partner + totals */}
        <div style={{
          display: "flex", flexDirection: "column", gap: 12, minWidth: 0,
        }}>
          <Card title="Korpa" subtitle={cart.length === 0 ? "Prazna" : `${cart.length} stavki`}>
            {cart.length === 0 ? (
              <div style={{ padding: "16px 4px", textAlign: "center", color: S.mutedLight, fontSize: 13 }}>
                Klikom na proizvod ga dodajete u korpu
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {cart.map((it) => (
                  <CartLine
                    key={it.uid}
                    item={it}
                    onInc={() => incCart(it.uid, 1)}
                    onDec={() => incCart(it.uid, -1)}
                    onQtyChange={(v) => updateCartItem(it.uid, { quantity: v })}
                    onPriceChange={(v) => updateCartItem(it.uid, { unit_price: v })}
                    onRemove={() => removeCartItem(it.uid)}
                  />
                ))}
              </div>
            )}
          </Card>

          {/* Partner picker */}
          <Card title="Kupac">
            <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
              <TabBtn active={buyerTab === "partner"} onClick={() => setBuyerTab("partner")} label="Partner" />
              <TabBtn active={buyerTab === "newCompany"} onClick={() => setBuyerTab("newCompany")} label="Ručni unos" />
            </div>

            {buyerTab === "partner" ? (
              <div>
                {/* Search always visible */}
                <div style={{ position: "relative", marginBottom: 8 }}>
                  <Search size={14} color={S.mutedLight} style={{
                    position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
                  }} />
                  <input
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.target.value)}
                    placeholder="Pretraži partnere…"
                    style={{ ...S.input, paddingLeft: 32, paddingTop: 8, paddingBottom: 8, fontSize: 13 }}
                  />
                  {partnerSearch && (
                    <button
                      onClick={() => setPartnerSearch("")}
                      style={{
                        position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                        background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted,
                      }}
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>

                {/* Always-visible scrollable partner list */}
                <div style={{
                  maxHeight: 280, overflowY: "auto",
                  border: `1px solid ${S.border}`, borderRadius: 8,
                  background: "#fafafb",
                }}>
                  {filteredPartners.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: S.mutedLight, fontSize: 13 }}>
                      {partners.length === 0 ? "Nema partnera" : "Nema rezultata"}
                    </div>
                  ) : (
                    filteredPartners.map((p, i) => {
                      const selected = p.id === partnerId;
                      return (
                        <button
                          key={p.id}
                          onClick={() => setPartnerId(selected ? 0 : p.id)}
                          style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 12px",
                            background: selected ? S.accentSoft : "white",
                            border: "none",
                            borderBottom: i < filteredPartners.length - 1 ? `1px solid ${S.borderSoft}` : "none",
                            cursor: "pointer", textAlign: "left", fontFamily: S.font,
                            transition: "background 0.1s",
                          }}
                          onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "#f3f4f6"; }}
                          onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "white"; }}
                        >
                          <div style={{
                            width: 28, height: 28, borderRadius: 7,
                            background: selected ? S.accent : S.borderSoft,
                            color: selected ? "white" : S.muted,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}>
                            <Building2 size={14} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontWeight: 600, fontSize: 13, color: S.dark,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {dh(p.name)}
                            </div>
                            <div style={{ fontSize: 11, color: S.muted, fontFamily: S.mono }}>
                              {p.tax_id ? `PIB: ${p.tax_id}` : "Bez PIB-a"}
                              {p.city && ` · ${dh(p.city)}`}
                            </div>
                          </div>
                          {selected && (
                            <CircleCheck size={16} color={S.accent} style={{ flexShrink: 0 }} />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>

                {selectedPartner && (
                  <div style={{
                    marginTop: 8, padding: "8px 10px",
                    background: S.accentSoft, borderRadius: 6,
                    fontSize: 12, color: S.accent, fontWeight: 600,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    <CircleCheck size={14} />
                    Izabran: {dh(selectedPartner.name)}
                  </div>
                )}
              </div>
            ) : (
              <div>
                <Field label="Naziv firme" required>
                  <input
                    value={newBuyer.name}
                    onChange={(e) => setNewBuyer({ ...newBuyer, name: e.target.value })}
                    placeholder="Bisha DOO"
                    style={S.input}
                  />
                </Field>
                <div style={{ marginTop: 8 }}>
                  <Field label="PIB">
                    <input
                      value={newBuyer.tax_id}
                      onChange={(e) => setNewBuyer({ ...newBuyer, tax_id: e.target.value.replace(/\D/g, "").slice(0, 13) })}
                      placeholder="8 cifara"
                      inputMode="numeric"
                      style={{ ...S.input, fontFamily: S.mono }}
                    />
                  </Field>
                </div>
                <div style={{ display: "grid", gap: 8, gridTemplateColumns: "1fr 1fr", marginTop: 8 }}>
                  <Field label="Adresa">
                    <input value={newBuyer.address} onChange={(e) => setNewBuyer({ ...newBuyer, address: e.target.value })} style={S.input} />
                  </Field>
                  <Field label="Grad">
                    <input value={newBuyer.city} onChange={(e) => setNewBuyer({ ...newBuyer, city: e.target.value })} style={S.input} />
                  </Field>
                </div>
              </div>
            )}
          </Card>

          {/* Quick options */}
          <Card title="Rok plaćanja">
            <Select
              value={paymentDue}
              onChange={setPaymentDue}
              options={PAYMENT_DUE_PRESETS}
              icon={Calendar}
            />
            {paymentDue === "custom" && (
              <div style={{ marginTop: 8 }}>
                <input
                  type="date"
                  value={paymentDueDate}
                  onChange={(e) => setPaymentDueDate(e.target.value)}
                  style={S.input}
                />
              </div>
            )}
          </Card>

          {/* Totals */}
          <Card title="Iznos">
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <Row label="Osnovica" value={fmtMoney(totals.net)} />
              <Row label="PDV" value={fmtMoney(totals.vat)} />
              <div style={{ height: 1, background: S.border, margin: "2px 0" }} />
              <Row label="Ukupno" value={fmtMoney(totals.total)} bold large />
            </div>
          </Card>

          {/* Fiscalize button — sticky on mobile */}
          <button
            onClick={fiscalize}
            disabled={submitting || cart.length === 0 || noCertWarning}
            style={{
              ...S.btn(S.green),
              justifyContent: "center",
              padding: "16px",
              fontSize: 17,
              opacity: (submitting || cart.length === 0 || noCertWarning) ? 0.5 : 1,
              position: isMobile ? "sticky" : "static",
              bottom: isMobile ? 90 : "auto",
              zIndex: 5,
              boxShadow: isMobile ? "0 4px 20px rgba(0,0,0,0.15)" : "none",
            }}
          >
            {submitting ? <Loader2 size={18} className="spin" /> : <Zap size={20} />}
            {submitting ? "Fiskalizujem…" : "Fiskalizuj"}
          </button>
        </div>

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// ── ProductTile (POS-style click-to-add card) ─────────────────────────────
function ProductTile({ product, cartQty, onClick }) {
  const priceGross = parseFloat(product.price || 0) * (1 + parseFloat(product.vat_rate || 0) / 100);
  return (
    <button
      onClick={onClick}
      style={{
        position: "relative", textAlign: "left",
        background: cartQty > 0 ? S.accentSoft : "white",
        border: `1px solid ${cartQty > 0 ? S.accent : S.border}`,
        borderRadius: 10, padding: "10px 12px", cursor: "pointer",
        display: "flex", flexDirection: "column", gap: 4,
        fontFamily: S.font, transition: "all 0.1s",
      }}
      onMouseEnter={(e) => { if (cartQty === 0) e.currentTarget.style.borderColor = S.accent; }}
      onMouseLeave={(e) => { if (cartQty === 0) e.currentTarget.style.borderColor = S.border; }}
    >
      <div style={{
        fontSize: 13, fontWeight: 600, color: S.dark, lineHeight: 1.25,
        overflow: "hidden", textOverflow: "ellipsis",
        display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical",
        minHeight: 32,
      }}>
        {dh(product.name)}
      </div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: "auto" }}>
        <span style={{ fontSize: 14, fontFamily: S.mono, fontWeight: 700, color: S.dark }}>
          {fmtMoney(priceGross)}
        </span>
        <span style={{ fontSize: 10, color: S.muted, fontFamily: S.mono }}>
          {product.vat_rate}%
        </span>
      </div>
      {cartQty > 0 && (
        <span style={{
          position: "absolute", top: 6, right: 6,
          background: S.accent, color: "white",
          padding: "2px 7px", borderRadius: 10, fontSize: 11, fontWeight: 700,
          fontFamily: S.mono, minWidth: 22, textAlign: "center",
        }}>
          {fmtNum(cartQty)}
        </span>
      )}
    </button>
  );
}

// ── CartLine ──────────────────────────────────────────────────────────────
function CartLine({ item, onInc, onDec, onQtyChange, onPriceChange, onRemove }) {
  const q = parseNum(item.quantity);
  const p = parseNum(item.unit_price);
  const lineTotal = q * p * (1 + parseNum(item.vat_rate) / 100);

  return (
    <div style={{
      padding: 8, border: `1px solid ${S.borderSoft}`, borderRadius: 8,
      background: "#fafafb",
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 6, marginBottom: 6 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, color: S.dark, overflow: "hidden", textOverflow: "ellipsis" }}>
          {dh(item.name)}
        </div>
        <button onClick={onRemove} style={{ background: "none", border: "none", cursor: "pointer", padding: 2, color: S.red }}>
          <X size={14} />
        </button>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <button onClick={onDec} style={qtyBtn}>−</button>
        <input
          type="text" inputMode="decimal"
          value={item.quantity}
          onChange={(e) => onQtyChange(e.target.value)}
          style={{
            ...S.input, width: 50, padding: "5px 6px", textAlign: "center",
            fontFamily: S.mono, fontSize: 13,
          }}
        />
        <button onClick={onInc} style={qtyBtn}>+</button>
        <span style={{ fontSize: 11, color: S.mutedLight }}>×</span>
        <input
          type="text" inputMode="decimal"
          value={item.unit_price}
          onChange={(e) => onPriceChange(e.target.value)}
          style={{
            ...S.input, flex: 1, padding: "5px 8px", fontFamily: S.mono, fontSize: 12,
          }}
        />
        <span style={{
          fontFamily: S.mono, fontSize: 12, fontWeight: 700, color: S.dark,
          whiteSpace: "nowrap", marginLeft: 4,
        }}>
          {fmtMoney(lineTotal)}
        </span>
      </div>
    </div>
  );
}

const qtyBtn = {
  width: 28, height: 28, border: `1px solid ${S.border}`, background: "white",
  borderRadius: 6, cursor: "pointer", fontWeight: 700, color: S.dark,
  fontSize: 16, padding: 0, flexShrink: 0,
};

function CategoryChip({ active, onClick, label }) {
  return (
    <button onClick={onClick} style={{
      padding: "6px 14px", border: `1px solid ${active ? S.accent : S.border}`,
      background: active ? S.accent : "white", color: active ? "white" : S.muted,
      borderRadius: 18, fontSize: 12, fontWeight: 600, cursor: "pointer",
      whiteSpace: "nowrap", fontFamily: S.font, flexShrink: 0,
    }}>
      {label}
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// PAYMENTS SCREEN (Uplate)
// ════════════════════════════════════════════════════════════════════════════
const PAYMENT_METHOD_OPTIONS = [
  { id: "bank_transfer", label: "Virman" },
  { id: "cash",          label: "Gotovina" },
  { id: "card",          label: "Kartica" },
  { id: "compensation",  label: "Kompenzacija" },
  { id: "other",         label: "Drugo" },
];
const PAYMENT_METHOD_LABEL = {
  bank_transfer: "Virman",
  cash:          "Gotovina",
  card:          "Kartica",
  compensation:  "Kompenzacija",
  other:         "Drugo",
};
const PAYMENT_STATUS_OPTIONS = [
  { id: "all",         label: "Svi statusi" },
  { id: "unallocated", label: "Nealocirane" },
  { id: "partial",     label: "Djelimično" },
  { id: "allocated",   label: "Alocirane" },
];
const PAYMENT_STATUS_META = {
  unallocated: { label: "Nealocirana",   color: "#d97706", bg: "#fef3c7" },
  partial:     { label: "Djelimično",    color: "#0284c7", bg: "#dbeafe" },
  allocated:   { label: "Alocirana",     color: "#059669", bg: "#dcfce7" },
};

function PaymentsScreen({ isMobile, canWrite }) {
  // Filters
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);
  const [status, setStatus] = useState("all");
  const [partnerId, setPartnerId] = useState(0);
  const [datePreset, setPreset] = useState("month");
  const [partners, setPartners] = useState([]);

  const [page, setPage] = useState(1);
  const perPage = isMobile ? 25 : 50;

  const [data, setData] = useState({ items: [], total: 0, total_pages: 1, summary: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const r = await api("/partners");
      if (!active) return;
      if (r.success) {
        const list = Array.isArray(r.data) ? r.data
                   : (Array.isArray(r.data?.partners) ? r.data.partners : []);
        setPartners(list);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => { setPage(1); }, [search, status, partnerId, datePreset]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const { from, to } = resolvePresetRange(datePreset);
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (status !== "all") qs.set("status", status);
    if (partnerId > 0) qs.set("partner_id", String(partnerId));
    if (from) qs.set("date_from", from);
    if (to)   qs.set("date_to",   to);
    qs.set("sort", "date");
    qs.set("direction", "desc");
    qs.set("page", String(page));
    qs.set("per_page", String(perPage));
    const r = await api(`/b2b/payments?${qs.toString()}`);
    setLoading(false);
    if (r.success) setData(r.data);
    else setError(r.message || "Greška pri učitavanju");
  }, [search, status, partnerId, datePreset, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const items = Array.isArray(data.items) ? data.items : [];
  const summary = data.summary || {};

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const filterPanel = (
    <PaymentsFilterPanel
      searchInput={searchInput} setSearchInput={setSearchInput}
      status={status} setStatus={setStatus}
      datePreset={datePreset} setPreset={setPreset}
      partnerId={partnerId} setPartnerId={setPartnerId}
      partners={partners}
      isMobile={isMobile}
    />
  );

  return (
    <>
      <TopHeader
        title="Uplate"
        subtitle={loading ? "Učitavam…" : `${fmtNum(summary.total_count || 0)} uplata · ${fmtMoney(summary.total_amount || 0)}`}
        isMobile={isMobile}
        right={
          isMobile ? (
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setFilterOpen(true)} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "8px 10px" }}>
                <FilterIcon size={14} />
              </button>
              {canWrite && (
                <button onClick={() => setShowForm(true)} style={{ ...S.btnSm(S.dark, "white"), padding: "8px 12px" }}>
                  <Plus size={14} />
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={load} disabled={loading} style={{ ...S.btnSm("white", S.muted), border: `1px solid ${S.border}`, padding: "8px 10px" }}>
                <RefreshCw size={14} className={loading ? "spin" : ""} />
                {loading ? "Učitavam" : "Osvježi"}
              </button>
              {canWrite && (
                <button onClick={() => setShowForm(true)} style={{ ...S.btnSm(S.dark, "white"), padding: "8px 14px" }}>
                  <Plus size={14} /> Nova uplata
                </button>
              )}
            </div>
          )
        }
      />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1600 }}>
        {!isMobile && <div style={{ marginBottom: 16 }}>{filterPanel}</div>}

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
            padding: "14px 18px", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {/* Summary cards */}
        {!loading && (
          <div style={{
            display: "grid", gap: 10, marginBottom: 14,
            gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(180px, 1fr))",
          }}>
            <SummaryCard icon={CreditCard} color={S.accent} label="Uplata" value={fmtNum(summary.total_count || 0)} />
            <SummaryCard icon={Euro}       color={S.dark}   label="Ukupno"  value={fmtMoney(summary.total_amount || 0)} />
            <SummaryCard icon={CircleCheck} color={S.green} label="Alocirano"   value={fmtMoney(summary.total_allocated || 0)} />
            <SummaryCard icon={AlertCircle} color={S.orange} label="Nealocirano" value={fmtMoney(summary.total_unallocated || 0)} />
          </div>
        )}

        <div style={{
          background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
          overflow: "hidden",
        }}>
          {loading && items.length === 0 ? (
            <div style={{ padding: 24 }}><Skeleton lines={6} /></div>
          ) : items.length === 0 ? (
            <div style={{ padding: 60, textAlign: "center", color: S.mutedLight }}>
              <CreditCard size={36} color={S.mutedLight} />
              <div style={{ marginTop: 12, fontSize: 14 }}>
                {search ? "Nema rezultata" : "Još nemate uplata"}
              </div>
              {canWrite && !search && (
                <button onClick={() => setShowForm(true)} style={{ ...S.btn(S.accent), marginTop: 14 }}>
                  <Plus size={16} /> Evidentiraj prvu uplatu
                </button>
              )}
            </div>
          ) : isMobile ? (
            <PaymentCardList items={items} onSelect={setSelectedId} />
          ) : (
            <PaymentTable items={items} onSelect={setSelectedId} />
          )}
        </div>

        {data.total_pages > 1 && (
          <Pagination page={page} totalPages={data.total_pages} onChange={setPage} isMobile={isMobile} />
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {/* Mobile filter sheet */}
      {isMobile && filterOpen && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 110, display: "flex", alignItems: "flex-end" }}
          onClick={() => setFilterOpen(false)}
        >
          <div onClick={(e) => e.stopPropagation()} className="slide-up" style={{
            width: "100%", background: S.card, borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "85vh", overflowY: "auto"
          }}>
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px" }}>
              <div style={{ width: 40, height: 4, background: S.border, borderRadius: 2 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px 12px" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: S.dark }}>Filteri</div>
              <button onClick={() => setFilterOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: "0 16px 24px" }}>
              {filterPanel}
              <button onClick={() => setFilterOpen(false)} style={{ ...S.btn(S.dark), width: "100%", marginTop: 18 }}>Primijeni</button>
            </div>
          </div>
        </div>
      )}

      {selectedId !== null && (
        <PaymentDetailPanel
          id={selectedId}
          onClose={() => setSelectedId(null)}
          isMobile={isMobile}
          canWrite={canWrite}
          onAfterChange={() => load()}
          onAfterDelete={() => { setSelectedId(null); load(); showToast("success", "Uplata obrisana"); }}
        />
      )}

      {showForm && (
        <PaymentFormModal
          onClose={() => setShowForm(false)}
          onSaved={(id) => {
            setShowForm(false);
            load();
            showToast("success", "Uplata kreirana");
            setSelectedId(id);
          }}
          isMobile={isMobile}
          partners={partners}
        />
      )}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

function PaymentsFilterPanel({
  searchInput, setSearchInput,
  status, setStatus,
  datePreset, setPreset,
  partnerId, setPartnerId, partners,
  isMobile,
}) {
  return (
    <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: isMobile ? 12 : 14 }}>
      <div style={{ position: "relative", marginBottom: 12 }}>
        <Search size={16} color={S.mutedLight} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input
          value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Pretraži broj izvoda, referencu, partnera…"
          style={{ ...S.input, paddingLeft: 36 }}
        />
        {searchInput && (
          <button onClick={() => setSearchInput("")} style={{
            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted,
          }}>
            <X size={14} />
          </button>
        )}
      </div>
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr" }}>
        <Select value={status} onChange={setStatus} options={PAYMENT_STATUS_OPTIONS} icon={CircleCheck} />
        <Select value={datePreset} onChange={setPreset} options={DATE_RANGES} icon={Calendar} />
        <Select
          value={String(partnerId)}
          onChange={(v) => setPartnerId(parseInt(v, 10) || 0)}
          options={[
            { id: "0", label: "Svi partneri" },
            ...partners.map((p) => ({ id: String(p.id), label: dh(p.name) })),
          ]}
          icon={Building2}
        />
      </div>
    </div>
  );
}

function PaymentAllocStatusBadge({ status }) {
  const meta = PAYMENT_STATUS_META[status];
  if (!meta) return null;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px",
      background: meta.bg, color: meta.color,
      borderRadius: 12, fontSize: 11, fontWeight: 600, fontFamily: S.font,
      whiteSpace: "nowrap",
    }}>{meta.label}</span>
  );
}

function PaymentTable({ items, onSelect }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 900 }}>
        <thead style={{ background: "#fafafb" }}>
          <tr>
            <th style={th}>Datum</th>
            <th style={th}>Partner</th>
            <th style={{ ...th, textAlign: "right" }}>Iznos</th>
            <th style={{ ...th, textAlign: "right" }}>Alocirano</th>
            <th style={{ ...th, textAlign: "right" }}>Preostalo</th>
            <th style={th}>Način</th>
            <th style={th}>Referenca</th>
            <th style={{ ...th, textAlign: "right" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr
              key={r.id}
              onClick={() => onSelect(r.id)}
              style={{ borderBottom: `1px solid ${S.borderSoft}`, cursor: "pointer" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#fafafb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <td style={{ ...td, color: S.muted, whiteSpace: "nowrap" }}>{fmtDate(r.payment_date)}</td>
              <td style={{ ...td, fontWeight: 600 }}>{dh(r.partner_name) || <span style={{ color: S.mutedLight }}>—</span>}</td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700, whiteSpace: "nowrap" }}>
                {fmtMoney(r.amount, r.currency)}
              </td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, color: S.muted, whiteSpace: "nowrap" }}>
                {fmtMoney(r.allocated_total)}
              </td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600, color: r.remaining > 0 ? S.orange : S.mutedLight, whiteSpace: "nowrap" }}>
                {r.remaining > 0 ? fmtMoney(r.remaining) : "—"}
              </td>
              <td style={{ ...td, color: S.muted, fontSize: 12 }}>{PAYMENT_METHOD_LABEL[r.payment_method] || r.payment_method}</td>
              <td style={{ ...td, fontFamily: S.mono, fontSize: 12, color: S.muted }}>
                {r.reference || r.bank_izvod_no || <span style={{ color: S.mutedLight }}>—</span>}
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                <PaymentAllocStatusBadge status={r.allocation_status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function PaymentCardList({ items, onSelect }) {
  return (
    <div>
      {items.map((r, i) => (
        <div
          key={r.id}
          onClick={() => onSelect(r.id)}
          style={{
            padding: "14px 16px",
            borderBottom: i < items.length - 1 ? `1px solid ${S.borderSoft}` : "none",
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {dh(r.partner_name) || "—"}
              </div>
              <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                {fmtDate(r.payment_date)} · {PAYMENT_METHOD_LABEL[r.payment_method] || "—"}
              </div>
              {(r.reference || r.bank_izvod_no) && (
                <div style={{ fontSize: 11, color: S.mutedLight, fontFamily: S.mono, marginTop: 2 }}>
                  {r.reference || r.bank_izvod_no}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontFamily: S.mono, fontWeight: 700, color: S.dark }}>
                {fmtMoney(r.amount, r.currency)}
              </div>
              <div style={{ marginTop: 4 }}>
                <PaymentAllocStatusBadge status={r.allocation_status} />
              </div>
              {r.remaining > 0 && (
                <div style={{ fontSize: 10, fontFamily: S.mono, color: S.orange, marginTop: 2 }}>
                  Preostalo: {fmtMoney(r.remaining)}
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PAYMENT DETAIL PANEL
// ════════════════════════════════════════════════════════════════════════════
function PaymentDetailPanel({ id, onClose, isMobile, canWrite, onAfterChange, onAfterDelete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAllocate, setShowAllocate] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const r = await api(`/b2b/payment/${id}`);
    setLoading(false);
    if (r.success) setData(r.data?.payment);
    else setError(r.message || "Greška");
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleDelete = async () => {
    setBusy(true);
    const r = await api(`/b2b/payment-delete/${id}`, { method: "POST", body: JSON.stringify({}) });
    setBusy(false);
    setConfirmDelete(false);
    if (r.success) onAfterDelete();
    else showToast("error", r.message || "Greška pri brisanju");
  };

  const panelStyle = isMobile
    ? { position: "fixed", inset: 0, background: S.bg, zIndex: 120, display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease-out" }
    : { position: "fixed", top: 0, right: 0, bottom: 0, width: 720, maxWidth: "85vw", background: S.bg, zIndex: 120, boxShadow: "-12px 0 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" };

  const p = data;

  return (
    <>
      {!isMobile && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 115 }} />}
      <div style={panelStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card, borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted, borderRadius: 6,
          }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
              {loading ? "Učitavam…" : p ? fmtMoney(p.amount, p.currency) : "Uplata"}
            </div>
            {p && (
              <div style={{ fontSize: 12, color: S.muted }}>
                {dh(p.partner_name)} · {fmtDate(p.payment_date)}
              </div>
            )}
          </div>
          {p && <PaymentAllocStatusBadge status={p.allocation_status} />}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 14 : 24 }}>
          {loading ? <Skeleton lines={10} /> : error ? (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
              padding: "14px 18px", display: "flex", alignItems: "center", gap: 8, color: S.red,
            }}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : p ? (
            <PaymentDetailBody payment={p} isMobile={isMobile} />
          ) : null}
        </div>

        {!loading && p && canWrite && (
          <div style={{
            padding: isMobile ? "12px 14px calc(12px + env(safe-area-inset-bottom))" : "14px 24px",
            background: S.card, borderTop: `1px solid ${S.border}`,
            display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap",
          }}>
            <button onClick={() => setShowAllocate(true)} disabled={busy} style={{ ...S.btnSm(S.accent), padding: "10px 14px", flex: 1, minWidth: 130, justifyContent: "center" }}>
              <ArrowRight size={14} /> Alociraj
            </button>
            <button onClick={() => setShowEdit(true)} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 14px" }}>
              <Edit3 size={14} /> Uredi
            </button>
            <button onClick={() => setConfirmDelete(true)} disabled={busy} style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "10px 14px" }}>
              <Trash2 size={14} />
            </button>
          </div>
        )}

        {confirmDelete && (
          <div onClick={() => !busy && setConfirmDelete(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}>
            <div onClick={(e) => e.stopPropagation()} style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 420, width: "100%" }}>
              <h3 style={{ margin: 0, fontSize: 17, color: S.dark }}>Obrisati uplatu?</h3>
              <p style={{ fontSize: 14, color: S.muted, marginTop: 8, marginBottom: 20 }}>
                Svi povezani računi će se ponovo označiti kao neplaćeni.
              </p>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setConfirmDelete(false)} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>
                  Otkaži
                </button>
                <button onClick={handleDelete} disabled={busy} style={{ ...S.btnSm(S.red), padding: "10px 16px" }}>
                  {busy ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Obriši
                </button>
              </div>
            </div>
          </div>
        )}

        {showEdit && p && (
          <PaymentFormModal
            isEdit
            payment={p}
            onClose={() => setShowEdit(false)}
            onSaved={() => { setShowEdit(false); load(); onAfterChange(); showToast("success", "Uplata ažurirana"); }}
            isMobile={isMobile}
            partners={[]} // not editable on existing — partner stays same
          />
        )}

        {showAllocate && p && (
          <AllocationModal
            payment={p}
            onClose={() => setShowAllocate(false)}
            onSaved={() => { setShowAllocate(false); load(); onAfterChange(); showToast("success", "Alokacije sačuvane"); }}
            isMobile={isMobile}
          />
        )}

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </>
  );
}

function PaymentDetailBody({ payment, isMobile }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card title="Iznos">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <Row label="Uplata" value={fmtMoney(payment.amount, payment.currency)} bold large />
          <Row label="Alocirano" value={fmtMoney(payment.allocated_total)} />
          {payment.remaining > 0 && (
            <Row label="Nealocirano" value={fmtMoney(payment.remaining)} muted />
          )}
        </div>
      </Card>

      <Card title="Detalji uplate">
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
          <KV label="Partner" value={dh(payment.partner_name)} />
          <KV label="PIB" value={payment.partner_tax_id} mono />
          <KV label="Datum" value={fmtDate(payment.payment_date)} />
          <KV label="Način" value={PAYMENT_METHOD_LABEL[payment.payment_method] || payment.payment_method} />
          {payment.bank_izvod_no && <KV label="Broj izvoda" value={dh(payment.bank_izvod_no)} mono />}
          {payment.reference && <KV label="Poziv na broj" value={dh(payment.reference)} mono />}
          {payment.partner_bank_account && <KV label="Žiro račun" value={dh(payment.partner_bank_account)} mono />}
          {payment.operator_name && <KV label="Operator" value={dh(payment.operator_name)} />}
        </div>
        {payment.notes && (
          <div style={{ marginTop: 12, borderTop: `1px solid ${S.borderSoft}`, paddingTop: 12 }}>
            <div style={{ fontSize: 11, color: S.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 4 }}>Napomena</div>
            <div style={{ fontSize: 13, color: S.dark, whiteSpace: "pre-wrap" }}>{dh(payment.notes)}</div>
          </div>
        )}
      </Card>

      <Card title="Alokacije" subtitle={`${payment.allocations?.length || 0} računa`}>
        {!payment.allocations || payment.allocations.length === 0 ? (
          <div style={{ padding: "8px 4px", textAlign: "center", color: S.mutedLight, fontSize: 13 }}>
            Uplata nije alocirana ni jednom računu
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 420 }}>
              <thead>
                <tr>
                  <th style={th}>Račun</th>
                  <th style={th}>Datum</th>
                  <th style={{ ...th, textAlign: "right" }}>Iznos računa</th>
                  <th style={{ ...th, textAlign: "right" }}>Alocirano</th>
                  <th style={{ ...th, textAlign: "right" }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {payment.allocations.map((a) => (
                  <tr key={a.id} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={{ ...td, fontFamily: S.mono, fontWeight: 600 }}>{a.display_no || "—"}</td>
                    <td style={{ ...td, color: S.muted, whiteSpace: "nowrap" }}>{fmtDate(a.invoice_date)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtMoney(a.invoice_total)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>{fmtMoney(a.allocated_amount)}</td>
                    <td style={{ ...td, textAlign: "right" }}>
                      {a.is_paid ? (
                        <span style={{ background: "#dcfce7", color: S.green, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>Plaćeno</span>
                      ) : (
                        <span style={{ background: "#fef3c7", color: S.amber, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>Djelimično</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div style={{ height: 16 }} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// PAYMENT FORM MODAL (create + edit)
// ════════════════════════════════════════════════════════════════════════════
function PaymentFormModal({ payment, isEdit, onClose, onSaved, isMobile, partners }) {
  const today = new Date().toISOString().slice(0, 10);
  const [partnerId, setPartnerId] = useState(payment?.partner_id || 0);
  const [partnerSearch, setPartnerSearch] = useState("");
  const [amount, setAmount] = useState(payment?.amount ?? "");
  const [paymentDate, setPaymentDate] = useState(payment?.payment_date?.slice(0, 10) || today);
  const [method, setMethod] = useState(payment?.payment_method || "bank_transfer");
  const [bankIzvod, setBankIzvod] = useState(dh(payment?.bank_izvod_no) || "");
  const [partnerBank, setPartnerBank] = useState(dh(payment?.partner_bank_account) || "");
  const [reference, setReference] = useState(dh(payment?.reference) || "");
  const [notes, setNotes] = useState(dh(payment?.notes) || "");

  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  const filteredPartners = (() => {
    if (!partnerSearch) return partners.slice(0, 100);
    const q = partnerSearch.toLowerCase();
    return partners.filter((p) =>
      (p.name || "").toLowerCase().includes(q) ||
      (p.tax_id || "").includes(partnerSearch)
    ).slice(0, 100);
  })();

  const submit = async () => {
    const e = {};
    if (!isEdit && !partnerId) e.partnerId = "Izaberite partnera";
    if (!amount || parseNum(amount) <= 0) e.amount = "Iznos je obavezan";
    if (!paymentDate) e.paymentDate = "Datum je obavezan";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setBusy(true);
    const body = {
      partner_id: partnerId,
      amount: parseNum(amount),
      payment_date: paymentDate,
      payment_method: method,
      bank_izvod_no: bankIzvod,
      partner_bank_account: partnerBank,
      reference,
      notes,
    };

    const url = isEdit ? `/b2b/payment-update/${payment.id}` : `/b2b/payment-store`;
    const r = await api(url, { method: "POST", body: JSON.stringify(body) });
    setBusy(false);

    if (r.success) {
      onSaved(r.data?.id || payment?.id);
    } else {
      setToast({ type: "error", message: r.message || "Greška pri čuvanju" });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const modalStyle = isMobile
    ? { position: "fixed", inset: 0, background: S.bg, zIndex: 140, display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease-out" }
    : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 140, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  const contentStyle = isMobile
    ? { background: S.bg, flex: 1, display: "flex", flexDirection: "column" }
    : { background: S.card, borderRadius: 14, width: "100%", maxWidth: 620, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" };

  return (
    <div onClick={!isMobile && !busy ? onClose : undefined} style={modalStyle}>
      <div onClick={(e) => e.stopPropagation()} style={contentStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card, borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} disabled={busy} style={{
            background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted, borderRadius: 6,
          }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
            {isEdit ? "Uredi uplatu" : "Nova uplata"}
          </div>
          <button onClick={submit} disabled={busy} style={{ ...S.btnSm(S.dark, "white"), padding: "10px 18px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />}
            {busy ? "Čuvam…" : "Sačuvaj"}
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 14 : 24 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            {!isEdit && (
              <Card title="Partner">
                {errors.partnerId && (
                  <div style={{ color: S.red, fontSize: 12, marginBottom: 8 }}>{errors.partnerId}</div>
                )}
                <div style={{ position: "relative", marginBottom: 8 }}>
                  <Search size={14} color={S.mutedLight} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)" }} />
                  <input
                    value={partnerSearch}
                    onChange={(e) => setPartnerSearch(e.target.value)}
                    placeholder="Pretraži partnere…"
                    style={{ ...S.input, paddingLeft: 32, paddingTop: 8, paddingBottom: 8, fontSize: 13 }}
                  />
                  {partnerSearch && (
                    <button onClick={() => setPartnerSearch("")} style={{
                      position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)",
                      background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted,
                    }}>
                      <X size={13} />
                    </button>
                  )}
                </div>
                <div style={{
                  maxHeight: 240, overflowY: "auto",
                  border: `1px solid ${S.border}`, borderRadius: 8,
                  background: "#fafafb",
                }}>
                  {filteredPartners.length === 0 ? (
                    <div style={{ padding: 20, textAlign: "center", color: S.mutedLight, fontSize: 13 }}>
                      {partners.length === 0 ? "Nema partnera" : "Nema rezultata"}
                    </div>
                  ) : (
                    filteredPartners.map((p, i) => {
                      const selected = p.id === partnerId;
                      return (
                        <button
                          key={p.id}
                          onClick={() => setPartnerId(selected ? 0 : p.id)}
                          style={{
                            width: "100%", display: "flex", alignItems: "center", gap: 10,
                            padding: "10px 12px",
                            background: selected ? S.accentSoft : "white",
                            border: "none",
                            borderBottom: i < filteredPartners.length - 1 ? `1px solid ${S.borderSoft}` : "none",
                            cursor: "pointer", textAlign: "left", fontFamily: S.font,
                          }}
                        >
                          <div style={{
                            width: 28, height: 28, borderRadius: 7,
                            background: selected ? S.accent : S.borderSoft,
                            color: selected ? "white" : S.muted,
                            display: "flex", alignItems: "center", justifyContent: "center",
                            flexShrink: 0,
                          }}>
                            <Building2 size={14} />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 600, fontSize: 13, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {dh(p.name)}
                            </div>
                            <div style={{ fontSize: 11, color: S.muted, fontFamily: S.mono }}>
                              {p.tax_id ? `PIB: ${p.tax_id}` : "Bez PIB-a"}
                            </div>
                          </div>
                          {selected && <CircleCheck size={16} color={S.accent} style={{ flexShrink: 0 }} />}
                        </button>
                      );
                    })
                  )}
                </div>
              </Card>
            )}

            {isEdit && payment?.partner_name && (
              <Card title="Partner">
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Building2 size={18} color={S.muted} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: S.dark }}>{dh(payment.partner_name)}</div>
                    <div style={{ fontSize: 12, color: S.muted, fontFamily: S.mono }}>
                      {payment.partner_tax_id ? `PIB: ${payment.partner_tax_id}` : ""}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <Card title="Iznos i datum">
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
                <Field label="Iznos (€)" required error={errors.amount}>
                  <input
                    type="text" inputMode="decimal"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0,00"
                    style={{ ...S.input, fontFamily: S.mono, borderColor: errors.amount ? S.red : S.border }}
                  />
                </Field>
                <Field label="Datum uplate" required error={errors.paymentDate}>
                  <input
                    type="date" value={paymentDate}
                    onChange={(e) => setPaymentDate(e.target.value)}
                    style={{ ...S.input, borderColor: errors.paymentDate ? S.red : S.border }}
                  />
                </Field>
              </div>
              <div style={{ marginTop: 10 }}>
                <Field label="Način plaćanja">
                  <Select value={method} onChange={setMethod} options={PAYMENT_METHOD_OPTIONS} icon={Banknote} />
                </Field>
              </div>
            </Card>

            <Card title="Reference (opciono)">
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
                <Field label="Broj bankovnog izvoda">
                  <input
                    value={bankIzvod} onChange={(e) => setBankIzvod(e.target.value)}
                    placeholder="npr. 123/2026"
                    style={{ ...S.input, fontFamily: S.mono }}
                  />
                </Field>
                <Field label="Poziv na broj">
                  <input
                    value={reference} onChange={(e) => setReference(e.target.value)}
                    style={{ ...S.input, fontFamily: S.mono }}
                  />
                </Field>
              </div>
              <div style={{ marginTop: 10 }}>
                <Field label="Žiro račun partnera">
                  <input
                    value={partnerBank} onChange={(e) => setPartnerBank(e.target.value)}
                    placeholder="510-XXXXXXXX-XX"
                    style={{ ...S.input, fontFamily: S.mono }}
                  />
                </Field>
              </div>
              <div style={{ marginTop: 10 }}>
                <Field label="Napomena">
                  <textarea
                    value={notes} onChange={(e) => setNotes(e.target.value)}
                    rows={2}
                    style={{ ...S.input, resize: "vertical", minHeight: 50, fontFamily: S.font }}
                  />
                </Field>
              </div>
            </Card>
          </div>
        </div>

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ALLOCATION MODAL — assign payment to invoices
// ════════════════════════════════════════════════════════════════════════════
function AllocationModal({ payment, onClose, onSaved, isMobile }) {
  const [unpaid, setUnpaid] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // allocations[invoice_id] = "12.34"  (string for editing)
  const [allocations, setAllocations] = useState({});
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  // Load unpaid invoices for this partner + seed allocations from existing
  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      const r = await api(`/b2b/partner-unpaid/${payment.partner_id}`);
      if (!active) return;
      setLoading(false);
      if (r.success) {
        const items = Array.isArray(r.data?.items) ? r.data.items : [];
        // Merge in existing allocations
        const existing = {};
        for (const a of payment.allocations || []) {
          existing[a.invoice_id] = String(a.allocated_amount);
        }
        // Also include already-allocated invoices that might be marked paid
        // (so user can adjust down)
        for (const a of payment.allocations || []) {
          if (!items.find((it) => it.id === a.invoice_id)) {
            items.push({
              id: a.invoice_id,
              invoice_number: a.invoice_number,
              display_no: a.display_no,
              invoice_date: a.invoice_date,
              total_amount: a.invoice_total,
              allocated: a.allocated_amount,
              remaining: 0,
              currency: a.invoice_currency,
            });
          }
        }
        setUnpaid(items);
        setAllocations(existing);
      } else {
        setError(r.message || "Greška");
      }
    })();
    return () => { active = false; };
  }, [payment.partner_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateAlloc = (invId, value) => {
    setAllocations((prev) => ({ ...prev, [invId]: value }));
  };

  const allocTotal = Object.values(allocations).reduce((sum, v) => sum + parseNum(v || 0), 0);
  const paymentRemaining = payment.amount - allocTotal;

  const autoFillOldestFirst = () => {
    let remaining = payment.amount;
    const newAllocs = {};
    for (const inv of unpaid) {
      if (remaining <= 0.005) break;
      const existingForInv = parseNum(allocations[inv.id] || 0);
      // The "remaining for invoice" excludes this payment's existing alloc but includes others
      const otherAllocs = inv.allocated - existingForInv;
      const invDue = Math.max(0, inv.total_amount - otherAllocs);
      const toAlloc = Math.min(remaining, invDue);
      if (toAlloc > 0) {
        newAllocs[inv.id] = (Math.round(toAlloc * 100) / 100).toFixed(2);
        remaining -= toAlloc;
      }
    }
    setAllocations(newAllocs);
  };

  const clearAll = () => setAllocations({});

  const submit = async () => {
    if (allocTotal > payment.amount + 0.005) {
      setToast({ type: "error", message: "Zbir alokacija prelazi iznos uplate" });
      setTimeout(() => setToast(null), 3500);
      return;
    }
    setBusy(true);
    const allocs = Object.entries(allocations)
      .map(([invId, amt]) => ({ invoice_id: parseInt(invId, 10), amount: parseNum(amt) }))
      .filter((a) => a.amount > 0);
    const r = await api(`/b2b/payment-allocate/${payment.id}`, {
      method: "POST",
      body: JSON.stringify({ allocations: allocs }),
    });
    setBusy(false);
    if (r.success) onSaved();
    else {
      setToast({ type: "error", message: r.message || "Greška" });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const modalStyle = isMobile
    ? { position: "fixed", inset: 0, background: S.bg, zIndex: 160, display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease-out" }
    : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 160, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  const contentStyle = isMobile
    ? { background: S.bg, flex: 1, display: "flex", flexDirection: "column" }
    : { background: S.card, borderRadius: 14, width: "100%", maxWidth: 720, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" };

  return (
    <div onClick={!isMobile && !busy ? onClose : undefined} style={modalStyle}>
      <div onClick={(e) => e.stopPropagation()} style={contentStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card, borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} disabled={busy} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>Alociraj uplatu</div>
            <div style={{ fontSize: 12, color: S.muted }}>
              {fmtMoney(payment.amount, payment.currency)} · {dh(payment.partner_name)}
            </div>
          </div>
          <button onClick={submit} disabled={busy || allocTotal > payment.amount + 0.005} style={{ ...S.btnSm(S.dark, "white"), padding: "10px 18px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />}
            {busy ? "Čuvam…" : "Sačuvaj"}
          </button>
        </div>

        {/* Summary bar */}
        <div style={{
          padding: isMobile ? "10px 16px" : "12px 24px",
          background: paymentRemaining < -0.005 ? "#fef2f2" : (Math.abs(paymentRemaining) < 0.005 ? "#dcfce7" : S.accentSoft),
          borderBottom: `1px solid ${S.border}`,
          display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
          fontSize: 13, flexShrink: 0,
        }}>
          <div>Iznos uplate: <strong style={{ fontFamily: S.mono }}>{fmtMoney(payment.amount)}</strong></div>
          <div>Alocirano: <strong style={{ fontFamily: S.mono, color: paymentRemaining < -0.005 ? S.red : S.dark }}>{fmtMoney(allocTotal)}</strong></div>
          <div>
            {paymentRemaining < -0.005
              ? <span style={{ color: S.red }}>Višak: {fmtMoney(Math.abs(paymentRemaining))}</span>
              : <>Preostalo: <strong style={{ fontFamily: S.mono, color: Math.abs(paymentRemaining) < 0.005 ? S.green : S.orange }}>{fmtMoney(Math.max(0, paymentRemaining))}</strong></>}
          </div>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 14 : 20 }}>
          {loading ? <Skeleton lines={5} /> : error ? (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
              padding: "14px 18px", display: "flex", alignItems: "center", gap: 8, color: S.red,
            }}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : unpaid.length === 0 ? (
            <div style={{ padding: 40, textAlign: "center", color: S.mutedLight }}>
              <CircleCheck size={36} color={S.green} />
              <div style={{ marginTop: 12, fontSize: 14 }}>Ovaj partner nema neplaćenih računa</div>
            </div>
          ) : (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
                <button onClick={autoFillOldestFirst} style={{ ...S.btnSm(S.accentSoft, S.accent), padding: "8px 12px", fontSize: 12 }}>
                  <Zap size={12} /> Auto-rasporedi (od najstarijeg)
                </button>
                <button onClick={clearAll} style={{ ...S.btnSm("white", S.muted), border: `1px solid ${S.border}`, padding: "8px 12px", fontSize: 12 }}>
                  <X size={12} /> Obriši sve
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {unpaid.map((inv) => (
                  <AllocationRow
                    key={inv.id}
                    inv={inv}
                    value={allocations[inv.id] || ""}
                    onChange={(v) => updateAlloc(inv.id, v)}
                    existingForThisPayment={parseNum((payment.allocations || []).find((a) => a.invoice_id === inv.id)?.allocated_amount || 0)}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </div>
  );
}

function AllocationRow({ inv, value, onChange, existingForThisPayment }) {
  // The maximum this row can have: invoice total - (allocated by other payments)
  // = invoice.total - (inv.allocated - existingForThisPayment)
  const otherAllocs = Math.max(0, inv.allocated - existingForThisPayment);
  const maxThisRow = Math.max(0, inv.total_amount - otherAllocs);

  const numValue = parseNum(value || 0);
  const fillsRow = numValue >= maxThisRow - 0.005 && numValue > 0;

  return (
    <div style={{
      padding: 10, border: `1px solid ${fillsRow ? S.green : S.border}`, borderRadius: 8,
      background: fillsRow ? "#f0fdf4" : "white",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: S.dark, fontFamily: S.mono }}>
            {inv.display_no || inv.invoice_number}
          </div>
          <div style={{ fontSize: 12, color: S.muted }}>
            {fmtDate(inv.invoice_date)} · Iznos: <span style={{ fontFamily: S.mono }}>{fmtMoney(inv.total_amount, inv.currency)}</span>
          </div>
          {otherAllocs > 0 && (
            <div style={{ fontSize: 11, color: S.muted, marginTop: 2 }}>
              Već alocirano (drugo): <span style={{ fontFamily: S.mono }}>{fmtMoney(otherAllocs)}</span>
            </div>
          )}
        </div>
        {fillsRow && <CircleCheck size={18} color={S.green} />}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <input
          type="text" inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="0.00"
          style={{ ...S.input, fontFamily: S.mono, fontSize: 14, padding: "8px 10px" }}
        />
        <button
          onClick={() => onChange((Math.round(maxThisRow * 100) / 100).toFixed(2))}
          style={{
            background: "white", border: `1px solid ${S.border}`,
            padding: "8px 12px", borderRadius: 6, fontSize: 12, color: S.accent,
            cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap",
          }}
        >
          Max ({fmtMoney(maxThisRow)})
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// QUICK MARK-PAID MODAL (used from invoice detail panel)
// ════════════════════════════════════════════════════════════════════════════
function QuickMarkPaidModal({ invoice, onClose, onSaved, isMobile }) {
  const today = new Date().toISOString().slice(0, 10);
  const [amount, setAmount] = useState(invoice.total_amount?.toFixed(2) || "");
  const [paymentDate, setPaymentDate] = useState(today);
  const [method, setMethod] = useState("bank_transfer");
  const [reference, setReference] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  const submit = async () => {
    if (!amount || parseNum(amount) <= 0) {
      setToast({ type: "error", message: "Iznos je obavezan" });
      setTimeout(() => setToast(null), 3500);
      return;
    }
    setBusy(true);
    const r = await api(`/b2b/invoice-mark-paid/${invoice.id}`, {
      method: "POST",
      body: JSON.stringify({
        amount: parseNum(amount),
        payment_date: paymentDate,
        payment_method: method,
        reference,
      }),
    });
    setBusy(false);
    if (r.success) onSaved();
    else {
      setToast({ type: "error", message: r.message || "Greška" });
      setTimeout(() => setToast(null), 4000);
    }
  };

  return (
    <div
      onClick={!busy ? onClose : undefined}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div onClick={(e) => e.stopPropagation()} style={{
        background: S.card, borderRadius: 14, width: "100%", maxWidth: 480,
        boxShadow: "0 20px 60px rgba(0,0,0,0.2)", overflow: "hidden",
      }}>
        <div style={{
          padding: "16px 20px", borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <CircleCheck size={20} color={S.green} />
          <h3 style={{ margin: 0, fontSize: 17, color: S.dark, flex: 1 }}>Označi račun kao plaćen</h3>
          <button onClick={onClose} disabled={busy} style={{ background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted }}>
            <X size={18} />
          </button>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <Field label="Iznos (€)" required>
              <input
                type="text" inputMode="decimal"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                style={{ ...S.input, fontFamily: S.mono }}
              />
            </Field>
            <Field label="Datum uplate">
              <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} style={S.input} />
            </Field>
            <Field label="Način plaćanja">
              <Select value={method} onChange={setMethod} options={PAYMENT_METHOD_OPTIONS} icon={Banknote} />
            </Field>
            <Field label="Poziv na broj (opciono)">
              <input value={reference} onChange={(e) => setReference(e.target.value)} style={{ ...S.input, fontFamily: S.mono }} />
            </Field>
          </div>
        </div>
        <div style={{
          padding: "12px 20px", borderTop: `1px solid ${S.border}`, background: "#fafafb",
          display: "flex", gap: 8, justifyContent: "flex-end",
        }}>
          <button onClick={onClose} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>
            Otkaži
          </button>
          <button onClick={submit} disabled={busy} style={{ ...S.btnSm(S.green), padding: "10px 16px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />}
            {busy ? "Čuvam…" : "Označi plaćenim"}
          </button>
        </div>
        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// EXPENSES SCREEN (Troškovi)
// ════════════════════════════════════════════════════════════════════════════
const EXPENSE_STATUS_OPTIONS = [
  { id: "all",     label: "Svi statusi" },
  { id: "paid",    label: "Plaćeno" },
  { id: "unpaid",  label: "Neplaćeno" },
  { id: "pending", label: "Na čekanju" },
];
const EXPENSE_STATUS_META = {
  paid:    { label: "Plaćeno",     color: "#059669", bg: "#dcfce7" },
  unpaid:  { label: "Neplaćeno",   color: "#dc2626", bg: "#fee2e2" },
  pending: { label: "Na čekanju",  color: "#d97706", bg: "#fef3c7" },
};
const EXPENSE_GROUP_OPTIONS = [
  { id: "all",     label: "Sve grupe" },
  { id: "daily",   label: "Dnevni" },
  { id: "monthly", label: "Mjesečni" },
];
const PAY_METHOD_OPTIONS_EXP = [
  { id: "",            label: "—" },
  { id: "cash",        label: "Gotovina" },
  { id: "bank_transfer", label: "Virman" },
  { id: "debit_card",  label: "Debit kartica" },
  { id: "credit_card", label: "Kreditna kartica" },
  { id: "other",       label: "Drugo" },
];
const EXP_VAT_RATES = [
  { id: "0",  label: "0%" },
  { id: "7",  label: "7%" },
  { id: "15", label: "15%" },
  { id: "21", label: "21%" },
];

function ExpensesScreen({ isMobile, canWrite }) {
  // View mode: "list" (flat list, default) or "table" (pivot like PHP webapp)
  const [viewMode, setViewMode] = useState("list");

  // Filters (only used in list mode)
  const [searchInput, setSearchInput] = useState("");
  const search = useDebounced(searchInput, 350);
  const [status, setStatus] = useState("all");
  const [categoryId, setCategoryId] = useState(0);
  const [group, setGroup] = useState("all");
  const [datePreset, setPreset] = useState("month");
  const [vatOnly, setVatOnly] = useState(false);

  const [page, setPage] = useState(1);
  const perPage = isMobile ? 25 : 50;

  const [data, setData]     = useState({ items: [], total: 0, total_pages: 1, summary: {} });
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [selectedId, setSelectedId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const r = await api("/b2b/expense-categories");
      if (!active) return;
      if (r.success) setCategories(Array.isArray(r.data?.items) ? r.data.items : []);
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => { setPage(1); }, [search, status, categoryId, group, datePreset, vatOnly]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const { from, to } = resolvePresetRange(datePreset);
    const qs = new URLSearchParams();
    if (search) qs.set("search", search);
    if (status !== "all") qs.set("payment_status", status);
    if (categoryId > 0) qs.set("category_id", String(categoryId));
    if (group !== "all") qs.set("group", group);
    if (from) qs.set("date_from", from);
    if (to)   qs.set("date_to",   to);
    if (vatOnly) qs.set("vat_only", "1");
    qs.set("page", String(page));
    qs.set("per_page", String(perPage));
    const r = await api(`/b2b/expenses?${qs.toString()}`);
    setLoading(false);
    if (r.success) setData(r.data);
    else setError(r.message || "Greška pri učitavanju");
  }, [search, status, categoryId, group, datePreset, vatOnly, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const items = Array.isArray(data.items) ? data.items : [];
  const summary = data.summary || {};

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const filterPanel = (
    <ExpensesFilterPanel
      searchInput={searchInput} setSearchInput={setSearchInput}
      status={status} setStatus={setStatus}
      categoryId={categoryId} setCategoryId={setCategoryId}
      categories={categories}
      group={group} setGroup={setGroup}
      datePreset={datePreset} setPreset={setPreset}
      vatOnly={vatOnly} setVatOnly={setVatOnly}
      isMobile={isMobile}
    />
  );

  return (
    <>
      <TopHeader
        title="Troškovi"
        subtitle={loading ? "Učitavam…" : `${fmtNum(summary.total_count || 0)} troškova · ${fmtMoney(summary.total_amount || 0)}`}
        isMobile={isMobile}
        right={
          isMobile ? (
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => setFilterOpen(true)} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "8px 10px" }}>
                <FilterIcon size={14} />
              </button>
              {canWrite && (
                <button onClick={() => setShowForm(true)} style={{ ...S.btnSm(S.dark, "white"), padding: "8px 12px" }}>
                  <Plus size={14} />
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={load} disabled={loading} style={{ ...S.btnSm("white", S.muted), border: `1px solid ${S.border}`, padding: "8px 10px" }}>
                <RefreshCw size={14} className={loading ? "spin" : ""} />
                {loading ? "Učitavam" : "Osvježi"}
              </button>
              {canWrite && (
                <button onClick={() => setShowForm(true)} style={{ ...S.btnSm(S.dark, "white"), padding: "8px 14px" }}>
                  <Plus size={14} /> Novi trošak
                </button>
              )}
            </div>
          )
        }
      />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1600 }}>
        {/* View toggle: Lista / Tabelarno / Kategorije */}
        <div style={{
          display: "flex", gap: 6, marginBottom: 14,
          background: S.card, border: `1px solid ${S.border}`,
          borderRadius: 10, padding: 4, width: "fit-content",
        }}>
          <ViewModeBtn active={viewMode === "list"}       onClick={() => setViewMode("list")}       icon={FilterIcon}      label="Lista" />
          <ViewModeBtn active={viewMode === "table"}      onClick={() => setViewMode("table")}      icon={LayoutDashboard} label="Tabelarno" />
          <ViewModeBtn active={viewMode === "categories"} onClick={() => setViewMode("categories")} icon={Package}         label="Kategorije" />
        </div>

        {error && (
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
            padding: "14px 18px", marginBottom: 16,
            display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <AlertCircle size={16} /> {error}
          </div>
        )}

        {viewMode === "categories" ? (
          <CategoriesView
            isMobile={isMobile}
            canWrite={canWrite}
            onCategoriesChanged={async () => {
              // Refresh categories list in parent so dropdowns stay in sync
              const r = await api("/b2b/expense-categories");
              if (r.success) setCategories(Array.isArray(r.data?.items) ? r.data.items : []);
            }}
          />
        ) : viewMode === "table" ? (
          <ExpensesTableView
            isMobile={isMobile}
            canWrite={canWrite}
            categories={categories}
            onOpenExpense={(id) => setSelectedId(id)}
            onCreateExpense={() => setShowForm(true)}
            onAfterChange={() => { /* table view manages its own refresh */ }}
          />
        ) : (
          <>
            {!isMobile && <div style={{ marginBottom: 16 }}>{filterPanel}</div>}

            {!loading && (
              <div style={{
                display: "grid", gap: 10, marginBottom: 14,
                gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(160px, 1fr))",
              }}>
                <SummaryCard icon={Wallet}     color={S.dark}   label="Ukupno"     value={fmtMoney(summary.total_amount || 0)} />
                <SummaryCard icon={CircleCheck} color={S.green} label="Plaćeno"    value={fmtMoney(summary.total_paid || 0)} />
                <SummaryCard icon={AlertCircle} color={S.red}   label="Neplaćeno"  value={fmtMoney(summary.total_unpaid || 0)} />
                <SummaryCard icon={Clock}       color={S.amber} label="Na čekanju" value={fmtMoney(summary.total_pending || 0)} />
                <SummaryCard icon={TrendingUp}  color={S.accent} label="PDV odbitni" value={fmtMoney(summary.total_vat_deductible || 0)} />
              </div>
            )}

            <div style={{
              background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
              overflow: "hidden",
            }}>
              {loading && items.length === 0 ? (
                <div style={{ padding: 24 }}><Skeleton lines={6} /></div>
              ) : items.length === 0 ? (
                <div style={{ padding: 60, textAlign: "center", color: S.mutedLight }}>
                  <Wallet size={36} color={S.mutedLight} />
                  <div style={{ marginTop: 12, fontSize: 14 }}>
                    {search ? "Nema rezultata" : "Još nemate troškova"}
                  </div>
                  {canWrite && !search && (
                    <button onClick={() => setShowForm(true)} style={{ ...S.btn(S.accent), marginTop: 14 }}>
                      <Plus size={16} /> Evidentiraj prvi trošak
                    </button>
                  )}
                </div>
              ) : isMobile ? (
                <ExpenseCardList items={items} onSelect={setSelectedId} />
              ) : (
                <ExpenseTable items={items} onSelect={setSelectedId} />
              )}
            </div>

            {data.total_pages > 1 && (
              <Pagination page={page} totalPages={data.total_pages} onChange={setPage} isMobile={isMobile} />
            )}
          </>
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {isMobile && filterOpen && (
        <div onClick={() => setFilterOpen(false)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 110,
          display: "flex", alignItems: "flex-end",
        }}>
          <div onClick={(e) => e.stopPropagation()} className="slide-up" style={{
            width: "100%", background: S.card, borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: "85vh", overflowY: "auto",
          }}>
            <div style={{ display: "flex", justifyContent: "center", padding: "8px 0 4px" }}>
              <div style={{ width: 40, height: 4, background: S.border, borderRadius: 2 }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 16px 12px" }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: S.dark }}>Filteri</div>
              <button onClick={() => setFilterOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}>
                <X size={20} />
              </button>
            </div>
            <div style={{ padding: "0 16px 24px" }}>
              {filterPanel}
              <button onClick={() => setFilterOpen(false)} style={{ ...S.btn(S.dark), width: "100%", marginTop: 18 }}>Primijeni</button>
            </div>
          </div>
        </div>
      )}

      {selectedId !== null && (
        <ExpenseDetailPanel
          id={selectedId}
          onClose={() => setSelectedId(null)}
          isMobile={isMobile}
          canWrite={canWrite}
          categories={categories}
          onAfterChange={() => load()}
          onAfterDelete={() => { setSelectedId(null); load(); showToast("success", "Trošak obrisan"); }}
        />
      )}

      {showForm && (
        <ExpenseFormModal
          categories={categories}
          onClose={() => setShowForm(false)}
          onSaved={(id) => {
            setShowForm(false);
            load();
            showToast("success", "Trošak kreiran");
            setSelectedId(id);
          }}
          isMobile={isMobile}
        />
      )}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

function ExpensesFilterPanel({
  searchInput, setSearchInput,
  status, setStatus,
  categoryId, setCategoryId, categories,
  group, setGroup,
  datePreset, setPreset,
  vatOnly, setVatOnly,
  isMobile,
}) {
  return (
    <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: isMobile ? 12 : 14 }}>
      <div style={{ position: "relative", marginBottom: 12 }}>
        <Search size={16} color={S.mutedLight} style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
        <input
          value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Pretraži opis, kategoriju, napomenu…"
          style={{ ...S.input, paddingLeft: 36 }}
        />
        {searchInput && (
          <button onClick={() => setSearchInput("")} style={{
            position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)",
            background: "none", border: "none", cursor: "pointer", padding: 4, color: S.muted,
          }}>
            <X size={14} />
          </button>
        )}
      </div>
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr" }}>
        <Select value={status} onChange={setStatus} options={EXPENSE_STATUS_OPTIONS} icon={CircleCheck} />
        <Select value={group} onChange={setGroup} options={EXPENSE_GROUP_OPTIONS} icon={ChevronsUpDown} />
        <Select value={datePreset} onChange={setPreset} options={DATE_RANGES} icon={Calendar} />
      </div>
      <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", marginTop: 10 }}>
        <Select
          value={String(categoryId)}
          onChange={(v) => setCategoryId(parseInt(v, 10) || 0)}
          options={[
            { id: "0", label: "Sve kategorije" },
            ...categories.map((c) => ({ id: String(c.id), label: `${dh(c.name)} (${c.category_group === "daily" ? "D" : "M"})` })),
          ]}
          icon={Package}
        />
        <label style={{
          display: "flex", alignItems: "center", gap: 8,
          padding: "10px 12px", border: `1px solid ${vatOnly ? S.accent : S.border}`,
          background: vatOnly ? S.accentSoft : "#f9fafb",
          borderRadius: 8, cursor: "pointer", fontSize: 13,
          color: vatOnly ? S.accent : S.dark, fontWeight: vatOnly ? 600 : 500,
        }}>
          <input
            type="checkbox" checked={vatOnly}
            onChange={(e) => setVatOnly(e.target.checked)}
            style={{ width: 14, height: 14, accentColor: S.accent }}
          />
          Samo sa PDV-om
        </label>
      </div>
    </div>
  );
}

function ExpenseStatusBadge({ status }) {
  const meta = EXPENSE_STATUS_META[status];
  if (!meta) return null;
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px",
      background: meta.bg, color: meta.color,
      borderRadius: 12, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    }}>{meta.label}</span>
  );
}

function ExpenseTable({ items, onSelect }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14, minWidth: 900 }}>
        <thead style={{ background: "#fafafb" }}>
          <tr>
            <th style={th}>Datum</th>
            <th style={th}>Opis</th>
            <th style={th}>Kategorija</th>
            <th style={{ ...th, textAlign: "right" }}>Iznos</th>
            <th style={{ ...th, textAlign: "right" }}>PDV</th>
            <th style={{ ...th, textAlign: "right" }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr
              key={r.id}
              onClick={() => onSelect(r.id)}
              style={{ borderBottom: `1px solid ${S.borderSoft}`, cursor: "pointer" }}
              onMouseEnter={(e) => e.currentTarget.style.background = "#fafafb"}
              onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
            >
              <td style={{ ...td, color: S.muted, whiteSpace: "nowrap" }}>{fmtDate(r.expense_date)}</td>
              <td style={{ ...td, fontWeight: 600 }}>
                {dh(r.description)}
              </td>
              <td style={td}>
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "2px 8px", borderRadius: 12, fontSize: 11, fontWeight: 600,
                  background: `${r.category_color || S.border}22`, color: r.category_color || S.muted,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: 3, background: r.category_color || S.muted }} />
                  {dh(r.category_name) || "—"}
                </span>
              </td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700, whiteSpace: "nowrap" }}>
                {fmtMoney(r.amount)}
              </td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, color: S.muted, fontSize: 12 }}>
                {r.include_in_vat
                  ? <><span style={{ color: S.accent, fontWeight: 600 }}>{r.vat_rate}%</span> · {fmtMoney(r.vat_amount)}</>
                  : <span style={{ color: S.mutedLight }}>—</span>}
              </td>
              <td style={{ ...td, textAlign: "right" }}>
                <ExpenseStatusBadge status={r.payment_status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ExpenseCardList({ items, onSelect }) {
  return (
    <div>
      {items.map((r, i) => (
        <div
          key={r.id}
          onClick={() => onSelect(r.id)}
          style={{
            padding: "14px 16px",
            borderBottom: i < items.length - 1 ? `1px solid ${S.borderSoft}` : "none",
            cursor: "pointer",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {dh(r.description)}
              </div>
              <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                {fmtDate(r.expense_date)}
                {r.category_name && (
                  <span style={{ marginLeft: 4, color: r.category_color || S.muted }}>
                    · {dh(r.category_name)}
                  </span>
                )}
              </div>
              {r.include_in_vat && (
                <div style={{ fontSize: 11, color: S.accent, fontFamily: S.mono, marginTop: 2 }}>
                  {r.vat_rate}% PDV · {fmtMoney(r.vat_amount)}
                </div>
              )}
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{ fontSize: 15, fontFamily: S.mono, fontWeight: 700, color: S.dark }}>
                {fmtMoney(r.amount)}
              </div>
              <div style={{ marginTop: 4 }}>
                <ExpenseStatusBadge status={r.payment_status} />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// EXPENSE DETAIL PANEL
// ────────────────────────────────────────────────────────────────────────────
function ExpenseDetailPanel({ id, onClose, isMobile, canWrite, categories, onAfterChange, onAfterDelete }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const r = await api(`/b2b/expense/${id}`);
    setLoading(false);
    if (r.success) setData(r.data?.expense);
    else setError(r.message || "Greška");
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const handleDelete = async () => {
    setBusy(true);
    const r = await api(`/b2b/expense-delete/${id}`, { method: "POST", body: JSON.stringify({}) });
    setBusy(false);
    setConfirmDelete(false);
    if (r.success) onAfterDelete();
    else showToast("error", r.message || "Greška pri brisanju");
  };

  const e = data;

  const panelStyle = isMobile
    ? { position: "fixed", inset: 0, background: S.bg, zIndex: 120, display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease-out" }
    : { position: "fixed", top: 0, right: 0, bottom: 0, width: 720, maxWidth: "85vw", background: S.bg, zIndex: 120, boxShadow: "-12px 0 40px rgba(0,0,0,0.18)", display: "flex", flexDirection: "column" };

  return (
    <>
      {!isMobile && <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 115 }} />}
      <div style={panelStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card, borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted, borderRadius: 6 }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
              {loading ? "Učitavam…" : e ? fmtMoney(e.amount) : "Trošak"}
            </div>
            {e && <div style={{ fontSize: 12, color: S.muted }}>{dh(e.description)} · {fmtDate(e.expense_date)}</div>}
          </div>
          {e && <ExpenseStatusBadge status={e.payment_status} />}
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 14 : 24 }}>
          {loading ? <Skeleton lines={10} /> : error ? (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
              padding: "14px 18px", display: "flex", alignItems: "center", gap: 8, color: S.red,
            }}>
              <AlertCircle size={16} /> {error}
            </div>
          ) : e ? (
            <ExpenseDetailBody expense={e} isMobile={isMobile} />
          ) : null}
        </div>

        {!loading && e && canWrite && (
          <div style={{
            padding: isMobile ? "12px 14px calc(12px + env(safe-area-inset-bottom))" : "14px 24px",
            background: S.card, borderTop: `1px solid ${S.border}`,
            display: "flex", gap: 8, flexShrink: 0, flexWrap: "wrap",
          }}>
            <button onClick={() => setShowEdit(true)} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 14px", flex: 1, minWidth: 130, justifyContent: "center" }}>
              <Edit3 size={14} /> Uredi
            </button>
            <button onClick={() => setConfirmDelete(true)} disabled={busy} style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "10px 14px" }}>
              <Trash2 size={14} />
            </button>
          </div>
        )}

        {confirmDelete && (
          <div onClick={() => !busy && setConfirmDelete(false)} style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
          }}>
            <div onClick={(ev) => ev.stopPropagation()} style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 420, width: "100%" }}>
              <h3 style={{ margin: 0, fontSize: 17, color: S.dark }}>Obrisati trošak?</h3>
              <p style={{ fontSize: 14, color: S.muted, marginTop: 8, marginBottom: 20 }}>
                Ova akcija se ne može poništiti.
              </p>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button onClick={() => setConfirmDelete(false)} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>
                  Otkaži
                </button>
                <button onClick={handleDelete} disabled={busy} style={{ ...S.btnSm(S.red), padding: "10px 16px" }}>
                  {busy ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Obriši
                </button>
              </div>
            </div>
          </div>
        )}

        {showEdit && e && (
          <ExpenseFormModal
            isEdit
            expense={e}
            categories={categories}
            onClose={() => setShowEdit(false)}
            onSaved={() => { setShowEdit(false); load(); onAfterChange(); showToast("success", "Trošak ažuriran"); }}
            isMobile={isMobile}
          />
        )}

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </>
  );
}

function ExpenseDetailBody({ expense, isMobile }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card title="Detalji">
        <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
          <KV label="Opis" value={dh(expense.description)} />
          <KV label="Iznos" value={fmtMoney(expense.amount)} mono />
          <KV label="Datum" value={fmtDate(expense.expense_date)} />
          <KV label="Kategorija" value={dh(expense.category_name)} />
          <KV label="Grupa" value={expense.category_group === "daily" ? "Dnevni" : "Mjesečni"} />
          <KV label="Status" value={EXPENSE_STATUS_META[expense.payment_status]?.label || expense.payment_status} />
          {expense.payment_method && (
            <KV label="Način plaćanja" value={PAY_METHOD_OPTIONS_EXP.find((m) => m.id === expense.payment_method)?.label || expense.payment_method} />
          )}
          {expense.payment_date && <KV label="Datum plaćanja" value={fmtDate(expense.payment_date)} />}
        </div>
      </Card>

      {expense.include_in_vat ? (
        <Card title="PDV">
          <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr" }}>
            <KV label="Stopa" value={`${expense.vat_rate}%`} mono />
            <KV label="Iznos PDV-a" value={fmtMoney(expense.vat_amount)} mono />
            <KV label="Mjesec PDV-a" value={expense.vat_month} mono />
          </div>
        </Card>
      ) : null}

      {expense.notes && (
        <Card title="Napomena">
          <div style={{ fontSize: 13, color: S.dark, whiteSpace: "pre-wrap" }}>{dh(expense.notes)}</div>
        </Card>
      )}

      <div style={{ height: 16 }} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// EXPENSE FORM MODAL — create + edit
// ────────────────────────────────────────────────────────────────────────────
function ExpenseFormModal({ expense, isEdit, categories, onClose, onSaved, isMobile }) {
  const today = new Date().toISOString().slice(0, 10);
  const [description, setDescription] = useState(dh(expense?.description) || "");
  const [amount, setAmount] = useState(expense?.amount ?? "");
  const [expenseDate, setExpenseDate] = useState(expense?.expense_date?.slice(0, 10) || today);
  const [categoryId, setCategoryId] = useState(expense?.category_id || 0);
  const [paymentStatus, setPaymentStatus] = useState(expense?.payment_status || "pending");
  const [paymentMethod, setPaymentMethod] = useState(expense?.payment_method || "");
  const [paymentDate, setPaymentDate] = useState(expense?.payment_date?.slice(0, 10) || "");
  const [notes, setNotes] = useState(dh(expense?.notes) || "");

  const [includeInVat, setIncludeInVat] = useState(!!expense?.include_in_vat);
  const [vatRate, setVatRate] = useState(expense?.vat_rate?.toString() || "21");
  const [vatMonth, setVatMonth] = useState(expense?.vat_month || expenseDate.slice(0, 7));

  // Auto-sync vat_month with expense_date for new expenses
  useEffect(() => { if (!isEdit) setVatMonth(expenseDate.slice(0, 7)); }, [expenseDate, isEdit]);

  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  // Live VAT amount preview
  const computedVatAmount = (() => {
    if (!includeInVat) return 0;
    const a = parseNum(amount);
    const r = parseNum(vatRate);
    if (a <= 0 || r <= 0) return 0;
    return Math.round((a - a / (1 + r / 100)) * 100) / 100;
  })();

  const submit = async () => {
    const e = {};
    if (!description.trim()) e.description = "Opis je obavezan";
    if (!amount || parseNum(amount) <= 0) e.amount = "Iznos je obavezan";
    if (!categoryId) e.categoryId = "Izaberite kategoriju";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setBusy(true);
    const body = {
      description,
      amount: parseNum(amount),
      expense_date: expenseDate,
      category_id: categoryId,
      payment_status: paymentStatus,
      payment_method: paymentMethod,
      payment_date: paymentDate || null,
      notes,
      include_in_vat: includeInVat ? 1 : 0,
      vat_rate: includeInVat ? parseNum(vatRate) : null,
      vat_month: includeInVat ? vatMonth : null,
    };
    const url = isEdit ? `/b2b/expense-update/${expense.id}` : `/b2b/expense-store`;
    const r = await api(url, { method: "POST", body: JSON.stringify(body) });
    setBusy(false);
    if (r.success) onSaved(r.data?.id || expense?.id);
    else {
      setToast({ type: "error", message: r.message || "Greška" });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const modalStyle = isMobile
    ? { position: "fixed", inset: 0, background: S.bg, zIndex: 140, display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease-out" }
    : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 140, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  const contentStyle = isMobile
    ? { background: S.bg, flex: 1, display: "flex", flexDirection: "column" }
    : { background: S.card, borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" };

  // Group categories by daily/monthly for the dropdown
  const dailyCats = categories.filter((c) => c.category_group === "daily");
  const monthlyCats = categories.filter((c) => c.category_group === "monthly");

  return (
    <div onClick={!isMobile && !busy ? onClose : undefined} style={modalStyle}>
      <div onClick={(e) => e.stopPropagation()} style={contentStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card, borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} disabled={busy} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted, borderRadius: 6 }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
            {isEdit ? "Uredi trošak" : "Novi trošak"}
          </div>
          <button onClick={submit} disabled={busy} style={{ ...S.btnSm(S.dark, "white"), padding: "10px 18px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />}
            {busy ? "Čuvam…" : "Sačuvaj"}
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 14 : 24 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Card title="Osnovni podaci">
              <Field label="Opis" required error={errors.description}>
                <input
                  value={description} onChange={(e) => setDescription(e.target.value)}
                  placeholder="npr. Kupovina hrane"
                  style={{ ...S.input, borderColor: errors.description ? S.red : S.border }}
                />
              </Field>
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr", marginTop: 10 }}>
                <Field label="Iznos (€)" required error={errors.amount}>
                  <input
                    type="text" inputMode="decimal"
                    value={amount} onChange={(e) => setAmount(e.target.value)}
                    placeholder="0,00"
                    style={{ ...S.input, fontFamily: S.mono, borderColor: errors.amount ? S.red : S.border }}
                  />
                </Field>
                <Field label="Datum">
                  <input type="date" value={expenseDate} onChange={(e) => setExpenseDate(e.target.value)} style={S.input} />
                </Field>
              </div>
              <div style={{ marginTop: 10 }}>
                <Field label="Kategorija" required error={errors.categoryId}>
                  <select
                    value={String(categoryId || "")}
                    onChange={(e) => setCategoryId(parseInt(e.target.value, 10) || 0)}
                    style={{ ...S.input, cursor: "pointer", borderColor: errors.categoryId ? S.red : S.border }}
                  >
                    <option value="">— Izaberi —</option>
                    {dailyCats.length > 0 && (
                      <optgroup label="Dnevni troškovi">
                        {dailyCats.map((c) => (
                          <option key={c.id} value={c.id}>{dh(c.name)}</option>
                        ))}
                      </optgroup>
                    )}
                    {monthlyCats.length > 0 && (
                      <optgroup label="Mjesečni troškovi">
                        {monthlyCats.map((c) => (
                          <option key={c.id} value={c.id}>{dh(c.name)}</option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </Field>
              </div>
            </Card>

            <Card title="Plaćanje">
              <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
                <Field label="Status plaćanja">
                  <Select value={paymentStatus} onChange={setPaymentStatus} options={EXPENSE_STATUS_OPTIONS.filter((o) => o.id !== "all")} icon={CircleCheck} />
                </Field>
                <Field label="Način plaćanja">
                  <Select value={paymentMethod} onChange={setPaymentMethod} options={PAY_METHOD_OPTIONS_EXP} icon={Banknote} />
                </Field>
              </div>
              {paymentStatus === "paid" && (
                <div style={{ marginTop: 10 }}>
                  <Field label="Datum plaćanja">
                    <input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} style={S.input} />
                  </Field>
                </div>
              )}
            </Card>

            <Card title="PDV (opciono)">
              <label style={{
                display: "flex", alignItems: "center", gap: 8,
                cursor: "pointer", fontSize: 13, color: S.dark, marginBottom: 10,
              }}>
                <input
                  type="checkbox" checked={includeInVat}
                  onChange={(e) => setIncludeInVat(e.target.checked)}
                  style={{ width: 16, height: 16, accentColor: S.accent }}
                />
                Uključi u PDV evidenciju
              </label>
              {includeInVat && (
                <>
                  <div style={{ display: "grid", gap: 10, gridTemplateColumns: isMobile ? "1fr 1fr" : "1fr 1fr 1fr" }}>
                    <Field label="Stopa PDV">
                      <Select value={vatRate} onChange={setVatRate} options={EXP_VAT_RATES} icon={ChevronsUpDown} />
                    </Field>
                    <Field label="Mjesec PDV">
                      <input
                        type="month" value={vatMonth}
                        onChange={(e) => setVatMonth(e.target.value)}
                        style={{ ...S.input, fontFamily: S.mono }}
                      />
                    </Field>
                    <Field label="Iznos PDV-a">
                      <div style={{
                        ...S.input, fontFamily: S.mono, color: S.accent, fontWeight: 600,
                        background: S.accentSoft, borderColor: S.accent,
                        display: "flex", alignItems: "center",
                      }}>
                        {fmtMoney(computedVatAmount)}
                      </div>
                    </Field>
                  </div>
                  <div style={{ marginTop: 8, fontSize: 11, color: S.muted, fontStyle: "italic" }}>
                    Iznos PDV-a se automatski računa iz bruto iznosa. Mjesec PDV-a je period u kojem se trošak računa u izvještaju.
                  </div>
                </>
              )}
            </Card>

            <Card title="Napomena">
              <textarea
                value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                placeholder="Opciono…"
                style={{ ...S.input, resize: "vertical", minHeight: 50, fontFamily: S.font }}
              />
            </Card>
          </div>
        </div>

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// EXPENSES TABLE (Pivot) — matches PHP webapp's "Tabelarni pregled" layout
// ════════════════════════════════════════════════════════════════════════════
const MONTH_NAMES_SR_SHORT = ["Jan","Feb","Mar","Apr","Maj","Jun","Jul","Avg","Sep","Okt","Nov","Dec"];
const MONTH_NAMES_SR_FULL  = ["Januar","Februar","Mart","April","Maj","Jun","Jul","Avgust","Septembar","Oktobar","Novembar","Decembar"];

function ViewModeBtn({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: "flex", alignItems: "center", gap: 6,
        padding: "8px 14px",
        background: active ? S.dark : "transparent",
        color: active ? "white" : S.muted,
        border: "none", borderRadius: 7,
        fontSize: 13, fontWeight: 600, cursor: "pointer",
        fontFamily: S.font, transition: "all 0.15s",
      }}
    >
      <Icon size={14} />
      {label}
    </button>
  );
}

function ExpensesTableView({ isMobile, canWrite, categories, onOpenExpense, onCreateExpense, onAfterChange }) {
  const now = new Date();
  const [year, setYear]   = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cellModal, setCellModal] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      const r = await api(`/b2b/expenses-table?year=${year}&month=${month}`);
      if (!active) return;
      setLoading(false);
      if (r.success) setData(r.data);
      else setError(r.message || "Greška");
    })();
    return () => { active = false; };
  }, [year, month, refreshKey]);

  const refreshAll = () => setRefreshKey((k) => k + 1);

  const handlePrevMonth = () => {
    let m = month - 1, y = year;
    if (m < 1) { m = 12; y--; }
    setMonth(m); setYear(y);
  };
  const handleNextMonth = () => {
    let m = month + 1, y = year;
    if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };
  const handlePrevYear = () => setYear(year - 1);
  const handleNextYear = () => setYear(year + 1);

  const goToday = () => {
    const n = new Date();
    setYear(n.getFullYear());
    setMonth(n.getMonth() + 1);
  };

  if (loading && !data) {
    return (
      <div style={{ background: S.card, borderRadius: 12, padding: 24, border: `1px solid ${S.border}` }}>
        <Skeleton lines={8} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
        padding: "14px 18px", display: "flex", alignItems: "center", gap: 8, color: S.red,
      }}>
        <AlertCircle size={16} /> {error}
      </div>
    );
  }

  if (!data) return null;

  const openCellModal = (categoryId, categoryName, categoryColor, dateFrom, dateTo, label) => {
    setCellModal({ categoryId, categoryName, categoryColor, dateFrom, dateTo, label });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>
      {/* ── Daily section ──────────────────────────────────────────────── */}
      <div>
        <div style={{
          background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
          padding: isMobile ? 12 : 16, marginBottom: 12,
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button onClick={handlePrevMonth} style={navBtnStyle}>
              <ChevronLeft size={16} />
            </button>
            <button onClick={goToday} style={{
              ...navBtnStyle, padding: "8px 14px", fontWeight: 600,
              minWidth: isMobile ? 130 : 180, fontSize: 14,
            }}>
              {MONTH_NAMES_SR_FULL[month - 1]} {year}
            </button>
            <button onClick={handleNextMonth} style={navBtnStyle}>
              <ChevronRight size={16} />
            </button>
          </div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 15 : 17, color: S.dark, fontWeight: 700, flex: 1 }}>
            <Calendar size={isMobile ? 14 : 16} style={{ display: "inline", verticalAlign: "middle", marginRight: 6, color: S.muted }} />
            Dnevni troškovi
          </h2>
          <div style={{
            background: S.accentSoft, color: S.accent,
            padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700,
            fontFamily: S.mono,
          }}>
            Σ {fmtMoney(data.daily.grand_total)}
          </div>
        </div>

        <PivotTable
          categories={data.daily.categories}
          cells={data.daily.cells}
          rowTotals={data.daily.cat_totals}
          colTotals={data.daily.day_totals}
          grandTotal={data.daily.grand_total}
          columnsCount={data.days_in_month}
          columnLabel={(n) => String(n)}
          buildCellRange={(catId, day) => {
            const dt = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            return { from: dt, to: dt };
          }}
          onCellClick={(cat, day) => {
            const dt = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
            openCellModal(cat.id, cat.name, cat.color, dt, dt, `${day}.${month}.${year}.`);
          }}
          emptyLabel={canWrite ? "+" : "—"}
          isMobile={isMobile}
        />
      </div>

      {/* ── Monthly section ─────────────────────────────────────────────── */}
      <div>
        <div style={{
          background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
          padding: isMobile ? 12 : 16, marginBottom: 12,
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <button onClick={handlePrevYear} style={navBtnStyle}>
              <ChevronLeft size={16} />
            </button>
            <div style={{
              ...navBtnStyle, padding: "8px 14px", fontWeight: 600,
              minWidth: 80, fontSize: 14, cursor: "default",
              textAlign: "center",
            }}>
              {year}
            </div>
            <button onClick={handleNextYear} style={navBtnStyle}>
              <ChevronRight size={16} />
            </button>
          </div>
          <h2 style={{ margin: 0, fontSize: isMobile ? 15 : 17, color: S.dark, fontWeight: 700, flex: 1 }}>
            <Calendar size={isMobile ? 14 : 16} style={{ display: "inline", verticalAlign: "middle", marginRight: 6, color: S.muted }} />
            Mjesečni troškovi
          </h2>
          <div style={{
            background: S.accentSoft, color: S.accent,
            padding: "6px 12px", borderRadius: 8, fontSize: 13, fontWeight: 700,
            fontFamily: S.mono,
          }}>
            Σ {fmtMoney(data.monthly.grand_total)}
          </div>
        </div>

        <PivotTable
          categories={data.monthly.categories}
          cells={data.monthly.cells}
          rowTotals={data.monthly.cat_totals}
          colTotals={data.monthly.month_totals}
          grandTotal={data.monthly.grand_total}
          columnsCount={12}
          columnLabel={(n) => MONTH_NAMES_SR_SHORT[n - 1]}
          buildCellRange={(catId, mo) => {
            const lastDay = new Date(year, mo, 0).getDate();
            return {
              from: `${year}-${String(mo).padStart(2, "0")}-01`,
              to:   `${year}-${String(mo).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`,
            };
          }}
          onCellClick={(cat, mo) => {
            const lastDay = new Date(year, mo, 0).getDate();
            const from = `${year}-${String(mo).padStart(2, "0")}-01`;
            const to   = `${year}-${String(mo).padStart(2, "0")}-${String(lastDay).padStart(2, "0")}`;
            openCellModal(cat.id, cat.name, cat.color, from, to, `${MONTH_NAMES_SR_FULL[mo - 1]} ${year}`);
          }}
          emptyLabel={canWrite ? "+" : "—"}
          isMobile={isMobile}
        />
      </div>

      {/* Quick add button (visible only if there are categories) */}
      {canWrite && categories.length > 0 && (
        <div style={{ display: "flex", justifyContent: "center", marginTop: 4 }}>
          <button
            onClick={onCreateExpense}
            style={{ ...S.btn(S.accent), padding: "12px 20px", fontSize: 14 }}
          >
            <Plus size={16} /> Novi trošak
          </button>
        </div>
      )}

      {cellModal && (
        <CellExpensesModal
          isMobile={isMobile}
          canWrite={canWrite}
          categoryId={cellModal.categoryId}
          categoryName={cellModal.categoryName}
          categoryColor={cellModal.categoryColor}
          dateFrom={cellModal.dateFrom}
          dateTo={cellModal.dateTo}
          label={cellModal.label}
          onClose={() => setCellModal(null)}
          onSelectExpense={(id) => { setCellModal(null); onOpenExpense(id); }}
          onAddNew={() => { setCellModal(null); onCreateExpense(); }}
          onAfterChange={() => { refreshAll(); onAfterChange(); }}
        />
      )}
    </div>
  );
}

const navBtnStyle = {
  width: 36, height: 36, border: `1px solid ${S.border}`,
  background: "white", borderRadius: 7, cursor: "pointer",
  color: S.dark, display: "inline-flex", alignItems: "center", justifyContent: "center",
  fontFamily: S.font,
};

function PivotTable({ categories, cells, rowTotals, colTotals, grandTotal, columnsCount, columnLabel, buildCellRange, onCellClick, emptyLabel, isMobile }) {
  if (categories.length === 0) {
    return (
      <div style={{
        background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
        padding: 30, textAlign: "center", color: S.mutedLight, fontSize: 14,
      }}>
        Nema kategorija u ovoj grupi
      </div>
    );
  }

  // Responsive: smaller cells on narrower screens
  const colW = columnsCount > 15 ? 38 : 56;
  const rowH = 44;

  return (
    <div style={{
      background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
      overflow: "auto", maxWidth: "100%",
    }}>
      <table style={{
        borderCollapse: "separate", borderSpacing: 0,
        width: "100%", minWidth: 160 + columnsCount * colW + 80,
        fontSize: 12, fontFamily: S.font,
      }}>
        <thead>
          <tr>
            <th style={{
              ...pivotHeadStyle,
              position: "sticky", left: 0, zIndex: 3, background: "#fafafb",
              textAlign: "left", paddingLeft: 12, width: 160, minWidth: 160,
            }}>
              Kategorija
            </th>
            {Array.from({ length: columnsCount }, (_, i) => i + 1).map((c) => (
              <th key={c} style={{ ...pivotHeadStyle, minWidth: colW, width: colW }}>
                {columnLabel(c)}
              </th>
            ))}
            <th style={{
              ...pivotHeadStyle,
              background: S.dark, color: "white", minWidth: 78,
              position: "sticky", right: 0, zIndex: 2,
            }}>
              Ukupno
            </th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat) => {
            const catTotal = rowTotals[cat.id] || 0;
            return (
              <tr key={cat.id} style={{ height: rowH }}>
                <td style={{
                  ...pivotCellStyle,
                  background: cat.color ? `${cat.color}22` : "#fafafb",
                  borderLeft: `4px solid ${cat.color || S.border}`,
                  fontWeight: 600, color: S.dark, textAlign: "left", paddingLeft: 10,
                  position: "sticky", left: 0, zIndex: 1,
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                  {dh(cat.name)}
                </td>
                {Array.from({ length: columnsCount }, (_, i) => i + 1).map((col) => {
                  const cell = cells[`${cat.id}-${col}`];
                  const hasData = cell && cell.total > 0;
                  return (
                    <td
                      key={col}
                      onClick={() => onCellClick(cat, col)}
                      style={{
                        ...pivotCellStyle,
                        cursor: "pointer",
                        background: hasData ? `${cat.color || S.accent}11` : "white",
                        fontWeight: hasData ? 600 : 400,
                        color: hasData ? S.dark : S.mutedLight,
                        transition: "background 0.1s",
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.background = hasData ? `${cat.color || S.accent}33` : "#f3f4f6"}
                      onMouseLeave={(e) => e.currentTarget.style.background = hasData ? `${cat.color || S.accent}11` : "white"}
                    >
                      {hasData ? (
                        <div style={{ fontFamily: S.mono, fontSize: 11, lineHeight: 1.2 }}>
                          <div>{fmtMoneyCompact(cell.total)}</div>
                          {cell.count > 1 && (
                            <div style={{ fontSize: 9, color: S.muted, marginTop: 1 }}>×{cell.count}</div>
                          )}
                        </div>
                      ) : (
                        <span style={{ fontSize: 14, color: S.mutedLight, opacity: 0.6 }}>{emptyLabel}</span>
                      )}
                    </td>
                  );
                })}
                <td style={{
                  ...pivotCellStyle,
                  background: S.accentSoft, color: S.accent,
                  fontWeight: 700, fontFamily: S.mono, fontSize: 12,
                  position: "sticky", right: 0, zIndex: 1,
                }}>
                  {catTotal > 0 ? fmtMoneyCompact(catTotal) : "—"}
                </td>
              </tr>
            );
          })}

          {/* Totals row */}
          <tr style={{ background: S.dark, color: "white" }}>
            <td style={{
              ...pivotCellStyle,
              background: S.dark, color: "white",
              fontWeight: 700, textAlign: "left", paddingLeft: 12,
              position: "sticky", left: 0, zIndex: 1,
            }}>
              Σ Ukupno
            </td>
            {Array.from({ length: columnsCount }, (_, i) => i + 1).map((col) => (
              <td key={col} style={{
                ...pivotCellStyle,
                background: S.dark, color: "white",
                fontFamily: S.mono, fontSize: 11, fontWeight: 600,
              }}>
                {colTotals[col] > 0 ? fmtMoneyCompact(colTotals[col]) : "—"}
              </td>
            ))}
            <td style={{
              ...pivotCellStyle,
              background: "#000", color: "white",
              fontFamily: S.mono, fontSize: 13, fontWeight: 700,
              position: "sticky", right: 0, zIndex: 1,
            }}>
              {fmtMoneyCompact(grandTotal)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

const pivotHeadStyle = {
  padding: "10px 6px", fontSize: 11, fontWeight: 700, color: S.muted,
  borderBottom: `1px solid ${S.border}`, background: "#fafafb",
  textAlign: "center", textTransform: "uppercase", letterSpacing: 0.3,
};
const pivotCellStyle = {
  padding: "4px 6px", borderBottom: `1px solid ${S.borderSoft}`,
  textAlign: "center", verticalAlign: "middle",
};

function fmtMoneyCompact(amount) {
  const n = Number(amount) || 0;
  if (n >= 10000) return `${(n / 1000).toFixed(1)}k`;
  return `${n.toFixed(2)}`;
}

// ────────────────────────────────────────────────────────────────────────────
// CELL EXPENSES MODAL — shown when user clicks a pivot cell
// ────────────────────────────────────────────────────────────────────────────
function CellExpensesModal({
  isMobile, canWrite,
  categoryId, categoryName, categoryColor,
  dateFrom, dateTo, label,
  onClose, onSelectExpense, onAddNew,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      setError("");
      const qs = new URLSearchParams({
        category_id: String(categoryId),
        date_from: dateFrom, date_to: dateTo,
      });
      const r = await api(`/b2b/expenses-for-cell?${qs.toString()}`);
      if (!active) return;
      setLoading(false);
      if (r.success) setItems(Array.isArray(r.data?.items) ? r.data.items : []);
      else setError(r.message || "Greška");
    })();
    return () => { active = false; };
  }, [categoryId, dateFrom, dateTo]);

  const total = items.reduce((sum, it) => sum + parseNum(it.amount), 0);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 150,
        display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: S.card, borderRadius: 14, width: "100%", maxWidth: 600,
          maxHeight: "90vh", display: "flex", flexDirection: "column",
          overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.2)",
        }}
      >
        <div style={{
          padding: "16px 20px", borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <div style={{
            width: 8, height: 36, background: categoryColor || S.accent, borderRadius: 4,
          }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: S.dark }}>
              {dh(categoryName)}
            </div>
            <div style={{ fontSize: 12, color: S.muted }}>{label}</div>
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}>
            <X size={20} />
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: 16 }}>
          {loading ? (
            <Skeleton lines={4} />
          ) : error ? (
            <div style={{
              background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
              padding: "12px 16px", color: S.red, fontSize: 13,
              display: "flex", alignItems: "center", gap: 8,
            }}>
              <AlertCircle size={14} /> {error}
            </div>
          ) : items.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: S.mutedLight, fontSize: 14 }}>
              <Wallet size={32} color={S.mutedLight} />
              <div style={{ marginTop: 10 }}>Nema troškova u ovom periodu</div>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {items.map((it) => (
                <div
                  key={it.id}
                  onClick={() => onSelectExpense(it.id)}
                  style={{
                    padding: 12, border: `1px solid ${S.border}`, borderRadius: 8,
                    cursor: "pointer", transition: "background 0.1s",
                    display: "flex", alignItems: "center", gap: 10,
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "#fafafb"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {dh(it.description)}
                    </div>
                    <div style={{ fontSize: 11, color: S.muted, marginTop: 2 }}>
                      {fmtDate(it.expense_date)}
                      {it.include_in_vat && (
                        <span style={{ marginLeft: 8, color: S.accent, fontFamily: S.mono }}>
                          · {it.vat_rate}% PDV
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    <div style={{ fontSize: 14, fontFamily: S.mono, fontWeight: 700, color: S.dark }}>
                      {fmtMoney(it.amount)}
                    </div>
                    <div style={{ marginTop: 4 }}>
                      <ExpenseStatusBadge status={it.payment_status} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{
          padding: "12px 20px", borderTop: `1px solid ${S.border}`, background: "#fafafb",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8,
        }}>
          <div style={{ fontSize: 13, color: S.muted }}>
            Ukupno: <strong style={{ fontFamily: S.mono, color: S.dark }}>{fmtMoney(total)}</strong>
          </div>
          {canWrite && (
            <button onClick={onAddNew} style={{ ...S.btnSm(S.accent), padding: "9px 14px" }}>
              <Plus size={14} /> Dodaj trošak
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// REPORTS (Izvještaji)
// 4 reports: sales, vat, partners, products
// ════════════════════════════════════════════════════════════════════════════
const REPORT_TABS = [
  { id: "sales",    label: "Prodaja",       icon: Receipt },
  { id: "vat",      label: "PDV evidencija", icon: TrendingUp },
  { id: "partners", label: "Po partnerima", icon: Users },
  { id: "products", label: "Po proizvodima", icon: Package },
];

function ReportsScreen({ isMobile }) {
  const [tab, setTab] = useState("sales");
  // Period (shared between sales/partners/products; vat uses its own year/month)
  const [datePreset, setPreset] = useState("month");
  // For VAT report — year+month
  const now = new Date();
  const [vatYear, setVatYear]   = useState(now.getFullYear());
  const [vatMonth, setVatMonth] = useState(now.getMonth() + 1);

  return (
    <>
      <TopHeader title="Izvještaji" isMobile={isMobile} />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1600 }}>
        {/* Tabs */}
        <div style={{
          display: "flex", gap: 4,
          background: S.card, border: `1px solid ${S.border}`,
          borderRadius: 10, padding: 4, marginBottom: 14,
          overflowX: "auto",
        }} className="hide-scrollbar">
          {REPORT_TABS.map((t) => (
            <ViewModeBtn
              key={t.id}
              active={tab === t.id}
              onClick={() => setTab(t.id)}
              icon={t.icon}
              label={t.label}
            />
          ))}
        </div>

        {/* Period selector (shown for non-VAT reports) */}
        {tab !== "vat" && (
          <div style={{
            background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
            padding: 14, marginBottom: 14, display: "flex", alignItems: "center", gap: 12,
          }}>
            <Calendar size={16} color={S.muted} />
            <div style={{ fontSize: 13, color: S.muted, marginRight: 4 }}>Period:</div>
            <div style={{ flex: 1 }}>
              <Select value={datePreset} onChange={setPreset} options={DATE_RANGES} icon={Calendar} />
            </div>
          </div>
        )}

        {tab === "sales"    && <SalesReport    isMobile={isMobile} datePreset={datePreset} />}
        {tab === "vat"      && <VatReport      isMobile={isMobile} year={vatYear} month={vatMonth} setYear={setVatYear} setMonth={setVatMonth} />}
        {tab === "partners" && <PartnersReport isMobile={isMobile} datePreset={datePreset} />}
        {tab === "products" && <ProductsReport isMobile={isMobile} datePreset={datePreset} />}

        {isMobile && <div style={{ height: 80 }} />}
      </div>
    </>
  );
}

// ── SALES REPORT ─────────────────────────────────────────────────────────────
function SalesReport({ isMobile, datePreset }) {
  const [invType, setInvType] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError("");
      const { from, to } = resolvePresetRange(datePreset);
      const qs = new URLSearchParams();
      if (from) qs.set("date_from", from);
      if (to) qs.set("date_to", to);
      if (invType !== "all") qs.set("invoice_type", invType);
      const r = await api(`/b2b/report-sales?${qs.toString()}`);
      if (!active) return;
      setLoading(false);
      if (r.success) setData(r.data); else setError(r.message || "Greška");
    })();
    return () => { active = false; };
  }, [datePreset, invType]);

  if (loading && !data) return <Skeleton lines={10} />;
  if (error) return <ReportError msg={error} />;
  if (!data) return null;

  const t = data.totals || {};
  const { from: dfrom, to: dto } = resolvePresetRange(datePreset);
  const periodStr = (dfrom || dto) ? `${(dfrom || "").replace(/-/g, "")}_${(dto || "").replace(/-/g, "")}` : "all";
  const exportParams = new URLSearchParams();
  if (dfrom) exportParams.set("date_from", dfrom);
  if (dto)   exportParams.set("date_to",   dto);
  if (invType !== "all") exportParams.set("invoice_type", invType);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
        padding: 14, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center",
      }}>
        <FilterChip active={invType === "all"}      onClick={() => setInvType("all")}      label="Svi" />
        <FilterChip active={invType === "cash"}     onClick={() => setInvType("cash")}     label="Gotovinski" />
        <FilterChip active={invType === "non_cash"} onClick={() => setInvType("non_cash")} label="Virmani" />
        <div style={{ flex: 1 }} />
        <ExportButtons
          label="KIF"
          downloads={[
            { kind: "pdf", path: `/b2b/export-sales-pdf?${exportParams.toString()}`,
              filename: `KIF_${periodStr}.pdf` },
            { kind: "csv", path: `/b2b/export-sales-csv?${exportParams.toString()}`,
              filename: `KIF_${periodStr}.csv` },
          ]}
          isMobile={isMobile}
        />
      </div>

      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(160px, 1fr))",
      }}>
        <SummaryCard icon={Receipt}    color={S.dark}  label="Računa" value={fmtNum(t.count || 0)} />
        <SummaryCard icon={Euro}       color={S.dark}  label="Osnovica" value={fmtMoney(t.net || 0)} />
        <SummaryCard icon={TrendingUp} color={S.accent} label="PDV"     value={fmtMoney(t.vat || 0)} />
        <SummaryCard icon={Euro}       color={S.green} label="Ukupno"   value={fmtMoney(t.gross || 0)} />
      </div>

      {/* VAT breakdown by rate */}
      {data.vat_by_rate && data.vat_by_rate.length > 0 && (
        <Card title="PDV po stopama">
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 420 }}>
              <thead style={{ background: "#fafafb" }}>
                <tr>
                  <th style={th}>Stopa</th>
                  <th style={{ ...th, textAlign: "right" }}>Osnovica</th>
                  <th style={{ ...th, textAlign: "right" }}>PDV</th>
                  <th style={{ ...th, textAlign: "right" }}>Stavki</th>
                </tr>
              </thead>
              <tbody>
                {data.vat_by_rate.map((v) => (
                  <tr key={v.rate} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={{ ...td, fontFamily: S.mono, fontWeight: 600 }}>{v.rate}%</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtMoney(v.base)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>{fmtMoney(v.vat)}</td>
                    <td style={{ ...td, textAlign: "right", color: S.muted, fontFamily: S.mono }}>{v.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Invoice list */}
      <Card title="Računi" subtitle={`${fmtNum(t.count || 0)} · ${t.cash_count || 0} gotovinski · ${t.non_cash_count || 0} virmana`}>
        {data.invoices.length === 0 ? (
          <Empty text="Nema računa u izabranom periodu" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 700 }}>
              <thead style={{ background: "#fafafb" }}>
                <tr>
                  <th style={th}>Broj</th>
                  <th style={th}>Datum</th>
                  <th style={th}>Kupac</th>
                  <th style={th}>Tip</th>
                  <th style={{ ...th, textAlign: "right" }}>Osnovica</th>
                  <th style={{ ...th, textAlign: "right" }}>PDV</th>
                  <th style={{ ...th, textAlign: "right" }}>Ukupno</th>
                </tr>
              </thead>
              <tbody>
                {data.invoices.map((i) => (
                  <tr key={i.id} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={{ ...td, fontFamily: S.mono, fontWeight: 600 }}>{i.display_no}</td>
                    <td style={{ ...td, color: S.muted, whiteSpace: "nowrap" }}>{fmtDate(i.invoice_date)}</td>
                    <td style={td}>{dh(i.partner_name || i.buyer_name) || <span style={{ color: S.mutedLight }}>—</span>}</td>
                    <td style={{ ...td, color: S.muted, fontSize: 11 }}>
                      <span style={{
                        background: i.is_cash ? "#fef3c7" : "#dbeafe",
                        color: i.is_cash ? "#92400e" : "#1e40af",
                        padding: "2px 8px", borderRadius: 10, fontWeight: 600,
                      }}>
                        {i.is_cash ? "Gotovinski" : "Virman"}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtMoney(i.subtotal)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtMoney(i.vat_amount)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700 }}>{fmtMoney(i.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot style={{ background: "#fafafb", fontWeight: 700 }}>
                <tr>
                  <td colSpan={4} style={{ ...td, fontWeight: 700 }}>Σ Ukupno ({t.count})</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700 }}>{fmtMoney(t.net)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700, color: S.accent }}>{fmtMoney(t.vat)}</td>
                  <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700, color: S.dark, fontSize: 14 }}>{fmtMoney(t.gross)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── VAT REPORT (PDV evidencija) ──────────────────────────────────────────────
function VatReport({ isMobile, year, month, setYear, setMonth }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError("");
      const r = await api(`/b2b/report-vat?year=${year}&month=${month}`);
      if (!active) return;
      setLoading(false);
      if (r.success) setData(r.data); else setError(r.message || "Greška");
    })();
    return () => { active = false; };
  }, [year, month]);

  if (loading && !data) return <Skeleton lines={10} />;
  if (error) return <ReportError msg={error} />;
  if (!data) return null;

  const navPrev = () => {
    let m = month - 1, y = year;
    if (m < 1) { m = 12; y--; }
    setMonth(m); setYear(y);
  };
  const navNext = () => {
    let m = month + 1, y = year;
    if (m > 12) { m = 1; y++; }
    setMonth(m); setYear(y);
  };

  const netVat = data.net_vat || 0;
  const netColor = netVat > 0.005 ? S.red : (netVat < -0.005 ? S.green : S.muted);
  const netLabel = netVat > 0.005 ? "PDV za uplatu" : (netVat < -0.005 ? "Povraćaj PDV-a" : "Neutralno");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Period nav */}
      <div style={{
        background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
        padding: 14, display: "flex", alignItems: "center", gap: 8,
      }}>
        <button onClick={navPrev} style={navBtnStyle}><ChevronLeft size={16} /></button>
        <div style={{
          ...navBtnStyle, padding: "8px 14px", fontWeight: 700,
          minWidth: 180, fontSize: 14, cursor: "default", textAlign: "center",
        }}>
          {MONTH_NAMES_SR_FULL[month - 1]} {year}
        </div>
        <button onClick={navNext} style={navBtnStyle}><ChevronRight size={16} /></button>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 12, color: S.muted, fontFamily: S.mono }}>
          {data.period?.from} → {data.period?.to}
        </div>
      </div>

      {/* KNJIGA exports */}
      <div style={{
        background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
        padding: 14, display: "flex", flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center", gap: 10, flexWrap: "wrap",
      }}>
        <div style={{ fontSize: 13, color: S.muted, fontWeight: 600 }}>Preuzmi za računovođu:</div>
        <ExportButtons
          label="PDV evidencija"
          downloads={[
            { kind: "pdf", path: `/b2b/export-vat-pdf?year=${year}&month=${month}`,
              filename: `PDV_evidencija_${year}-${String(month).padStart(2, "0")}.pdf` },
          ]}
          isMobile={isMobile}
        />
        <ExportButtons
          label="KUF"
          downloads={[
            { kind: "pdf", path: `/b2b/export-kuf-pdf?year=${year}&month=${month}`,
              filename: `KUF_${year}-${String(month).padStart(2, "0")}.pdf` },
            { kind: "csv", path: `/b2b/export-kuf-csv?year=${year}&month=${month}`,
              filename: `KUF_${year}-${String(month).padStart(2, "0")}.csv` },
          ]}
          isMobile={isMobile}
        />
      </div>

      {/* Net VAT — the headline */}
      <div style={{
        background: netVat > 0.005 ? "#fef2f2" : (netVat < -0.005 ? "#f0fdf4" : "#fafafb"),
        border: `1px solid ${netColor}33`, borderRadius: 12, padding: 20,
        display: "flex", flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center", gap: 12,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: 11, color: S.muted, fontWeight: 600,
            textTransform: "uppercase", letterSpacing: 0.5,
          }}>
            Neto PDV
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: netColor, fontFamily: S.mono, marginTop: 4 }}>
            {fmtMoney(Math.abs(netVat))}
          </div>
          <div style={{ fontSize: 13, color: netColor, fontWeight: 600, marginTop: 2 }}>
            {netLabel}
          </div>
        </div>
        <div style={{ fontSize: 12, color: S.muted, textAlign: isMobile ? "left" : "right", lineHeight: 1.6 }}>
          <div>Izlazni PDV (računi): <strong style={{ fontFamily: S.mono, color: S.dark }}>{fmtMoney(data.output_vat?.total_vat)}</strong></div>
          <div>Ulazni PDV (troškovi): <strong style={{ fontFamily: S.mono, color: S.dark }}>{fmtMoney(data.input_vat?.total_vat)}</strong></div>
        </div>
      </div>

      {/* Output VAT (from sales) */}
      <Card title="Izlazni PDV (od prodaje)" subtitle={`Osnovica: ${fmtMoney(data.output_vat?.total_base || 0)} · PDV: ${fmtMoney(data.output_vat?.total_vat || 0)}`}>
        {!data.output_vat?.by_rate || data.output_vat.by_rate.length === 0 ? (
          <Empty text="Nema prodaje u ovom mjesecu" />
        ) : (
          <VatRateTable rows={data.output_vat.by_rate} extraColKey="invoice_count" extraColLabel="Računa" totalBase={data.output_vat.total_base} totalVat={data.output_vat.total_vat} />
        )}
      </Card>

      {/* Input VAT (from expenses) */}
      <Card title="Ulazni PDV (od troškova)" subtitle={`Osnovica: ${fmtMoney(data.input_vat?.total_base || 0)} · PDV: ${fmtMoney(data.input_vat?.total_vat || 0)}`}>
        {!data.input_vat?.by_rate || data.input_vat.by_rate.length === 0 ? (
          <Empty text="Nema troškova sa PDV-om u ovom mjesecu" />
        ) : (
          <VatRateTable rows={data.input_vat.by_rate} extraColKey="expense_count" extraColLabel="Troškova" totalBase={data.input_vat.total_base} totalVat={data.input_vat.total_vat} />
        )}
      </Card>
    </div>
  );
}

function VatRateTable({ rows, extraColKey, extraColLabel, totalBase, totalVat }) {
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 420 }}>
        <thead style={{ background: "#fafafb" }}>
          <tr>
            <th style={th}>Stopa</th>
            <th style={{ ...th, textAlign: "right" }}>Osnovica</th>
            <th style={{ ...th, textAlign: "right" }}>PDV</th>
            <th style={{ ...th, textAlign: "right" }}>{extraColLabel}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.rate} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
              <td style={{ ...td, fontFamily: S.mono, fontWeight: 600 }}>{r.rate}%</td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtMoney(r.base)}</td>
              <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600 }}>{fmtMoney(r.vat)}</td>
              <td style={{ ...td, textAlign: "right", color: S.muted, fontFamily: S.mono }}>{r[extraColKey]}</td>
            </tr>
          ))}
        </tbody>
        <tfoot style={{ background: "#fafafb" }}>
          <tr>
            <td style={{ ...td, fontWeight: 700 }}>Σ</td>
            <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700 }}>{fmtMoney(totalBase)}</td>
            <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700, color: S.accent }}>{fmtMoney(totalVat)}</td>
            <td />
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

// ── PARTNERS REPORT ──────────────────────────────────────────────────────────
function PartnersReport({ isMobile, datePreset }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError("");
      const { from, to } = resolvePresetRange(datePreset);
      const qs = new URLSearchParams();
      if (from) qs.set("date_from", from);
      if (to) qs.set("date_to", to);
      const r = await api(`/b2b/report-partners?${qs.toString()}`);
      if (!active) return;
      setLoading(false);
      if (r.success) setData(r.data); else setError(r.message || "Greška");
    })();
    return () => { active = false; };
  }, [datePreset]);

  if (loading && !data) return <Skeleton lines={8} />;
  if (error) return <ReportError msg={error} />;
  if (!data) return null;

  const t = data.totals || {};
  const { from: dfrom, to: dto } = resolvePresetRange(datePreset);
  const periodStr = `${(dfrom || "").replace(/-/g, "")}_${(dto || "").replace(/-/g, "")}`;
  const exportParams = new URLSearchParams();
  if (dfrom) exportParams.set("date_from", dfrom);
  if (dto)   exportParams.set("date_to",   dto);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(160px, 1fr))",
      }}>
        <SummaryCard icon={Users}       color={S.dark}  label="Partneri" value={fmtNum(t.partner_count || 0)} />
        <SummaryCard icon={Euro}        color={S.green} label="Ukupno"   value={fmtMoney(t.total_sales || 0)} />
        <SummaryCard icon={AlertCircle} color={S.orange} label="Neplaćeno" value={fmtMoney(t.total_outstanding || 0)} />
      </div>

      <div style={{
        background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
        padding: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      }}>
        <div style={{ fontSize: 13, color: S.muted, fontWeight: 600, flex: 1 }}>
          Eksport
        </div>
        <ExportButtons
          label="Partneri"
          downloads={[
            { kind: "csv", path: `/b2b/export-partners-csv?${exportParams.toString()}`,
              filename: `Partneri_${periodStr}.csv` },
          ]}
          isMobile={isMobile}
        />
      </div>

      <Card title="Partneri" subtitle={`${fmtNum(t.partner_count || 0)} · ${fmtMoney(t.total_sales || 0)}`}>
        {data.partners.length === 0 ? (
          <Empty text="Nema podataka u izabranom periodu" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 700 }}>
              <thead style={{ background: "#fafafb" }}>
                <tr>
                  <th style={th}>Partner</th>
                  <th style={th}>PIB / ID</th>
                  <th style={{ ...th, textAlign: "right" }}>Računa</th>
                  <th style={{ ...th, textAlign: "right" }}>Prodaja</th>
                  <th style={{ ...th, textAlign: "right" }}>Plaćeno</th>
                  <th style={{ ...th, textAlign: "right" }}>Dug</th>
                </tr>
              </thead>
              <tbody>
                {data.partners.map((p) => (
                  <tr key={p.id} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {dh(p.name)}
                      {p.city && <span style={{ color: S.muted, fontWeight: 400, marginLeft: 6, fontSize: 11 }}>· {dh(p.city)}</span>}
                    </td>
                    <td style={{ ...td, fontFamily: S.mono, fontSize: 11, color: S.muted }}>{p.tax_id || "—"}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{p.invoice_count}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700 }}>{fmtMoney(p.total_sales)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, color: S.green }}>{fmtMoney(p.total_paid)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 600, color: p.total_outstanding > 0 ? S.orange : S.mutedLight }}>
                      {p.total_outstanding > 0 ? fmtMoney(p.total_outstanding) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── PRODUCTS REPORT ──────────────────────────────────────────────────────────
function ProductsReport({ isMobile, datePreset }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true); setError("");
      const { from, to } = resolvePresetRange(datePreset);
      const qs = new URLSearchParams();
      if (from) qs.set("date_from", from);
      if (to) qs.set("date_to", to);
      qs.set("limit", "100");
      const r = await api(`/b2b/report-products?${qs.toString()}`);
      if (!active) return;
      setLoading(false);
      if (r.success) setData(r.data); else setError(r.message || "Greška");
    })();
    return () => { active = false; };
  }, [datePreset]);

  if (loading && !data) return <Skeleton lines={8} />;
  if (error) return <ReportError msg={error} />;
  if (!data) return null;

  const t = data.totals || {};
  const { from: dfrom, to: dto } = resolvePresetRange(datePreset);
  const periodStr = `${(dfrom || "").replace(/-/g, "")}_${(dto || "").replace(/-/g, "")}`;
  const exportParams = new URLSearchParams();
  if (dfrom) exportParams.set("date_from", dfrom);
  if (dto)   exportParams.set("date_to",   dto);
  exportParams.set("limit", "500");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        display: "grid", gap: 10,
        gridTemplateColumns: isMobile ? "1fr 1fr" : "repeat(auto-fit, minmax(160px, 1fr))",
      }}>
        <SummaryCard icon={Package} color={S.dark}  label="Proizvoda"   value={fmtNum(t.product_count || 0)} />
        <SummaryCard icon={Euro}    color={S.green} label="Bruto"        value={fmtMoney(t.total_gross || 0)} />
        <SummaryCard icon={Euro}    color={S.muted} label="Neto"         value={fmtMoney(t.total_net || 0)} />
      </div>

      <div style={{
        background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
        padding: 14, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
      }}>
        <div style={{ fontSize: 13, color: S.muted, fontWeight: 600, flex: 1 }}>
          Eksport
        </div>
        <ExportButtons
          label="Proizvodi"
          downloads={[
            { kind: "csv", path: `/b2b/export-products-csv?${exportParams.toString()}`,
              filename: `Proizvodi_${periodStr}.csv` },
          ]}
          isMobile={isMobile}
        />
      </div>

      <Card title="Top proizvodi" subtitle={`${fmtNum(t.product_count || 0)} stavki · ${fmtMoney(t.total_gross || 0)} bruto`}>
        {data.products.length === 0 ? (
          <Empty text="Nema prodaje u izabranom periodu" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 600 }}>
              <thead style={{ background: "#fafafb" }}>
                <tr>
                  <th style={th}>#</th>
                  <th style={th}>Proizvod</th>
                  <th style={{ ...th, textAlign: "right" }}>Količina</th>
                  <th style={{ ...th, textAlign: "right" }}>Računa</th>
                  <th style={{ ...th, textAlign: "right" }}>Neto</th>
                  <th style={{ ...th, textAlign: "right" }}>Bruto</th>
                </tr>
              </thead>
              <tbody>
                {data.products.map((p, i) => (
                  <tr key={`${p.product_id || "x"}-${i}`} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={{ ...td, color: S.muted, fontFamily: S.mono, fontSize: 11 }}>{i + 1}</td>
                    <td style={{ ...td, fontWeight: 600 }}>
                      {dh(p.name)}
                      {!p.product_id && (
                        <span style={{ marginLeft: 6, fontSize: 10, color: S.mutedLight, fontStyle: "italic" }}>(slobodan unos)</span>
                      )}
                    </td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtNum(p.qty_sold)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, color: S.muted }}>{p.invoice_count}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{fmtMoney(p.net_revenue)}</td>
                    <td style={{ ...td, textAlign: "right", fontFamily: S.mono, fontWeight: 700 }}>{fmtMoney(p.gross_revenue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

// ── Shared helpers ───────────────────────────────────────────────────────────
function ReportError({ msg }) {
  return (
    <div style={{
      background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
      padding: "14px 18px", display: "flex", alignItems: "center", gap: 8, color: S.red,
    }}>
      <AlertCircle size={16} /> {msg}
    </div>
  );
}

function FilterChip({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: "8px 16px",
        background: active ? S.dark : "white",
        color: active ? "white" : S.muted,
        border: `1px solid ${active ? S.dark : S.border}`,
        borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer",
        fontFamily: S.font,
      }}
    >
      {label}
    </button>
  );
}

// ── EXPORT BUTTONS ───────────────────────────────────────────────────────────
// Renders one or more download buttons (PDF/CSV) for a given report.
// Shows loading spinner per-button + toast on error.
function ExportButtons({ label, downloads, isMobile }) {
  const [busyIdx, setBusyIdx] = useState(-1);
  const [toast, setToast] = useState(null);

  const handleDownload = async (idx, dl) => {
    setBusyIdx(idx);
    const r = await downloadExport(dl.path, dl.filename);
    setBusyIdx(-1);
    if (!r.success) {
      setToast({ type: "error", message: r.message || "Greška pri preuzimanju" });
      setTimeout(() => setToast(null), 5000);
    }
  };

  return (
    <>
      <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
        {label && (
          <span style={{
            fontSize: 11, color: S.muted, fontWeight: 700,
            textTransform: "uppercase", letterSpacing: 0.4,
            marginRight: 2,
          }}>
            {label}
          </span>
        )}
        {downloads.map((dl, i) => {
          const isPdf = dl.kind === "pdf";
          return (
            <button
              key={i}
              onClick={() => handleDownload(i, dl)}
              disabled={busyIdx !== -1}
              title={`Preuzmi ${dl.kind.toUpperCase()}`}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6,
                padding: "8px 12px", borderRadius: 7,
                background: isPdf ? S.red : S.green,
                color: "white", border: "none",
                fontSize: 12, fontWeight: 700, cursor: "pointer",
                fontFamily: S.font, letterSpacing: 0.2,
                textTransform: "uppercase",
                opacity: busyIdx !== -1 && busyIdx !== i ? 0.5 : 1,
                transition: "transform 0.1s, opacity 0.15s",
              }}
              onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.97)"}
              onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
            >
              {busyIdx === i ? (
                <Loader2 size={13} className="spin" />
              ) : isPdf ? (
                <FileText size={13} />
              ) : (
                <Download size={13} />
              )}
              {dl.kind}
            </button>
          );
        })}
      </div>
      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// EXPENSE CATEGORIES CRUD
// Tab inside Troškovi screen.
// ════════════════════════════════════════════════════════════════════════════
const CATEGORY_COLOR_PALETTE = [
  "#10b981", "#3b82f6", "#f59e0b", "#ef4444", "#8b5cf6",
  "#6b7280", "#dc2626", "#0891b2", "#059669", "#4f46e5",
  "#ea580c", "#db2777", "#0284c7", "#7c3aed", "#16a34a",
];

function CategoriesView({ isMobile, canWrite, onCategoriesChanged }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filter, setFilter] = useState("all"); // all | daily | monthly | global | own

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const r = await api("/b2b/expense-categories");
    setLoading(false);
    if (r.success) setItems(Array.isArray(r.data?.items) ? r.data.items : []);
    else setError(r.message || "Greška");
  }, []);
  useEffect(() => { load(); }, [load]);

  const showToast = (type, message, ms = 3500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  const filtered = items.filter((c) => {
    if (filter === "daily")   return c.category_group === "daily";
    if (filter === "monthly") return c.category_group === "monthly";
    if (filter === "global")  return c.is_global;
    if (filter === "own")     return !c.is_global;
    return true;
  });

  const dailyCats   = filtered.filter((c) => c.category_group === "daily");
  const monthlyCats = filtered.filter((c) => c.category_group === "monthly");

  const handleSaved = async () => {
    setShowForm(false);
    setEditing(null);
    await load();
    onCategoriesChanged && onCategoriesChanged();
    showToast("success", editing ? "Kategorija ažurirana" : "Kategorija kreirana");
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setBusy(true);
    const r = await api(`/b2b/expense-category-delete/${confirmDelete.id}`, {
      method: "POST", body: JSON.stringify({}),
    });
    setBusy(false);
    setConfirmDelete(null);
    if (r.success) {
      await load();
      onCategoriesChanged && onCategoriesChanged();
      showToast("success", "Kategorija obrisana");
    } else {
      showToast("error", r.message || "Greška pri brisanju", 5000);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
        padding: 14, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
      }}>
        <FilterChip active={filter === "all"}     onClick={() => setFilter("all")}     label="Sve" />
        <FilterChip active={filter === "daily"}   onClick={() => setFilter("daily")}   label="Dnevni" />
        <FilterChip active={filter === "monthly"} onClick={() => setFilter("monthly")} label="Mjesečni" />
        <div style={{ flex: 1 }} />
        {canWrite && (
          <button onClick={() => { setEditing(null); setShowForm(true); }} style={{ ...S.btnSm(S.dark, "white"), padding: "9px 14px" }}>
            <Plus size={14} /> Nova kategorija
          </button>
        )}
      </div>

      {error && (
        <div style={{
          background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
          padding: "14px 18px", display: "flex", alignItems: "center", gap: 8, color: S.red,
        }}>
          <AlertCircle size={16} /> {error}
        </div>
      )}

      {loading ? (
        <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: 24 }}>
          <Skeleton lines={6} />
        </div>
      ) : (
        <>
          {dailyCats.length > 0 && (
            <CategoryGroupSection
              title="Dnevne kategorije"
              subtitle={`${dailyCats.length} kategorija`}
              items={dailyCats}
              canWrite={canWrite}
              onEdit={(c) => { setEditing(c); setShowForm(true); }}
              onDelete={(c) => setConfirmDelete(c)}
              isMobile={isMobile}
            />
          )}
          {monthlyCats.length > 0 && (
            <CategoryGroupSection
              title="Mjesečne kategorije"
              subtitle={`${monthlyCats.length} kategorija`}
              items={monthlyCats}
              canWrite={canWrite}
              onEdit={(c) => { setEditing(c); setShowForm(true); }}
              onDelete={(c) => setConfirmDelete(c)}
              isMobile={isMobile}
            />
          )}
          {dailyCats.length === 0 && monthlyCats.length === 0 && (
            <div style={{
              background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
              padding: 60, textAlign: "center", color: S.mutedLight,
            }}>
              <Package size={36} color={S.mutedLight} />
              <div style={{ marginTop: 12, fontSize: 14 }}>Nema kategorija</div>
            </div>
          )}
        </>
      )}

      {showForm && (
        <CategoryFormModal
          isMobile={isMobile}
          editing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={handleSaved}
        />
      )}

      {confirmDelete && (
        <div onClick={() => !busy && setConfirmDelete(null)} style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
          display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
        }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 460, width: "100%" }}>
            <h3 style={{ margin: 0, fontSize: 17, color: S.dark, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 14, height: 14, borderRadius: 4, background: confirmDelete.color }} />
              Obrisati "{dh(confirmDelete.name)}"?
            </h3>
            <p style={{ fontSize: 14, color: S.muted, marginTop: 10, marginBottom: 20, lineHeight: 1.5 }}>
              {confirmDelete.usage_count > 0
                ? `Ova kategorija se koristi u ${confirmDelete.usage_count} troškova. Prvo obrišite ili premjestite te troškove.`
                : "Ova akcija se ne može poništiti."}
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmDelete(null)} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>
                Otkaži
              </button>
              <button
                onClick={handleDelete}
                disabled={busy || confirmDelete.usage_count > 0}
                style={{
                  ...S.btnSm(S.red), padding: "10px 16px",
                  opacity: (busy || confirmDelete.usage_count > 0) ? 0.5 : 1,
                }}
              >
                {busy ? <Loader2 size={14} className="spin" /> : <Trash2 size={14} />} Obriši
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </div>
  );
}

function CategoryGroupSection({ title, subtitle, items, canWrite, onEdit, onDelete, isMobile }) {
  return (
    <div style={{
      background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
      overflow: "hidden",
    }}>
      <div style={{
        padding: "12px 16px", borderBottom: `1px solid ${S.borderSoft}`,
        background: "#fafafb",
        display: "flex", alignItems: "baseline", gap: 8,
      }}>
        <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: S.dark }}>{title}</h3>
        <span style={{ fontSize: 12, color: S.muted }}>{subtitle}</span>
      </div>
      <div>
        {items.map((c, i) => (
          <div
            key={c.id}
            style={{
              padding: "12px 16px",
              borderBottom: i < items.length - 1 ? `1px solid ${S.borderSoft}` : "none",
              display: "flex", alignItems: "center", gap: 12,
            }}
          >
            <div style={{
              width: 32, height: 32, borderRadius: 8, background: c.color,
              flexShrink: 0,
            }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: S.dark }}>
                  {dh(c.name)}
                </span>
                {c.is_global && (
                  <span style={{
                    background: S.borderSoft, color: S.muted,
                    padding: "1px 8px", borderRadius: 10,
                    fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3,
                  }}>
                    Globalna
                  </span>
                )}
                {c.usage_count > 0 && (
                  <span style={{
                    background: S.accentSoft, color: S.accent,
                    padding: "1px 8px", borderRadius: 10,
                    fontSize: 10, fontWeight: 600,
                  }}>
                    {c.usage_count} {c.usage_count === 1 ? "trošak" : "troškova"}
                  </span>
                )}
              </div>
              {c.description && (
                <div style={{
                  fontSize: 12, color: S.muted, marginTop: 2,
                  overflow: "hidden", textOverflow: "ellipsis",
                  whiteSpace: isMobile ? "normal" : "nowrap",
                }}>
                  {dh(c.description)}
                </div>
              )}
            </div>
            {canWrite && !c.is_global && (
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  onClick={() => onEdit(c)}
                  style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "7px 10px" }}
                  title="Uredi"
                >
                  <Edit3 size={13} />
                </button>
                <button
                  onClick={() => onDelete(c)}
                  style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "7px 10px" }}
                  title="Obriši"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            )}
            {canWrite && c.is_global && (
              <div style={{ fontSize: 11, color: S.mutedLight, fontStyle: "italic" }}>
                Predefinisana
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function CategoryFormModal({ editing, onClose, onSaved, isMobile }) {
  const isEdit = !!editing;
  const [name, setName] = useState(dh(editing?.name) || "");
  const [group, setGroup] = useState(editing?.category_group || "daily");
  const [color, setColor] = useState(editing?.color || CATEGORY_COLOR_PALETTE[0]);
  const [description, setDescription] = useState(dh(editing?.description) || "");

  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const h = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose, busy]);

  const submit = async () => {
    const e = {};
    if (!name.trim()) e.name = "Naziv je obavezan";
    if (!group) e.group = "Grupa je obavezna";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setBusy(true);
    const body = { name, category_group: group, color, description };
    const url = isEdit
      ? `/b2b/expense-category-update/${editing.id}`
      : `/b2b/expense-category-store`;
    const r = await api(url, { method: "POST", body: JSON.stringify(body) });
    setBusy(false);
    if (r.success) onSaved();
    else {
      setToast({ type: "error", message: r.message || "Greška" });
      setTimeout(() => setToast(null), 4000);
    }
  };

  const modalStyle = isMobile
    ? { position: "fixed", inset: 0, background: S.bg, zIndex: 140, display: "flex", flexDirection: "column", animation: "slideUp 0.22s ease-out" }
    : { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 140, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };

  const contentStyle = isMobile
    ? { background: S.bg, flex: 1, display: "flex", flexDirection: "column" }
    : { background: S.card, borderRadius: 14, width: "100%", maxWidth: 520, maxHeight: "92vh", overflow: "hidden", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" };

  return (
    <div onClick={!isMobile && !busy ? onClose : undefined} style={modalStyle}>
      <div onClick={(e) => e.stopPropagation()} style={contentStyle}>
        <div style={{
          padding: isMobile ? "14px 16px" : "16px 24px",
          background: S.card, borderBottom: `1px solid ${S.border}`,
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
        }}>
          <button onClick={onClose} disabled={busy} style={{ background: "none", border: "none", cursor: "pointer", padding: 6, color: S.muted }}>
            <X size={20} />
          </button>
          <div style={{ flex: 1, fontSize: isMobile ? 17 : 19, fontWeight: 700, color: S.dark }}>
            {isEdit ? "Uredi kategoriju" : "Nova kategorija"}
          </div>
          <button onClick={submit} disabled={busy} style={{ ...S.btnSm(S.dark, "white"), padding: "10px 18px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />}
            {busy ? "Čuvam…" : "Sačuvaj"}
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? 14 : 24 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Card title="Podaci">
              <Field label="Naziv" required error={errors.name}>
                <input
                  value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="npr. Marketing"
                  style={{ ...S.input, borderColor: errors.name ? S.red : S.border }}
                  autoFocus
                />
              </Field>
              <div style={{ marginTop: 10 }}>
                <Field label="Grupa" required error={errors.group}>
                  <div style={{ display: "flex", gap: 8 }}>
                    <GroupBtn
                      active={group === "daily"}
                      onClick={() => setGroup("daily")}
                      label="Dnevni"
                      hint="Često se javljaju"
                    />
                    <GroupBtn
                      active={group === "monthly"}
                      onClick={() => setGroup("monthly")}
                      label="Mjesečni"
                      hint="Fiksni mjesečni troškovi"
                    />
                  </div>
                </Field>
              </div>
              <div style={{ marginTop: 10 }}>
                <Field label="Opis (opciono)">
                  <textarea
                    value={description} onChange={(e) => setDescription(e.target.value)}
                    rows={2}
                    placeholder="Kratki opis kategorije…"
                    style={{ ...S.input, resize: "vertical", minHeight: 50, fontFamily: S.font }}
                  />
                </Field>
              </div>
            </Card>

            <Card title="Boja">
              <div style={{
                display: "grid",
                gridTemplateColumns: isMobile ? "repeat(5, 1fr)" : "repeat(8, 1fr)",
                gap: 8,
                marginBottom: 12,
              }}>
                {CATEGORY_COLOR_PALETTE.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    style={{
                      width: "100%", paddingTop: "100%", position: "relative",
                      background: c, border: color === c ? `3px solid ${S.dark}` : `2px solid white`,
                      borderRadius: 8, cursor: "pointer",
                      boxShadow: color === c ? "0 0 0 2px white inset, 0 2px 6px rgba(0,0,0,0.15)" : "0 1px 3px rgba(0,0,0,0.1)",
                      transition: "transform 0.1s",
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.transform = "scale(1.08)"}
                    onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
                  >
                    {color === c && (
                      <CircleCheck
                        size={16} color="white"
                        style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)" }}
                      />
                    )}
                  </button>
                ))}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 8, background: color,
                  border: `1px solid ${S.border}`, flexShrink: 0,
                }} />
                <input
                  type="text" value={color}
                  onChange={(e) => {
                    const v = e.target.value;
                    setColor(v);
                  }}
                  placeholder="#RRGGBB"
                  style={{ ...S.input, fontFamily: S.mono, flex: 1 }}
                  pattern="^#[0-9a-fA-F]{6}$"
                />
              </div>
            </Card>

            {/* Preview */}
            <Card title="Pregled">
              <div style={{
                padding: 12, border: `1px solid ${S.border}`, borderRadius: 8,
                display: "flex", alignItems: "center", gap: 12,
              }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: color, flexShrink: 0 }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14, color: S.dark }}>
                    {name.trim() || "Naziv kategorije"}
                  </div>
                  <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>
                    {group === "daily" ? "Dnevni troškovi" : "Mjesečni troškovi"}
                    {description && ` · ${description}`}
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
      </div>
    </div>
  );
}

function GroupBtn({ active, onClick, label, hint }) {
  return (
    <button
      onClick={onClick}
      style={{
        flex: 1, padding: "10px 14px", textAlign: "left",
        border: `1px solid ${active ? S.accent : S.border}`,
        background: active ? S.accentSoft : "white",
        color: active ? S.accent : S.dark,
        borderRadius: 8, cursor: "pointer",
        fontFamily: S.font, transition: "all 0.1s",
      }}
    >
      <div style={{ fontWeight: 600, fontSize: 13 }}>{label}</div>
      <div style={{ fontSize: 11, color: active ? S.accent : S.muted, marginTop: 1 }}>
        {hint}
      </div>
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// PDF ŠABLONI (PDF Template Configurator)
// Lets the company customize the look of invoice PDFs:
//   - logo, primary/secondary colors
//   - header & footer text
//   - bank account info (for B2B virmani)
//   - signature image + name/title
//   - QR / signature toggles
//   - additional notes
// Single template per company (the "default" / active one).
// ════════════════════════════════════════════════════════════════════════════
function PdfTemplateScreen({ isMobile, canWrite }) {
  // List of all templates for this company (3 kinds, seeded by SQL)
  const [templates, setTemplates] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  // Bank accounts available to tick (domestic + intl, from company settings)
  const [bankAccounts, setBankAccounts] = useState([]);
  // Which template the user is currently editing (defaults to selected)
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmReset, setConfirmReset] = useState(false);

  // Local form state for the currently-edited template
  const [cfg, setCfg] = useState(null);

  // ── Sekundarni naziv (company-wide, NOT per-template) ──────────────────
  // companies.secondary_name — when set, replaces company_name in the PDF
  // header for ALL templates (invoices + drafts + proformas). Editable here
  // because this is the screen where PDF appearance is configured, but it's
  // saved via its own endpoint (POST /b2b/company-secondary-name), not the
  // per-template config JSON.
  const [secondaryName, setSecondaryName] = useState("");
  const [savedSecondaryName, setSavedSecondaryName] = useState("");
  // PDV broj (companies.pdv_broj) — shown in the PDF seller block right
  // after the PIB line when set. Saved via its own endpoint like the
  // secondary name.
  const [pdvBroj, setPdvBroj] = useState("");
  const [savedPdvBroj, setSavedPdvBroj] = useState("");
  const [pdvBusy, setPdvBusy] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const [nameBusy, setNameBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const r = await api("/b2b/pdf-templates");
    setLoading(false);
    if (r.success) {
      const tpls = Array.isArray(r.data?.templates) ? r.data.templates : [];
      setTemplates(tpls);
      setBankAccounts(Array.isArray(r.data?.bank_accounts) ? r.data.bank_accounts : []);
      // Secondary name (company-wide) — string fields, default empty.
      // dh() decodes html entities: the backend stores via sanitize()
      // (htmlspecialchars), so names with quotes/ampersands come back as
      // &quot;/&amp; — decode for display, same pattern as notes fields.
      setCompanyName(dh(typeof r.data?.company_name === "string" ? r.data.company_name : ""));
      const sn = dh(typeof r.data?.secondary_name === "string" ? r.data.secondary_name : "");
      setSecondaryName(sn);
      setSavedSecondaryName(sn);
      const pb = dh(typeof r.data?.pdv_broj === "string" ? r.data.pdv_broj : "");
      setPdvBroj(pb);
      setSavedPdvBroj(pb);
      const sel = r.data?.selected_template_id || (tpls[0]?.id ?? null);
      setSelectedId(sel);
      if (!editingId && tpls.length > 0) {
        const initialEdit = tpls.find((t) => t.id === sel) || tpls[0];
        setEditingId(initialEdit.id);
        setCfg({ ...initialEdit.config });
      }
    } else {
      setError(r.message || "Greška");
    }
  }, [editingId]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const showToast = (type, message, ms = 3500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  const editing = templates.find((t) => t.id === editingId);

  const startEditing = (id) => {
    const t = templates.find((x) => x.id === id);
    if (!t) return;
    setEditingId(id);
    setCfg({ ...t.config });
  };

  const setSelected = async (id) => {
    setBusy(true);
    const r = await api(`/b2b/pdf-template-select/${id}`, { method: "POST", body: "{}" });
    setBusy(false);
    if (r.success) {
      setSelectedId(id);
      showToast("success", "Šablon postavljen kao aktivan");
    } else {
      showToast("error", r.message || "Greška");
    }
  };

  const save = async () => {
    if (!editing || !cfg) return;
    setBusy(true);
    const r = await api("/b2b/pdf-template-save", {
      method: "POST",
      body: JSON.stringify({ id: editing.id, config: cfg }),
    });
    setBusy(false);
    if (r.success) {
      // Refresh
      await load();
      showToast("success", "Šablon sačuvan");
    } else {
      showToast("error", r.message || "Greška");
    }
  };

  // Save (or clear) the company-wide secondary name. Sends the current input
  // value; empty string clears the field on the server (stored as NULL) and
  // the PDF falls back to the legal company name.
  const savePdvBroj = async () => {
    setPdvBusy(true);
    const r = await api("/b2b/company-pdv-broj", {
      method: "POST",
      body: JSON.stringify({ pdv_broj: pdvBroj.trim() }),
    });
    setPdvBusy(false);
    if (r.success) {
      const stored = dh(typeof r.data?.pdv_broj === "string" ? r.data.pdv_broj : "");
      setPdvBroj(stored);
      setSavedPdvBroj(stored);
      showToast("success", r.message || "Sačuvano");
    } else {
      showToast("error", r.message || "Greška");
    }
  };

  const saveSecondaryName = async () => {
    setNameBusy(true);
    const r = await api("/b2b/company-secondary-name", {
      method: "POST",
      body: JSON.stringify({ secondary_name: secondaryName.trim() }),
    });
    setNameBusy(false);
    if (r.success) {
      // Decode entities same as on load — keeps the input and the dirty-check
      // comparison consistent (backend stores htmlspecialchars-escaped text).
      const stored = dh(typeof r.data?.secondary_name === "string" ? r.data.secondary_name : "");
      setSecondaryName(stored);
      setSavedSecondaryName(stored);
      showToast("success", r.message || "Sačuvano");
    } else {
      showToast("error", r.message || "Greška");
    }
  };

  const reset = async () => {
    if (!editing) return;
    setBusy(true);
    const r = await api(`/b2b/pdf-template-reset/${editing.id}`, { method: "POST", body: "{}" });
    setBusy(false);
    setConfirmReset(false);
    if (r.success) {
      await load();
      // Re-edit to refresh local state
      const refreshed = templates.find((t) => t.id === editing.id);
      if (refreshed) setCfg({ ...refreshed.config });
      showToast("success", "Šablon resetovan");
    } else {
      showToast("error", r.message || "Greška");
    }
  };

  const previewPdf = async () => {
    if (!editing) return;
    const token = localStorage.getItem(TOKEN_KEY);
    try {
      const r = await fetch(`${API_BASE}/b2b/pdf-template-preview/${editing.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 10000);
    } catch (e) {
      showToast("error", e.message || "Greška pri pregledu");
    }
  };

  if (loading && templates.length === 0) {
    return (
      <>
        <TopHeader title="PDF Šabloni" isMobile={isMobile} />
        <div style={{ padding: 28, maxWidth: 1100 }}><Skeleton lines={8} /></div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <TopHeader title="PDF Šabloni" isMobile={isMobile} />
        <div style={{ padding: 28 }}><ReportError msg={error} /></div>
      </>
    );
  }

  const kindLabel = (k) =>
    k === "modern_centered" ? "Moderni" :
    k === "minimal" ? "Minimalan" :
    k === "compact" ? "Kompaktni" :
    "Klasičan";
  const kindHint = (k) =>
    k === "modern_centered" ? "Logo banner + Seller/QR/Buyer u 3 kolone" :
    k === "minimal" ? "Čist monohromatski dizajn, premium usluge" :
    k === "compact" ? "Tradicionalni izgled — uokvirena tabela, potpisi, sve na jednoj strani" :
    "Klasičan izgled — kao postojeća PHP aplikacija";

  return (
    <>
      <TopHeader
        title="PDF Šabloni"
        subtitle="Izaberite šablon i prilagodite ga"
        isMobile={isMobile}
      />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1200 }}>
        {/* ── Sekundarni naziv (company-wide) ──────────────────────────────
            Applies to ALL templates, so it lives above the per-template
            picker. When set, this name replaces the legal company name in
            the PDF header (invoices, drafts, proformas). Fiscal XML sent to
            Poreska is NOT affected — it always carries the legal name. */}
        <div style={{
          background: "white", border: `1px solid ${S.border}`, borderRadius: 12,
          padding: isMobile ? 14 : 18, marginBottom: 18,
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: S.dark, marginBottom: 4 }}>
            Naziv na fakturi (sekundarni naziv)
          </div>
          <div style={{ fontSize: 12, color: S.muted, marginBottom: 12, lineHeight: 1.5 }}>
            Ako je unesen, ovaj naziv se prikazuje u zaglavlju PDF računa umjesto
            zvaničnog naziva firme ({companyName || "—"}). Ostavite prazno da se
            prikazuje zvanični naziv. Ne utiče na fiskalizaciju — Poreskoj upravi
            se uvijek šalje zvanični naziv.
          </div>
          <div style={{
            display: "flex", gap: 10,
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
          }}>
            <input
              type="text"
              value={secondaryName}
              onChange={(e) => setSecondaryName(e.target.value)}
              placeholder={companyName || "Npr. brend / komercijalni naziv"}
              maxLength={255}
              disabled={!canWrite}
              style={{ ...S.input, flex: 1, minWidth: 0 }}
            />
            {canWrite && (
              <button
                onClick={saveSecondaryName}
                disabled={nameBusy || secondaryName.trim() === savedSecondaryName.trim()}
                style={{
                  ...S.btn(S.accent), justifyContent: "center",
                  padding: "10px 18px", whiteSpace: "nowrap",
                  opacity: (nameBusy || secondaryName.trim() === savedSecondaryName.trim()) ? 0.55 : 1,
                  cursor: (nameBusy || secondaryName.trim() === savedSecondaryName.trim()) ? "default" : "pointer",
                }}
              >
                {nameBusy ? "Čuvam…" : "Sačuvaj naziv"}
              </button>
            )}
          </div>

          {/* PDV broj — company VAT registration number, printed in the PDF
              seller block right after "PIB: (ME) …" on every template.
              Leave empty if the company is not in the PDV system. */}
          <div style={{ fontWeight: 700, fontSize: 14, color: S.dark, margin: "16px 0 4px" }}>
            PDV broj
          </div>
          <div style={{ fontSize: 12, color: S.muted, marginBottom: 12, lineHeight: 1.5 }}>
            Ako je unesen, prikazuje se na PDF računu odmah ispod PIB-a
            (npr. „PDV broj: 80/31-04541-0"). Ostavite prazno ako firma nije u
            sistemu PDV-a.
          </div>
          <div style={{
            display: "flex", gap: 10,
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
          }}>
            <input
              type="text"
              value={pdvBroj}
              onChange={(e) => setPdvBroj(e.target.value)}
              placeholder="npr. 80/31-04541-0"
              maxLength={50}
              disabled={!canWrite}
              style={{ ...S.input, flex: 1, minWidth: 0, fontFamily: S.mono }}
            />
            {canWrite && (
              <button
                onClick={savePdvBroj}
                disabled={pdvBusy || pdvBroj.trim() === savedPdvBroj.trim()}
                style={{
                  ...S.btn(S.accent), justifyContent: "center",
                  padding: "10px 18px", whiteSpace: "nowrap",
                  opacity: (pdvBusy || pdvBroj.trim() === savedPdvBroj.trim()) ? 0.55 : 1,
                  cursor: (pdvBusy || pdvBroj.trim() === savedPdvBroj.trim()) ? "default" : "pointer",
                }}
              >
                {pdvBusy ? "Čuvam…" : "Sačuvaj PDV broj"}
              </button>
            )}
          </div>
        </div>

        {/* Template picker — one card per template kind. auto-fit so the new
            4th (Kompaktni) template lays out cleanly without a hardcoded
            column count. */}
        <div style={{
          display: "grid", gap: 12, marginBottom: 18,
          gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(230px, 1fr))",
        }}>
          {templates.map((t) => {
            const isActive = t.id === selectedId;
            const isEditing = t.id === editingId;
            return (
              <div
                key={t.id}
                onClick={() => startEditing(t.id)}
                style={{
                  background: "white",
                  border: `2px solid ${isEditing ? S.accent : isActive ? S.green : S.border}`,
                  borderRadius: 12, padding: 14, cursor: "pointer",
                  transition: "all 0.12s",
                  position: "relative",
                }}
              >
                {isActive && (
                  <span style={{
                    position: "absolute", top: 10, right: 10,
                    background: S.green, color: "white",
                    padding: "2px 8px", borderRadius: 8, fontSize: 10, fontWeight: 700,
                  }}>AKTIVAN</span>
                )}
                <div style={{
                  width: 48, height: 48, borderRadius: 10,
                  background: t.config.primary_color, color: "white",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  marginBottom: 10,
                }}>
                  <FileBadge size={22} />
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, color: S.dark, marginBottom: 3 }}>
                  {kindLabel(t.template_kind)}
                </div>
                <div style={{ fontSize: 12, color: S.muted, marginBottom: 12 }}>
                  {kindHint(t.template_kind)}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  {!isActive && canWrite && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setSelected(t.id); }}
                      disabled={busy}
                      style={{ ...S.btnSm(S.accent), padding: "6px 12px", flex: 1, justifyContent: "center", fontSize: 12 }}
                    >
                      Postavi kao aktivan
                    </button>
                  )}
                  {isActive && (
                    <button
                      disabled
                      style={{ ...S.btnSm("white", S.green), border: `1px solid ${S.green}`, padding: "6px 12px", flex: 1, justifyContent: "center", fontSize: 12, cursor: "default" }}
                    >
                      <CircleCheck size={12} /> Aktivan
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Editor for the currently-selected template */}
        {editing && cfg && (
          <>
            <div style={{
              display: "flex", alignItems: "center", gap: 10, marginBottom: 14,
              padding: "10px 14px", background: S.card, border: `1px solid ${S.border}`, borderRadius: 10,
            }}>
              <FileBadge size={18} color={S.accent} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: S.dark }}>
                  Uređivanje: {kindLabel(editing.template_kind)}
                </div>
                <div style={{ fontSize: 12, color: S.muted }}>{kindHint(editing.template_kind)}</div>
              </div>
              <button onClick={previewPdf} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "8px 14px" }}>
                <ExternalLink size={13} /> Pregled
              </button>
            </div>

            <div style={{ display: "grid", gap: 14, gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr" }}>
              <Card title="Boje šablona">
                <ColorField
                  label="Primarna boja"
                  hint="Header borderi, naslov, akcenti"
                  value={cfg.primary_color || "#34495e"}
                  onChange={(v) => setCfg({ ...cfg, primary_color: v })}
                />
                <div style={{ marginTop: 10 }}>
                  <ColorField
                    label="Sekundarna boja"
                    hint="Sekundarni elementi, akcenti"
                    value={cfg.accent_color || "#2c3e50"}
                    onChange={(v) => setCfg({ ...cfg, accent_color: v })}
                  />
                </div>
              </Card>

              <Card title="Šta prikazati na PDF-u">
                <CheckboxField
                  label="IKOF (IIC) kod"
                  checked={!!cfg.show_iic}
                  onChange={(v) => setCfg({ ...cfg, show_iic: v ? 1 : 0 })}
                  hint="Identifikator računa izdavača"
                />
                <CheckboxField
                  label="JIKR (FIC) kod"
                  checked={!!cfg.show_jikr}
                  onChange={(v) => setCfg({ ...cfg, show_jikr: v ? 1 : 0 })}
                  hint="Jedinstveni identifikator Poreske"
                />
                <CheckboxField
                  label="QR kod"
                  checked={!!cfg.show_qr}
                  onChange={(v) => setCfg({ ...cfg, show_qr: v ? 1 : 0 })}
                  hint="QR za verifikaciju kod Poreske"
                />
                <CheckboxField
                  label="Polje za potpis"
                  checked={!!cfg.show_signature}
                  onChange={(v) => setCfg({ ...cfg, show_signature: v ? 1 : 0 })}
                  hint="Prazno polje za potpis ovlaštenog lica"
                />

                {/* Bank accounts subsection */}
                <div style={{
                  marginTop: 14, paddingTop: 14,
                  borderTop: `1px solid ${S.borderSoft}`,
                }}>
                  <CheckboxField
                    label="Bankovni računi"
                    checked={!!cfg.show_banks}
                    onChange={(v) => setCfg({ ...cfg, show_banks: v ? 1 : 0 })}
                    hint="Master prekidač — ako je isključen, ne prikazuju se uopšte"
                  />

                  {!!cfg.show_banks && (
                    bankAccounts.length === 0 ? (
                      <div style={{
                        marginTop: 10, padding: "8px 10px",
                        background: "#fef3c7", border: "1px solid #fde68a",
                        color: "#92400e", borderRadius: 6, fontSize: 12,
                      }}>
                        Nemate dodatih bankovnih računa. Dodajte ih u
                        {" "}<strong>Podešavanja → Logo & Banke</strong>.
                      </div>
                    ) : (
                      <div style={{ marginTop: 10, paddingLeft: 8 }}>
                        <div style={{
                          fontSize: 11, color: S.muted, fontWeight: 600,
                          textTransform: "uppercase", letterSpacing: 0.3,
                          marginBottom: 8,
                        }}>
                          Koje račune prikazati ({(cfg.bank_accounts_on_pdf || []).length === 0
                            ? "svi"
                            : `${(cfg.bank_accounts_on_pdf || []).length} od ${bankAccounts.length}`})
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                          {bankAccounts.map((ba) => {
                            const list = Array.isArray(cfg.bank_accounts_on_pdf) ? cfg.bank_accounts_on_pdf : [];
                            // Empty list = show all → render as all checked
                            const isChecked = list.length === 0 || list.includes(ba.account);
                            return (
                              <BankAccountCheckbox
                                key={ba.account}
                                account={ba.account}
                                kind={ba.kind}
                                checked={isChecked}
                                onChange={(checked) => {
                                  // If currently treating as "all" (empty list), materialize the full list first
                                  let next = list.length === 0
                                    ? bankAccounts.map((b) => b.account)
                                    : [...list];
                                  if (checked) {
                                    if (!next.includes(ba.account)) next.push(ba.account);
                                  } else {
                                    next = next.filter((a) => a !== ba.account);
                                  }
                                  // If everything is selected, normalize back to empty (= "all")
                                  if (next.length === bankAccounts.length) next = [];
                                  setCfg({ ...cfg, bank_accounts_on_pdf: next });
                                }}
                              />
                            );
                          })}
                        </div>
                        <div style={{ marginTop: 8, fontSize: 11, color: S.mutedLight }}>
                          Prazno = svi računi se prikazuju. Možete odznačiti neke da bi se prikazao samo izabrani podskup.
                        </div>
                      </div>
                    )
                  )}
                </div>
              </Card>
            </div>

            <Card title="Tekstovi" style={{ marginTop: 14 }}>
              <Field label="Header tekst (opciono)" hint="Pojavljuje se iznad naslova računa">
                <input
                  value={cfg.header_text || ""}
                  onChange={(e) => setCfg({ ...cfg, header_text: e.target.value })}
                  placeholder="npr. Otvoreno preduzeće za usluge"
                  style={S.input}
                />
              </Field>
              <div style={{ marginTop: 12 }}>
                <Field label="Footer tekst / Napomena" hint="Pojavljuje se na dnu PDF-a">
                  <textarea
                    value={cfg.footer_text || ""}
                    onChange={(e) => setCfg({ ...cfg, footer_text: e.target.value })}
                    rows={3}
                    placeholder="npr. Hvala na povjerenju! Plaćanje strogo na bankovni račun."
                    style={{ ...S.input, resize: "vertical", minHeight: 80, fontFamily: S.font }}
                  />
                </Field>
              </div>
            </Card>

            <div style={{
              marginTop: 14, padding: 12,
              background: "#fef3c7", border: "1px solid #fde68a", borderRadius: 10,
              fontSize: 12, color: "#92400e", display: "flex", gap: 8, alignItems: "flex-start",
            }}>
              <AlertCircle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>
                Logo se uzima iz <strong>Podešavanja → Logo & Banke</strong> (gornji deo).
                Bankovni računi (domaći + međunarodni) takođe iz Podešavanja. Ovde se kontroliše SAMO izgled (boje, šta se prikazuje na PDF-u).
              </span>
            </div>

            {canWrite && (
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button
                  onClick={() => setConfirmReset(true)}
                  disabled={busy}
                  style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}
                >
                  <RefreshCw size={13} /> Resetuj
                </button>
                <button
                  onClick={save}
                  disabled={busy}
                  style={{ ...S.btn(S.dark), padding: "10px 22px" }}
                >
                  {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />} Sačuvaj šablon
                </button>
              </div>
            )}
          </>
        )}

        {confirmReset && (
          <ConfirmModal
            title={`Resetovati šablon "${editing ? kindLabel(editing.template_kind) : ""}"?`}
            body="Sve izmjene (boje, tekstovi, toggle-i) biće vraćene na podrazumijevane vrijednosti za ovaj tip šablona."
            confirmLabel="Resetuj"
            danger
            onConfirm={reset}
            onCancel={() => setConfirmReset(false)}
            busy={busy}
          />
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}


// ── Inline color field with palette + hex input ──────────────────────────────
function ColorField({ label, hint, value, onChange }) {
  return (
    <Field label={label} hint={hint}>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{
          width: 40, height: 40, borderRadius: 8, background: value,
          border: `1px solid ${S.border}`, flexShrink: 0,
        }} />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#RRGGBB"
          style={{ ...S.input, fontFamily: S.mono, flex: 1 }}
          pattern="^#[0-9a-fA-F]{6}$"
        />
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 40, height: 40, border: `1px solid ${S.border}`, borderRadius: 8, cursor: "pointer", padding: 2 }}
          title="Otvori biraču boja"
        />
      </div>
    </Field>
  );
}

// ── Reusable checkbox row ────────────────────────────────────────────────────
function CheckboxField({ label, checked, onChange, hint }) {
  return (
    <label style={{
      display: "flex", alignItems: "flex-start", gap: 10, cursor: "pointer",
      padding: "10px 12px", border: `1px solid ${S.border}`, borderRadius: 8,
      background: checked ? S.accentSoft : "white",
      transition: "background 0.1s",
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ marginTop: 2, accentColor: S.accent, width: 16, height: 16, cursor: "pointer" }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: S.dark }}>{label}</div>
        {hint && <div style={{ fontSize: 12, color: S.muted, marginTop: 2 }}>{hint}</div>}
      </div>
    </label>
  );
}

function BankAccountCheckbox({ account, kind, checked, onChange }) {
  const isIntl = kind === "international";
  return (
    <label style={{
      display: "flex", alignItems: "center", gap: 10, cursor: "pointer",
      padding: "8px 10px", borderRadius: 6,
      background: checked ? S.accentSoft : "white",
      border: `1px solid ${checked ? S.accent : S.borderSoft}`,
      transition: "background 0.1s",
    }}>
      <input
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        style={{ accentColor: S.accent, width: 14, height: 14, cursor: "pointer" }}
      />
      <span style={{
        display: "inline-block",
        padding: "2px 6px", borderRadius: 4,
        background: isIntl ? "#dbeafe" : "#dcfce7",
        color: isIntl ? "#1e40af" : "#166534",
        fontSize: 10, fontWeight: 700,
        letterSpacing: 0.3,
      }}>{isIntl ? "IBAN" : "DOM"}</span>
      <span style={{ fontFamily: S.mono, fontSize: 13, color: S.dark, wordBreak: "break-all", flex: 1 }}>
        {account}
      </span>
    </label>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// PODEŠAVANJA (Settings)
//
// 5 tabs:
//   Firma     — company info (editable: name, address, contact, etc; PIB locked)
//   Logo & Banke — logo upload + bank accounts CRUD
//   Email     — SMTP configuration + test email
//   B2B       — B2B-specific defaults (currency, VAT, due days, footer)
//   Sistem    — READ-ONLY info: subscription, certificate, operators, locations,
//               TCRs, is_pausalac. "Contact EFIS PRO support to change."
//
// Editable parts mirror what the existing PHP webapp DashboardController:settings
// allows. PIB, ikof_code, certificate, operators, locations, TCRs, pausalac
// status, subscription tier are READ-ONLY here and surface a hint to contact
// support.
// ════════════════════════════════════════════════════════════════════════════
const SETTINGS_TABS = [
  { id: "company",  label: "Firma",        icon: Building2 },
  { id: "branding", label: "Logo & Banke", icon: ImageIcon },
  { id: "email",    label: "Email (SMTP)", icon: Mail },
  { id: "b2b",      label: "B2B",          icon: FileBadge },
  { id: "system",   label: "Sistem",       icon: Shield },
];

function SettingsScreen({ isMobile, canWrite }) {
  const [tab, setTab] = useState("company");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const r = await api("/b2b/settings");
    setLoading(false);
    if (r.success) setData(r.data); else setError(r.message || "Greška");
  }, []);
  useEffect(() => { load(); }, [load]);

  const showToast = (type, message, ms = 3500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  if (loading && !data) {
    return (
      <>
        <TopHeader title="Podešavanja" isMobile={isMobile} />
        <div style={{ padding: 28, maxWidth: 1100 }}>
          <Skeleton lines={10} />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <TopHeader title="Podešavanja" isMobile={isMobile} />
        <div style={{ padding: 28 }}><ReportError msg={error} /></div>
      </>
    );
  }

  return (
    <>
      <TopHeader title="Podešavanja" isMobile={isMobile} />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1100 }}>
        <div style={{
          display: "flex", gap: 4,
          background: S.card, border: `1px solid ${S.border}`,
          borderRadius: 10, padding: 4, marginBottom: 16,
          overflowX: "auto",
        }} className="hide-scrollbar">
          {SETTINGS_TABS.map((t) => (
            <ViewModeBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)} icon={t.icon} label={t.label} />
          ))}
        </div>

        {tab === "company" && (
          <CompanySettingsPanel
            isMobile={isMobile}
            canWrite={canWrite}
            company={data.company}
            onSaved={async () => { await load(); showToast("success", "Sačuvano"); }}
            onError={(m) => showToast("error", m, 5000)}
          />
        )}
        {tab === "branding" && (
          <BrandingSettingsPanel
            isMobile={isMobile}
            canWrite={canWrite}
            company={data.company}
            onSaved={async () => { await load(); showToast("success", "Sačuvano"); }}
            onError={(m) => showToast("error", m, 5000)}
          />
        )}
        {tab === "email" && (
          <EmailSettingsPanel
            isMobile={isMobile}
            canWrite={canWrite}
            smtp={data.smtp}
            onSaved={async () => { await load(); showToast("success", "Sačuvano"); }}
            onSent={(to) => showToast("success", `Test poslat na: ${to}`, 4000)}
            onError={(m) => showToast("error", m, 5000)}
          />
        )}
        {tab === "b2b" && (
          <B2bSettingsPanel
            isMobile={isMobile}
            canWrite={canWrite}
            b2b={data.b2b}
            onSaved={async () => { await load(); showToast("success", "Sačuvano"); }}
            onError={(m) => showToast("error", m, 5000)}
          />
        )}
        {tab === "system" && (
          <SystemInfoPanel
            isMobile={isMobile}
            data={data}
          />
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// ── COMPANY TAB ──────────────────────────────────────────────────────────────
function CompanySettingsPanel({ isMobile, canWrite, company, onSaved, onError }) {
  const [form, setForm] = useState({
    company_name:    dh(company.company_name) || "",
    address:         dh(company.address) || "",
    city:            dh(company.city) || "",
    postal_code:     dh(company.postal_code) || "",
    phone:           dh(company.phone) || "",
    email:           dh(company.email) || "",
    contact_person:  dh(company.contact_person) || "",
    business_type:   company.business_type || "retail",
    // PDV broj — printed on the PDF seller block after PIB when set.
    pdv_broj:        dh(company.pdv_broj) || "",
  });
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    const e = {};
    if (!form.company_name.trim()) e.company_name = "Naziv firme je obavezan";
    if (!form.email.trim())        e.email        = "Email je obavezan";
    else if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Neispravan email";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setBusy(true);
    const r = await api("/b2b/settings-company", { method: "POST", body: JSON.stringify(form) });
    setBusy(false);
    if (r.success) onSaved(); else onError(r.message || "Greška");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Card title="Osnovni podaci" subtitle="Podaci o vašoj firmi koji se prikazuju na računima">
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
          <Field label="Naziv firme" required error={errors.company_name}>
            <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} style={{ ...S.input, borderColor: errors.company_name ? S.red : S.border }} />
          </Field>
          <Field label="PIB" hint="Zaključano — za izmjenu kontaktirajte EFIS.PRO support">
            <input value={company.pib} readOnly disabled style={{ ...S.input, background: "#f3f4f6", color: S.muted, fontFamily: S.mono, cursor: "not-allowed" }} />
          </Field>
          <Field label="PDV broj" hint="Prikazuje se na PDF računu ispod PIB-a. Ostavite prazno ako firma nije u sistemu PDV-a.">
            <input
              value={form.pdv_broj}
              onChange={(e) => set("pdv_broj", e.target.value)}
              maxLength={50}
              placeholder="npr. 80/31-04541-0"
              style={{ ...S.input, fontFamily: S.mono }}
            />
          </Field>
          <Field label="Adresa">
            <input value={form.address} onChange={(e) => set("address", e.target.value)} style={S.input} />
          </Field>
          <Field label="Grad">
            <input value={form.city} onChange={(e) => set("city", e.target.value)} style={S.input} />
          </Field>
          <Field label="Poštanski broj">
            <input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} style={S.input} />
          </Field>
          <Field label="Telefon">
            <input value={form.phone} onChange={(e) => set("phone", e.target.value)} style={S.input} />
          </Field>
          <Field label="Email" required error={errors.email}>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} style={{ ...S.input, borderColor: errors.email ? S.red : S.border }} />
          </Field>
          <Field label="Kontakt osoba">
            <input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} style={S.input} />
          </Field>
          <Field label="Tip biznisa">
            <Select
              value={form.business_type}
              onChange={(v) => set("business_type", v)}
              options={[
                { value: "retail", label: "Trgovina" },
                { value: "hotel", label: "Hotel" },
                { value: "restaurant", label: "Restoran" },
                { value: "service", label: "Usluga" },
                { value: "other", label: "Ostalo" },
              ]}
              icon={Building2}
            />
          </Field>
          <Field label="IKOF kod" hint="Zaključano — postavlja se od strane EFIS.PRO support">
            <input value={company.ikof_code || ""} readOnly disabled style={{ ...S.input, background: "#f3f4f6", color: S.muted, fontFamily: S.mono, cursor: "not-allowed" }} />
          </Field>
        </div>
      </Card>

      {canWrite && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={save} disabled={busy} style={{ ...S.btn(S.dark), padding: "10px 22px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />} Sačuvaj
          </button>
        </div>
      )}
    </div>
  );
}

// ── BRANDING TAB (logo + banks: domestic + international) ───────────────────
function BrandingSettingsPanel({ isMobile, canWrite, company, onSaved, onError }) {
  const [domestic, setDomestic]   = useState(Array.isArray(company.bank_accounts_domestic) ? [...company.bank_accounts_domestic] : []);
  const [international, setIntl]  = useState(Array.isArray(company.bank_accounts_international) ? [...company.bank_accounts_international] : []);
  const [newDom, setNewDom] = useState("");
  const [newIntl, setNewIntl] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const addDomestic = () => {
    const v = newDom.trim();
    if (!v) return;
    if (domestic.includes(v)) { onError("Račun već postoji u domaćim"); return; }
    setDomestic((a) => [...a, v]);
    setNewDom("");
  };
  const addInternational = () => {
    const v = newIntl.trim();
    if (!v) return;
    if (international.includes(v)) { onError("Račun već postoji u međunarodnim"); return; }
    setIntl((a) => [...a, v]);
    setNewIntl("");
  };
  const removeDomestic = (acc) => setDomestic((a) => a.filter((x) => x !== acc));
  const removeInternational = (acc) => setIntl((a) => a.filter((x) => x !== acc));

  const saveBanks = async () => {
    setBusy(true);
    const r = await api("/b2b/settings-banks", {
      method: "POST",
      body: JSON.stringify({
        bank_accounts_domestic: domestic,
        bank_accounts_international: international,
      }),
    });
    setBusy(false);
    if (r.success) onSaved(); else onError(r.message || "Greška");
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("logo", file);
      const token = localStorage.getItem(TOKEN_KEY);
      const resp = await fetch(`${API_BASE}/b2b/settings-logo`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const j = await resp.json();
      setBusy(false);
      if (j.success) onSaved();
      else onError(j.message || "Greška pri uploadu");
    } catch (e) {
      setBusy(false);
      onError(e.message || "Greška");
    }
  };

  const removeLogo = async () => {
    setBusy(true);
    const r = await api("/b2b/settings-logo-remove", { method: "POST", body: "{}" });
    setBusy(false);
    if (r.success) onSaved(); else onError(r.message || "Greška");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Card title="Logo firme" subtitle="JPEG, PNG, GIF, WebP ili SVG — max 2MB">
        <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
          <div style={{
            width: 140, height: 100, borderRadius: 8,
            border: `1px dashed ${S.border}`, background: "#fafafb",
            display: "flex", alignItems: "center", justifyContent: "center",
            overflow: "hidden", flexShrink: 0,
          }}>
            {company.logo_url ? (
              <img src={company.logo_url} alt="Logo" style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }} />
            ) : (
              <ImageIcon size={32} color={S.mutedLight} />
            )}
          </div>
          {canWrite && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <input
                ref={fileRef}
                type="file"
                accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                style={{ display: "none" }}
                onChange={(e) => uploadLogo(e.target.files?.[0])}
              />
              <button onClick={() => fileRef.current?.click()} disabled={busy} style={{ ...S.btn(S.dark), padding: "9px 16px" }}>
                {busy ? <Loader2 size={14} className="spin" /> : <Upload size={14} />}
                {company.logo_url ? " Zamijeni logo" : " Uploaduj logo"}
              </button>
              {company.logo_url && (
                <button onClick={removeLogo} disabled={busy} style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "8px 14px" }}>
                  <Trash2 size={13} /> Ukloni logo
                </button>
              )}
            </div>
          )}
        </div>
      </Card>

      <BankAccountList
        title="Domaći bankovni računi"
        subtitle="Žiro računi (npr. 565-7643-50)"
        accounts={domestic}
        canWrite={canWrite}
        newValue={newDom}
        setNewValue={setNewDom}
        onAdd={addDomestic}
        onRemove={removeDomestic}
        placeholder="npr. 565-7643-50"
        busy={busy}
      />

      <BankAccountList
        title="Međunarodni bankovni računi"
        subtitle="IBAN / SWIFT (npr. ME25 5101 0000 0000 0001 23)"
        accounts={international}
        canWrite={canWrite}
        newValue={newIntl}
        setNewValue={setNewIntl}
        onAdd={addInternational}
        onRemove={removeInternational}
        placeholder="npr. ME25 5101 0000 0000 0001 23"
        busy={busy}
      />

      {canWrite && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={saveBanks} disabled={busy} style={{ ...S.btn(S.dark), padding: "10px 22px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />} Sačuvaj račune
          </button>
        </div>
      )}
    </div>
  );
}

function BankAccountList({ title, subtitle, accounts, canWrite, newValue, setNewValue, onAdd, onRemove, placeholder, busy }) {
  return (
    <Card title={title} subtitle={subtitle}>
      {canWrite && (
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <input
            value={newValue}
            onChange={(e) => setNewValue(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && onAdd()}
            placeholder={placeholder}
            style={{ ...S.input, flex: 1, fontFamily: S.mono }}
          />
          <button onClick={onAdd} disabled={busy || !newValue.trim()} style={{ ...S.btn(S.accent), padding: "9px 16px" }}>
            <Plus size={14} /> Dodaj
          </button>
        </div>
      )}

      {accounts.length === 0 ? (
        <div style={{ padding: 24, textAlign: "center", color: S.mutedLight, fontSize: 13 }}>
          Nema dodatih računa
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {accounts.map((acc) => (
            <div key={acc} style={{
              padding: 10, border: `1px solid ${S.border}`, borderRadius: 8,
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{ flex: 1, minWidth: 0, fontFamily: S.mono, fontWeight: 600, color: S.dark, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {acc}
              </div>
              {canWrite && (
                <button onClick={() => onRemove(acc)} style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "6px 10px" }}>
                  <Trash2 size={13} />
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ── EMAIL TAB ────────────────────────────────────────────────────────────────
function EmailSettingsPanel({ isMobile, canWrite, smtp, onSaved, onSent, onError }) {
  const [form, setForm] = useState({
    enabled:    !!smtp.enabled,
    host:       smtp.host || "smtp.gmail.com",
    port:       smtp.port || 587,
    username:   smtp.username || "",
    password:   "",
    encryption: smtp.encryption || "tls",
    from_email: smtp.from_email || "",
    from_name:  smtp.from_name || "",
  });
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    const r = await api("/b2b/settings-smtp", {
      method: "POST",
      body: JSON.stringify(form),
    });
    setBusy(false);
    if (r.success) { onSaved(); setForm((f) => ({ ...f, password: "" })); }
    else onError(r.message || "Greška");
  };

  const sendTest = async () => {
    setBusy(true);
    const r = await api("/b2b/settings-smtp-test", {
      method: "POST",
      body: JSON.stringify({ to: testTo || undefined }),
    });
    setBusy(false);
    if (r.success) onSent(r.data?.sent_to || "primaocu"); else onError(r.message || "Greška");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 10,
        padding: "12px 16px", display: "flex", gap: 8, fontSize: 13, color: "#1e40af",
        alignItems: "flex-start",
      }}>
        <Mail size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          Konfigurišite SMTP server kako biste mogli slati račune direktno klijentima.
          Za Gmail koristite "app password" — <strong>ne</strong> svoju glavnu lozinku.
        </div>
      </div>

      <Card title="SMTP postavke">
        <div style={{ marginBottom: 14 }}>
          <CheckboxField
            label="Email funkcionalnost aktivna"
            checked={form.enabled}
            onChange={(v) => set("enabled", v)}
            hint="Kada je isključeno, slanje emaila nije moguće"
          />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "2fr 1fr", gap: 14 }}>
          <Field label="SMTP host" required>
            <input value={form.host} onChange={(e) => set("host", e.target.value)} placeholder="smtp.gmail.com" style={{ ...S.input, fontFamily: S.mono }} />
          </Field>
          <Field label="Port" required>
            <input type="number" value={form.port} onChange={(e) => set("port", parseInt(e.target.value) || 587)} style={{ ...S.input, fontFamily: S.mono }} />
          </Field>
          <Field label="Username (email)" required>
            <input type="email" value={form.username} onChange={(e) => set("username", e.target.value)} placeholder="example@gmail.com" style={S.input} />
          </Field>
          <Field label="Šifrovanje">
            <Select
              value={form.encryption}
              onChange={(v) => set("encryption", v)}
              options={[
                { value: "tls",  label: "TLS (preporučeno)" },
                { value: "ssl",  label: "SSL" },
                { value: "none", label: "Bez šifrovanja" },
              ]}
              icon={Shield}
            />
          </Field>
          <Field
            label={smtp.password_set ? "Lozinka (postavljena)" : "Lozinka"}
            hint={smtp.password_set ? "Ostavite prazno da zadržite postojeću" : "Za Gmail koristite app password"}
          >
            <input
              type="password"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              placeholder={smtp.password_set ? "••••••••" : "Lozinka"}
              autoComplete="new-password"
              style={S.input}
            />
          </Field>
          <div /> {/* spacer */}
          <Field label="From email (opciono)" hint="Ako prazno, koristi se username">
            <input type="email" value={form.from_email} onChange={(e) => set("from_email", e.target.value)} style={S.input} />
          </Field>
          <Field label="From name (opciono)" hint="Naziv pošiljaoca">
            <input value={form.from_name} onChange={(e) => set("from_name", e.target.value)} style={S.input} />
          </Field>
        </div>

        {canWrite && (
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
            <button onClick={save} disabled={busy} style={{ ...S.btn(S.dark), padding: "9px 18px" }}>
              {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />} Sačuvaj
            </button>
          </div>
        )}
      </Card>

      {smtp.enabled && smtp.password_set && (
        <Card title="Test email" subtitle="Pošaljite test poruku da provjerite postavke">
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <Field label="Primaoc (opciono)" hint="Ako prazno, šalje se na vaš SMTP username">
                <input
                  type="email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  placeholder={smtp.username}
                  style={S.input}
                />
              </Field>
            </div>
            <button onClick={sendTest} disabled={busy} style={{ ...S.btn(S.green), padding: "10px 18px" }}>
              {busy ? <Loader2 size={14} className="spin" /> : <Mail size={14} />} Pošalji test
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}

// ── B2B DEFAULTS TAB ─────────────────────────────────────────────────────────
function B2bSettingsPanel({ isMobile, canWrite, b2b, onSaved, onError }) {
  const [form, setForm] = useState({
    default_payment_due_days: b2b.default_payment_due_days || 15,
    default_currency:         b2b.default_currency || "EUR",
    default_vat_rate:         b2b.default_vat_rate || 21,
    show_iic_on_pdf:          !!b2b.show_iic_on_pdf,
    show_jikr_on_pdf:         !!b2b.show_jikr_on_pdf,
    show_qr_on_pdf:           !!b2b.show_qr_on_pdf,
    custom_footer_text:       b2b.custom_footer_text || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    const r = await api("/b2b/settings-b2b", {
      method: "POST",
      body: JSON.stringify(form),
    });
    setBusy(false);
    if (r.success) onSaved(); else onError(r.message || "Greška");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Card title="B2B podrazumijevane vrijednosti">
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
          <Field label="Standardni rok plaćanja" hint="Dana od datuma računa">
            <input
              type="number" min={0} max={365}
              value={form.default_payment_due_days}
              onChange={(e) => set("default_payment_due_days", parseInt(e.target.value) || 15)}
              style={{ ...S.input, fontFamily: S.mono }}
            />
          </Field>
          <Field label="Valuta">
            <Select
              value={form.default_currency}
              onChange={(v) => set("default_currency", v)}
              options={[
                { value: "EUR", label: "EUR (€)" },
                { value: "USD", label: "USD ($)" },
                { value: "RSD", label: "RSD (din)" },
                { value: "BAM", label: "BAM (KM)" },
                { value: "HRK", label: "HRK (kn)" },
                { value: "MKD", label: "MKD" },
                { value: "ALL", label: "ALL" },
              ]}
              icon={Euro}
            />
          </Field>
          <Field label="Podrazumijevana PDV stopa">
            <Select
              value={String(form.default_vat_rate)}
              onChange={(v) => set("default_vat_rate", parseFloat(v))}
              options={[
                { value: "0", label: "0%" },
                { value: "7", label: "7%" },
                { value: "15", label: "15%" },
                { value: "21", label: "21%" },
              ]}
              icon={TrendingUp}
            />
          </Field>
        </div>
      </Card>

      <div style={{
        background: "#dbeafe", border: "1px solid #93c5fd", borderRadius: 10,
        padding: "10px 14px", fontSize: 12, color: "#1e40af",
        display: "flex", gap: 8, alignItems: "flex-start",
      }}>
        <FileBadge size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          Kontrole prikaza na PDF računu (IIC/JIKR/QR kodovi, footer tekst, boje) su
          premještene u <strong>PDF Šabloni</strong> sektor, gdje se postavljaju po šablonu.
        </span>
      </div>

      {canWrite && (
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={save} disabled={busy} style={{ ...S.btn(S.dark), padding: "10px 22px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />} Sačuvaj B2B postavke
          </button>
        </div>
      )}
    </div>
  );
}

// ── SYSTEM INFO TAB (read-only) ──────────────────────────────────────────────
function SystemInfoPanel({ isMobile, data }) {
  const { subscription, certificate, operators, locations } = data;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{
        background: "#fef3c7", border: "1px solid #fbbf24", borderRadius: 10,
        padding: "12px 16px", display: "flex", gap: 8, fontSize: 13, color: "#92400e",
        alignItems: "flex-start",
      }}>
        <Shield size={16} style={{ flexShrink: 0, marginTop: 2 }} />
        <div>
          Sljedeći podaci su samo za pregled. Za izmjene (certifikat, operateri,
          lokacije, TCR, paušalni status, pretplata) kontaktirajte EFIS.PRO support.
        </div>
      </div>

      {/* Subscription */}
      <Card title="Pretplata">
        <InfoRow label="Tier"        value={tierBadge(subscription.tier)} />
        <InfoRow label="Status"      value={statusBadge(subscription.status)} />
        <InfoRow label="Aktivirano"  value={subscription.started_at ? fmtDate(subscription.started_at) : "—"} />
        <InfoRow label="Ističe"      value={subscription.expires_at ? fmtDate(subscription.expires_at) : "—"} />
        {/* Mjesečni limit računa row intentionally removed.
            We don't limit the number of invoices clients can issue.
            The `invoices_this_month` and `monthly_invoice_limit` fields
            still exist in the DB (and the limit field is editable by
            superadmin), but they're not exposed to clients and the
            backend canCreateInvoice() is a no-op (always returns true). */}
        <InfoRow label="Paušalni obveznik" value={
          subscription.is_pausalac ? (
            <span style={{ background: "#fef3c7", color: "#92400e", padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700 }}>
              DA — svi proizvodi 0% PDV
            </span>
          ) : (
            <span style={{ color: S.muted, fontSize: 13 }}>Ne</span>
          )
        } />
      </Card>

      {/* Certificate */}
      <Card title="Fiskalni certifikat" subtitle={certificate ? "Aktivan" : "Nema aktivnog certifikata"}>
        {certificate ? (
          <>
            <InfoRow label="Izdavač"     value={certificate.issuer || "—"} />
            <InfoRow label="PIB"         value={certificate.tax_id} mono />
            <InfoRow label="Naziv"       value={certificate.business_name || "—"} />
            <InfoRow label="Software code" value={certificate.software_code || "—"} mono />
            <InfoRow label="Maintainer code" value={certificate.maintainer_code || "—"} mono />
            <InfoRow label="Izdat"       value={fmtDate(certificate.issued_at)} />
            <InfoRow label="Ističe" value={
              <span style={{ color: certificate.days_left !== null && certificate.days_left < 60 ? S.red : S.dark }}>
                {fmtDate(certificate.expires_at)}
                {certificate.days_left !== null && (
                  <span style={{ marginLeft: 8, fontSize: 12, color: certificate.days_left < 60 ? S.red : S.muted }}>
                    ({certificate.days_left} dana)
                  </span>
                )}
              </span>
            } />
            <InfoRow label="Status" value={
              <span style={{
                background: certificate.status === "active" ? "#dcfce7" : "#fee2e2",
                color: certificate.status === "active" ? "#059669" : "#dc2626",
                padding: "2px 10px", borderRadius: 10, fontSize: 11, fontWeight: 700, textTransform: "uppercase",
              }}>
                {certificate.status}
              </span>
            } />
          </>
        ) : (
          <div style={{ padding: 20, textAlign: "center", color: S.mutedLight }}>
            Trenutno nema aktivnog certifikata
          </div>
        )}
      </Card>

      {/* Locations & TCRs */}
      <Card title="Lokacije i TCR-ovi" subtitle={`${locations.length} ${locations.length === 1 ? "lokacija" : "lokacija"}`}>
        {locations.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: S.mutedLight }}>Nema registrovanih lokacija</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {locations.map((loc) => (
              <div key={loc.id} style={{
                border: `1px solid ${S.border}`, borderRadius: 8, padding: 12,
                background: loc.is_active ? "white" : "#fafafb",
              }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                  <strong style={{ color: S.dark, fontSize: 14 }}>{dh(loc.location_name)}</strong>
                  <code style={{ background: S.accentSoft, color: S.accent, padding: "2px 8px", borderRadius: 6, fontSize: 11 }}>
                    {loc.business_unit_code}
                  </code>
                  {!loc.is_active && <span style={{ fontSize: 11, color: S.muted }}>· neaktivna</span>}
                </div>
                <div style={{ fontSize: 12, color: S.muted, marginBottom: 8 }}>
                  {dh(loc.address)}, {dh(loc.city)}
                </div>
                {loc.tcrs && loc.tcrs.length > 0 ? (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {loc.tcrs.map((tcr) => (
                      <div key={tcr.id} style={{
                        display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                        background: "#fafafb", borderRadius: 6,
                      }}>
                        <code style={{ fontFamily: S.mono, fontWeight: 700, color: S.dark, fontSize: 12 }}>
                          {tcr.tcr_code}
                        </code>
                        <span style={{ fontSize: 13, color: S.dark }}>
                          {dh(tcr.tcr_name) || "(bez imena)"}
                        </span>
                        <span style={{ fontSize: 11, color: S.muted }}>· {tcr.type}</span>
                        <div style={{ flex: 1 }} />
                        <span style={{
                          fontSize: 10, fontWeight: 700, textTransform: "uppercase",
                          background: tcr.status === "active" ? "#dcfce7" : "#fee2e2",
                          color: tcr.status === "active" ? "#059669" : "#dc2626",
                          padding: "2px 8px", borderRadius: 8,
                        }}>
                          {tcr.status}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: S.mutedLight, fontStyle: "italic" }}>Nema registrovanih TCR-ova</div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* Operators */}
      <Card title="Operateri" subtitle={`${operators.length} ${operators.length === 1 ? "operater" : "operatera"}`}>
        {operators.length === 0 ? (
          <div style={{ padding: 20, textAlign: "center", color: S.mutedLight }}>Nema registrovanih operatera</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 600 }}>
              <thead style={{ background: "#fafafb" }}>
                <tr>
                  <th style={th}>Ime</th>
                  <th style={th}>Email</th>
                  <th style={th}>Uloga</th>
                  <th style={th}>Op. kod</th>
                  <th style={th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {operators.map((op) => (
                  <tr key={op.id} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={{ ...td, fontWeight: 600 }}>{dh(op.full_name)}</td>
                    {/* Username column intentionally removed — it's used to log
                        into the legacy PHP webapp which B2B clients should NOT
                        access. Username is managed by superadmin only. Clients
                        log into the frontend SPAs via email. */}
                    <td style={{ ...td, color: S.muted, fontSize: 12 }}>{op.email || "—"}</td>
                    <td style={td}>
                      <span style={{
                        background: op.role === "admin" ? "#fef3c7" : (op.role === "viewer" ? S.borderSoft : S.accentSoft),
                        color:      op.role === "admin" ? "#92400e" : (op.role === "viewer" ? S.muted : S.accent),
                        padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      }}>
                        {op.role}
                      </span>
                    </td>
                    <td style={{ ...td, fontFamily: S.mono, color: S.muted, fontSize: 12 }}>{op.operator_code}</td>
                    <td style={td}>
                      {op.is_active ? (
                        <span style={{ background: "#dcfce7", color: "#059669", padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700 }}>Aktivan</span>
                      ) : (
                        <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700 }}>Neaktivan</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

function InfoRow({ label, value, mono }) {
  return (
    <div style={{
      display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "9px 0", borderBottom: `1px solid ${S.borderSoft}`, gap: 10,
    }}>
      <div style={{ fontSize: 13, color: S.muted, flexShrink: 0 }}>{label}</div>
      <div style={{
        fontSize: 13, color: S.dark, fontWeight: 600, textAlign: "right",
        fontFamily: mono ? S.mono : S.font,
      }}>
        {value}
      </div>
    </div>
  );
}

function tierBadge(tier) {
  const colors = {
    trial:        { bg: "#f3f4f6", fg: "#6b7280" },
    basic:        { bg: "#dbeafe", fg: "#1e40af" },
    professional: { bg: "#ede9fe", fg: "#5b21b6" },
    enterprise:   { bg: "#fef3c7", fg: "#92400e" },
    standard:     { bg: "#dbeafe", fg: "#1e40af" },
    hotel:        { bg: "#fce7f3", fg: "#9d174d" },
    premium:      { bg: "#fef3c7", fg: "#92400e" },
  };
  const c = colors[tier] || colors.basic;
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "3px 12px", borderRadius: 10, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
      {tier}
    </span>
  );
}

function statusBadge(status) {
  const colors = {
    active:     { bg: "#dcfce7", fg: "#059669" },
    suspended:  { bg: "#fef3c7", fg: "#92400e" },
    expired:    { bg: "#fee2e2", fg: "#dc2626" },
    cancelled:  { bg: "#fee2e2", fg: "#dc2626" },
  };
  const c = colors[status] || colors.active;
  return (
    <span style={{ background: c.bg, color: c.fg, padding: "3px 12px", borderRadius: 10, fontSize: 11, fontWeight: 700, textTransform: "uppercase" }}>
      {status}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════════

// ════════════════════════════════════════════════════════════════════════════
// SUPERADMIN
//
// Strict access: only visible if me.permissions.is_superadmin === true
// (backend gates by company_id=1 && operator_id=1).
//
// Two views:
//   - list:   table of all clients with summary stats
//   - detail: per-client tabs (Info, Cert, Operateri, Lokacije + TCR)
// ════════════════════════════════════════════════════════════════════════════
function SuperadminScreen({ isMobile, isSuperadmin }) {
  const [clientId, setClientId] = useState(null);

  if (!isSuperadmin) {
    return (
      <>
        <TopHeader title="Superadmin" isMobile={isMobile} />
        <div style={{ padding: 28 }}>
          <div style={{
            background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10,
            padding: "14px 18px", display: "flex", alignItems: "center", gap: 8, color: S.red,
          }}>
            <Shield size={16} /> Pristup odbijen. Samo glavni administrator (company_id=1, operator_id=1) može pristupiti.
          </div>
        </div>
      </>
    );
  }

  return clientId ? (
    <ClientDetailView
      isMobile={isMobile}
      clientId={clientId}
      onBack={() => setClientId(null)}
    />
  ) : (
    <ClientsListView
      isMobile={isMobile}
      onSelect={setClientId}
    />
  );
}

// ── CLIENTS LIST ─────────────────────────────────────────────────────────────
function ClientsListView({ isMobile, onSelect }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const r = await api("/b2b/admin-clients");
    setLoading(false);
    if (r.success) setClients(Array.isArray(r.data?.clients) ? r.data.clients : []);
    else setError(r.message || "Greška");
  }, []);
  useEffect(() => { load(); }, [load]);

  const showToast = (type, message, ms = 3500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  const filtered = clients.filter((c) => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (c.company_name || "").toLowerCase().includes(s)
        || (c.pib || "").toLowerCase().includes(s)
        || (c.email || "").toLowerCase().includes(s)
        || (c.city || "").toLowerCase().includes(s);
  });

  return (
    <>
      <TopHeader
        title="Superadmin · Klijenti"
        subtitle={`${clients.length} ${clients.length === 1 ? "klijent" : "klijenata"} ukupno`}
        isMobile={isMobile}
        right={
          <button onClick={() => setShowCreate(true)} style={{ ...S.btn(S.accent), padding: "8px 14px" }}>
            <Plus size={14} /> Novi klijent
          </button>
        }
      />
      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1600 }}>
        <div style={{
          background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
          padding: 14, marginBottom: 14, display: "flex", gap: 8, alignItems: "center",
        }}>
          <Search size={16} color={S.muted} />
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Traži po nazivu, PIB-u, emailu, gradu…"
            style={{ ...S.input, flex: 1, border: "none" }}
          />
        </div>

        {error && <ReportError msg={error} />}
        {loading && !clients.length ? (
          <Skeleton lines={8} />
        ) : filtered.length === 0 ? (
          <div style={{
            background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
            padding: 60, textAlign: "center", color: S.mutedLight,
          }}>
            <Users size={36} color={S.mutedLight} />
            <div style={{ marginTop: 12, fontSize: 14 }}>Nema rezultata</div>
          </div>
        ) : (
          <div style={{
            background: S.card, borderRadius: 12, border: `1px solid ${S.border}`,
            overflow: "hidden",
          }}>
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 1100 }}>
                <thead style={{ background: "#fafafb" }}>
                  <tr>
                    <th style={th}>#</th>
                    <th style={th}>Firma</th>
                    <th style={th}>PIB / ID</th>
                    <th style={th}>Email</th>
                    <th style={th}>Grad</th>
                    <th style={th}>Tier</th>
                    <th style={th}>Status</th>
                    <th style={{ ...th, textAlign: "right" }}>Računa</th>
                    <th style={{ ...th, textAlign: "right" }}>Op.</th>
                    <th style={{ ...th, textAlign: "right" }}>Lok.</th>
                    <th style={{ ...th, textAlign: "right" }}>TCR</th>
                    <th style={th}>Cert</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const certExp = c.cert_expires_at ? new Date(c.cert_expires_at) : null;
                    const certDaysLeft = certExp ? Math.floor((certExp - new Date()) / 86400000) : null;
                    return (
                      <tr
                        key={c.id}
                        onClick={() => onSelect(c.id)}
                        style={{
                          borderBottom: `1px solid ${S.borderSoft}`, cursor: "pointer",
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.background = "#fafafb"}
                        onMouseLeave={(e) => e.currentTarget.style.background = "white"}
                      >
                        <td style={{ ...td, fontFamily: S.mono, color: S.mutedLight }}>{c.id}</td>
                        <td style={{ ...td, fontWeight: 600 }}>
                          {dh(c.company_name)}
                          {c.is_self && (
                            <span style={{ marginLeft: 6, background: "#fef3c7", color: "#92400e", padding: "1px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                              VI
                            </span>
                          )}
                          {!!c.is_pausalac && (
                            <span style={{ marginLeft: 6, background: "#fef3c7", color: "#92400e", padding: "1px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
                              Paušalac
                            </span>
                          )}
                        </td>
                        <td style={{ ...td, fontFamily: S.mono, fontSize: 11, color: S.muted }}>{c.pib}</td>
                        <td style={{ ...td, fontSize: 12, color: S.muted }}>{c.email || "—"}</td>
                        <td style={{ ...td, color: S.muted }}>{dh(c.city) || "—"}</td>
                        <td style={td}>{tierBadge(c.subscription_tier)}</td>
                        <td style={td}>{statusBadge(c.status)}</td>
                        <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{c.invoice_count_total}</td>
                        <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{c.operator_count}</td>
                        <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{c.location_count}</td>
                        <td style={{ ...td, textAlign: "right", fontFamily: S.mono }}>{c.tcr_count}</td>
                        <td style={td}>
                          {certExp ? (
                            <span style={{
                              fontSize: 11, fontWeight: 600,
                              color: certDaysLeft < 30 ? S.red : (certDaysLeft < 90 ? S.orange : S.green),
                            }}>
                              {certDaysLeft > 0 ? `${certDaysLeft}d` : "isteko"}
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, color: S.red }}>nema</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {showCreate && (
        <ClientCreateModal
          isMobile={isMobile}
          onClose={() => setShowCreate(false)}
          onCreated={async (id) => {
            setShowCreate(false);
            await load();
            showToast("success", "Klijent kreiran");
            onSelect(id);
          }}
          onError={(m) => showToast("error", m, 5000)}
        />
      )}

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// ── CLIENT DETAIL VIEW (with tabs) ───────────────────────────────────────────
const CLIENT_DETAIL_TABS = [
  { id: "info",        label: "Info",        icon: Building2 },
  { id: "certificates", label: "Certifikati", icon: Shield },
  { id: "operators",   label: "Operateri",   icon: Users },
  { id: "locations",   label: "Lokacije & TCR", icon: LayoutDashboard },
];

function ClientDetailView({ isMobile, clientId, onBack }) {
  const [tab, setTab] = useState("info");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    const r = await api(`/b2b/admin-client/${clientId}`);
    setLoading(false);
    if (r.success) setData(r.data); else setError(r.message || "Greška");
  }, [clientId]);
  useEffect(() => { load(); }, [load]);

  const showToast = (type, message, ms = 3500) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), ms);
  };

  if (loading && !data) {
    return (
      <>
        <TopHeader title="..." isMobile={isMobile} />
        <div style={{ padding: 28 }}><Skeleton lines={10} /></div>
      </>
    );
  }
  if (error) {
    return (
      <>
        <TopHeader title="Greška" isMobile={isMobile} />
        <div style={{ padding: 28 }}>
          <ReportError msg={error} />
          <div style={{ marginTop: 16 }}>
            <button onClick={onBack} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "8px 14px" }}>
              <ChevronLeft size={14} /> Nazad na listu
            </button>
          </div>
        </div>
      </>
    );
  }
  if (!data) return null;

  const c = data.company;

  return (
    <>
      <TopHeader
        title={dh(c.company_name)}
        subtitle={`PIB: ${c.pib} · ${dh(c.city) || "—"} · ${data.locations.length} lok. · ${data.operators.length} op. · ${data.certificates.length} cert.`}
        isMobile={isMobile}
        right={
          <button onClick={onBack} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "8px 14px" }}>
            <ChevronLeft size={14} /> Lista
          </button>
        }
      />

      <div style={{ padding: isMobile ? 12 : 28, maxWidth: 1300 }}>
        <div style={{
          display: "flex", gap: 4,
          background: S.card, border: `1px solid ${S.border}`,
          borderRadius: 10, padding: 4, marginBottom: 16, overflowX: "auto",
        }} className="hide-scrollbar">
          {CLIENT_DETAIL_TABS.map((t) => (
            <ViewModeBtn key={t.id} active={tab === t.id} onClick={() => setTab(t.id)} icon={t.icon} label={t.label} />
          ))}
        </div>

        {tab === "info"        && <ClientInfoTab        data={data} reload={load} onSaved={() => showToast("success", "Sačuvano")} onError={(m) => showToast("error", m, 5000)} isMobile={isMobile} />}
        {tab === "certificates" && <ClientCertsTab       data={data} reload={load} onSaved={(m) => showToast("success", m || "Sačuvano")} onError={(m) => showToast("error", m, 5000)} isMobile={isMobile} />}
        {tab === "operators"   && <ClientOperatorsTab   data={data} reload={load} onSaved={(m) => showToast("success", m || "Sačuvano")} onError={(m) => showToast("error", m, 5000)} isMobile={isMobile} />}
        {tab === "locations"   && <ClientLocationsTab   data={data} reload={load} onSaved={(m) => showToast("success", m || "Sačuvano")} onError={(m) => showToast("error", m, 5000)} isMobile={isMobile} />}

        {isMobile && <div style={{ height: 80 }} />}
      </div>

      {toast && <FloatingToast toast={toast} isMobile={isMobile} />}
    </>
  );
}

// ── INFO TAB ─────────────────────────────────────────────────────────────────
function ClientInfoTab({ data, reload, onSaved, onError, isMobile }) {
  const c = data.company;
  const [form, setForm] = useState({
    company_name: dh(c.company_name) || "",
    pib:          c.pib || "",
    email:        c.email || "",
    phone:        c.phone || "",
    address:      dh(c.address) || "",
    city:         dh(c.city) || "",
    postal_code:  c.postal_code || "",
    contact_person: dh(c.contact_person) || "",
    business_type: c.business_type || "retail",
    ikof_code:    c.ikof_code || "",
    subscription_tier: c.subscription_tier || "basic",
    subscription_expires_at: c.subscription_expires_at || "",
    monthly_invoice_limit: c.monthly_invoice_limit || 100,
    status:       c.status || "active",
    is_pausalac:  !!c.is_pausalac,
    allowed_apps: c.allowed_apps || "",
  });
  const [busy, setBusy] = useState(false);
  const [confirmSuspend, setConfirmSuspend] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    const r = await api(`/b2b/admin-client-update/${c.id}`, {
      method: "POST",
      body: JSON.stringify(form),
    });
    setBusy(false);
    if (r.success) { reload(); onSaved(); } else onError(r.message || "Greška");
  };

  const toggleSuspend = async () => {
    setBusy(true);
    const path = c.status === "suspended" ? "admin-client-activate" : "admin-client-suspend";
    const r = await api(`/b2b/${path}/${c.id}`, { method: "POST", body: "{}" });
    setBusy(false);
    setConfirmSuspend(false);
    if (r.success) { reload(); onSaved(); } else onError(r.message || "Greška");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Card title="Osnovni podaci">
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
          <Field label="Naziv firme" required>
            <input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} style={S.input} />
          </Field>
          <Field label="PIB" required>
            <input value={form.pib} onChange={(e) => set("pib", e.target.value)} style={{ ...S.input, fontFamily: S.mono }} />
          </Field>
          <Field label="Email"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} style={S.input} /></Field>
          <Field label="Telefon"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} style={S.input} /></Field>
          <Field label="Adresa"><input value={form.address} onChange={(e) => set("address", e.target.value)} style={S.input} /></Field>
          <Field label="Grad"><input value={form.city} onChange={(e) => set("city", e.target.value)} style={S.input} /></Field>
          <Field label="Poštanski broj"><input value={form.postal_code} onChange={(e) => set("postal_code", e.target.value)} style={S.input} /></Field>
          <Field label="Kontakt osoba"><input value={form.contact_person} onChange={(e) => set("contact_person", e.target.value)} style={S.input} /></Field>
          <Field label="Tip biznisa">
            <Select value={form.business_type} onChange={(v) => set("business_type", v)} options={[
              { value: "retail", label: "Trgovina" }, { value: "hotel", label: "Hotel" },
              { value: "restaurant", label: "Restoran" }, { value: "service", label: "Usluga" }, { value: "other", label: "Ostalo" },
            ]} icon={Building2} />
          </Field>
          <Field label="IKOF kod"><input value={form.ikof_code} onChange={(e) => set("ikof_code", e.target.value)} style={{ ...S.input, fontFamily: S.mono }} /></Field>
        </div>
      </Card>

      <Card title="Pretplata">
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
          <Field label="Tier">
            <Select value={form.subscription_tier} onChange={(v) => set("subscription_tier", v)} options={[
              { value: "trial", label: "Trial" }, { value: "basic", label: "Basic" }, { value: "standard", label: "Standard" },
              { value: "professional", label: "Professional" }, { value: "enterprise", label: "Enterprise" },
              { value: "hotel", label: "Hotel" }, { value: "premium", label: "Premium" },
            ]} icon={Shield} />
          </Field>
          <Field label="Pretplata ističe">
            <input type="date" value={form.subscription_expires_at || ""} onChange={(e) => set("subscription_expires_at", e.target.value)} style={{ ...S.input, fontFamily: S.mono }} />
          </Field>
          {/* "Mjesečni limit računa" field removed — we don't limit clients.
              The DB column still exists for legacy compat; defaults to 100
              on new client creation but is never enforced. */}
          <Field label="Aplikacije" hint="Comma-separated: webapp, pos, minipos, hotel">
            <input value={form.allowed_apps} onChange={(e) => set("allowed_apps", e.target.value)} placeholder="webapp,pos,minipos,hotel" style={{ ...S.input, fontFamily: S.mono }} />
          </Field>
        </div>
        <div style={{ marginTop: 14 }}>
          <CheckboxField
            label="Paušalni obveznik"
            checked={form.is_pausalac}
            onChange={(v) => set("is_pausalac", v)}
            hint="Svi proizvodi 0% PDV"
          />
        </div>
      </Card>

      <div style={{
        background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
        padding: 14, display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap",
      }}>
        {c.id !== 1 && (
          <button
            onClick={() => setConfirmSuspend(true)}
            disabled={busy}
            style={{ ...S.btnSm("white", c.status === "suspended" ? S.green : S.red),
                     border: `1px solid ${c.status === "suspended" ? S.green : S.red}`, padding: "9px 14px" }}
          >
            {c.status === "suspended" ? <><Power size={13} /> Aktiviraj</> : <><AlertCircle size={13} /> Suspenduj</>}
          </button>
        )}
        <div style={{ flex: 1 }} />
        <button onClick={save} disabled={busy} style={{ ...S.btn(S.dark), padding: "10px 22px" }}>
          {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />} Sačuvaj
        </button>
      </div>

      {confirmSuspend && (
        <ConfirmModal
          title={c.status === "suspended" ? `Aktivirati "${dh(c.company_name)}"?` : `Suspendovati "${dh(c.company_name)}"?`}
          body={c.status === "suspended" ? "Klijent će ponovo moći koristiti aplikaciju." : "Klijent neće moći koristiti aplikaciju dok ne bude aktiviran."}
          confirmLabel={c.status === "suspended" ? "Aktiviraj" : "Suspenduj"}
          danger={c.status !== "suspended"}
          onConfirm={toggleSuspend}
          onCancel={() => setConfirmSuspend(false)}
          busy={busy}
        />
      )}
    </div>
  );
}

// ── CERTS TAB ────────────────────────────────────────────────────────────────
function ClientCertsTab({ data, reload, onSaved, onError, isMobile }) {
  const c = data.company;
  const certs = data.certificates;
  const [showUpload, setShowUpload] = useState(false);

  const revoke = async (cert) => {
    if (!window.confirm(`Povući certifikat "${cert.file_name}"?`)) return;
    const r = await api(`/b2b/admin-cert-revoke/${cert.id}`, { method: "POST", body: "{}" });
    if (r.success) { reload(); onSaved("Certifikat povučen"); } else onError(r.message || "Greška");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => setShowUpload(true)} style={{ ...S.btn(S.accent), padding: "10px 18px" }}>
          <Upload size={14} /> Uploaduj certifikat
        </button>
      </div>

      {certs.length === 0 ? (
        <div style={{
          background: S.card, border: `1px solid ${S.border}`, borderRadius: 12,
          padding: 60, textAlign: "center", color: S.mutedLight,
        }}>
          <Shield size={36} color={S.mutedLight} />
          <div style={{ marginTop: 12, fontSize: 14 }}>Klijent još nema certifikata</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {certs.map((cert) => (
            <div key={cert.id} style={{
              background: S.card, border: `1px solid ${cert.is_current ? S.accent : S.border}`,
              borderRadius: 12, padding: 14,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                <Shield size={16} color={cert.is_current ? S.accent : S.muted} />
                <code style={{ fontFamily: S.mono, fontSize: 12, fontWeight: 700, color: S.dark }}>{cert.file_name}</code>
                {!!cert.is_current && (
                  <span style={{ background: S.accentSoft, color: S.accent, padding: "2px 8px", borderRadius: 10, fontSize: 10, fontWeight: 700, textTransform: "uppercase" }}>
                    Aktivan
                  </span>
                )}
                {statusBadge(cert.status)}
                <div style={{ flex: 1 }} />
                {cert.status !== "revoked" && (
                  <button onClick={() => revoke(cert)} style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "6px 12px" }}>
                    <X size={13} /> Povuci
                  </button>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 8, fontSize: 12 }}>
                <div><span style={{ color: S.muted }}>Izdavač:</span> {cert.issuer}</div>
                <div><span style={{ color: S.muted }}>PIB:</span> <code style={{ fontFamily: S.mono }}>{cert.tax_id}</code></div>
                <div><span style={{ color: S.muted }}>Izdat:</span> {fmtDate(cert.issued_at)}</div>
                <div>
                  <span style={{ color: S.muted }}>Ističe:</span> {fmtDate(cert.expires_at)}
                  {cert.days_left !== null && (
                    <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600,
                      color: cert.days_left < 30 ? S.red : (cert.days_left < 90 ? S.orange : S.green) }}>
                      ({cert.days_left}d)
                    </span>
                  )}
                </div>
                <div><span style={{ color: S.muted }}>Software kod:</span> <code style={{ fontFamily: S.mono }}>{cert.software_code}</code></div>
                <div><span style={{ color: S.muted }}>Maintainer kod:</span> <code style={{ fontFamily: S.mono }}>{cert.maintainer_code}</code></div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showUpload && (
        <CertUploadModal
          isMobile={isMobile}
          companyId={c.id}
          onClose={() => setShowUpload(false)}
          onUploaded={() => { setShowUpload(false); reload(); onSaved("Certifikat uploadovan"); }}
          onError={onError}
        />
      )}
    </div>
  );
}

// ── OPERATORS TAB ────────────────────────────────────────────────────────────
function ClientOperatorsTab({ data, reload, onSaved, onError, isMobile }) {
  const c = data.company;
  const ops = data.operators;
  const [showCreate, setShowCreate] = useState(false);

  const toggleActive = async (op) => {
    const path = op.is_active ? "admin-operator-deactivate" : "admin-operator-activate";
    const r = await api(`/b2b/${path}/${op.id}`, { method: "POST", body: "{}" });
    if (r.success) { reload(); onSaved(op.is_active ? "Deaktiviran" : "Aktiviran"); } else onError(r.message || "Greška");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => setShowCreate(true)} style={{ ...S.btn(S.accent), padding: "10px 18px" }}>
          <Plus size={14} /> Novi operater
        </button>
      </div>

      {ops.length === 0 ? (
        <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: 60, textAlign: "center", color: S.mutedLight }}>
          <Users size={36} color={S.mutedLight} />
          <div style={{ marginTop: 12, fontSize: 14 }}>Nema operatera</div>
        </div>
      ) : (
        <div style={{ background: S.card, borderRadius: 12, border: `1px solid ${S.border}`, overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, minWidth: 700 }}>
              <thead style={{ background: "#fafafb" }}>
                <tr>
                  <th style={th}>Ime</th>
                  <th style={th}>Username</th>
                  <th style={th}>Email</th>
                  <th style={th}>Uloga</th>
                  <th style={th}>Op. kod</th>
                  <th style={th}>Reg.</th>
                  <th style={th}>Status</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {ops.map((op) => (
                  <tr key={op.id} style={{ borderBottom: `1px solid ${S.borderSoft}` }}>
                    <td style={{ ...td, fontWeight: 600 }}>{dh(op.full_name)}</td>
                    <td style={{ ...td, fontFamily: S.mono, fontSize: 12, color: S.muted }}>{op.username}</td>
                    <td style={{ ...td, color: S.muted, fontSize: 12 }}>{op.email || "—"}</td>
                    <td style={td}>
                      <span style={{
                        background: op.role === "admin" ? "#fef3c7" : (op.role === "viewer" ? S.borderSoft : S.accentSoft),
                        color:      op.role === "admin" ? "#92400e" : (op.role === "viewer" ? S.muted : S.accent),
                        padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      }}>{op.role}</span>
                    </td>
                    <td style={{ ...td, fontFamily: S.mono, fontSize: 11, color: S.muted }}>{op.operator_code}</td>
                    <td style={{ ...td, fontSize: 11, color: S.muted }}>{op.registration_status}</td>
                    <td style={td}>
                      {op.is_active ? (
                        <span style={{ background: "#dcfce7", color: "#059669", padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700 }}>Aktivan</span>
                      ) : (
                        <span style={{ background: "#fee2e2", color: "#dc2626", padding: "2px 8px", borderRadius: 8, fontSize: 11, fontWeight: 700 }}>Neaktivan</span>
                      )}
                    </td>
                    <td style={td}>
                      <button onClick={() => toggleActive(op)} style={{ ...S.btnSm("white", op.is_active ? S.red : S.green), border: `1px solid ${op.is_active ? S.red : S.green}`, padding: "5px 10px" }}>
                        {op.is_active ? <X size={12} /> : <Power size={12} />}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {showCreate && (
        <OperatorCreateModal
          isMobile={isMobile}
          companyId={c.id}
          locations={data.locations}
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); reload(); onSaved("Operater kreiran"); }}
          onError={onError}
        />
      )}
    </div>
  );
}

// ── LOCATIONS TAB ────────────────────────────────────────────────────────────
function ClientLocationsTab({ data, reload, onSaved, onError, isMobile }) {
  const c = data.company;
  const locs = data.locations;
  const [showLocCreate, setShowLocCreate] = useState(false);
  const [tcrForLocation, setTcrForLocation] = useState(null);

  const revokeTcr = async (tcr) => {
    if (!window.confirm(`Povući TCR "${tcr.tcr_code}"?`)) return;
    const r = await api(`/b2b/admin-tcr-revoke/${tcr.id}`, { method: "POST", body: "{}" });
    if (r.success) { reload(); onSaved("TCR povučen"); } else onError(r.message || "Greška");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button onClick={() => setShowLocCreate(true)} style={{ ...S.btn(S.accent), padding: "10px 18px" }}>
          <Plus size={14} /> Nova lokacija
        </button>
      </div>

      {locs.length === 0 ? (
        <div style={{ background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: 60, textAlign: "center", color: S.mutedLight }}>
          <LayoutDashboard size={36} color={S.mutedLight} />
          <div style={{ marginTop: 12, fontSize: 14 }}>Nema lokacija</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {locs.map((loc) => (
            <div key={loc.id} style={{
              background: S.card, border: `1px solid ${S.border}`, borderRadius: 12, padding: 14,
              opacity: loc.is_active ? 1 : 0.6,
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
                <strong style={{ color: S.dark, fontSize: 14 }}>{dh(loc.location_name)}</strong>
                <code style={{ background: S.accentSoft, color: S.accent, padding: "2px 8px", borderRadius: 6, fontSize: 11 }}>
                  {loc.business_unit_code}
                </code>
                {!loc.is_active && <span style={{ fontSize: 11, color: S.muted }}>· neaktivna</span>}
                <span style={{ fontSize: 12, color: S.muted }}>· {dh(loc.address)}, {dh(loc.city)}</span>
                <div style={{ flex: 1 }} />
                <button onClick={() => setTcrForLocation(loc)} style={{ ...S.btnSm(S.accent), padding: "6px 12px" }}>
                  <Plus size={12} /> Novi TCR
                </button>
              </div>
              {loc.tcrs && loc.tcrs.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {loc.tcrs.map((tcr) => (
                    <div key={tcr.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 10px", background: "#fafafb", borderRadius: 6 }}>
                      <code style={{ fontFamily: S.mono, fontWeight: 700, color: S.dark, fontSize: 12 }}>{tcr.tcr_code}</code>
                      <span style={{ fontSize: 13, color: S.dark }}>{dh(tcr.tcr_name) || "(bez imena)"}</span>
                      <span style={{ fontSize: 11, color: S.muted }}>· {tcr.type}</span>
                      <div style={{ flex: 1 }} />
                      {statusBadge(tcr.status)}
                      {tcr.status !== "revoked" && (
                        <button onClick={() => revokeTcr(tcr)} style={{ ...S.btnSm("white", S.red), border: `1px solid ${S.red}`, padding: "4px 10px" }}>
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 12, color: S.mutedLight, fontStyle: "italic" }}>Nema TCR-ova na ovoj lokaciji</div>
              )}
            </div>
          ))}
        </div>
      )}

      {showLocCreate && (
        <LocationCreateModal
          isMobile={isMobile}
          companyId={c.id}
          onClose={() => setShowLocCreate(false)}
          onCreated={() => { setShowLocCreate(false); reload(); onSaved("Lokacija kreirana"); }}
          onError={onError}
        />
      )}

      {tcrForLocation && (
        <TcrCreateModal
          isMobile={isMobile}
          location={tcrForLocation}
          onClose={() => setTcrForLocation(null)}
          onCreated={() => { setTcrForLocation(null); reload(); onSaved("TCR registrovan kod Poreske"); }}
          onError={onError}
        />
      )}
    </div>
  );
}

// ── MODALS ────────────────────────────────────────────────────────────────────
function ConfirmModal({ title, body, confirmLabel, danger, onConfirm, onCancel, busy }) {
  return (
    <div onClick={() => !busy && onCancel()} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 460, width: "100%" }}>
        <h3 style={{ margin: 0, fontSize: 17, color: S.dark }}>{title}</h3>
        <p style={{ fontSize: 14, color: S.muted, marginTop: 10, marginBottom: 20 }}>{body}</p>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>Otkaži</button>
          <button onClick={onConfirm} disabled={busy} style={{ ...S.btnSm(danger ? S.red : S.dark), padding: "10px 16px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />} {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function ClientCreateModal({ isMobile, onClose, onCreated, onError }) {
  const [form, setForm] = useState({
    company_name: "", pib: "", email: "", phone: "",
    address: "", city: "", postal_code: "", contact_person: "",
    business_type: "retail", subscription_tier: "basic",
    monthly_invoice_limit: 100, subscription_expires_at: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.company_name.trim()) { onError("Naziv firme je obavezan"); return; }
    if (!form.pib.trim())          { onError("PIB je obavezan"); return; }
    setBusy(true);
    const r = await api("/b2b/admin-client-create", { method: "POST", body: JSON.stringify(form) });
    setBusy(false);
    if (r.success) onCreated(r.data?.id); else onError(r.message || "Greška");
  };

  return (
    <div onClick={() => !busy && onClose()} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 600, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <h3 style={{ margin: 0, marginBottom: 16, color: S.dark }}>Novi klijent</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <Field label="Naziv firme" required><input value={form.company_name} onChange={(e) => set("company_name", e.target.value)} style={S.input} autoFocus /></Field>
          <Field label="PIB" required><input value={form.pib} onChange={(e) => set("pib", e.target.value)} style={{ ...S.input, fontFamily: S.mono }} /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} style={S.input} /></Field>
          <Field label="Telefon"><input value={form.phone} onChange={(e) => set("phone", e.target.value)} style={S.input} /></Field>
          <Field label="Adresa"><input value={form.address} onChange={(e) => set("address", e.target.value)} style={S.input} /></Field>
          <Field label="Grad"><input value={form.city} onChange={(e) => set("city", e.target.value)} style={S.input} /></Field>
          <Field label="Tier">
            <Select value={form.subscription_tier} onChange={(v) => set("subscription_tier", v)} options={[
              { value: "trial", label: "Trial" }, { value: "basic", label: "Basic" }, { value: "standard", label: "Standard" },
              { value: "premium", label: "Premium" }, { value: "hotel", label: "Hotel" },
            ]} icon={Shield} />
          </Field>
          <Field label="Pretplata ističe"><input type="date" value={form.subscription_expires_at} onChange={(e) => set("subscription_expires_at", e.target.value)} style={S.input} /></Field>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onClose} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>Otkaži</button>
          <button onClick={submit} disabled={busy} style={{ ...S.btn(S.dark), padding: "10px 20px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <CircleCheck size={14} />} Kreiraj
          </button>
        </div>
      </div>
    </div>
  );
}

function CertUploadModal({ isMobile, companyId, onClose, onUploaded, onError }) {
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState("");
  const [taxId, setTaxId] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [address, setAddress] = useState("");
  const [town, setTown] = useState("");
  const [softwareCode, setSoftwareCode] = useState("");
  const [maintainerCode, setMaintainerCode] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const submit = async () => {
    if (!file)     { onError("Izaberite .p12 ili .pfx fajl"); return; }
    if (!password) { onError("Lozinka je obavezna"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("certificate_file", file);
      fd.append("certificate_password", password);
      if (taxId)        fd.append("tax_id", taxId);
      if (businessName) fd.append("business_name", businessName);
      if (address)      fd.append("address", address);
      if (town)         fd.append("town", town);
      if (softwareCode) fd.append("software_code", softwareCode);
      if (maintainerCode) fd.append("maintainer_code", maintainerCode);
      const token = localStorage.getItem(TOKEN_KEY);
      const resp = await fetch(`${API_BASE}/b2b/admin-cert-upload/${companyId}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const j = await resp.json();
      setBusy(false);
      if (j.success) onUploaded();
      else onError(j.message || "Greška");
    } catch (e) {
      setBusy(false);
      onError(e.message || "Greška");
    }
  };

  return (
    <div onClick={() => !busy && onClose()} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 600, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <h3 style={{ margin: 0, marginBottom: 6, color: S.dark }}>Uploaduj fiskalni certifikat</h3>
        <p style={{ fontSize: 13, color: S.muted, marginTop: 0, marginBottom: 16 }}>
          .p12 ili .pfx fajl izdat od CTrust GP CA, Telekom Crne Gore, ili drugog ovlaštenog izdavača.
        </p>

        <Field label="Certifikat fajl" required>
          <input ref={fileRef} type="file" accept=".p12,.pfx" onChange={(e) => setFile(e.target.files?.[0] || null)} style={{ ...S.input, padding: 8 }} />
        </Field>
        <div style={{ marginTop: 12 }}>
          <Field label="Lozinka certifikata" required>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" style={S.input} />
          </Field>
        </div>
        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <Field label="PIB (opciono)"><input value={taxId} onChange={(e) => setTaxId(e.target.value)} style={{ ...S.input, fontFamily: S.mono }} /></Field>
          <Field label="Naziv biznisa (opciono)"><input value={businessName} onChange={(e) => setBusinessName(e.target.value)} style={S.input} /></Field>
          <Field label="Adresa (opciono)"><input value={address} onChange={(e) => setAddress(e.target.value)} style={S.input} /></Field>
          <Field label="Grad (opciono)"><input value={town} onChange={(e) => setTown(e.target.value)} style={S.input} /></Field>
          <Field label="Software kod"><input value={softwareCode} onChange={(e) => setSoftwareCode(e.target.value)} placeholder="ss123ss123" style={{ ...S.input, fontFamily: S.mono }} /></Field>
          <Field label="Maintainer kod"><input value={maintainerCode} onChange={(e) => setMaintainerCode(e.target.value)} style={{ ...S.input, fontFamily: S.mono }} /></Field>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onClose} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>Otkaži</button>
          <button onClick={submit} disabled={busy} style={{ ...S.btn(S.dark), padding: "10px 20px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <Upload size={14} />} Uploaduj
          </button>
        </div>
      </div>
    </div>
  );
}

function OperatorCreateModal({ isMobile, companyId, locations, onClose, onCreated, onError }) {
  const [form, setForm] = useState({
    full_name: "", username: "", password: "", email: "",
    role: "operator", operator_type: "REGULAR", id_type: "ID",
    personal_id: "", location_id: locations[0]?.id || "",
    valid_from: new Date().toISOString().slice(0, 10), valid_to: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.full_name.trim()) { onError("Ime i prezime su obavezni"); return; }
    if (!form.username.trim())  { onError("Username je obavezan"); return; }
    if (form.password.length < 6) { onError("Lozinka mora imati najmanje 6 karaktera"); return; }
    setBusy(true);
    const r = await api(`/b2b/admin-operator-create/${companyId}`, {
      method: "POST",
      body: JSON.stringify({
        ...form,
        location_id: form.location_id || null,
      }),
    });
    setBusy(false);
    if (r.success) onCreated(); else onError(r.message || "Greška");
  };

  return (
    <div onClick={() => !busy && onClose()} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 600, width: "100%", maxHeight: "90vh", overflowY: "auto" }}>
        <h3 style={{ margin: 0, marginBottom: 16, color: S.dark }}>Novi operater</h3>
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 12 }}>
          <Field label="Ime i prezime" required><input value={form.full_name} onChange={(e) => set("full_name", e.target.value)} style={S.input} autoFocus /></Field>
          <Field label="Username" required><input value={form.username} onChange={(e) => set("username", e.target.value)} style={{ ...S.input, fontFamily: S.mono }} autoComplete="off" /></Field>
          <Field label="Lozinka" required hint="Najmanje 6 karaktera"><input type="password" value={form.password} onChange={(e) => set("password", e.target.value)} autoComplete="new-password" style={S.input} /></Field>
          <Field label="Email"><input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} style={S.input} /></Field>
          <Field label="Uloga">
            <Select value={form.role} onChange={(v) => set("role", v)} options={[
              { value: "operator", label: "Operater" }, { value: "admin", label: "Admin" }, { value: "viewer", label: "Viewer" },
            ]} icon={UserIcon} />
          </Field>
          <Field label="Tip operatera">
            <Select value={form.operator_type} onChange={(v) => set("operator_type", v)} options={[
              { value: "REGULAR", label: "REGULAR" }, { value: "TEMPORARY", label: "TEMPORARY" },
            ]} icon={UserIcon} />
          </Field>
          <Field label="Lični broj (JMBG/Pasoš)"><input value={form.personal_id} onChange={(e) => set("personal_id", e.target.value)} style={{ ...S.input, fontFamily: S.mono }} /></Field>
          <Field label="Tip dokumenta">
            <Select value={form.id_type} onChange={(v) => set("id_type", v)} options={[
              { value: "ID", label: "ID karta" }, { value: "PASSPORT", label: "Pasoš" }, { value: "OTHER", label: "Ostalo" },
            ]} icon={UserIcon} />
          </Field>
          <Field label="Lokacija">
            <Select value={String(form.location_id || "")} onChange={(v) => set("location_id", v)} options={[
              { value: "", label: "— Bez lokacije —" },
              ...locations.map((l) => ({ value: String(l.id), label: dh(l.location_name) })),
            ]} icon={LayoutDashboard} />
          </Field>
          <Field label="Važi od"><input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} style={S.input} /></Field>
          <Field label="Važi do (opciono)"><input type="date" value={form.valid_to} onChange={(e) => set("valid_to", e.target.value)} style={S.input} /></Field>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onClose} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>Otkaži</button>
          <button onClick={submit} disabled={busy} style={{ ...S.btn(S.dark), padding: "10px 20px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Kreiraj
          </button>
        </div>
      </div>
    </div>
  );
}

function LocationCreateModal({ isMobile, companyId, onClose, onCreated, onError }) {
  const [form, setForm] = useState({
    location_name: "", business_unit_code: "", address: "", city: "", has_cash_sales: false,
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.location_name.trim())      { onError("Naziv lokacije je obavezan"); return; }
    if (!form.business_unit_code.trim()) { onError("Business unit kod je obavezan"); return; }
    if (!form.address.trim())            { onError("Adresa je obavezna"); return; }
    if (!form.city.trim())               { onError("Grad je obavezan"); return; }
    setBusy(true);
    const r = await api(`/b2b/admin-location-create/${companyId}`, { method: "POST", body: JSON.stringify(form) });
    setBusy(false);
    if (r.success) onCreated(); else onError(r.message || "Greška");
  };

  return (
    <div onClick={() => !busy && onClose()} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 500, width: "100%" }}>
        <h3 style={{ margin: 0, marginBottom: 6, color: S.dark }}>Nova lokacija</h3>
        <p style={{ fontSize: 13, color: S.muted, marginTop: 0, marginBottom: 16 }}>
          Business unit code dobijate od Poreske uprave prilikom registracije lokacije.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Naziv lokacije" required><input value={form.location_name} onChange={(e) => set("location_name", e.target.value)} style={S.input} autoFocus /></Field>
          <Field label="Business unit code" required hint="npr. lc460yo052"><input value={form.business_unit_code} onChange={(e) => set("business_unit_code", e.target.value)} style={{ ...S.input, fontFamily: S.mono }} /></Field>
          <Field label="Adresa" required><input value={form.address} onChange={(e) => set("address", e.target.value)} style={S.input} /></Field>
          <Field label="Grad" required><input value={form.city} onChange={(e) => set("city", e.target.value)} style={S.input} /></Field>
          <CheckboxField label="Ima gotovinsku prodaju" checked={form.has_cash_sales} onChange={(v) => set("has_cash_sales", v)} />
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onClose} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>Otkaži</button>
          <button onClick={submit} disabled={busy} style={{ ...S.btn(S.dark), padding: "10px 20px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <Plus size={14} />} Kreiraj
          </button>
        </div>
      </div>
    </div>
  );
}

function TcrCreateModal({ isMobile, location, onClose, onCreated, onError }) {
  const [form, setForm] = useState({
    tcr_name: "", tcr_type: "REGULAR",
    valid_from: new Date().toISOString().slice(0, 10), valid_to: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.tcr_name.trim()) { onError("Naziv TCR-a je obavezan"); return; }
    setBusy(true);
    const r = await api(`/b2b/admin-tcr-create/${location.id}`, { method: "POST", body: JSON.stringify(form) });
    setBusy(false);
    if (r.success) onCreated(); else onError(r.message || "Greška");
  };

  return (
    <div onClick={() => !busy && onClose()} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200,
      display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
    }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: S.card, borderRadius: 14, padding: 24, maxWidth: 500, width: "100%" }}>
        <h3 style={{ margin: 0, marginBottom: 6, color: S.dark }}>Novi TCR — {dh(location.location_name)}</h3>
        <p style={{ fontSize: 13, color: S.muted, marginTop: 0, marginBottom: 16 }}>
          Ovo će poslati zahtjev Poreskoj upravi za registraciju nove kase. Potrebno je da klijent ima aktivan certifikat.
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <Field label="Naziv TCR-a" required hint="npr. Magazin, Šank, Pos 1"><input value={form.tcr_name} onChange={(e) => set("tcr_name", e.target.value)} style={S.input} autoFocus /></Field>
          <Field label="Tip TCR-a">
            <Select value={form.tcr_type} onChange={(v) => set("tcr_type", v)} options={[
              { value: "REGULAR", label: "REGULAR (klasična kasa)" },
              { value: "VENDING", label: "VENDING (samouslužni automat)" },
            ]} icon={LayoutDashboard} />
          </Field>
          <Field label="Važi od" required><input type="date" value={form.valid_from} onChange={(e) => set("valid_from", e.target.value)} style={S.input} /></Field>
          <Field label="Važi do (opciono)"><input type="date" value={form.valid_to} onChange={(e) => set("valid_to", e.target.value)} style={S.input} /></Field>
        </div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 20 }}>
          <button onClick={onClose} disabled={busy} style={{ ...S.btnSm("white", S.dark), border: `1px solid ${S.border}`, padding: "10px 16px" }}>Otkaži</button>
          <button onClick={submit} disabled={busy} style={{ ...S.btn(S.dark), padding: "10px 20px" }}>
            {busy ? <Loader2 size={14} className="spin" /> : <Send size={14} />} Pošalji Poreskoj
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════

function ComingSoon({ view, isMobile }) {
  const item = NAV_ITEMS.find((n) => n.id === view);
  return (
    <>
      <TopHeader title={item?.label || "Uskoro"} isMobile={isMobile} />
      <div style={{ padding: isMobile ? 16 : 28 }}>
        <div style={{
          background: S.card, borderRadius: 14, padding: 40,
          textAlign: "center", border: `1px solid ${S.border}`,
        }}>
          <div style={{
            display: "inline-flex", width: 64, height: 64, borderRadius: 16,
            background: S.accentSoft, alignItems: "center", justifyContent: "center",
            marginBottom: 16,
          }}>
            {item?.icon ? <item.icon size={30} color={S.accent} /> : <ArrowUpRight size={30} color={S.accent} />}
          </div>
          <h2 style={{ margin: 0, fontSize: 20, color: S.dark }}>Uskoro dostupno</h2>
          <p style={{ color: S.muted, fontSize: 14, marginTop: 8, marginBottom: 0 }}>
            Ova sekcija stiže u sljedećoj fazi razvoja.
          </p>
        </div>
      </div>
    </>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// ROOT — auth gate
// ════════════════════════════════════════════════════════════════════════════
export default function App() {
  const [auth, setAuth] = useState(() => {
    try {
      const t = localStorage.getItem(TOKEN_KEY);
      const u = localStorage.getItem(USER_KEY);
      if (t && u && u !== "undefined") {
        return { token: t, user: JSON.parse(u) };
      }
    } catch (e) {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
    }
    return null;
  });

  const handleLogin = (token, user) => {
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    setAuth({ token, user });
  };

  const handleLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setAuth(null);
  };

  if (!auth) return <LoginScreen onLogin={handleLogin} />;
  return <B2BApp user={auth.user} onLogout={handleLogout} />;
}
